import React, { useState, useRef, useCallback, useEffect } from "react";
import { initializeApp, getApp, getApps } from "firebase/app";
import { getFirestore, doc, setDoc, getDoc, onSnapshot, collection, getDocs, query, where } from "firebase/firestore";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";

// ===================== Firebase 初期化 =====================
const firebaseConfig = {
  apiKey: "AIzaSyBasqGPLXHlA64XhFUN3X3GaTcwbLP-kdc",
  authDomain: "ms-payroll.firebaseapp.com",
  projectId: "ms-payroll",
  storageBucket: "ms-payroll.firebasestorage.app",
  messagingSenderId: "746031943325",
  appId: "1:746031943325:web:65c6ca539ab53ef8414e74",
};
const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);

// v63: ms-salon-4b402 Firebase secondary app（読み取り専用）
const msSalonConfig = {
  apiKey: "AIzaSyBkeghOscBJWkGgceGIMpQ1oYvefK95dLs",
  authDomain: "ms-salon-4b402.firebaseapp.com",
  projectId: "ms-salon-4b402",
  storageBucket: "ms-salon-4b402.firebasestorage.app",
  messagingSenderId: "37454013538",
  appId: "1:37454013538:web:22275fb32d3432df895d3b",
};
const msSalonApp = !getApps().find(a => a.name === "ms-salon")
  ? initializeApp(msSalonConfig, "ms-salon")
  : getApp("ms-salon");
const msSalonDb = getFirestore(msSalonApp);

// サロン指名料アプリ（ms-payroll-kouyama）secondary app。
// 歩合データは管理者ログインが必要なので、取り込み時にGoogleでログインする。
const salonAppConfig = {
  apiKey: "AIzaSyAL2VcTza5uIfdxdmZ72p2484wCkGNRqBA",
  authDomain: "ms-payroll-kouyama.firebaseapp.com",
  projectId: "ms-payroll-kouyama",
  storageBucket: "ms-payroll-kouyama.firebasestorage.app",
  messagingSenderId: "240834766232",
  appId: "1:240834766232:web:28ce0b947bdcee890bb7ed",
};
const salonApp = !getApps().find(a => a.name === "salon-app")
  ? initializeApp(salonAppConfig, "salon-app")
  : getApp("salon-app");
const salonAppDb = getFirestore(salonApp);
const salonAppAuth = getAuth(salonApp);

// v140: Ms-guarantee（案件スケジュール）secondary app。
// 現場タブに日付・現場名を取り込む。金額（ギャランティ）は取り込まず、手入力のまま。
const guaranteeConfig = {
  apiKey: "AIzaSyBBh3cGfGi2o7s09J7-btl0DxF9F3E6w74",
  authDomain: "ms-guarantee.firebaseapp.com",
  projectId: "ms-guarantee",
  storageBucket: "ms-guarantee.firebasestorage.app",
  messagingSenderId: "566920516141",
  appId: "1:566920516141:web:0cf9582a2fa581edc21ea4",
};
const guaranteeApp = !getApps().find(a => a.name === "guarantee-app")
  ? initializeApp(guaranteeConfig, "guarantee-app")
  : getApp("guarantee-app");
const guaranteeDb = getFirestore(guaranteeApp);
const guaranteeAuth = getAuth(guaranteeApp);

// ms-payroll の staff.type → Ms-guarantee 側の固定スタッフ名（姓）
const GUARANTEE_STAFF_NAME = {
  matsumoto: "松本",
  takeuchi: "竹内",
  fukazawa: "深澤",
  aomi: "青海",
  takayama: "髙山",
  akino: "秋野",
  hirasawa: "平澤",
  kasugai: "春日井",
};

async function ensureGuaranteeLogin() {
  if (guaranteeAuth.currentUser) return;
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  await signInWithPopup(guaranteeAuth, provider);
}

// Ms-guarantee の案件 → そのスタッフが担当の「日付・現場名」だけを取り出す
//   data/schedule_YYYY_MM の items に、1案件1レコードで入っている
//   期間案件（rangeStart〜rangeEnd）は、その月に入る日ごとに1行ずつ出す
function guaranteeStaffNames(item) {
  const names = [item && item.staff, ...((item && item.staffList) || [])].filter(Boolean);
  const outNames = (item && item.outNames) || {};
  // 外注スロットは実名に置き換わるので、固定スタッフ名の判定には元の名前を使う
  return names.map((raw, i) => ({ raw: String(raw).trim(), out: String(outNames[`slot_${i}`] || "").trim() }));
}
function guaranteeDays(item, monthKey) {
  const [yy, mm] = monthKey.split("-").map(Number);
  const parse = (raw) => {
    const m = String(raw || "").match(/(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  };
  const start = parse(item.rangeStart || item.date);
  if (!start) return [];
  const end = parse(item.rangeEnd) || start;
  const out = [];
  for (let d = new Date(start); d <= end && out.length < 62; d.setDate(d.getDate() + 1)) {
    if (d.getFullYear() === yy && d.getMonth() + 1 === mm) out.push(`${d.getMonth() + 1}/${d.getDate()}`);
  }
  return out;
}
async function fetchGuaranteeJobs(monthKey, staffType) {
  const name = GUARANTEE_STAFF_NAME[staffType];
  if (!name) return [];
  await ensureGuaranteeLogin();
  const [yy, mm] = monthKey.split("-");
  const snap = await getDoc(doc(guaranteeDb, "data", `schedule_${yy}_${mm}`));
  const items = snap.exists() ? (snap.data().items || []) : [];
  const rows = [];
  items.forEach((it) => {
    if (!it || it.removed) return;
    const mine = guaranteeStaffNames(it).some(n => n.raw === name);
    if (!mine) return;
    const client = String(it.content || "").trim();
    guaranteeDays(it, monthKey).forEach((date, di) => {
      rows.push({
        gid: `${it.sid || it.groupId || client}_${date}`,
        date,
        client,
        _order: date.split("/").map(Number),
        _di: di,
      });
    });
  });
  rows.sort((a, b) => (a._order[0] - b._order[0]) || (a._order[1] - b._order[1]) || String(a.client).localeCompare(String(b.client)));
  return rows;
}

// 現場タブの取り込み差分（v137のサロンと同じ考え方）
//   変更なし：そのまま（手入力したギャランティも残る）
//   更新：日付・現場名だけ書き換え、ギャランティはそのまま
//   追加：日付・現場名だけ入れる（ギャランティは空欄）
//   削除：Ms-guarantee から消えた行を外す
function diffGuaranteeJobs(currentJobs, fetched, nid) {
  const cur = (currentJobs || []);
  const byGid = new Map();
  cur.forEach((j, i) => { if (j.gid) byGid.set(j.gid, { j, i }); });
  const stats = { same: 0, updated: 0, added: 0, removed: 0 };
  const status = new Map();
  const used = new Set();
  const result = [];
  cur.forEach((j) => { if (!j.gid) result.push(j); });   // 手入力の行はそのまま
  fetched.forEach((f) => {
    const hit = byGid.get(f.gid);
    if (hit) {
      used.add(f.gid);
      const same = (hit.j.date || "") === f.date && (hit.j.client || "") === f.client;
      if (same) { stats.same++; status.set(f.gid, "same"); result.push(hit.j); }
      else { stats.updated++; status.set(f.gid, "updated"); result.push({ ...hit.j, date: f.date, client: f.client }); }
    } else {
      stats.added++; status.set(f.gid, "added");
      result.push({ id: nid(), gid: f.gid, date: f.date, client: f.client, guarantee: null, payment: null });
    }
  });
  const removed = cur.filter(j => j.gid && !used.has(j.gid));
  stats.removed = removed.length;
  return { result, stats, status, removed };
}

// ms-payroll の staff.type → サロン指名料アプリ側の staffId
const SALON_APP_STAFF_ID = {
  takayama: "kouyama",
  takeuchi: "takeuchi",
  fukazawa: "fukazawa",
  aomi: "aomi",
  matsumoto: "matsumoto",
};

async function ensureSalonAppLogin() {
  if (salonAppAuth.currentUser) return;
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  await signInWithPopup(salonAppAuth, provider);
}

// その月・そのスタッフの行を読む（salonCommission/<staffId>_<YYYY-MM>）
async function fetchSalonAppRows(monthKey, staffType) {
  const id = SALON_APP_STAFF_ID[staffType];
  if (!id) return [];
  await ensureSalonAppLogin();
  const snap = await getDoc(doc(salonAppDb, "salonCommission", `${id}_${monthKey}`));
  return snap.exists() ? (snap.data().rows || []) : [];
}

const yenStr = n => (n == null ? "" : Math.round(n).toLocaleString("ja-JP"));
const nz = n => (n == null ? 0 : Number(n) || 0);

// 髙山（固定単価）：内容の select の選択肢（TAKAYAMA_NOM_OPTIONS）に合わせた文字列
function takayamaHairMemo(row) {
  const label = String(row.hairLabel || "");
  if (label.includes("【A】")) return "ヘアセットA (400)";
  if (label.includes("【B】")) return "ヘアセットB (400)";
  if (label.includes("【C】")) return "ヘアセット C (600)";
  if (nz(row.hairPrice) === 600) return "ヘアセット C (600)";
  if (nz(row.hairPrice) === 400) return "ヘアセットA (400)";
  return ""; // 選択肢に無いものは空にして comment に品目名を残す
}
// v136: メイクの内容。900円の組み合わせ品目（DDアーティスト割引価格・アーティストメイク・証明ヘアメイク）は 900 の選択肢に合わせる
function takayamaMakeMemo(row) {
  if (nz(row.makePrice) === 900) {
    return String(row.makeLabel || "").includes("証明") ? "証明ヘアメイク (900)" : "アーティストヘアメイク (900)";
  }
  return "メイク (500)";
}
function takayamaHanbaiMemo(row) {
  const t = String(row.note || "") + String(row.makeLabel || "") + String(row.hairLabel || "");
  if (t.includes("パイラ") || t.includes("バイラ")) return "バイラナイト販売手数料";
  return "リファ販売手数料";
}

// サロン指名料アプリの1行から「お客様支払額」を求める（管理画面と同じ優先順位）
// 1. customerPay（手入力・一括登録で入れた値）
// 2. Square取り込みの行は squareItems の金額合計
// 3. どちらも無ければ null
function appRowPay(row) {
  if (row.customerPay != null) return Number(row.customerPay) || 0;
  if (row.source === "square" && Array.isArray(row.squareItems) && row.squareItems.length) {
    return row.squareItems.reduce((a, it) => a + (Number(it.amount) || 0), 0);
  }
  return null;
}

// v137: 再取り込みの差分判定
// アプリの1行（appRowId）→ ms-payroll の行（髙山は複数行）の中身から指紋を作る。
// 前回取り込んだ時点の指紋を appHash として各行に持たせ、次の取り込みで比べる。
const APP_HASH_FIELDS = ["date", "name", "payment", "discount", "nom100", "nomShimei", "nomSoucho", "nomHanbai", "nomCancel", "memo", "comment"];
function salonGroupHash(groupRows) {
  const norm = v => (v === undefined || v === "" ? null : v);
  const str = JSON.stringify((groupRows || []).map(r => APP_HASH_FIELDS.map(f => norm(r[f]))));
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36) + "-" + str.length.toString(36);
}
// appRowId ごとにまとめる（出てきた順を保つ）
function groupByAppRow(rows) {
  const map = new Map();
  (rows || []).forEach(r => {
    if (!r.appRowId) return;
    if (!map.has(r.appRowId)) map.set(r.appRowId, []);
    map.get(r.appRowId).push(r);
  });
  return map;
}
// 今のサロン行と、アプリから変換した行を比べて、取り込み後の行と件数を出す
//   変更なし：今の行をそのまま残す（ms-payroll で直したメモなども残る）
//   更新：アプリの新しい内容に入れ替える
//   追加：アプリに増えた行
//   削除：アプリから消えた行（ms-payroll からも外す）
//   手入力の行（appRowId なし）はそのまま
function diffSalonImport(currentSalon, converted) {
  const cur = groupByAppRow(currentSalon);
  const next = groupByAppRow(converted);
  const status = new Map(); // appRowId -> same | updated | added
  const stats = { same: 0, updated: 0, added: 0, removed: 0 };
  const appRows = [];
  next.forEach((group, id) => {
    const hash = salonGroupHash(group);
    const tagged = group.map(r => ({ ...r, appHash: hash }));
    const old = cur.get(id);
    if (!old) { status.set(id, "added"); stats.added++; appRows.push(...tagged); return; }
    // 前回の指紋があればそれと比べる。無ければ（v136以前に取り込んだ行）今の中身と比べる
    const oldHash = old[0].appHash || salonGroupHash(old);
    if (oldHash === hash) {
      status.set(id, "same"); stats.same++;
      appRows.push(...old.map(r => (r.appHash ? r : { ...r, appHash: hash })));
    } else {
      status.set(id, "updated"); stats.updated++; appRows.push(...tagged);
    }
  });
  const removed = [];
  cur.forEach((group, id) => { if (!next.has(id)) { removed.push(group); stats.removed++; } });
  const manual = (currentSalon || []).filter(r => !r.appRowId);
  return { result: [...manual, ...appRows], status, stats, removed, manualCount: manual.length };
}

// サロン指名料アプリの行 → ms-payroll の salon 行
function salonAppRowsToSalon(rows, staffType, monthKey) {
  const today = new Date().toISOString().slice(0, 10);
  const base = row => ({
    id: nid(),
    date: isoDateToMD(row.date),
    name: row.customer || "",
    payment: null,
    discount: null,
    nom100: null,
    nomination: null,
    memo: "",
    comment: String(row.note || "").trim(),
    appRowId: row.id,          // 二重取り込み防止（再取り込み時に入れ替える）
    importedFrom: "salon-app",
    importedAt: today,
  });
  const out = [];

  // v136: 日付順に並べて取り込む。髙山は同じ日のうち早朝料金が付いた行を先頭にする（アプリの一覧と同じ並び）
  const n0 = n => (n == null ? 0 : Number(n) || 0);
  const ordered = [...rows].sort((a, b) => {
    const d = String(a.date || "9999-99-99").localeCompare(String(b.date || "9999-99-99"));
    if (d !== 0) return d;
    if (staffType === "takayama") {
      const ya = n0(a.yakinAmount) > 0 ? 0 : 1, yb = n0(b.yakinAmount) > 0 ? 0 : 1;
      if (ya !== yb) return ya - yb;
    }
    return n0(a.createdAt) - n0(b.createdAt);
  });

  for (const row of ordered) {
    if (staffType === "takayama") {
      // 固定単価：項目ごとに1行。memo は選択肢の文字列に合わせる
      // 髙山は memo が選択式なので、アプリの自由記入メモは comment に載せる
      // お客様支払は最初の1行目にだけ載せる（サロンタブの見え方と同じ）
      const pay = appRowPay(row);
      const startLen = out.length;
      if (row.makePrice != null) out.push({ ...base(row), memo: takayamaMakeMemo(row), nom100: nz(row.makePrice) });
      if (row.hairPrice != null) {
        const memo = takayamaHairMemo(row);
        out.push({
          ...base(row),
          memo,
          nom100: nz(row.hairPrice),
          // 選択肢に無い品目名は comment に。メモがあれば「品目名 / メモ」
          comment: [memo ? "" : String(row.hairLabel || ""), String(row.note || "").trim()].filter(Boolean).join(" / "),
        });
      }
      if (row.yakinAmount != null && nz(row.yakinAmount) > 0) out.push({ ...base(row), memo: "早朝深夜料金", nom100: nz(row.yakinAmount) });
      if (row.shimei != null && nz(row.shimei) > 0) out.push({ ...base(row), memo: "指名料 (500)", nom100: nz(row.shimei) });
      if (row.hanbai != null && nz(row.hanbai) > 0) out.push({ ...base(row), memo: takayamaHanbaiMemo(row), nom100: nz(row.hanbai) });
      if (row.cancel != null && nz(row.cancel) > 0) out.push({ ...base(row), memo: "当日無断キャンセル", nom100: nz(row.cancel) });
      if (pay != null && out.length > startLen) {
        out[startLen] = { ...out[startLen], payment: pay };
      }
      continue;
    }

    // 率方式（松本・竹内・深澤・青海）：1件 → 1行。
    // payment = 施術の支払、discount = 割引。率の計算は ms-payroll 側で行う（（支払 − 割引）× 率）。
    // nom100 = スタッフ指名料 ＋ 早朝深夜 ＋ 販売手数料 ＋ キャンセル料（100%扱い）
    const parts = [];
    if (row.shimei != null && nz(row.shimei) > 0) parts.push(`スタッフ指名料 ${yenStr(row.shimei)}`);
    if (row.yakinAmount != null && nz(row.yakinAmount) > 0) parts.push(`早朝深夜 ${yenStr(row.yakinAmount)}`);
    if (row.hanbai != null && nz(row.hanbai) > 0) parts.push(`販売手数料 ${yenStr(row.hanbai)}`);
    if (row.cancel != null && nz(row.cancel) > 0) parts.push(`キャンセル料 ${yenStr(row.cancel)}`);
    if (row.source === "manual") parts.push("手入力");
    const n100 = nz(row.shimei) + nz(row.yakinAmount) + nz(row.hanbai) + nz(row.cancel);
    // v136: アプリが「支払（施術の定価）」と「割引（会計のクーポン全額）」を分けて持っている行は、
    // 歩合表と同じく payment ＝ 支払、discount ＝ 割引 で入れる（65% ＝（支払 − 割引）× 率）。
    // 分けていない行（手入力・古い行）は、これまでどおり payment ＝ 割引後の施術額。
    const hasSplit = row.payGross != null && row.discount != null &&
      row.serviceAmount != null && nz(row.payGross) - nz(row.discount) === nz(row.serviceAmount);
    const payment = hasSplit ? nz(row.payGross) : (row.serviceAmount != null ? nz(row.serviceAmount) : null);
    const discount = hasSplit && nz(row.discount) > 0 ? nz(row.discount) : null;
    if (payment == null && n100 === 0) continue; // 何も入っていない行は送らない
    const split = isSplitMonth(monthKey);
    // v139: キャンセル代は早朝深夜と分けて、専用の項目に入れる
    out.push({
      ...base(row),
      payment,
      discount,
      nom100:    split ? null : (n100 > 0 ? n100 : null),
      nomShimei: split ? (row.shimei != null ? nz(row.shimei) : null) : undefined,
      nomSoucho: split ? (row.yakinAmount != null ? nz(row.yakinAmount) : null) : undefined,
      nomHanbai: split ? (row.hanbai != null ? nz(row.hanbai) : null) : undefined,
      nomCancel: split ? (row.cancel != null ? nz(row.cancel) : null) : undefined,
      // 率方式の自由記入欄は memo（既存の ms-salon 取り込みと同じ扱い）
      memo: [parts.join("／"), String(row.note || "").trim()].filter(Boolean).join("\n"),
      comment: "",
    });
  }
  return out;
}

// v63: スタッフ type → ms-salon の staff 名 マッピング
// ms-salon は和名（"竹内", "深澤" 等）で staff フィールドを保存している
const MS_SALON_STAFF_NAME = {
  takeuchi:  "竹内",
  fukazawa:  "深澤",
  matsumoto: "松本",
  takayama:  "髙山",
  aomi:      "青海",
  kasugai:   "春日井",
};

// v63: ms-salon の records/{year}/entries から 該当月・該当スタッフの売上行を取得
// 戻り値: [{ id, date, name, staff, amount, payment, paymentLabel, memo, squarePaymentId, receipt_url, ... }]
async function fetchMsSalonRecords(year, monthNum, staffType) {
  const salonStaffName = MS_SALON_STAFF_NAME[staffType];
  if (!salonStaffName) return [];
  const yearStr = String(year);
  const monthStr = String(monthNum).padStart(2, "0");
  const datePrefix = `${yearStr}-${monthStr}`;
  try {
    // staff フィールドでフィルタ
    const q = query(
      collection(msSalonDb, "records", yearStr, "entries"),
      where("staff", "==", salonStaffName)
    );
    const snap = await getDocs(q);
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // 当月のみ + 売上のみ（外注費・現金化・回収金は除外）
    return all.filter(r =>
      r.date && r.date.startsWith(datePrefix) &&
      !r.isCollected && !r.isGaichuu && !r.isGenkinka &&
      r.name && r.name !== "回収金" && r.name !== "外注費" && r.name !== "現金化"
    );
  } catch (e) {
    console.error("ms-salon 取得エラー:", e);
    throw e;
  }
}

// v63: "YYYY-MM-DD" → "M/D" 変換
function isoDateToMD(iso) {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}`;
}

// Firestoreへの保存（デバウンス付き）
let saveTimer = null;
const saveToFirestore = (staffList) => {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    try {
      await setDoc(doc(db, "payroll", "data"), { staffList, updatedAt: Date.now() });
      console.log("✅ 保存完了");
    } catch (e) {
      console.error("❌ 保存失敗:", e);
    }
  }, 1500);
};

// ===================== ユーティリティ =====================
const fmt = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  // v102/v103: 数字と「円」の間にNBSP（改行されない空白）
  if (n === 0) return "0\u00a0円";
  return n.toLocaleString("ja-JP") + "\u00a0円";
};
const fmtN = (v) => {
  // 数値のみ（入力欄の表示値）
  if (v == null || v === "") return "";
  const n = Number(v);
  if (isNaN(n)) return "";
  return n.toLocaleString("ja-JP");
};
// v32: 円マークなし表示（個別行の金額用、合計以外）
// "—" は値が無いとき、"0" は0円表示
const fmtNum = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString("ja-JP");
};

// ===================== プルダウン選択肢 =====================
// 髙山サロン指名料プルダウン
const TAKAYAMA_NOM_OPTIONS = [
  { label:"ヘアセットA (400)", value:400 },
  { label:"ヘアセットB (400)", value:400 },
  { label:"ヘアセット C (600)", value:600 },
  { label:"メイク (500)", value:500 },
  { label:"指名料 (500)", value:500 },
  { label:"宣材写真 (900)", value:900 },
  { label:"早朝深夜料金", value:null },
  { label:"(300)", value:300 },
  { label:"アーティストヘアメイク (900)", value:900 },
  { label:"リファ/ストレート (900)", value:900 },
  { label:"メイクレッスン (1000)", value:1000 },
  { label:"ヘアセットレッスン (1000)", value:1000 },
  { label:"卒業式ヘアセット (600)", value:600 },
  { label:"卒業式ヘアメイク (900)", value:900 },
  { label:"成人式(卒業式）ヘアメイク (1,100)", value:1100 },
  { label:"証明ヘアメイク (900)", value:900 },
  { label:"リファ販売手数料", value:null },
  { label:"バイラナイト販売手数料", value:null },
  { label:"当日無断キャンセル", value:null },
];

// 春日井サロン内容プルダウン
const KASUGAI_SALON_OPTIONS = [
  { label:"ヘアセットA (300)", value:300 },
  { label:"ヘアセットB (300)", value:300 },
  { label:"ヘアセット C (600)", value:600 },
  { label:"メイク (400)", value:400 },
  { label:"指名料 (500)", value:500 },
  { label:"宣材写真 (900)", value:900 },
  { label:"春日井メイク (3,000)", value:3000 },
  { label:"春日井セット (2,000)", value:2000 },
  { label:"春日井セットC (4,000)", value:4000 },
  { label:"メイクレッスン (6,000)", value:6000 },
  { label:"早朝深夜料金", value:null },
  { label:"その他", value:null },
];
const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0);
const INT = (v) => Math.floor(num(v));

// v138: 現場の「50%」は原則 ギャラ÷2 の自動計算。
// 案件ごとに支払額が決まっている場合（松本さん指示など）は jitsu に手入力して上書きできる。
// jitsu が null のときは自動（ギャラ÷2）。青海の「実数値」と同じ項目を使う。
const jobHalf = (j) => (j && j.jitsu != null ? num(j.jitsu) : INT(num(j && j.guarantee) / 2));
const sumJobHalf = (list) => (list || []).reduce((s, j) => s + jobHalf(j), 0);
const isHalfManual = (j) => !!j && j.jitsu != null;
// 50%欄の入力：自動値と同じなら自動（jitsu=null）に戻す
const setJobHalf = (j, v, onSet) => onSet(v == null || num(v) === INT(num(j && j.guarantee) / 2) ? null : v);

// v60: 子ども・子育て支援金の適用判定
// 2024年4月以降、かつ春日井（業務委託）以外で表示
const showChildSupport = (monthKey, staffType) => {
  if (staffType === "kasugai") return false;
  if (!monthKey) return false;
  const [y, mo] = monthKey.split("-").map(Number);
  if (!y || !mo) return false;
  // 2024年4月以降
  return (y > 2024) || (y === 2024 && mo >= 4);
};

// === レスポンシブ用フック（v28共通化） ===
// PC/タブレット: > 640px、モバイル: 380〜640px、極狭(Galaxy Z Fold4): ≤ 380px
function useViewport() {
  const [winW, setWinW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setWinW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return {
    winW,
    isNarrow: winW <= 380,   // Galaxy Z Fold4など極狭
    isMobile: winW <= 900,   // モバイル+タブレット（v31で拡張：iPad縦も含める）
    isPhone: winW <= 640,    // スマホのみ
    isTablet: winW > 640 && winW <= 900, // タブレット
    isPC: winW > 900,        // PC（v31で変更：900px超のみ）
  };
}

// === 統一タイポグラフィ（v28） ===
const FONT = {
  body: { fontSize:13, lineHeight:1.45 },        // 通常テキスト
  small: { fontSize:11, lineHeight:1.4 },        // 補足テキスト
  table: { fontSize:12, lineHeight:1.4 },        // テーブルセル
  tableMobile: { fontSize:11, lineHeight:1.35 }, // モバイルテーブル
  heading: { fontSize:14, fontWeight:"bold" },   // 見出し
  subhead: { fontSize:13, fontWeight:"bold" },   // 小見出し
  label: { fontSize:11, color:"#666" },          // ラベル
  badge: { fontSize:10, fontWeight:"bold" },     // バッジ
};

// 一覧出力スタイルのモバイル編集テーブル用ヘルパー（v30）
// 薄青ヘッダー + 青字、ゆとりある行高、「円」付き表記
// v34: 表示と編集を切り替える数値セル（カンマ付き表示、タップで編集モード）
function EditableNumberCell({ value, onChange, style, placeholder, color, fontWeight, background }) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);
  // 共通の表示スタイル
  const baseStyle = {
    display:"block",
    width:"100%",
    padding:"2px 3px",
    fontSize:"inherit",
    fontFamily:"inherit",
    textAlign:"right",
    color: color || "#000",
    fontWeight: fontWeight || "normal",
    background: background || "transparent",
    border:"none",
    outline:"none",
    boxSizing:"border-box",
    cursor: editing ? "text" : "pointer",
    minHeight:"1.4em",
    lineHeight:1.4,
    ...style,
  };
  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") { e.target.blur(); } }}
        style={baseStyle}
      />
    );
  }
  // 表示モード：カンマ付き
  return (
    <span
      onClick={() => setEditing(true)}
      style={baseStyle}
    >
      {value == null || value === "" ? <span style={{color:"#ccc"}}>—</span> : Number(value).toLocaleString("ja-JP")}
    </span>
  );
}

// v34: 表示と編集を切り替えるテキストセル（タップで編集モード）
function EditableTextCell({ value, onChange, style, placeholder }) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);
  const baseStyle = {
    display:"block",
    width:"100%",
    padding:"2px 3px",
    fontSize:"inherit",
    fontFamily:"inherit",
    border:"none",
    outline:"none",
    background:"transparent",
    boxSizing:"border-box",
    cursor: editing ? "text" : "pointer",
    minHeight:"1.4em",
    lineHeight:1.4,
    ...style,
  };
  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") { e.target.blur(); } }}
        style={baseStyle}
      />
    );
  }
  return (
    <span onClick={() => setEditing(true)} style={baseStyle}>
      {value || <span style={{color:"#ccc"}}>　</span>}
    </span>
  );
}

// v36: 修正コメント機能（行の右端に吹き出しアイコン、タップでダイアログ、PC でも見える）
// v37: React key追加で強制再レンダリング、hasComment判定の確実化
function CommentButton({ comment, onChange, label }) {
  // 厳密な判定：undefined/null/空文字/空白のみ全て「コメントなし」
  const hasComment = typeof comment === "string" && comment.trim().length > 0;
  const handleClick = () => {
    const current = hasComment ? comment : "";
    const next = window.prompt(
      `${label||'この行'}への修正メモを入力（空白で削除）：\n\n例：「ギャラ違う」「1/5→1/6」`,
      current
    );
    if (next === null) return; // キャンセル
    const trimmed = next.trim();
    // 空文字なら null、それ以外は文字列で保存
    onChange(trimmed === "" ? null : trimmed);
  };
  return (
    <button
      key={hasComment ? "has" : "none"}
      onClick={handleClick}
      title={hasComment ? `コメント: ${comment}` : "修正メモを追加"}
      style={{
        background: hasComment ? "#fee2e2" : "transparent",
        border: hasComment ? "1px solid #dc2626" : "none",
        borderRadius: hasComment ? 3 : 0,
        padding:"0 3px",
        fontSize:14,
        cursor:"pointer",
        lineHeight:1,
        color: hasComment ? "#dc2626" : "#999",
        fontWeight: hasComment ? "bold" : "normal",
      }}
    >
      {hasComment ? "💬" : "💭"}
    </button>
  );
}

const makeOverviewStyles = (isNarrow) => {
  const fs = isNarrow ? 10 : 11;
  return {
    fs,
    TH: { border:"1px solid #c8d4f0", padding:"4px 3px", background:"#dde3f0", color:"#1e3a8a", fontWeight:"bold", fontSize:fs, textAlign:"center", whiteSpace:"nowrap" },
    TD: { border:"1px solid #e5e7eb", padding:"3px 4px", fontSize:fs, verticalAlign:"middle", lineHeight:1.4 },
    TDR: { border:"1px solid #e5e7eb", padding:"3px 4px", fontSize:fs, verticalAlign:"middle", lineHeight:1.4, textAlign:"right" },
    TDC: { border:"1px solid #e5e7eb", padding:"3px 4px", fontSize:fs, verticalAlign:"middle", lineHeight:1.4, textAlign:"center" },
    // 長文セル：右スワイプで全文確認可能（v32新規）
    TDS: { border:"1px solid #e5e7eb", padding:"3px 4px", fontSize:fs, verticalAlign:"middle", lineHeight:1.4, overflowX:"auto", WebkitOverflowScrolling:"touch", whiteSpace:"nowrap", maxWidth:0 },
    INP: { border:"none", background:"transparent", fontSize:fs, padding:"2px 3px", width:"100%", boxSizing:"border-box", outline:"none", fontFamily:"inherit" },
    INPR: { border:"none", background:"transparent", fontSize:fs, padding:"2px 3px", width:"100%", boxSizing:"border-box", outline:"none", fontFamily:"inherit", textAlign:"right" },
    // スワイプ可能セル内の input：min-widthを大きくして右スワイプ可能にする
    INPS: { border:"none", background:"transparent", fontSize:fs, padding:"2px 3px", minWidth:"160px", boxSizing:"border-box", outline:"none", fontFamily:"inherit" },
    DELBTN: { background:"transparent", color:"#999", border:"none", padding:"2px", fontSize:12, cursor:"pointer", width:"100%", lineHeight:1 },
    // 確認ダイアログ付き削除
    confirmDelete: (onDelete, idx, label) => {
      if (window.confirm(`この行（${label||'行 '+(idx+1)}）を削除しますか？`)) onDelete(idx);
    },
  };
};
// "16日" などの文字列から数字を抽出（互換用。出張日数は数値フィールドに移行）
const extractDays = (v) => {
  if (v == null || v === "") return 0;
  const m = String(v).match(/\d+/);
  return m ? Number(m[0]) : 0;
};
// 出張手当自動計算：出張日数 × 出張手当単価（手動値優先）
const calcShucchouteate = (staff, salary) => {
  if (salary.出張手当手動 != null) return salary.出張手当手動;
  const unitPrice = staff?.出張手当単価;
  if (unitPrice == null) return num(salary.出張手当);
  const days = extractDays(salary.出張日数);
  return days * unitPrice;
};
const parseTab = (text) =>
  text.trim().split("\n").map((r) => r.split("\t").map((c) => c.trim()));
let _id = 2000;
const nid = () => ++_id;

// ===================== スタイル =====================
const TH   = { border:"1px solid #bbb", padding:"5px 8px", background:"#dde3f0", textAlign:"center", fontSize:12, whiteSpace:"nowrap", fontWeight:"bold" };
const TD   = { border:"1px solid #ddd", padding:"3px 6px", fontSize:12 };
const TDR  = { border:"1px solid #ddd", padding:"3px 6px", textAlign:"right", fontSize:12 };
const TDC  = { border:"1px solid #ddd", padding:"3px 5px", textAlign:"center", fontSize:12 };
const TSUM = { border:"1px solid #bbb", padding:"5px 8px", textAlign:"right", fontSize:12, background:"#dde3f0", fontWeight:"bold", whiteSpace:"nowrap" };
const INP  = { border:"1px solid #ccc", padding:"4px 6px", fontSize:12, width:"100%", boxSizing:"border-box", background:"#fff" };
const INPR = { border:"1px solid #ccc", padding:"4px 6px", fontSize:12, width:"100%", textAlign:"right", boxSizing:"border-box", background:"#fff" };
const BTN  = (bg="#1e3a8a") => ({ padding:"4px 12px", background:bg, color:"#fff", border:"none", cursor:"pointer", fontSize:12, borderRadius:3 });
const BTND = { padding:"2px 8px", background:"#b91c1c", color:"#fff", border:"none", cursor:"pointer", fontSize:11, borderRadius:2 };
const TAB  = (on) => ({ padding:"5px 12px", cursor:"pointer", background:on?"#fff":"#e8eaf0", border:"1px solid #999", borderBottom:on?"2px solid #1e3a8a":"1px solid #999", fontSize:11, fontWeight:on?"bold":"normal", marginRight:2, color:on?"#1e3a8a":"#333", whiteSpace:"nowrap" });
// 金額入力セル：min-widthで見切れ防止
const AmtTD  = { border:"1px solid #ddd", padding:"3px 4px", minWidth:95 };
// 自動計算値セル（青背景）
const AutoTD = { border:"1px solid #ddd", padding:"4px 10px", textAlign:"right", fontSize:12, background:"#eef2ff", minWidth:85, whiteSpace:"nowrap", color:"#1e3a8a", fontWeight:"bold" };
// 合計行の自動計算
const AutoTS = { border:"1px solid #bbb", padding:"5px 8px", textAlign:"right", fontSize:12, background:"#c8d4f8", fontWeight:"bold", whiteSpace:"nowrap" };
// 合計行の指名料
const NomTS  = { border:"1px solid #bbb", padding:"5px 8px", textAlign:"right", fontSize:13, background:"#a8bcf5", fontWeight:"bold", whiteSpace:"nowrap" };

// ===================== グローバルCSS（スマホ対応） =====================
const GLOBAL_CSS = `
  * { box-sizing: border-box; }
  /* スマホ共通：テーブルを横スクロール */
  .scroll-x { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  /* v34: input/textareaのデフォルト下線・スタイルを完全クリア（!important多重、全状態） */
  input, textarea, select,
  input:focus, textarea:focus, select:focus,
  input:hover, textarea:hover, select:hover,
  input:active, textarea:active, select:active {
    background: transparent;
    border: none !important;
    border-top: none !important;
    border-bottom: none !important;
    border-left: none !important;
    border-right: none !important;
    outline: none !important;
    box-shadow: none !important;
    text-decoration: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    -webkit-tap-highlight-color: rgba(0,0,0,0) !important;
  }
  /* iOS Safari の数値入力のスピンボタンも消去 */
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
  }
  input[type="number"] { -moz-appearance: textfield !important; }
  /* iOS自動リンク化を抑止（電話番号・住所など） */
  td, span, a { text-decoration: none !important; }
  /* スパン（表示モード）のフォーカス・ホバー時の枠も消去 */
  td span, td span:focus, td span:hover, td span:active {
    outline: none !important;
    border: none !important;
    background: transparent;
  }
  /* スマホ向けテーブル文字を小さく */
  @media (max-width: 480px) {
    table { font-size: 10px !important; }
    th, td { padding: 2px 3px !important; font-size: 10px !important; white-space: nowrap; }
    .tab-btn { padding: 4px 7px !important; font-size: 10px !important; }
    .main-title { font-size: 12px !important; }
    .month-btn { padding: 4px 8px !important; font-size: 10px !important; }
    input[type="number"], input[type="text"], input[type="month"] {
      font-size: 11px !important;
      padding: 2px 4px !important;
      border: none !important;
      border-bottom: none !important;
      outline: none !important;
      box-shadow: none !important;
      -webkit-appearance: none !important;
      appearance: none !important;
    }
    .sidebar { flex-direction: row !important; flex-wrap: wrap !important; width: 100% !important; border-right: none !important; border-bottom: 1px solid #ccc !important; padding: 4px !important; }
    .staff-item { flex: 0 0 auto !important; max-width: 48% !important; font-size: 11px !important; }
    .main-layout { flex-direction: column !important; }
    .add-btn-wrap { display: none; }
  }
  @media (min-width: 481px) and (max-width: 768px) {
    table { font-size: 11px !important; }
    th, td { padding: 2px 4px !important; font-size: 11px !important; white-space: nowrap; }
    .tab-btn { padding: 4px 9px !important; font-size: 11px !important; }
    .sidebar { width: 140px !important; }
  }
  /* 内容列は1行に強制 */
  .memo-cell { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 120px; }
  @media (max-width: 480px) {
    .memo-cell { max-width: 80px; }
  }
`;

// ===================== 計算関数 =====================
const calcSalon65 = (payment, discount, rate) => Math.round((num(payment) - num(discount)) * rate);
const calcNomination = (payment, discount, rate, nom100) => calcSalon65(payment, discount, rate) + num(nom100);

// v132: 2026年9月以降か（サロン100%を指名料／早朝深夜／販売手数料に分割）
function isSplitMonth(monthKey) {
  return String(monthKey || "") >= "2026-09";
}
// v134/v135: サロンの確定（締め）。
// サロン指名料アプリの salonClose/<アプリのスタッフID>_<YYYY-MM>（{ closed }）をスタッフ別・月別に読む。
// 確定・取り消しはサロン指名料アプリの管理画面（管理者のみ）で行う。
// ドキュメントが無い月・読めないとき・アプリ対象外（春日井）は、SALON_CLOSED_THROUGH までを確定扱い。
const SALON_CLOSED_THROUGH = "2026-08";
function isSalonClosedMonth(monthKey) {
  const m = String(monthKey || "");
  return m !== "" && m <= SALON_CLOSED_THROUGH;
}
const SALON_CLOSED_NOTE = "🔒 確定済みの月のため、取り込み・Excelペーストはできません（行の修正はできます）";
function useSalonClosed(staffType, monthKey) {
  const id = SALON_APP_STAFF_ID[staffType];
  const [st, setSt] = useState({ loading: !!id, closed: isSalonClosedMonth(monthKey) });
  useEffect(() => {
    if (!id || !monthKey) { setSt({ loading: false, closed: isSalonClosedMonth(monthKey) }); return; }
    setSt({ loading: true, closed: true });
    return onSnapshot(
      doc(salonAppDb, "salonClose", `${id}_${monthKey}`),
      (snap) => setSt({ loading: false, closed: snap.exists() ? !!snap.data().closed : isSalonClosedMonth(monthKey) }),
      () => setSt({ loading: false, closed: isSalonClosedMonth(monthKey) })
    );
  }, [id, monthKey]);
  // 読み込み中は安全側（確定扱い）
  return st.loading ? true : st.closed;
}

// v132: 9月以降は3項目の合計、8月以前は従来の nom100 を使う
function get100(row, monthKey) {
  if (isSplitMonth(monthKey)) {
    const a = num(row.nomShimei), b = num(row.nomSoucho), c = num(row.nomHanbai), d = num(row.nomCancel);
    // 4項目が未入力なら旧 nom100 にフォールバック（移行期の安全策）
    if (a === 0 && b === 0 && c === 0 && d === 0) return num(row.nom100);
    return a + b + c + d;
  }
  return num(row.nom100);
}

// ===================== 初期データ定義 =====================

// --- 松本1月 ---
const MATSUMOTO_JAN_JOBS = [
  { id:nid(), date:"1/5",  client:"そらまふうらさか　幕間",     guarantee:12000 },
  { id:nid(), date:"1/6",  client:"ナナクロニクル　ジャケ写",  guarantee:30000 },
  { id:nid(), date:"1/10", client:"堀江瞬、阿座上洋平",         guarantee:50000 },
  { id:nid(), date:"1/12", client:"うらたぬき　写真集サイン会", guarantee:30000 },
  { id:nid(), date:"1/15", client:"めいちゃん　幕間",           guarantee:50000 },
  { id:nid(), date:"1/17", client:"堀江瞬",                     guarantee:50000 },
  { id:nid(), date:"1/20", client:"ASH",                        guarantee:50000 },
  { id:nid(), date:"1/29", client:"浦島坂田船　OP撮影",         guarantee:75000 },
];
const MATSUMOTO_JAN_SALON = [
  { id:nid(), date:"1/14", name:"志麻",     payment:12000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"1/23", name:"Gero",     payment:12000, discount:null, nom100:1000,  memo:"" },
  { id:nid(), date:"1/26", name:"千葉佳織", payment:15000, discount:null, nom100:null,  memo:"" },
];
const MATSUMOTO_JAN_SAL = {
  役員報酬:700000, 出張手当:28000, 出張日数:"8日", 交通費:4442, 通勤費:2343, 年末調整還付金:null,
};

// --- 松本2月 ---
const MATSUMOTO_FEB_JOBS = [
  { id:nid(), date:"2/6",  client:"ASH オーディション",   guarantee:50000 },
  { id:nid(), date:"2/7",  client:"志麻",                  guarantee:60000 },
  { id:nid(), date:"2/12", client:"浦島坂田船 川口さん",   guarantee:100000 },
  { id:nid(), date:"2/13", client:"そまうさ RH",           guarantee:50000 },
  { id:nid(), date:"2/14", client:"そまうさ",              guarantee:55000 },
  { id:nid(), date:"2/15", client:"そまうさ",              guarantee:55000 },
  { id:nid(), date:"2/19", client:"Geroカット",            guarantee:5000 },
  { id:nid(), date:"2/21", client:"Gero",                  guarantee:50000 },
  { id:nid(), date:"2/21", client:"ちゃんげろソニック",    guarantee:50000 },
];
const MATSUMOTO_FEB_SALON = [
  { id:nid(), date:"", name:"志麻",     payment:null, discount:null, nom100:null, memo:"（サロンデータなし）" },
];
const MATSUMOTO_FEB_SAL = {
  役員報酬:700000, 出張手当:31500, 出張日数:"9日", 交通費:3110, 通勤費:1966, 年末調整還付金:null,
};

// --- 竹内1月 ---
const TAKEUCHI_JAN_JOBS = [
  { id:nid(), date:"1/5",  client:"そらまふうらさか　幕間",   guarantee:50000 },
  { id:nid(), date:"1/6",  client:"ナナクロニクル　ジャケ写", guarantee:30000 },
  { id:nid(), date:"1/6",  client:"いなふ、ひい　ダンス動画", guarantee:40000 },
  { id:nid(), date:"1/10", client:"堀江瞬、阿座上洋平",       guarantee:50000 },
  { id:nid(), date:"1/12", client:"小林親弘",                 guarantee:40000 },
  { id:nid(), date:"1/13", client:"ReLIT 撮影(現場)",         guarantee:50000 },
  { id:nid(), date:"1/15", client:"ReLIT MV(現場)",           guarantee:50000 },
  { id:nid(), date:"1/17", client:"センラ　リリイベ　大阪",   guarantee:30000 },
  { id:nid(), date:"1/18", client:"センラ　リリイベ　名古屋", guarantee:30000 },
  { id:nid(), date:"1/23", client:"堀江瞬",                   guarantee:40000 },
  { id:nid(), date:"1/24", client:"センラ　リリイベ　東京",   guarantee:30000 },
  { id:nid(), date:"1/25", client:"センラ　リリイベ　東京",   guarantee:30000 },
  { id:nid(), date:"1/26", client:"しゅーず",                 guarantee:70000 },
  { id:nid(), date:"1/27", client:"肉ちょも",                 guarantee:30000 },
  { id:nid(), date:"1/28", client:"山口ハイツ",               guarantee:30000 },
  { id:nid(), date:"1/29", client:"浦島坂田船　OP 撮影",      guarantee:75000 },
  { id:nid(), date:"1/31", client:"こーすけ　イベント",       guarantee:50000 },
];
const TAKEUCHI_JAN_SALON = [
  { id:nid(), date:"1/11", name:"安藤好紀",   payment:11500, discount:1000, nom100:1000, memo:"" },
  { id:nid(), date:"1/12", name:"大桃優太",   payment:11000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"1/12", name:"佐藤早苗",   payment:17000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"1/13", name:"中山亮",     payment:4000,  discount:null, nom100:null, memo:"" },
  { id:nid(), date:"1/13", name:"太田めぐみ", payment:17000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"1/13", name:"ARAN",       payment:12000, discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"1/13", name:"SEN",        payment:12000, discount:null, nom100:1000, memo:"" },
  { id:nid(), date:"1/14", name:"坂田",       payment:12000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"1/19", name:"早川青子",   payment:10000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"1/20", name:"早川青子",   payment:10000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"1/25", name:"藤田峻平",   payment:11000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"1/25", name:"中山亮",     payment:10000, discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"1/28", name:"高橋芽衣",   payment:5500,  discount:null, nom100:null, memo:"" },
];
const TAKEUCHI_JAN_SAL = {
  基本給:213019, 出張手当:32000, 出張日数:"16日", 保険料調整:450000,
  役職手当:100000, 交通費:8894, 通勤費:5985, オシャレ手当:10000, 年末調整還付金:null,
  健康保険料:12883, 厚生年金保険料:23790, 雇用保険料:6825, 所得税:null, 住民税:24500,
  サロン指名料:108300,
};
const TAKEUCHI_JAN_INS = [
  { label:"保険料調整合計",               amount:900000, sub:null },
  { label:"2026年1月/支払日：2/25",        amount:450000, sub:null },
  { label:"2026年2月/支払日：3/25",        amount:450000, sub:null },
  { label:"2026年3月分/支払日：4/24",      amount:0,      sub:300000 },
  { label:"2026年4月分/支払日：5/24",      amount:0,      sub:300000 },
  { label:"2026年5月分/支払日：6/24",      amount:0,      sub:300000 },
  { label:"調整月：2026年6月分/支払日：7/24", amount:null,  sub:null },
  { label:"差額分",                        amount:null,   sub:null, isDiff:true },
];

// --- 竹内2月 ---
const TAKEUCHI_FEB_JOBS = [
  { id:nid(), date:"2/1",  client:"堀江瞬、志庵",         guarantee:70000 },
  { id:nid(), date:"2/2",  client:"Gero",                  guarantee:40000 },
  { id:nid(), date:"2/3",  client:"肉ちょも",              guarantee:40000 },
  { id:nid(), date:"2/5",  client:"浦島坂田船　幕間",      guarantee:75000 },
  { id:nid(), date:"2/6",  client:"Mリーグ",               guarantee:50000 },
  { id:nid(), date:"2/7",  client:"ReLIT ジャケ写",        guarantee:50000 },
  { id:nid(), date:"2/8",  client:"堀江瞬",                guarantee:45000 },
  { id:nid(), date:"2/9",  client:"小林親弘",              guarantee:70000 },
  { id:nid(), date:"2/10", client:"肉ちょも",              guarantee:30000 },
  { id:nid(), date:"2/12", client:"浦島坂田船　川口さん",  guarantee:100000 },
  { id:nid(), date:"2/13", client:"そまうさ　RH",          guarantee:50000 },
  { id:nid(), date:"2/14", client:"そまうさ",              guarantee:55000 },
  { id:nid(), date:"2/15", client:"そまうさ",              guarantee:55000 },
  { id:nid(), date:"2/17", client:"小林親弘",              guarantee:40000 },
  { id:nid(), date:"2/21", client:"小林親弘",              guarantee:40000 },
  { id:nid(), date:"2/22", client:"ちゃんげろソニック",    guarantee:50000 },
  { id:nid(), date:"2/24", client:"肉ちょも",              guarantee:30000 },
  { id:nid(), date:"2/25", client:"小林親弘",              guarantee:40000 },
  { id:nid(), date:"2/27", client:"小林親弘",              guarantee:50000 },
  { id:nid(), date:"2/28", client:"堀江瞬・中澤まさとも",  guarantee:70000 },
];
const TAKEUCHI_FEB_SALON = [
  { id:nid(), date:"2/5",  name:"関根海",                         payment:4500,  discount:1000, nom100:4000, memo:"" },
  { id:nid(), date:"2/11", name:"藤田峻平",                       payment:11000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"2/11", name:"MeseMoa.あおい",                 payment:11000, discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"2/11", name:"よしき",                         payment:18500, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"2/11", name:"早川博隆",                       payment:12000, discount:null, nom100:1000, memo:"" },
  { id:nid(), date:"2/11", name:"村松優衣",                       payment:10000, discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"2/15", name:"榊颯馬",                         payment:19900, discount:null, nom100:4000, memo:"" },
  { id:nid(), date:"2/15", name:"長尾諒(Milky Monster)",          payment:5500,  discount:null, nom100:6000, memo:"" },
  { id:nid(), date:"2/17", name:"ラムネ サロンメイク いっちー",   payment:11000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"2/17", name:"ラムネ サロンメイク さっさー",   payment:11000, discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"2/17", name:"ペトリコール サロンメイク 結翔、類", payment:22000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"2/22", name:"村松優衣",                       payment:13600, discount:null, nom100:6000, memo:"" },
  { id:nid(), date:"2/23", name:"U&pia サロンメイク KAI",         payment:12000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"2/23", name:"U&pia サロンメイク LEN",         payment:12000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"2/23", name:"U&pia サロンメイクHARU",         payment:12000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"2/25", name:"藤田峻平",                       payment:11000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"2/26", name:"Gero",                           payment:12000, discount:null, nom100:4000, memo:"" },
  { id:nid(), date:"2/26", name:"田中美羽",                       payment:11000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"2/26", name:"長谷川瑞季",                     payment:11000, discount:1000, nom100:1000, memo:"" },
];
const TAKEUCHI_FEB_SAL = {
  基本給:213019, 出張手当:40000, 出張日数:"20日", 保険料調整:450000,
  役職手当:100000, 交通費:15712, 通勤費:6005, オシャレ手当:10000, 年末調整還付金:null,
  健康保険料:12883, 厚生年金保険料:23790, 雇用保険料:6825, 所得税:199769, 住民税:24500,
  サロン指名料:108300,
};
const TAKEUCHI_FEB_INS = [
  { label:"保険料調整合計",               amount:900000, sub:null },
  { label:"2026年1月/支払日：2/25",        amount:450000, sub:null },
  { label:"2026年2月/支払日：3/25",        amount:450000, sub:null },
  { label:"2026年3月分/支払日：4/24",      amount:0,      sub:300000 },
  { label:"2026年4月分/支払日：5/24",      amount:0,      sub:300000 },
  { label:"2026年5月分/支払日：6/24",      amount:0,      sub:300000 },
  { label:"調整月：2026年6月分/支払日：7/24", amount:null,  sub:null },
  { label:"差額分",                        amount:null,   sub:null, isDiff:true },
];

// --- 深澤1月 ---
const FUKAZAWA_JAN_JOBS = [
  { id:nid(), date:"1/3",  client:"相羽あいな",               guarantee:30000 },
  { id:nid(), date:"1/5",  client:"そらまふうらさか　幕間",   guarantee:12000 },
  { id:nid(), date:"1/6",  client:"ナナクロニクル　ジャケ写", guarantee:30000 },
  { id:nid(), date:"1/6",  client:"いなふ、ひい　ダンス動画", guarantee:40000 },
  { id:nid(), date:"1/7",  client:"defspiral 撮影",           guarantee:50000 },
  { id:nid(), date:"1/8",  client:"Verde/",                   guarantee:45000 },
  { id:nid(), date:"1/10", client:"La'cryma Christi",         guarantee:45000 },
  { id:nid(), date:"1/11", client:"摩天楼オペラ",             guarantee:45000 },
  { id:nid(), date:"1/13", client:"めいちゃん 東京",          guarantee:75000 },
  { id:nid(), date:"1/14", client:"La'cryma Christi",         guarantee:45000 },
  { id:nid(), date:"1/15", client:"めいちゃん　幕間",         guarantee:50000 },
  { id:nid(), date:"1/16", client:"苑、響",                   guarantee:45000 },
  { id:nid(), date:"1/18", client:"摩天楼オペラ",             guarantee:45000 },
  { id:nid(), date:"1/19", client:"めいちゃん　幕間",         guarantee:60000 },
  { id:nid(), date:"1/20", client:"肉ちょも",                 guarantee:30000 },
  { id:nid(), date:"1/23", client:"摩天楼オペラ",             guarantee:45000 },
  { id:nid(), date:"1/24", client:"ReLIT ツアーファイナル",   guarantee:30000 },
  { id:nid(), date:"1/25", client:"RENO ギタリストイベント",  guarantee:45000 },
  { id:nid(), date:"1/26", client:"VINANSHI kayoさん案件",    guarantee:50000 },
  { id:nid(), date:"1/30", client:"めいちゃん　広島(GP)",     guarantee:80000 },
  { id:nid(), date:"1/31", client:"めいちゃん　広島",         guarantee:75000 },
];
const FUKAZAWA_JAN_SALON = [
  { id:nid(), date:"1/12", name:"北出菫",           payment:30000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"1/12", name:"尾藤遼佳",         payment:30000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"1/18", name:"生駒音羽",         payment:11000, discount:null, nom100:4000, memo:"" },
  { id:nid(), date:"1/18", name:"香川瑶美",         payment:4750,  discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"1/21", name:"朋(THE MADNA)",    payment:18000, discount:null, nom100:4000, memo:"" },
  { id:nid(), date:"1/21", name:"中山亮",           payment:4000,  discount:null, nom100:null, memo:"" },
  { id:nid(), date:"1/21", name:"宮下タケル",       payment:4000,  discount:null, nom100:4000, memo:"" },
  { id:nid(), date:"1/29", name:"宮下タケル",       payment:4000,  discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"1/29", name:"浦上雅仁",         payment:4000,  discount:null, nom100:2000, memo:"" },
];
const FUKAZAWA_JAN_SAL = {
  基本給:213019, 出張手当:30000, 出張日数:"20日", 保険料調整:375000,
  役職手当:40000, 交通費:10280, 通勤費:2211, オシャレ手当:10000, 年末調整還付金:null,
  健康保険料:null, 厚生年金保険料:null, 雇用保険料:null, 所得税:null, 住民税:null,
  サロン指名料:90338,
};
const FUKAZAWA_JAN_INS = [
  { label:"保険料調整合計",               amount:750000, sub:null },
  { label:"2026年1月/支払日：2/25",        amount:375000, sub:null },
  { label:"2026年2月/支払日：3/25",        amount:375000, sub:null },
  { label:"2026年3月分/支払日：4/24",      amount:0,      sub:250000 },
  { label:"2026年4月分/支払日：5/24",      amount:0,      sub:250000 },
  { label:"2026年5月分/支払日：6/24",      amount:0,      sub:250000 },
  { label:"調整月：2026年6月分/支払日：7/24", amount:null,  sub:null },
  { label:"差額分",                        amount:null,   sub:null, isDiff:true },
];

// --- 深澤2月 ---
const FUKAZAWA_FEB_JOBS = [
  { id:nid(), date:"2/1",  client:"めいちゃん　愛媛",             guarantee:75000 },
  { id:nid(), date:"2/2",  client:"相羽あいな",                   guarantee:30000 },
  { id:nid(), date:"2/2",  client:"戻り日",                       guarantee:10000 },
  { id:nid(), date:"2/3",  client:"La'cryma Christi 撮影",        guarantee:50000 },
  { id:nid(), date:"2/4",  client:"jupiter 撮影",                 guarantee:50000 },
  { id:nid(), date:"2/5",  client:"浦島坂田船　幕間",             guarantee:75000 },
  { id:nid(), date:"2/6",  client:"Mリーグ",                      guarantee:50000 },
  { id:nid(), date:"2/8",  client:"めいちゃん　仙台",             guarantee:75000 },
  { id:nid(), date:"2/10", client:"あおい　生誕祭",               guarantee:30000 },
  { id:nid(), date:"2/11", client:"めいちゃん　福島",             guarantee:75000 },
  { id:nid(), date:"2/13", client:"Verde/",                       guarantee:45000 },
  { id:nid(), date:"2/14", client:"めいちゃん　愛知",             guarantee:75000 },
  { id:nid(), date:"2/15", client:"めいちゃん　愛知",             guarantee:75000 },
  { id:nid(), date:"2/16", client:"HIZAKI",                       guarantee:45000 },
  { id:nid(), date:"2/16", client:"戻り日",                       guarantee:10000 },
  { id:nid(), date:"2/17", client:"HIZAKI",                       guarantee:45000 },
  { id:nid(), date:"2/18", client:"D'ESPAIRSRAY アー写撮影",      guarantee:50000 },
  { id:nid(), date:"2/20", client:"前乗り",                       guarantee:10000 },
  { id:nid(), date:"2/21", client:"めいちゃん　京都",             guarantee:75000 },
  { id:nid(), date:"2/22", client:"ちゃんげろソニック",           guarantee:50000 },
  { id:nid(), date:"2/22", client:"戻り日",                       guarantee:10000 },
  { id:nid(), date:"2/23", client:"めいちゃん　兵庫",             guarantee:75000 },
  { id:nid(), date:"2/24", client:"戻り日",                       guarantee:10000 },
  { id:nid(), date:"2/24", client:"優介　撮影　摩天楼オペラ",     guarantee:45000 },
  { id:nid(), date:"2/26", client:"Verde/ MV撮影",                guarantee:50000 },
  { id:nid(), date:"2/28", client:"摩天楼オペラ",                 guarantee:45000 },
];
const FUKAZAWA_FEB_SALON = [
  { id:nid(), date:"2/6",  name:"今泉光耀",                   payment:11000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"2/7",  name:"和田優希(しま様 ナナロル)",  payment:5000,  discount:null, nom100:5000, memo:"" },
  { id:nid(), date:"2/7",  name:"MeseMoa.あおい",             payment:11000, discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"2/17", name:"ラムネ サロンメイク みつき", payment:11000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"2/20", name:"奏音69",                     payment:12000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"2/26", name:"めいちゃん",                 payment:12000, discount:null, nom100:4000, memo:"" },
];
const FUKAZAWA_FEB_SAL = {
  基本給:213019, 出張手当:31500, 出張日数:"21日", 保険料調整:375000,
  役職手当:40000, 交通費:6590, 通勤費:1898, オシャレ手当:10000, 年末調整還付金:null,
  健康保険料:null, 厚生年金保険料:null, 雇用保険料:null, 所得税:null, 住民税:null,
};
const FUKAZAWA_FEB_INS = [
  { label:"保険料調整合計",               amount:750000, sub:null },
  { label:"2026年1月/支払日：2/25",        amount:375000, sub:null },
  { label:"2026年2月/支払日：3/25",        amount:375000, sub:null },
  { label:"2026年3月分/支払日：4/24",      amount:0,      sub:250000 },
  { label:"2026年4月分/支払日：5/24",      amount:0,      sub:250000 },
  { label:"2026年5月分/支払日：6/24",      amount:0,      sub:250000 },
  { label:"調整月：2026年6月分/支払日：7/24", amount:null,  sub:null },
  { label:"差額分",                        amount:null,   sub:null, isDiff:true },
];

// --- 青海1月（実数値シート：D/2計算） ---
const AOMI_JAN_JOBS = [
  { id:nid(), date:"1/6",  client:"ホスト　ヘアセット",           guarantee:30000 },
  { id:nid(), date:"1/9",  client:"HIZAKI",                       guarantee:30000 },
  { id:nid(), date:"1/12", client:"EC サイト　撮影",              guarantee:40000 },
  { id:nid(), date:"1/13", client:"ReLIT 撮影 (現場)",            guarantee:40000 },
  { id:nid(), date:"1/14", client:"キャンセル→浦和希・今井文也", guarantee:15000 },
  { id:nid(), date:"1/15", client:"U&pia",                        guarantee:40000 },
  { id:nid(), date:"1/17", client:"小林親弘",                     guarantee:45000 },
  { id:nid(), date:"1/18", client:"shack ワンマン",               guarantee:50000 },
  { id:nid(), date:"1/19", client:"会社HP",                       guarantee:40000 },
  { id:nid(), date:"1/20", client:"浦和希　BS11",                 guarantee:30000 },
  { id:nid(), date:"1/21", client:"浦和希・今井文也",             guarantee:40000 },
  { id:nid(), date:"1/22", client:"UNIQLO 上條",                  guarantee:20000 },
  { id:nid(), date:"1/23", client:"会社HP",                       guarantee:30000 },
  { id:nid(), date:"1/24", client:"小林親弘　沖縄",               guarantee:50000 },
  { id:nid(), date:"1/25", client:"小林親弘　沖縄",               guarantee:50000 },
  { id:nid(), date:"1/27", client:"浦和希",                       guarantee:30000 },
  { id:nid(), date:"1/30", client:"そらる",                       guarantee:30000 },
  { id:nid(), date:"1/31", client:"小林裕介",                     guarantee:30000 },
];
const AOMI_JAN_SALON = [
  { id:nid(), date:"1/4",  name:"加留部輝",     payment:10500, discount:1000, nom100:null,  memo:"" },
  { id:nid(), date:"1/7",  name:"中山亮",       payment:3500,  discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"1/10", name:"気まぐれプリンス", payment:11000, discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"1/10", name:"佐藤陽奈",     payment:3500,  discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"1/10", name:"山岸美緒",     payment:11900, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"1/11", name:"中尾優希",     payment:14600, discount:null, nom100:6500,  memo:"" },
  { id:nid(), date:"1/11", name:"天谷未来",     payment:4500,  discount:null, nom100:2000,  memo:"" },
  { id:nid(), date:"1/11", name:"佐藤早苗",     payment:16500, discount:null, nom100:500,   memo:"" },
  { id:nid(), date:"1/12", name:"渡邉美夕",     payment:30000, discount:null, nom100:500,   memo:"" },
  { id:nid(), date:"1/12", name:"李 嘉櫻",      payment:30000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"1/13", name:"宇佐美花",     payment:13700, discount:null, nom100:3000,  memo:"" },
  { id:nid(), date:"1/13", name:"湯口萌",       payment:18700, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"1/13", name:"U&pia JEASUN", payment:12000, discount:null, nom100:1000,  memo:"" },
  { id:nid(), date:"1/15", name:"中村優里",     payment:11800, discount:null, nom100:1000,  memo:"" },
  { id:nid(), date:"1/18", name:"峯崎貴裕",     payment:11000, discount:1000, nom100:2000,  memo:"" },
  { id:nid(), date:"1/29", name:"寺脇空",       payment:3500,  discount:1000, nom100:null,  memo:"" },
  { id:nid(), date:"1/31", name:"寺脇空",       payment:3500,  discount:null, nom100:4000,  memo:"" },
  { id:nid(), date:"1/31", name:"高岡珠里",     payment:4500,  discount:null, nom100:3000,  memo:"" },
];
const AOMI_JAN_SAL = {
  基本給:213019, 出張手当:5600, 出張日数:"17日",
  役職手当:30000, 交通費:19020, 通勤費:0, オシャレ手当:0, 年末調整還付金:null,
  健康保険料:null, 厚生年金保険料:null, 雇用保険料:null, 所得税:null, 住民税:null,
  サロン指名料:99595,
};

// --- 青海2月 ---
const AOMI_FEB_JOBS = [
  { id:nid(), date:"2/1",  client:"浦和希",           guarantee:30000 },
  { id:nid(), date:"2/2",  client:"浦和希　BS11",     guarantee:30000 },
  { id:nid(), date:"2/5",  client:"浦和希",           guarantee:30000 },
  { id:nid(), date:"2/6",  client:"③Mリーグ",        guarantee:50000 },
  { id:nid(), date:"2/6",  client:"浦和希・今井文也", guarantee:40000 },
  { id:nid(), date:"2/13", client:"そまうさ　RH",     guarantee:30000 },
  { id:nid(), date:"2/14", client:"そまうさ",         guarantee:40000 },
  { id:nid(), date:"2/15", client:"そまうさ",         guarantee:40000 },
  { id:nid(), date:"2/16", client:"HIZAKI",           guarantee:30000 },
  { id:nid(), date:"2/19", client:"山口ハイツ",       guarantee:30000 },
  { id:nid(), date:"2/22", client:"ちゃんげろソニック", guarantee:50000 },
  { id:nid(), date:"2/23", client:"富田美憂",         guarantee:40000 },
  { id:nid(), date:"2/24", client:"浦和希",           guarantee:30000 },
  { id:nid(), date:"2/26", client:"浦和希",           guarantee:50000 },
  { id:nid(), date:"2/28", client:"浦和希",           guarantee:40000 },
  { id:nid(), date:"2/28", client:"成宮（中山）",     guarantee:30000 },
];
const AOMI_FEB_SALON = [
  { id:nid(), date:"2/3",  name:"朝賀立(RELIT)",                  payment:11000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/7",  name:"ReLIT 仁科ひびき",               payment:11000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/7",  name:"ReLIT 黄ノ瀬はるき様",           payment:11000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/7",  name:"門田紗子",                       payment:5600,  discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/7",  name:"長尾寛太",                       payment:10500, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/8",  name:"山下昇吾",                       payment:10000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/17", name:"ラムネ　サロンメイク いおり",    payment:11000, discount:null, nom100:3000,  memo:"" },
  { id:nid(), date:"2/17", name:"ラムネ　サロンメイク あづ",      payment:11000, discount:null, nom100:2000,  memo:"" },
  { id:nid(), date:"2/17", name:"ペトリコール サロンメイク 洸星、湊", payment:22000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"2/17", name:"高地真央",                       payment:10000, discount:null, nom100:1000,  memo:"" },
  { id:nid(), date:"2/19", name:"藤田峻平",                       payment:11000, discount:null, nom100:4000,  memo:"" },
  { id:nid(), date:"2/20", name:"寺脇空",                         payment:3500,  discount:null, nom100:3000,  memo:"" },
  { id:nid(), date:"2/20", name:"IKUO",                           payment:15000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/20", name:"北島ひなた",                     payment:6500,  discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/21", name:"日向ゆりこ",                     payment:15000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/21", name:"平山瑠羽",                       payment:10000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/21", name:"元吉有希子",                     payment:12000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/21", name:"小林哲也",                       payment:13500, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/22", name:"柳田珠佑",                       payment:4000,  discount:null, nom100:4000,  memo:"" },
  { id:nid(), date:"2/23", name:"岡田しおん",                     payment:4500,  discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/23", name:"U&pia サロンメイク ARAN",         payment:12000, discount:null, nom100:null,  memo:"" },
  { id:nid(), date:"2/23", name:"U&pia サロンメイク JEASUN",       payment:12000, discount:null, nom100:null,  memo:"" },
];
const AOMI_FEB_SAL = {
  基本給:213019, 出張手当:11200, 出張日数:"14日",
  役職手当:30000, 交通費:19322, 通勤費:0, オシャレ手当:0, 年末調整還付金:null,
  健康保険料:null, 厚生年金保険料:null, 雇用保険料:null, 所得税:null, 住民税:null,
};

// --- 髙山1月（歩合給：現場数×1000円） ---
const TAKAYAMA_JAN_JOBS = [
  { id:nid(), date:"1/9",  client:"NUL." },
  { id:nid(), date:"1/10", client:"小林裕介" },
  { id:nid(), date:"1/11", client:"鳴海米がち収穫祭" },
  { id:nid(), date:"1/13", client:"ReLIT 撮影" },
  { id:nid(), date:"1/15", client:"ReLIT MV" },
  { id:nid(), date:"1/16", client:"藤原 夏海" },
  { id:nid(), date:"1/17", client:"小林裕介" },
  { id:nid(), date:"1/17", client:"歌い手４人メイク　金子さん案件" },
  { id:nid(), date:"1/18", client:"ノクブMV" },
  { id:nid(), date:"1/19", client:"My_Stage アー写" },
  { id:nid(), date:"1/20", client:"My_Stage MV" },
  { id:nid(), date:"1/21", client:"AN×FAN 鈴木さん案件" },
  { id:nid(), date:"1/23", client:"My_Stage MV" },
  { id:nid(), date:"1/24", client:"ReLIT ツアーファイナル" },
  { id:nid(), date:"1/25", client:"小林裕介　沖縄" },
  { id:nid(), date:"1/27", client:"和田雅成" },
].map(j=>({...j, id:nid(), guarantee:null}));
// 髙山のギャランティは参考のみ（実際は1000円/現場）
const TAKAYAMA_JAN_SALON = [
  { id:nid(), date:"1/10", name:"寺脇空",   payment:8500,  nom100:5000,  memo:"早朝深夜料金" },
  { id:nid(), date:"1/10", name:"寺脇空",   payment:null,  nom100:400,   memo:"ヘアセットA (400)" },
  { id:nid(), date:"1/12", name:"中村優里", payment:30000, nom100:1100,  memo:"成人式(卒業式）ヘアメイク (1,100)" },
  { id:nid(), date:"1/12", name:"中村優里", payment:null,  nom100:7000,  memo:"早朝深夜料金" },
  { id:nid(), date:"1/12", name:"金子 実央", payment:30000, nom100:1100, memo:"成人式(卒業式）ヘアメイク (1,100)" },
  { id:nid(), date:"1/12", name:"クドウ マオ", payment:30000, nom100:1100, memo:"成人式(卒業式）ヘアメイク (1,100)" },
  { id:nid(), date:"1/13", name:"松尾千寿", payment:11000, nom100:500,   memo:"メイク (500)" },
  { id:nid(), date:"1/13", name:"KAI",      payment:14000, nom100:900,   memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"1/13", name:"KAI",      payment:null,  nom100:2000,  memo:"早朝深夜料金" },
  { id:nid(), date:"1/13", name:"松尾千寿", payment:11000, nom100:400,   memo:"ヘアセットA (400)" },
  { id:nid(), date:"1/13", name:"HARU",     payment:12000, nom100:900,   memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"1/13", name:"LEN",      payment:12000, nom100:900,   memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"1/14", name:"ゆみ(現場メイク)", payment:9000, nom100:1000, memo:"同行ヘアメイク" },
  { id:nid(), date:"1/14", name:"ゆみ(現場メイク)", payment:null, nom100:4000, memo:"早朝深夜料金" },
  { id:nid(), date:"1/21", name:"寺脇空",   payment:5000,  nom100:400,   memo:"ヘアセットA (400)" },
  { id:nid(), date:"1/21", name:"寺脇空",   payment:null,  nom100:2000,  memo:"早朝深夜料金" },
  { id:nid(), date:"1/22", name:"鈴木光朝", payment:5000,  nom100:500,   memo:"指名料 (500)" },
  { id:nid(), date:"1/22", name:"鈴木光朝", payment:null,  nom100:400,   memo:"ヘアセットB (400)" },
  { id:nid(), date:"1/30", name:"佐山未来", payment:5000,  nom100:400,   memo:"ヘアセットB (400)" },
];
const TAKAYAMA_JAN_SAL = {
  基本給:213019, 役職手当:10000, 交通費:8708, 年末調整還付金:null, サロン指名料:30000,
  健康保険料:null, 厚生年金保険料:null, 雇用保険料:null, 所得税:null, 住民税:null,
  支給合計:277727, 控除合計:41991, 差引支給額:235736,
};

// --- 髙山2月 ---
const TAKAYAMA_FEB_JOBS = [
  { id:nid(), date:"2/1",  client:"小林裕介" },
  { id:nid(), date:"2/2",  client:"ブル8" },
  { id:nid(), date:"2/5",  client:"EVER.CONFIDENT 鈴木さん案件" },
  { id:nid(), date:"2/6",  client:"NUL." },
  { id:nid(), date:"2/8",  client:"クインテ" },
  { id:nid(), date:"2/11", client:"defspiral" },
  { id:nid(), date:"2/13", client:"NUL." },
  { id:nid(), date:"2/14", client:"小林裕介" },
  { id:nid(), date:"2/15", client:"かんなちゃる" },
  { id:nid(), date:"2/16", client:"樋口さん撮影" },
  { id:nid(), date:"2/17", client:"VALHALLA" },
  { id:nid(), date:"2/18", client:"富田美憂" },
  { id:nid(), date:"2/21", client:"富田美憂" },
  { id:nid(), date:"2/22", client:"ちゃんげろソニック" },
  { id:nid(), date:"2/23", client:"VALHALLA" },
  { id:nid(), date:"2/27", client:"藤原夏海" },
  { id:nid(), date:"2/28", client:"VALHALLA" },
].map(j=>({...j, id:nid(), guarantee:null}));
const TAKAYAMA_FEB_SALON = [
  { id:nid(), date:"2/1",  name:"ぱっち",     payment:11000, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"2/1",  name:"熊懐綾子",   payment:17000, nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"2/1",  name:"熊懐綾子",   payment:null,  nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"2/5",  name:"寺脇空",     payment:5500,  nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"2/5",  name:"寺脇空",     payment:null,  nom100:2000, memo:"早朝深夜料金×2 (2,000)" },
  { id:nid(), date:"2/7",  name:"東川仁美",   payment:11800, nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"2/7",  name:"東川仁美",   payment:null,  nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"2/7",  name:"山田愛結",   payment:12100, nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"2/7",  name:"山田愛結",   payment:null,  nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"2/7",  name:"ReLITかなた", payment:25000, nom100:900, memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"2/7",  name:"ReLITまさや", payment:25000, nom100:900, memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"2/8",  name:"MeseMoa.あおい", payment:13000, nom100:900, memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"2/8",  name:"MeseMoa.あおい", payment:null, nom100:2000, memo:"早朝深夜料金×2 (2,000)" },
  { id:nid(), date:"2/11", name:"U&pia",       payment:19000, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"2/11", name:"U&pia",       payment:null,  nom100:7000, memo:"早朝深夜料金×7 (7,000)" },
  { id:nid(), date:"2/11", name:"土屋愛",      payment:9400,  nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"2/13", name:"なるき",      payment:14000, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"2/13", name:"なるき",      payment:null,  nom100:3000, memo:"早朝深夜料金×3 (3,000)" },
  { id:nid(), date:"2/14", name:"寺脇空",      payment:6500,  nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"2/14", name:"寺脇空",      payment:null,  nom100:3000, memo:"早朝深夜料金×3 (3,000)" },
  { id:nid(), date:"2/14", name:"佐貫まなみ",  payment:7500,  nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"2/14", name:"渡邉美夕",    payment:8500,  nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"2/14", name:"ペトスクリスティ", payment:null, nom100:400, memo:"ヘアセットB (400)" },
  { id:nid(), date:"2/14", name:"平川柚真",    payment:11000, nom100:900,  memo:"お任せメンズメニュー(900)" },
  { id:nid(), date:"2/14", name:"佐山未来",    payment:3500,  nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"2/14", name:"太田めぐみ",  payment:16500, nom100:1100, memo:"成人式(卒業式) ヘアメイク (1,100)" },
  { id:nid(), date:"2/14", name:"中山亮",      payment:3500,  nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"2/15", name:"寺脇空",      payment:8500,  nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"2/15", name:"寺脇空",      payment:null,  nom100:5000, memo:"早朝深夜料金×5 (5,000)" },
  { id:nid(), date:"2/15", name:"渡邉美夕",    payment:8500,  nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"2/15", name:"ぱっち",      payment:15000, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"2/15", name:"佐貫まなみ",  payment:7500,  nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"2/15", name:"五月女なみ",  payment:9000,  nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"2/15", name:"五月女なみ",  payment:null,  nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"2/18", name:"高木朋弥",    payment:10000, nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"2/18", name:"高木朋弥",    payment:null,  nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"2/18", name:"北島ひなた",  payment:6500,  nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"2/27", name:"佐山未来",    payment:4500,  nom100:400,  memo:"ヘアセットA (400)" },
];
const TAKAYAMA_FEB_SAL = {
  基本給:213019, 役職手当:10000, 交通費:7645, 年末調整還付金:null, サロン指名料:30000,
  健康保険料:null, 厚生年金保険料:null, 雇用保険料:null, 所得税:null, 住民税:null,
  支給合計:287964, 控除合計:42373, 差引支給額:245591,
};

// --- 春日井1月 ---
const KASUGAI_JAN_JOBS = [
  { id:nid(), date:"1/5",  client:"北千住ゲロッパ19:30〜22:30",    guarantee:9000,  payment:9000 },
  { id:nid(), date:"1/6",  client:"ホスト　ヘアセット",            guarantee:30000, payment:15000 },
  { id:nid(), date:"1/7",  client:"上野グランクリュ19:30〜22:30",  guarantee:9000,  payment:9000 },
  { id:nid(), date:"1/8",  client:"錦糸町蓮19:00〜22:00",          guarantee:9000,  payment:9000 },
  { id:nid(), date:"1/9",  client:"富田美憂",                       guarantee:30000, payment:15000 },
  { id:nid(), date:"1/9",  client:"上野グランクリュ19:30〜22:30",  guarantee:9000,  payment:9000 },
  { id:nid(), date:"1/11", client:"成人式　藤井さん",              guarantee:40000, payment:25000 },
  { id:nid(), date:"1/11", client:"鳴海米がち収穫祭",              guarantee:40000, payment:18000 },
  { id:nid(), date:"1/12", client:"成人式　藤井さん",              guarantee:42000, payment:25000 },
  { id:nid(), date:"1/12", client:"上野グランクリュ19:30〜22:30",  guarantee:9000,  payment:9000 },
  { id:nid(), date:"1/14", client:"五反田スパイス19:30〜23:00",    guarantee:10500, payment:9000 },
  { id:nid(), date:"1/15", client:"U&pia",                          guarantee:40000, payment:18000 },
  { id:nid(), date:"1/16", client:"上野グランクリュ19:30〜22:30",  guarantee:9000,  payment:9000 },
  { id:nid(), date:"1/19", client:"My_Stage アー写",               guarantee:40000, payment:18000 },
  { id:nid(), date:"1/20", client:"My_Stage アー写",               guarantee:40000, payment:18000 },
  { id:nid(), date:"1/23", client:"My_Stage アー写",               guarantee:40000, payment:18000 },
  { id:nid(), date:"1/24", client:"葛西ベルネス19:30〜22:30",      guarantee:10000, payment:9000 },
  { id:nid(), date:"1/25", client:"富田美憂",                       guarantee:50000, payment:15000 },
  { id:nid(), date:"1/25", client:"めーとる",                       guarantee:30000, payment:15000 },
  { id:nid(), date:"1/26", client:"新橋フローレンスクラブ19:30〜22:30", guarantee:10000, payment:9000 },
  { id:nid(), date:"1/28", client:"上野エピック19:00〜21:30",       guarantee:9000,  payment:9000 },
  { id:nid(), date:"1/29", client:"新橋フローレンスクラブ19:30〜22:30", guarantee:10000, payment:9000 },
  { id:nid(), date:"1/29", client:"三浦魁",                         guarantee:30000, payment:15000 },
  { id:nid(), date:"1/31", client:"銀座パラディ20:00〜23:00",       guarantee:15000, payment:9000 },
];
const KASUGAI_JAN_SALON = [
  { id:nid(), date:"1/13", name:"脇谷咲菜",   payment:15200, nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"1/13", name:"脇谷咲菜",   payment:null,  nomination:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"1/13", name:"脇谷咲菜",   payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/13", name:"松尾玲奈",   payment:10500, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/13", name:"松尾玲奈",   payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"1/13", name:"松尾玲奈",   payment:null,  nomination:500,  memo:"指名料 (500)" },
  { id:nid(), date:"1/13", name:"松尾玲奈",   payment:null,  nomination:1000, memo:"早朝深夜料金" },
  { id:nid(), date:"1/13", name:"森澄香",     payment:19900, nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"1/13", name:"森澄香",     payment:null,  nomination:4000, memo:"春日井セットC (4,000)" },
  { id:nid(), date:"1/13", name:"岩嵜好樹",   payment:11700, nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"1/13", name:"岩嵜好樹",   payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/13", name:"佐藤菜月",   payment:4500,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/24", name:"江西千恵子", payment:14000, nomination:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"1/24", name:"江西千恵子", payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/24", name:"江西千恵子", payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"1/24", name:"斎藤真直美", payment:14000, nomination:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"1/24", name:"斎藤真直美", payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/24", name:"斎藤真直美", payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"1/24", name:"重富祐奈",   payment:4600,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/24", name:"濱田夏郎",   payment:10000, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/24", name:"濱田夏郎",   payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"1/25", name:"寺脇空",     payment:7500,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/25", name:"寺脇空",     payment:null,  nomination:4000, memo:"早朝深夜料金" },
  { id:nid(), date:"1/25", name:"鈴木光朝",   payment:7500,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"1/25", name:"鈴木光朝",   payment:null,  nomination:3000, memo:"早朝深夜料金" },
];
const KASUGAI_JAN_TRAFFIC = [
  { id:nid(), date:"1/8",  memo:"グランブリエリハーサル", amount:500 },
  { id:nid(), date:"1/11", memo:"小竹向原駅〜京成成田駅 片道:1,160円、往復:2,320円", amount:2320 },
  { id:nid(), date:"1/12", memo:"小竹向原駅〜所沢駅 片道:320円、往復:640円", amount:640 },
  { id:nid(), date:"1/13", memo:"小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"1/13", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"1/24", memo:"小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"1/24", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"1/25", memo:"小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"1/25", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
];
const KASUGAI_JAN_SAL = {
  講習費天引き:2250, 備品代交通費:0,
};

// --- 春日井2月 ---
const KASUGAI_FEB_JOBS = [
  { id:nid(), date:"2/2",  client:"上野ラウンジクラス19:30〜22:30",    guarantee:9000,  payment:9000 },
  { id:nid(), date:"2/3",  client:"五反田スパイス19:30〜23:00",        guarantee:10500, payment:9000 },
  { id:nid(), date:"2/4",  client:"銀座ジェイボーグ19:00〜22:00",      guarantee:10000, payment:9000 },
  { id:nid(), date:"2/5",  client:"銀座パラディ20:00〜23:00",          guarantee:15000, payment:9000 },
  { id:nid(), date:"2/6",  client:"Mリーグ",                           guarantee:50000, payment:20000 },
  { id:nid(), date:"2/6",  client:"浦和希・今井文也",                  guarantee:30000, payment:15000 },
  { id:nid(), date:"2/7",  client:"グランブリエ",                      guarantee:null,  payment:20472 },
  { id:nid(), date:"2/7",  client:"銀座パラディ20:00〜23:00",          guarantee:15000, payment:9000 },
  { id:nid(), date:"2/8",  client:"富田美憂",                          guarantee:50000, payment:15000 },
  { id:nid(), date:"2/9",  client:"銀座ジェイボーグ19:00〜22:00",      guarantee:12000, payment:9000 },
  { id:nid(), date:"2/10", client:"新橋フローレンスクラブ19:30〜22:30", guarantee:10000, payment:9000 },
  { id:nid(), date:"2/11", client:"銀座パラディ20:00〜23:00",          guarantee:15000, payment:9000 },
  { id:nid(), date:"2/12", client:"銀座ジェイボーグ19:00〜22:00",      guarantee:13000, payment:10000 },
  { id:nid(), date:"2/13", client:"銀座ジェイボーグ19:00〜22:00",      guarantee:12000, payment:9000 },
  { id:nid(), date:"2/16", client:"浦和希　BS11",                      guarantee:30000, payment:15000 },
  { id:nid(), date:"2/17", client:"ラムネ",                            guarantee:30000, payment:20000 },
  { id:nid(), date:"2/17", client:"北千住グロッパ19:30〜22:30",        guarantee:10000, payment:9000 },
  { id:nid(), date:"2/19", client:"富田美憂",                          guarantee:15000, payment:15000 },
  { id:nid(), date:"2/20", client:"銀座ジェイボーグ19:00〜22:00",      guarantee:13000, payment:10000 },
  { id:nid(), date:"2/21", client:"新橋フローレンスクラブ19:30〜22:30", guarantee:10000, payment:9000 },
  { id:nid(), date:"2/22", client:"ちゃんげろソニック",                guarantee:50000, payment:20000 },
  { id:nid(), date:"2/24", client:"上野エピック19:00〜21:30",          guarantee:10000, payment:9000 },
  { id:nid(), date:"2/25", client:"北千住ましゅまろきゃんでぃ19:30〜", guarantee:10000, payment:9000 },
  { id:nid(), date:"2/26", client:"上野カポネ19:00〜22:00",            guarantee:10000, payment:9000 },
  { id:nid(), date:"2/27", client:"銀座ジェイボーグ19:00〜22:00",      guarantee:12000, payment:9000 },
];
const KASUGAI_FEB_SALON = [
  { id:nid(), date:"2/21", name:"江西 千恵子様", payment:14000, nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"2/21", name:"江西 千恵子様", payment:null,  nomination:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"2/21", name:"江西 千恵子様", payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/21", name:"野村 優輝様",   payment:11000, nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"2/21", name:"野村 優輝様",   payment:null,  nomination:1000, memo:"早朝深夜料金" },
  { id:nid(), date:"2/21", name:"野村 優輝様",   payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/21", name:"織田 一希様",   payment:2000,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/21", name:"臼井佑斗様",    payment:4000,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"2/21", name:"臼井佑斗様",    payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/22", name:"平山 瑠羽様",   payment:15000, nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"2/22", name:"平山 瑠羽様",   payment:null,  nomination:5000, memo:"早朝深夜料金" },
  { id:nid(), date:"2/22", name:"平山 瑠羽様",   payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/28", name:"安住元希様",    payment:7000,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/28", name:"安住元希様",    payment:null,  nomination:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"2/28", name:"原 彩様",       payment:5500,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/28", name:"原 彩様",       payment:null,  nomination:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"2/28", name:"白水浩貴様",    payment:10000, nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"2/28", name:"白水浩貴様",    payment:null,  nomination:2000, memo:"早朝深夜料金" },
  { id:nid(), date:"2/28", name:"白水浩貴様",    payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/28", name:"細川有希様",    payment:10000, nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"2/28", name:"細川有希様",    payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/28", name:"田中唯 様",     payment:9000,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/28", name:"峯崎貴裕様",    payment:4700,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"2/28", name:"峯崎貴裕様",    payment:null,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"2/28", name:"熊﨑愛加様",    payment:14500, nomination:5000, memo:"春日井セットC (5,000)" },
];
const KASUGAI_FEB_TRAFFIC = [
  { id:nid(), date:"2/5",  memo:"リハーサル交通費", amount:500 },
  { id:nid(), date:"2/7",  memo:"小竹向原駅〜池袋駅〜新橋駅　390円", amount:390 },
  { id:nid(), date:"2/7",  memo:"　新橋駅〜赤坂見附駅〜小竹向原駅　260円", amount:260 },
  { id:nid(), date:"2/21", memo:"小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"2/21", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"2/22", memo:"小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"2/22", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"2/28", memo:"小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"2/28", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
];
const KASUGAI_FEB_SAL = {
  講習費天引き:1500, 備品代交通費:0,
};

// --- 竹内3月 ---
const TAKEUCHI_MAR_JOBS = [
  { id:nid(), date:"3/1",  client:"僕達は夜な夜な",         guarantee:50000 },
  { id:nid(), date:"3/2",  client:"モリスレイ 撮影",        guarantee:50000 },
  { id:nid(), date:"3/4",  client:"浦島坂田船 ゲネ",        guarantee:75000 },
  { id:nid(), date:"3/5",  client:"浦島坂田船 北海道",      guarantee:75000 },
  { id:nid(), date:"3/6",  client:"移動日",                 guarantee:10000 },
  { id:nid(), date:"3/7",  client:"浦島坂田船 秋田",        guarantee:75000 },
  { id:nid(), date:"3/8",  client:"浦島坂田船 岩手",        guarantee:75000 },
  { id:nid(), date:"3/10", client:"肉ちょも",               guarantee:30000 },
  { id:nid(), date:"3/10", client:"重松千晴",               guarantee:30000 },
  { id:nid(), date:"3/11", client:"浦島坂田船 川口さん",    guarantee:100000 },
  { id:nid(), date:"3/14", client:"あらき 東京",            guarantee:80000 },
  { id:nid(), date:"3/15", client:"浦島坂田船 茨城",        guarantee:75000 },
  { id:nid(), date:"3/17", client:"肉ちょも",               guarantee:30000 },
  { id:nid(), date:"3/18", client:"坂田 グッズ撮影",        guarantee:65000 },
  { id:nid(), date:"3/19", client:"肉ちょも MV",            guarantee:50000 },
  { id:nid(), date:"3/20", client:"小林親弘",               guarantee:40000 },
  { id:nid(), date:"3/21", client:"浦島坂田船 高知",        guarantee:75000 },
  { id:nid(), date:"3/22", client:"浦島坂田船 香川",        guarantee:75000 },
  { id:nid(), date:"3/23", client:"戻り日",                 guarantee:10000 },
  { id:nid(), date:"3/24", client:"肉ちょも",               guarantee:30000 },
  { id:nid(), date:"3/28", client:"浦島坂田船 金沢",        guarantee:75000 },
  { id:nid(), date:"3/29", client:"浦島坂田船 福井",        guarantee:75000 },
  { id:nid(), date:"3/30", client:"小林親弘・伊藤健太郎",   guarantee:70000 },
  { id:nid(), date:"3/31", client:"肉ちょも",               guarantee:30000 },
];
const TAKEUCHI_MAR_SALON = [
  { id:nid(), date:"3/9", name:"山崎恒",       payment:12000, discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"3/9", name:"田岡憩多廊",   payment:4000,  discount:1000, nom100:2000, memo:"" },
];
const TAKEUCHI_MAR_SAL = {
  基本給:213019, 役職手当:100000, 通勤費:3537, 出張手当:42000, 出張日数:"21日",
  保険料調整:0, 交通費:13370, オシャレ手当:10000, 年末調整還付金:null,
  サロン指名料:13750,
  健康保険料:12883, 厚生年金保険料:23790, 雇用保険料:6825, 所得税:199769, 住民税:24500,
};
const TAKEUCHI_MAR_INS = [
  { label:"保険料調整合計（1〜2月: 各450,000円）",  amount:450000, sub:null },
  { label:"  うち社会保険料（健保＋厚年）",            amount:null,   sub:36673 },
  { label:"  うち所得税",                              amount:null,   sub:null  },
  { label:"  うち住民税",                              amount:null,   sub:24500 },
  { label:"  うち余剰分",                              amount:null,   sub:null  },
  { label:"差引（自動）", isDiff:true },
  { label:"保険料調整合計（3〜5月: 各300,000円）",  amount:300000, sub:null },
];

// --- 深澤3月 ---
const FUKAZAWA_MAR_JOBS = [
  { id:nid(), date:"3/1",  client:"摩天楼オペラ",          guarantee:45000 },
  { id:nid(), date:"3/2",  client:"ViViD",                 guarantee:45000 },
  { id:nid(), date:"3/3",  client:"肉ちょも",              guarantee:30000 },
  { id:nid(), date:"3/4",  client:"平賀 優介",             guarantee:40000 },
  { id:nid(), date:"3/5",  client:"HIZAKI ソロ",           guarantee:45000 },
  { id:nid(), date:"3/7",  client:"超学生 大阪",           guarantee:45000 },
  { id:nid(), date:"3/8",  client:"めいちゃん 石川",       guarantee:75000 },
  { id:nid(), date:"3/9",  client:"戻り日",                guarantee:10000 },
  { id:nid(), date:"3/11", client:"奏音69 撮影",           guarantee:50000 },
  { id:nid(), date:"3/12", client:"イクオ MV",             guarantee:50000 },
  { id:nid(), date:"3/13", client:"めいちゃん TikTok",     guarantee:50000 },
  { id:nid(), date:"3/13", client:"そらる",                guarantee:50000 },
  { id:nid(), date:"3/14", client:"めいちゃん 大阪",       guarantee:75000 },
  { id:nid(), date:"3/15", client:"めいちゃん 大阪",       guarantee:75000 },
  { id:nid(), date:"3/16", client:"戻り日",                guarantee:10000 },
  { id:nid(), date:"3/17", client:"摩天楼オペラ 神戸",     guarantee:45000 },
  { id:nid(), date:"3/18", client:"摩天楼オペラ 奈良",     guarantee:45000 },
  { id:nid(), date:"3/19", client:"移動日",                guarantee:10000 },
  { id:nid(), date:"3/20", client:"摩天楼オペラ 大阪",     guarantee:45000 },
  { id:nid(), date:"3/21", client:"めいちゃん 北海道",     guarantee:75000 },
  { id:nid(), date:"3/22", client:"戻り日",                guarantee:10000 },
  { id:nid(), date:"3/22", client:"超学生",                guarantee:45000 },
  { id:nid(), date:"3/24", client:"D'ESPAIRSRAY",          guarantee:50000 },
  { id:nid(), date:"3/27", client:"ViViD",                 guarantee:45000 },
  { id:nid(), date:"3/28", client:"めいちゃん 福岡",       guarantee:75000 },
  { id:nid(), date:"3/29", client:"めいちゃん 福岡",       guarantee:75000 },
  { id:nid(), date:"3/30", client:"戻り日",                guarantee:10000 },
  { id:nid(), date:"3/31", client:"Unlucky Morpheus 撮影", guarantee:45000 },
];
const FUKAZAWA_MAR_SALON = [
  { id:nid(), date:"3/11", name:"平澤梨央",     payment:4500,  discount:null, nom100:null, memo:"" },
  { id:nid(), date:"3/13", name:"田岡憩多廊",   payment:4000,  discount:null, nom100:7000, memo:"" },
  { id:nid(), date:"3/13", name:"宮下タケル",   payment:4000,  discount:null, nom100:2000, memo:"" },
  { id:nid(), date:"3/25", name:"寺脇空",       payment:4000,  discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"3/25", name:"浦上雅仁",     payment:11000, discount:null, nom100:3000, memo:"" },
  { id:nid(), date:"3/25", name:"高橋芽衣",     payment:7100,  discount:null, nom100:null, memo:"" },
];
const FUKAZAWA_MAR_SAL = {
  基本給:213019, 役職手当:40000, 通勤費:1168, 出張手当:33000, 出張日数:"22日",
  保険料調整:0, 交通費:10910, オシャレ手当:10000, 年末調整還付金:null,
  サロン指名料:57300,
  健康保険料:15856, 厚生年金保険料:29280, 雇用保険料:5886, 所得税:141118, 住民税:26000,
};
const FUKAZAWA_MAR_INS = [
  { label:"保険料調整合計（1〜2月: 各375,000円）",  amount:375000, sub:null },
  { label:"  うち社会保険料（健保＋厚年）",            amount:null,   sub:45136 },
  { label:"  うち所得税",                              amount:null,   sub:null  },
  { label:"  うち住民税",                              amount:null,   sub:26000 },
  { label:"  うち余剰分",                              amount:null,   sub:null  },
  { label:"差引（自動）", isDiff:true },
  { label:"保険料調整合計（3〜5月: 各250,000円）",  amount:250000, sub:null },
];

// --- 松本3月 ---
const MATSUMOTO_MAR_JOBS = [
  { id:nid(), date:"3/4",  client:"浦島坂田船 ゲネ",       guarantee:75000 },
  { id:nid(), date:"3/5",  client:"浦島坂田船 北海道",     guarantee:75000 },
  { id:nid(), date:"3/6",  client:"移動日",                guarantee:10000 },
  { id:nid(), date:"3/7",  client:"浦島坂田船 秋田",       guarantee:75000 },
  { id:nid(), date:"3/8",  client:"浦島坂田船 岩手",       guarantee:75000 },
  { id:nid(), date:"3/11", client:"浦島坂田船 川口さん",   guarantee:100000 },
  { id:nid(), date:"3/15", client:"浦島坂田船 茨城",       guarantee:75000 },
  { id:nid(), date:"3/17", client:"ASH",                   guarantee:50000 },
  { id:nid(), date:"3/18", client:"Geroカット",            guarantee:5000 },
  { id:nid(), date:"3/19", client:"肉ちょも MV",           guarantee:50000 },
  { id:nid(), date:"3/21", client:"浦島坂田船 高知",       guarantee:75000 },
  { id:nid(), date:"3/22", client:"浦島坂田船 香川",       guarantee:75000 },
  { id:nid(), date:"3/23", client:"戻り日",                guarantee:10000 },
  { id:nid(), date:"3/28", client:"浦島坂田船 金沢",       guarantee:75000 },
  { id:nid(), date:"3/29", client:"浦島坂田船 福井",       guarantee:75000 },
  { id:nid(), date:"3/30", client:"戻り日",                guarantee:10000 },
];
const MATSUMOTO_MAR_SALON = [
  { id:nid(), date:"3/17", name:"浦田　ヘアセット", payment:12000, discount:null, nom100:1000, memo:"" },
];
const MATSUMOTO_MAR_SAL = {
  役員報酬:900000, 出張手当:45500, 出張日数:"13日", 交通費:0, 通勤費:0, 年末調整還付金:null,
  健康保険料:35180, 介護保険料:5645, 厚生年金保険料:59475, 所得税:45390, 住民税:42600, 会社立替返済分:null,
};

// --- 髙山3月 ---
const TAKAYAMA_MAR_JOBS = [
  { id:nid(), date:"3/1",  client:"NUL." },
  { id:nid(), date:"3/3",  client:"ペトリコール" },
  { id:nid(), date:"3/4",  client:"樋口さん撮影" },
  { id:nid(), date:"3/5",  client:"眼鏡市場のＳＮＳ撮影　上條さん案件" },
  { id:nid(), date:"3/6",  client:"ラムネ商店街" },
  { id:nid(), date:"3/6",  client:"ホストPV" },
  { id:nid(), date:"3/7",  client:"当日キャンセル→歌い手LIVE 金子さん案件" },
  { id:nid(), date:"3/8",  client:"歌い手LIVE 金子さん案件" },
  { id:nid(), date:"3/9",  client:"富田美憂" },
  { id:nid(), date:"3/9",  client:"H@ng_oveR" },
  { id:nid(), date:"3/11", client:"ARAN. 撮影" },
  { id:nid(), date:"3/13", client:"和田雅成" },
  { id:nid(), date:"3/14", client:"きみとぴあ" },
  { id:nid(), date:"3/15", client:"超学生 名古屋" },
  { id:nid(), date:"3/16", client:"富田美憂" },
  { id:nid(), date:"3/20", client:"きんとき オンラインライブ" },
  { id:nid(), date:"3/21", client:"VALHALLA" },
  { id:nid(), date:"3/22", client:"作品撮り" },
  { id:nid(), date:"3/27", client:"U& pia MV" },
  { id:nid(), date:"3/28", client:"会沢沙耶『時々ボソッとロシア語でデレる隣のアーリャさん』" },
  { id:nid(), date:"3/28", client:"小林裕介　テレ東ブース" },
  { id:nid(), date:"3/29", client:"小林裕介　Re:ゼロから始める異世界生活" },
  { id:nid(), date:"3/29", client:"小林裕介「炎炎ノ消防隊」" },
  { id:nid(), date:"3/29", client:"小林裕介　Dr.STONE" },
  { id:nid(), date:"3/29", client:"浦和希　桃源暗鬼" },
  { id:nid(), date:"3/30", client:"DOLL$BOXX アー写撮影" },
  { id:nid(), date:"3/31", client:"こーすけ" },
];
const TAKAYAMA_MAR_SALON = [
  { id:nid(), date:"3/1",  name:"寺脇空",       payment:6500,  discount:null, nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"3/1",  name:"寺脇空",       payment:null,  discount:null, nom100:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/1",  name:"青木沙耶",     payment:6000,  discount:null, nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"3/12", name:"浦上雅仁",     payment:5500,  discount:null, nom100:2000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/12", name:"浦上雅仁",     payment:null,  discount:null, nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"3/12", name:"ReLITまさや",  payment:12000, discount:null, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"3/12", name:"ReLITかなた",  payment:12000, discount:null, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"3/12", name:"松尾唯優",     payment:7500,  discount:null, nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"3/12", name:"松尾唯優",     payment:null,  discount:null, nom100:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/12", name:"miro",         payment:null,  discount:null, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"3/12", name:"小川",         payment:12000, discount:null, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"3/12", name:"紫煉",         payment:12000, discount:null, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"3/15", name:"太田めぐみ",   payment:16100, discount:null, nom100:5000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/15", name:"太田めぐみ",   payment:null,  discount:null, nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"3/15", name:"太田めぐみ",   payment:null,  discount:null, nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"3/16", name:"寺脇空",       payment:null,  discount:null, nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"3/16", name:"川島望衣",     payment:2500,  discount:null, nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"3/16", name:"ミウラアイム", payment:12000, discount:null, nom100:900,  memo:"アーティストヘアメイク (900)" },
  { id:nid(), date:"3/17", name:"伊深まゆ",     payment:25000, discount:null, nom100:600,  memo:"卒業式 ヘアセット (600)" },
  { id:nid(), date:"3/20", name:"北島ひなた",   payment:26000, discount:null, nom100:7000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/20", name:"北島ひなた",   payment:null,  discount:null, nom100:1100, memo:"成人式(卒業式）ヘアメイク (1,100)" },
  { id:nid(), date:"3/20", name:"古畑涼太",     payment:15000, discount:null, nom100:500,  memo:"メイク (500)" },
  { id:nid(), date:"3/20", name:"古畑涼太",     payment:null,  discount:null, nom100:400,  memo:"ヘアセットA (400)" },
  { id:nid(), date:"3/24", name:"鈴木光朝",     payment:5200,  discount:null, nom100:500,  memo:"指名料 (500)" },
  { id:nid(), date:"3/24", name:"鈴木光朝",     payment:null,  discount:null, nom100:400,  memo:"ヘアセットB (400)" },
  { id:nid(), date:"3/25", name:"齋香穂子",     payment:33000, discount:null, nom100:4000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/25", name:"齋香穂子",     payment:null,  discount:null, nom100:1100, memo:"成人式(卒業式）ヘアメイク (1,100)" },
  { id:nid(), date:"3/26", name:"紫煉",         payment:12000, discount:null, nom100:900,  memo:"アーティストヘアメイク (900)" },
];
const TAKAYAMA_MAR_SAL = {
  基本給:213019, 役職手当:10000, サロン指名料:38200, 交通費:15155, 年末調整還付金:null,
  健康保険料:10901, 厚生年金保険料:20130, 雇用保険料:1542, 所得税:6000, 住民税:3800,
};

// --- 青海3月 ---
const AOMI_MAR_JOBS = [
  { id:nid(), date:"3/1",  client:"僕達は夜な夜な",                                            guarantee:50000 },
  { id:nid(), date:"3/4",  client:"佐藤元",                                                    guarantee:30000 },
  { id:nid(), date:"3/4",  client:"しゅーず あいす",                                           guarantee:40000 },
  { id:nid(), date:"3/5",  client:"HIZAKI ソロ",                                               guarantee:30000 },
  { id:nid(), date:"3/6",  client:"小林親弘",                                                  guarantee:40000 },
  { id:nid(), date:"3/7",  client:"小林裕介",                                                  guarantee:50000 },
  { id:nid(), date:"3/8",  client:"浦和希",                                                    guarantee:50000 },
  { id:nid(), date:"3/9",  client:"はへー",                                                    guarantee:40000 },
  { id:nid(), date:"3/9",  client:"浦和希 BS11",                                               guarantee:30000 },
  { id:nid(), date:"3/13", client:"浦和希・今井文也",                                          guarantee:40000 },
  { id:nid(), date:"3/14", client:"摩天楼オペラ",                                              guarantee:30000 },
  { id:nid(), date:"3/15", client:"摩天楼オペラ",                                              guarantee:30000 },
  { id:nid(), date:"3/16", client:"小林裕介",                                                  guarantee:30000 },
  { id:nid(), date:"3/18", client:"UNIQLO 上條さん案件",                                       guarantee:20000 },
  { id:nid(), date:"3/19", client:"UNIQLO 上條さん案件",                                       guarantee:20000 },
  { id:nid(), date:"3/20", client:"きんとき オンラインライブ",                                 guarantee:30000 },
  { id:nid(), date:"3/21", client:"小林裕介",                                                  guarantee:40000 },
  { id:nid(), date:"3/21", client:"浦和希",                                                    guarantee:50000 },
  { id:nid(), date:"3/22", client:"小林裕介",                                                  guarantee:30000 },
  { id:nid(), date:"3/23", client:"浦和希 BS11",                                               guarantee:30000 },
  { id:nid(), date:"3/23", client:"浦和希",                                                    guarantee:60000 },
  { id:nid(), date:"3/24", client:"浦和希",                                                    guarantee:30000 },
  { id:nid(), date:"3/26", client:"山口ハイツ",                                                guarantee:30000 },
  { id:nid(), date:"3/27", client:"浦和希 BS11",                                               guarantee:30000 },
  { id:nid(), date:"3/27", client:"U& pia MV",                                                 guarantee:30000 },
  { id:nid(), date:"3/28", client:"小林親弘『Netflix スペシャルステージ だんでらいおん』",      guarantee:50000 },
  { id:nid(), date:"3/29", client:"しゅーず、あいす",                                          guarantee:60000 },
  { id:nid(), date:"3/29", client:"小林親弘",                                                  guarantee:30000 },
  { id:nid(), date:"3/30", client:"DOLL$BOXX",                                                 guarantee:30000 },
  { id:nid(), date:"3/31", client:"Unlucky Morpheus 撮影",                                     guarantee:30000 },
];
const AOMI_MAR_SALON = [
  { id:nid(), date:"3/6",  name:"濱田夏郎",      payment:10500, discount:null, nom100:4500, memo:"" },
  { id:nid(), date:"3/11", name:"紫煉",          payment:12000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"3/12", name:"濱田夏郎",      payment:10500, discount:null, nom100:1500, memo:"" },
  { id:nid(), date:"3/12", name:"加留部輝",      payment:9000,  discount:null, nom100:500,  memo:"" },
  { id:nid(), date:"3/12", name:"ReLIT/れん",    payment:11000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"3/12", name:"ReLIT/りゅう",  payment:11000, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"3/13", name:"寺脇空",        payment:4000,  discount:null, nom100:null, memo:"" },
  { id:nid(), date:"3/13", name:"岡庭佳代",      payment:18900, discount:1000, nom100:null, memo:"" },
  { id:nid(), date:"3/15", name:"Jones Chloe",   payment:18300, discount:null, nom100:null, memo:"" },
  { id:nid(), date:"3/16", name:"林颯馬",        payment:9000,  discount:null, nom100:null, memo:"" },
  { id:nid(), date:"3/22", name:"峯崎貴裕",      payment:9500,  discount:null, nom100:3000, memo:"" },
];
const AOMI_MAR_SAL = {
  基本給:213019, 役職手当:30000, 出張手当:19200, 出張日数:"24日", 交通費:31482, 年末調整還付金:null,
  サロン指名料:52445,
  健康保険料:11892, 厚生年金保険料:21960, 雇用保険料:1964, 所得税:9280, 住民税:null,
};

// --- 春日井3月 ---
const KASUGAI_MAR_JOBS = [
  { id:nid(), date:"3/2",  client:"銀座ジェイボーグ19:00〜22:00",      guarantee:12000, payment:9000,  profit:3000 },
  { id:nid(), date:"3/3",  client:"上野グランクリュ19:30〜22:30",      guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/5",  client:"上野グランクリュ19:30〜22:30",      guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/6",  client:"ラムネ商店街",                       guarantee:30000, payment:18000, profit:12000 },
  { id:nid(), date:"3/6",  client:"銀座ジェイボーグ19:00〜22:00",      guarantee:13000, payment:9000,  profit:4000 },
  { id:nid(), date:"3/7",  client:"グランブリエ",                       guarantee:36180, payment:20940, profit:15240 },
  { id:nid(), date:"3/9",  client:"H@ng_oveR",                          guarantee:30000, payment:18000, profit:12000 },
  { id:nid(), date:"3/10", client:"上野クラッシュ19:30〜22:30",        guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/11", client:"上野クラッシュ19:30〜22:30",        guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/12", client:"新橋THE ROOM 20:00〜23:00",         guarantee:11000, payment:9000,  profit:2000 },
  { id:nid(), date:"3/13", client:"銀座ジェイボーグ19:00〜22:00",      guarantee:13000, payment:9000,  profit:4000 },
  { id:nid(), date:"3/15", client:"あらき様現場",                       guarantee:45000, payment:15000, profit:30000 },
  { id:nid(), date:"3/16", client:"新橋チェスター21:00〜23:30",        guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/17", client:"秋草学園短大卒業式",                 guarantee:10000, payment:8000,  profit:2000 },
  { id:nid(), date:"3/17", client:"銀座ジェイボーグ19:00〜22:00",      guarantee:12000, payment:9000,  profit:3000 },
  { id:nid(), date:"3/18", client:"久保先生",                           guarantee:30000, payment:15000, profit:15000 },
  { id:nid(), date:"3/18", client:"上野グランクリュ19:30〜22:30",      guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/19", client:"工学院卒業式",                       guarantee:10000, payment:8000,  profit:2000 },
  { id:nid(), date:"3/19", client:"上野グランクリュ19:30〜22:30",      guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/21", client:"グランブリエ",                       guarantee:37080, payment:21840, profit:15240 },
  { id:nid(), date:"3/24", client:"銀座ジェイボーグ19:00〜22:00",      guarantee:12000, payment:9000,  profit:3000 },
  { id:nid(), date:"3/25", client:"海老名卒業式",                       guarantee:18000, payment:8000,  profit:10000 },
  { id:nid(), date:"3/25", client:"上野グランクリュ19:30〜22:30",      guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/27", client:"U&pia MV",                           guarantee:30000, payment:18000, profit:12000 },
  { id:nid(), date:"3/27", client:"上野グランクリュ19:30〜22:30",      guarantee:12000, payment:9000,  profit:3000 },
  { id:nid(), date:"3/28", client:"AJ 丸岡和佳奈様",                    guarantee:30000, payment:15000, profit:15000 },
  { id:nid(), date:"3/28", client:"新橋フローレンスクラブ19:30〜22:30", guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/29", client:"AJ 富田美憂様",                      guarantee:30000, payment:15000, profit:15000 },
  { id:nid(), date:"3/29", client:"AJ 小林親弘様",                      guarantee:30000, payment:15000, profit:15000 },
  { id:nid(), date:"3/29", client:"小林親弘様　配信現場",               guarantee:30000, payment:15000, profit:15000 },
  { id:nid(), date:"3/30", client:"久保先生",                           guarantee:30000, payment:15000, profit:15000 },
  { id:nid(), date:"3/30", client:"上野グランクリュ19:30〜22:30",      guarantee:10000, payment:9000,  profit:1000 },
  { id:nid(), date:"3/31", client:"新橋フローレンスクラブ19:30〜22:30", guarantee:10000, payment:9000,  profit:1000 },
];
const KASUGAI_MAR_SALON = [
  { id:nid(), date:"3/7",  name:"大川翔也",     payment:10000, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/7",  name:"大川翔也",     payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/7",  name:"大川翔也",     payment:null,  nomination:2000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/8",  name:"林拓夢",       payment:15500, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/8",  name:"林拓夢",       payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/8",  name:"林拓夢",       payment:null,  nomination:4000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/8",  name:"千早玲央",     payment:11000, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/8",  name:"千早玲央",     payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/8",  name:"宮内拓海",     payment:21300, nomination:4000, memo:"春日井セットC (4,000)" },
  { id:nid(), date:"3/8",  name:"宮内拓海",     payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/12", name:"ReLIT/ひびき", payment:12000, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/12", name:"ReLIT/ひびき", payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/12", name:"ReLIT/青野",   payment:12000, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/12", name:"ReLIT/青野",   payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/12", name:"ReLIT/はるき", payment:12000, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/12", name:"ReLIT/はるき", payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/15", name:"村松優衣",     payment:20500, nomination:4000, memo:"春日井セットC (4,000)" },
  { id:nid(), date:"3/15", name:"村松優衣",     payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/15", name:"村松優衣",     payment:null,  nomination:5000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/15", name:"佐藤菜月様",   payment:7500,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/15", name:"佐藤菜月様",   payment:null,  nomination:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/20", name:"大川翔也様",   payment:21000, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/20", name:"大川翔也様",   payment:null,  nomination:6000, memo:"メイクレッスン (6,000)" },
  { id:nid(), date:"3/20", name:"大川翔也様",   payment:null,  nomination:2000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/20", name:"大川翔也様",   payment:null,  nomination:500,  memo:"指名料 (500)" },
  { id:nid(), date:"3/21", name:"天谷未来様",   payment:7500,  nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/21", name:"天谷未来様",   payment:null,  nomination:3000, memo:"早朝深夜料金" },
  { id:nid(), date:"3/21", name:"めいみ。様",   payment:14000, nomination:2000, memo:"春日井セット (2,000)" },
  { id:nid(), date:"3/21", name:"めいみ。様",   payment:null,  nomination:3000, memo:"春日井メイク (3,000)" },
  { id:nid(), date:"3/21", name:"めいみ。様",   payment:null,  nomination:2000, memo:"早朝深夜料金" },
];
const KASUGAI_MAR_TRAFFIC = [
  { id:nid(), date:"3/2",  memo:"グランブリエリハーサル交通費", amount:500 },
  { id:nid(), date:"3/7",  memo:"サロンお客様対応　小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"3/7",  memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"3/7",  memo:"小竹向原駅〜池袋駅〜新橋駅　431円", amount:431 },
  { id:nid(), date:"3/7",  memo:"　新橋駅〜赤坂見附駅〜小竹向原駅　252円", amount:252 },
  { id:nid(), date:"3/8",  memo:"サロンお客様対応　小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"3/8",  memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"3/12", memo:"サロンReLIT様　小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"3/12", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"3/15", memo:"サロンお客様対応　小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"3/15", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"3/17", memo:"小竹向原駅〜航空公園駅　往復740円", amount:740 },
  { id:nid(), date:"3/19", memo:"小竹向原駅〜新宿駅　往復420円", amount:420 },
  { id:nid(), date:"3/20", memo:"サロンお客様対応　小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"3/20", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"3/21", memo:"サロンお客様対応　小竹向原駅〜高田馬場駅　330円", amount:330 },
  { id:nid(), date:"3/21", memo:"　高田馬場駅〜小竹向原駅　330円", amount:330 },
  { id:nid(), date:"3/21", memo:"小竹向原駅〜池袋駅〜新橋駅　431円", amount:431 },
  { id:nid(), date:"3/21", memo:"　新橋駅〜赤坂見附駅〜小竹向原駅　252円", amount:252 },
  { id:nid(), date:"3/25", memo:"小竹向原駅〜海老名駅　往復2,040円", amount:2040 },
];
const KASUGAI_MAR_SAL = {
  講習費天引き:0, 備品代交通費:5300,
};

// ===================== v64: 秋野・平澤（業務委託） =====================
// 秋野桃葉 2-4月（実データ・Excel と完全一致検算済み）
const AKINO_FEB_JOBS = [
  {id:nid(),date:"2/23",content:"富田美憂 アシスタント",depart:"高田馬場",arrive:"外苑前",oneway:387,roundtrip:774,note:"高田馬場⇄外苑前"},
  {id:nid(),date:"2/24",content:"浦和希 アシスタント",depart:"高田馬場",arrive:"代々木",oneway:199,roundtrip:398,note:"高田馬場⇄代々木"},
  {id:nid(),date:"2/25",content:"新橋THE ROOM 20:00〜23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"ヘアセット ¥6,000"},
  {id:nid(),date:"2/25",content:"小林親弘 アシスタント",depart:"高田馬場",arrive:"不動前",oneway:349,roundtrip:698,note:"高田馬場⇄不動前"},
  {id:nid(),date:"2/25",content:"Verde / MV撮影 アシスタント",depart:"高田馬場",arrive:"武蔵小杉",oneway:436,roundtrip:null,note:"片道のみ"},
  {id:nid(),date:"2/26",content:"新橋THE ROOM 20:00〜23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"ヘアセット ¥6,000"},
];
// 秋野2月：基本給=ヘアセット報酬12,000円（2月3月は案件単位精算）
const AKINO_FEB_SAL = { 基本給:12000 };

const AKINO_MAR_JOBS = [
  {id:nid(),date:"3/26",content:"新橋フローレンスクラブ 19:30〜22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"ヘアセット ¥6,000"},
  {id:nid(),date:"3/27",content:"U&pia MV アシスタント",depart:"高田馬場",arrive:"南町田グランベリーパーク",oneway:556,roundtrip:1112,note:"高田馬場⇄南町田グランベリーパーク"},
  {id:nid(),date:"3/28",content:"AnimeJapan アシスタント",depart:"高田馬場",arrive:"東京ビッグサイト",oneway:641,roundtrip:1282,note:"高田馬場⇄東京ビッグサイト"},
  {id:nid(),date:"3/28",content:"新橋THE ROOM 20:00〜23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"ヘアセット ¥6,000"},
  {id:nid(),date:"3/29",content:"AnimeJapan アシスタント",depart:"高田馬場",arrive:"国際展示場",oneway:588,roundtrip:1176,note:"高田馬場⇄国際展示場"},
  {id:nid(),date:"3/29",content:"新橋THE ROOM 20:00〜23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"ヘアセット ¥6,000"},
];
const AKINO_MAR_SAL = { 基本給:18000 };

const AKINO_APR_JOBS = [
  {id:nid(),date:"4/1",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/2",content:"BULL ZEICHEN 88 アシスタント",depart:"高田馬場",arrive:"渋谷",oneway:209,roundtrip:null,note:""},
  {id:nid(),date:"4/2",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/3",content:"樋口さん撮影 アシスタント",depart:"高田馬場",arrive:"中目黒",oneway:387,roundtrip:774,note:""},
  {id:nid(),date:"4/3",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/4",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/6",content:"NUL. アシスタント",depart:"高田馬場",arrive:"目白",oneway:155,roundtrip:310,note:""},
  {id:nid(),date:"4/8",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/9",content:"BULL ZEICHEN 88 アシスタント",depart:"高田馬場",arrive:"渋谷",oneway:209,roundtrip:null,note:""},
  {id:nid(),date:"4/9",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/10",content:"新橋チェスター 21:00〜23:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/11",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/13",content:"摩天楼オペラ アシスタント",depart:"新宿",arrive:"高田馬場",oneway:155,roundtrip:null,note:""},
  {id:nid(),date:"4/14",content:"Milky Monster アシスタント",depart:"高田馬場",arrive:"横浜",oneway:518,roundtrip:1036,note:""},
  {id:nid(),date:"4/16",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/17",content:"青海 アシスタント",depart:"高田馬場",arrive:"渋谷",oneway:209,roundtrip:418,note:""},
  {id:nid(),date:"4/18",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/19",content:"富田美憂・稲垣好 アシスタント",depart:"高田馬場",arrive:"御茶ノ水",oneway:253,roundtrip:506,note:""},
  {id:nid(),date:"4/20",content:"髙山・青海・春日井 アシスタント",depart:"日本大通り",arrive:"高田馬場",oneway:742,roundtrip:null,note:""},
  {id:nid(),date:"4/21",content:"山口ハイツ アシスタント",depart:"高田馬場",arrive:"高円寺",oneway:313,roundtrip:626,note:""},
  {id:nid(),date:"4/23",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/24",content:"U&pia アシスタント",depart:"腰越",arrive:"高田馬場",oneway:1254,roundtrip:null,note:""},
  {id:nid(),date:"4/24",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/25",content:"ニコニコ超会議 アシスタント",depart:"高田馬場",arrive:"海浜幕張",oneway:803,roundtrip:null,note:""},
  {id:nid(),date:"4/25",content:"ニコニコ超会議 アシスタント",depart:"海浜幕張",arrive:"国際展示場",oneway:712,roundtrip:null,note:""},
  {id:nid(),date:"4/25",content:"浦島坂田船 ライブ見学",depart:"東雲",arrive:"高田馬場",oneway:651,roundtrip:null,note:""},
  {id:nid(),date:"4/26",content:"フレッシュ撮影会",depart:"高田馬場",arrive:"川越",oneway:636,roundtrip:1272,note:""},
  {id:nid(),date:"4/29",content:"ナナクロニクル アシスタント",depart:"高田馬場",arrive:"四谷三丁目",oneway:333,roundtrip:666,note:""},
  {id:nid(),date:"4/29",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/30",content:"グラブル 名古屋 アシスタント",depart:"東京駅",arrive:"名古屋駅",oneway:11110,roundtrip:null,note:"新幹線"},
  {id:nid(),date:"4/30",content:"グラブル 名古屋 アシスタント",depart:"名鉄名古屋",arrive:"中部国際空港",oneway:980,roundtrip:null,note:""},
];
const AKINO_APR_SAL = { 基本給:180000 };

// 平澤梨央 2-4月（実データ・Excel と完全一致検算済み）
const HIRASAWA_FEB_JOBS = [
  {id:nid(),date:"2/11",content:"UUUMサロンメイク アシスタント",depart:"玉川学園前",arrive:"高田馬場",oneway:511,roundtrip:1022,note:"玉川学園前⇄高田馬場"},
];
const HIRASAWA_FEB_SAL = { 基本給:0 }; // 2月は交通費のみ

const HIRASAWA_MAR_JOBS = [
  {id:nid(),date:"3/5",content:"502号室 業者点検対応／ウィッグ受取",depart:"玉川学園前",arrive:"高田馬場",oneway:511,roundtrip:1022,note:"玉川学園前⇄高田馬場"},
  {id:nid(),date:"3/5",content:"小倉さんサロン ウィッグ受取",depart:"高田馬場",arrive:"原宿",oneway:199,roundtrip:398,note:"高田馬場⇄原宿"},
  {id:nid(),date:"3/10",content:"現場準備・荷物回収のため出勤",depart:"玉川学園前",arrive:"高田馬場",oneway:511,roundtrip:1022,note:"玉川学園前⇄高田馬場"},
  {id:nid(),date:"3/10",content:"重松千晴 アシスタント",depart:"高田馬場",arrive:"芝浦ふ頭",oneway:460,roundtrip:920,note:"高田馬場⇄芝浦ふ頭"},
  {id:nid(),date:"3/27",content:"U&pia MV アシスタント",depart:"高田馬場",arrive:"南町田グランベリーパーク",oneway:556,roundtrip:null,note:"片道のみ(行き)"},
  {id:nid(),date:"3/27",content:"U&pia MV アシスタント",depart:"南町田グランベリーパーク",arrive:"高田馬場",oneway:724,roundtrip:null,note:"片道のみ(帰り)"},
  {id:nid(),date:"3/29",content:"AnimeJapan アシスタント",depart:"高田馬場",arrive:"東京ビッグサイト",oneway:641,roundtrip:1282,note:"高田馬場⇄東京ビッグサイト"},
  {id:nid(),date:"3/30",content:"DOLL$BOXX アー写撮影 アシスタント",depart:"高田馬場",arrive:"六本木",oneway:377,roundtrip:754,note:"高田馬場⇄六本木"},
];
const HIRASAWA_MAR_SAL = { 基本給:0 }; // 3月は交通費のみ

const HIRASAWA_APR_JOBS = [
  {id:nid(),date:"4/1",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/3",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/4",content:"踊り手イベント／アシスタント",depart:"高田馬場",arrive:"みなとみらい",oneway:711,roundtrip:1422,note:"高田馬場⇄みなとみらい"},
  {id:nid(),date:"4/5",content:"小林裕介 アシスタント",depart:"高田馬場",arrive:"霞ヶ関",oneway:209,roundtrip:418,note:"高田馬場⇄霞ヶ関"},
  {id:nid(),date:"4/7",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/8",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/10",content:"Jupiter アシスタント",depart:"高田馬場",arrive:"西荻窪",oneway:253,roundtrip:null,note:"片道のみ"},
  {id:nid(),date:"4/10",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/11",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/13",content:"kanatsu アシスタント",depart:"高田馬場",arrive:"駒場東大前",oneway:349,roundtrip:698,note:"高田馬場⇄駒場東大前"},
  {id:nid(),date:"4/14",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/16",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/17",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/18",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/22",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/25",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/26",content:"現場見学",depart:"高田馬場",arrive:"新豊洲",oneway:441,roundtrip:882,note:"高田馬場⇄新豊洲"},
  {id:nid(),date:"4/28",content:"新橋THE ROOM 20:00~23:00",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/29",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
  {id:nid(),date:"4/30",content:"新橋フローレンスクラブ 19:30~22:30",depart:"",arrive:"",oneway:null,roundtrip:null,note:"基本給に含む"},
];
const HIRASAWA_APR_SAL = { 基本給:180000 };

// 春日井の交通費明細テーブル
function SalonTable({ rows, rate, isTakayama, isKasugai, monthKey, closed = false, onUpdate, onDelete, onAdd, onPaste, onReorder }) {
  const pasteRef = useRef(null);
  const { isMobile } = useViewport();
  const split = isSplitMonth(monthKey) && !isKasugai && !isTakayama;

  // v113: 日付をソートキーに変換（"5/12" → 512, "12/3" → 1203）
  const dateToSortKey = (d) => {
    if (!d) return 9999;
    const m = String(d).match(/^(\d{1,2})\/(\d{1,2})$/);
    return m ? Number(m[1]) * 100 + Number(m[2]) : 9999;
  };

  // v113: ドラッグ&ドロップ
  const { onDragStart, onDragOver, onDragEnd, dragIdx } = useRowDragDrop(rows, onReorder || (()=>{}));

  // v113: 日付変更時に自動ソート
  const handleDateChange = (i, val) => {
    onUpdate(i, "date", val);
    if (!val || !onReorder) return;
    // 次のイベントループで rows が更新された後にソート
    setTimeout(() => {
      const updated = rows.map((r, idx) => idx === i ? {...r, date: val} : r);
      const sorted = [...updated].sort((a, b) => dateToSortKey(a.date) - dateToSortKey(b.date));
      // 順序が変わった場合のみ reorder
      const changed = sorted.some((r, idx) => r.id !== updated[idx]?.id);
      if (changed) onReorder(sorted);
    }, 300);
  };

  const totPayment  = rows.reduce((s,r) => s+num(r.payment),0);
  const totDiscount = rows.reduce((s,r) => s+num(r.discount),0);
  const tot65       = isKasugai || isTakayama ? 0 : rows.reduce((s,r) => s+calcSalon65(r.payment,r.discount,rate),0);
  const tot100      = isKasugai || isTakayama
    ? rows.reduce((s,r) => s+num(r.nom100),0)
    : rows.reduce((s,r) => s+get100(r, monthKey),0);
  const totShimei   = split ? rows.reduce((s,r) => s+num(r.nomShimei),0) : 0;
  const totSoucho   = split ? rows.reduce((s,r) => s+num(r.nomSoucho),0) : 0;
  const totHanbai   = split ? rows.reduce((s,r) => s+num(r.nomHanbai),0) : 0;
  const totCancel   = split ? rows.reduce((s,r) => s+num(r.nomCancel),0) : 0;
  const totNom      = isKasugai
    ? rows.reduce((s,r) => s+num(r.nomination),0)
    : isTakayama
      ? rows.reduce((s,r) => s+num(r.nom100),0)
      : rows.reduce((s,r) => s+calcNomination(r.payment,r.discount,rate,get100(r, monthKey)),0);

  // v59: スマート自動判定ペースト（列順を自動判定）
  const handlePaste = (e) => {
    if (closed) { e.preventDefault(); return; } // v134: 締めた月は貼り付けない
    const text = e.clipboardData.getData("text");
    if (!text) return;
    e.preventDefault();
    const rows2 = parseTab(text).filter(r => r.some(c=>c));

    // ヘッダ行があれば除外（「日付」「お名前」「支払」などが含まれる場合）
    const isHeader = (row) => row.some(c => /日付|お名前|支払|指名料|内容|割引|memo|メモ|早朝深夜|販売手数料/.test(String(c||"")));
    let dataRows = rows2;
    if (dataRows.length > 0 && isHeader(dataRows[0])) dataRows = dataRows.slice(1);

    // 数値かどうか判定
    const isNumeric = (s) => {
      if (!s) return false;
      const cleaned = String(s).replace(/,/g,"").replace(/\s/g,"").replace(/円/g,"").trim();
      if (cleaned === "" || cleaned === "—" || cleaned === "-") return false;
      return /^-?\d+(\.\d+)?$/.test(cleaned);
    };
    const toNum = (s) => {
      if (!s) return null;
      const cleaned = String(s).replace(/,/g,"").replace(/\s/g,"").replace(/円/g,"").trim();
      if (cleaned === "" || cleaned === "—" || cleaned === "-") return null;
      const n = Number(cleaned);
      return isNaN(n) ? null : n;
    };

    // 列順を判定（最大10行サンプル）
    const sample = dataRows.slice(0, Math.min(10, dataRows.length));
    let col4Numeric = 0, col4Text = 0;
    let col5Numeric = 0, col5Text = 0;
    let col6Numeric = 0;
    let col7Numeric = 0;      // v114: 列7(index6)=指名料列の数値判定
    let col7Exists = 0;
    let col10Exists = 0;      // v132: パターンD用（11列）
    let col5to7Numeric = 0;   // v132: 指名料/早朝深夜/販売手数料の数値行
    sample.forEach(r => {
      if (r[3]) {
        if (isNumeric(r[3])) col4Numeric++;
        else col4Text++;
      }
      if (r[4]) {
        if (isNumeric(r[4])) col5Numeric++;
        else col5Text++;
      }
      // v106: 列6(index5)の数値判定（パターンC用：100%列）
      if (r[5] && isNumeric(r[5])) col6Numeric++;
      // v114: 列7(index6)の数値判定（パターンC用：指名料列）
      if (r[6] && isNumeric(r[6])) col7Numeric++;
      // 列7以降の存在チェック
      if (r.length >= 7) col7Exists++;
      // v132: 11列形式（パターンD）
      if (r.length >= 10) col10Exists++;
      if (isNumeric(r[5]) || isNumeric(r[6]) || isNumeric(r[7])) col5to7Numeric++;
    });

    // ヘッダ由来の手がかり（除外前の先頭行）
    const headerHint = rows2.length > 0 && /スタッフ指名料|早朝深夜|販売手数料|100%計/.test(rows2[0].join("\t"));

    // パターン判定
    // パターンA（旧）：日付/名前/支払/指名料or割引/?/nom100/指名料/メモ → 列4=数値
    // パターンB（Excel）：日付/名前/支払/内容/指名料 → 列4=文字列、列5=数値
    // パターンC（v106: Google Sheets 7列）：日付/名前/支払/割引/65%/100%/指名料
    // パターンD（v132: 9月以降11列）：日付/名前/支払/割引/65%/指名料/早朝深夜/販売手数料/100%計/指名料計/メモ
    //   ※列数だけで判定しない（v106/v114の教訓）。月・列幅・ヘッダ/数値の型を併用。
    const isPatternD = split && col4Text === 0 && (
      headerHint ||
      (col10Exists >= Math.max(1, sample.length * 0.5) && col5to7Numeric >= 1)
    );
    const isPatternC = !isPatternD && col4Text === 0 && col5Numeric > col5Text && (
      col6Numeric >= sample.length * 0.5 ||
      (col7Exists >= sample.length * 0.5 && col7Numeric >= sample.length * 0.5)
    );
    const isPatternB = !isPatternD && !isPatternC && (col4Text > col4Numeric || col5Numeric > col5Text);

    const parsed = dataRows.map(r => {
      let payment = toNum(r[2]);
      let discount = null;
      let content = "";
      let nomination_val = null;
      let oldMemo = "";
      let nomShimei = null, nomSoucho = null, nomHanbai = null, nomCancel = null;

      if (isPatternD) {
        // v132: 11列形式
        // [日付, 名前, 支払, 割引, 65%, 指名料, 早朝深夜, 販売手数料, キャンセル代, 100%計, 指名料計, メモ]
        // ※ 11列（キャンセル代なし）の古い形も読めるようにする
        discount = toNum(r[3]);
        nomShimei = toNum(r[5]);
        nomSoucho = toNum(r[6]);
        nomHanbai = toNum(r[7]);
        const hasCancelCol = r.length >= 12;
        nomCancel = hasCancelCol ? toNum(r[8]) : null;
        oldMemo = (hasCancelCol ? r[11] : r[10]) || "";
      } else if (isPatternC) {
        // v106: Google Sheets 7列形式
        // [日付, 名前, 支払, 割引, 65%(自動計算), 100%(nom100), 指名料(自動計算), メモ?]
        discount = toNum(r[3]);
        // 65% (r[4]) と 指名料 (r[6]) は計算で再算出するので無視
        nomination_val = toNum(r[5]);  // 100%列を nom100 に
        oldMemo = r[7]||"";
      } else if (isPatternB) {
        // Excel形式：[日付, 名前, 支払, 内容, 指名料]
        content = String(r[3]||"").trim();
        nomination_val = toNum(r[4]);
      } else {
        // 旧形式：[日付, 名前, 支払, 割引, 内容?, nom100, 指名料, メモ]
        discount = toNum(r[3]);
        if (r[4] && !isNumeric(r[4])) content = String(r[4]).trim();
        nomination_val = toNum(r[6]);
        oldMemo = r[7]||"";
      }

      const item = { id:nid(), date:r[0]||"", name:r[1]||"", payment };
      if (discount != null) item.discount = discount;

      // スタッフタイプによって指名料の格納先と内容の扱いが違う
      if (isKasugai) {
        // 春日井：nomination + memo（プルダウン値）
        item.nomination = nomination_val;
        // 内容を memo に格納（プルダウンで認識される）
        item.memo = content || oldMemo || "";
      } else if (isTakayama) {
        // 髙山：nom100 + memo（プルダウン値）
        item.nom100 = nomination_val;
        item.memo = content || oldMemo || "";
      } else if (isPatternD) {
        item.nomShimei = nomShimei;
        item.nomSoucho = nomSoucho;
        item.nomHanbai = nomHanbai;
        item.nomCancel = nomCancel;
        item.memo = oldMemo || "";
      } else {
        // 通常スタッフ：nom100 として格納（指名料は計算で算出）
        item.nom100 = nomination_val;
        item.memo = oldMemo || "";
        // 通常スタッフは「内容」フィールドが現状ないので、memo に追加情報として格納
        if (content && !oldMemo) item.memo = content;
      }
      return item;
    });
    onPaste(parsed);
  };

  // ========== モバイル：カード形式（v37：お名前を大きく表示） ==========
  const { isNarrow } = useViewport();
  if (isMobile) {
    const S = makeOverviewStyles(isNarrow);
    const delLabel = (r) => `${r.date||'日付未設定'} ${r.name||'お名前未設定'}`;
    return (
      <div>
        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
          <b style={{fontSize:12,color:"#1e3a8a"}}>
            {isKasugai ? `■ 指名料　${rows.length}件　指名料計 ${fmt(totNom)}` : isTakayama ? `■ サロン売上　${rows.length}件　支払 ${fmt(totPayment)}　指名料計 ${fmt(totNom)}` : `■ サロン売上 ${(rate*100).toFixed(0)}%　${rows.length}件　支払 ${fmt(totPayment)}　指名料計 ${fmt(totNom)}`}
          </b>
          <button style={{...BTN(),fontSize:9,padding:"1px 6px",height:"auto",minHeight:0}} onClick={onAdd}>＋</button>
          {!closed && <button style={{...BTN("#374151"),fontSize:9,padding:"1px 6px",height:"auto",minHeight:0}} onClick={()=>pasteRef.current?.focus()}>📋</button>}
        </div>
        {closed ? (
          <div style={{fontSize:9,color:"#6b7280",marginBottom:4}}>{SALON_CLOSED_NOTE}</div>
        ) : (
        <textarea ref={pasteRef} onPaste={handlePaste} placeholder="Excelペースト"
          style={{width:"100%",height:14,fontSize:8,border:"1px dashed #ccc",padding:1,resize:"none",color:"#bbb",boxSizing:"border-box",marginBottom:4}}/>
        )}
        {/* v37: カード形式（1行1カード、お名前を大きく表示） */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {rows.map((r,i)=>{
            const v65 = isKasugai || isTakayama ? 0 : calcSalon65(r.payment, r.discount, rate);
            const n100 = isKasugai ? 0 : isTakayama ? num(r.nom100) : get100(r, monthKey);
            const vNom = isKasugai ? num(r.nomination) : isTakayama ? num(r.nom100) : calcNomination(r.payment, r.discount, rate, n100);
            const hasComment = r.comment && r.comment.trim() !== "";
            return (
              <div key={r.id||i} style={{
                border: hasComment ? "2px solid #dc2626" : "1px solid #d4deea",
                borderRadius:4,
                padding:"6px 8px",
                background: hasComment ? "#fff5f5" : (i%2===0?"#fff":"#f8f9fc"),
              }}>
                {/* 1行目：日付 + お名前（大きく） + コメント + 削除 */}
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                  <span style={{display:"inline-block",minWidth:"40px",fontSize:11,color:"#1e3a8a",fontWeight:"bold",textAlign:"center",background:"#eef2ff",padding:"2px 4px",borderRadius:3}}>
                    <EditableTextCell value={r.date||""} onChange={v=>onUpdate(i,"date",v)} style={{textAlign:"center",padding:0,fontSize:11,color:"#1e3a8a",fontWeight:"bold"}}/>
                  </span>
                  <div style={{flex:"1 1 auto",minWidth:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                    <EditableTextCell value={r.name||""} onChange={v=>onUpdate(i,"name",v)}
                      style={{fontSize:13,fontWeight:"bold",color:"#111",minWidth:"160px",padding:"2px 3px"}}/>
                  </div>
                  <CommentButton comment={r.comment} onChange={v=>onUpdate(i,"comment",v)} label={delLabel(r)}/>
                  <button onClick={()=>S.confirmDelete(onDelete,i,delLabel(r))}
                    style={{background:"transparent",color:"#999",border:"none",padding:"0 2px",fontSize:14,cursor:"pointer",lineHeight:1}} title="この行を削除">×</button>
                </div>
                {/* 2行目：金額 */}
                <div style={{display:"flex",alignItems:"center",gap:10,fontSize:11,paddingLeft:4,flexWrap:"wrap"}}>
                  <span style={{color:"#666"}}>支払</span>
                  <span style={{minWidth:"60px"}}>
                    <EditableNumberCell value={r.payment} onChange={v=>onUpdate(i,"payment",v)}
                      style={{textAlign:"left",padding:"1px 3px",fontWeight:"bold"}}/>
                  </span>
                  {!isKasugai && !isTakayama && (
                    <>
                      <span style={{color:"#666"}}>割引</span>
                      <span style={{minWidth:"50px"}}>
                        <EditableNumberCell value={r.discount} onChange={v=>onUpdate(i,"discount",v)}
                          style={{textAlign:"left",padding:"1px 3px"}}/>
                      </span>
                      <span style={{color:"#1e3a8a",fontWeight:"bold",background:"#eef5ff",padding:"1px 4px",borderRadius:3}}>
                        {(rate*100).toFixed(0)}% {fmtNum(v65)}
                      </span>
                      {split ? (
                        <>
                          <span style={{color:"#666"}}>指名料</span>
                          <span style={{minWidth:"50px"}}>
                            <EditableNumberCell value={r.nomShimei} onChange={v=>onUpdate(i,"nomShimei",v)}
                              style={{textAlign:"left",padding:"1px 3px"}}/>
                          </span>
                          <span style={{color:"#666"}}>早朝深夜</span>
                          <span style={{minWidth:"50px"}}>
                            <EditableNumberCell value={r.nomSoucho} onChange={v=>onUpdate(i,"nomSoucho",v)}
                              style={{textAlign:"left",padding:"1px 3px"}}/>
                          </span>
                          <span style={{color:"#666"}}>販売手数料</span>
                          <span style={{minWidth:"50px"}}>
                            <EditableNumberCell value={r.nomHanbai} onChange={v=>onUpdate(i,"nomHanbai",v)}
                              style={{textAlign:"left",padding:"1px 3px"}}/>
                          </span>
                          <span style={{color:"#666"}}>キャンセル代</span>
                          <span style={{minWidth:"50px"}}>
                            <EditableNumberCell value={r.nomCancel} onChange={v=>onUpdate(i,"nomCancel",v)}
                              style={{textAlign:"left",padding:"1px 3px"}}/>
                          </span>
                          <span style={{color:"#1e3a8a",fontWeight:"bold",background:"#eef5ff",padding:"1px 4px",borderRadius:3}}>
                            100%計 {fmtNum(n100)}
                          </span>
                        </>
                      ) : (
                        <>
                          <span style={{color:"#666"}}>100%</span>
                          <span style={{minWidth:"50px"}}>
                            <EditableNumberCell value={r.nom100} onChange={v=>onUpdate(i,"nom100",v)}
                              style={{textAlign:"left",padding:"1px 3px"}}/>
                          </span>
                        </>
                      )}
                    </>
                  )}
                  {isKasugai && (
                    <>
                      <span style={{color:"#666"}}>指名</span>
                      <span style={{minWidth:"50px"}}>
                        <EditableNumberCell value={r.nomination} onChange={v=>onUpdate(i,"nomination",v)}
                          style={{textAlign:"left",padding:"1px 3px"}}/>
                      </span>
                    </>
                  )}
                  {isTakayama && (
                    <>
                      <span style={{color:"#666"}}>指名</span>
                      <span style={{minWidth:"50px"}}>
                        <EditableNumberCell value={r.nom100} onChange={v=>onUpdate(i,"nom100",v)}
                          style={{textAlign:"left",padding:"1px 3px"}}/>
                      </span>
                    </>
                  )}
                  {!isKasugai && !isTakayama && (
                    <span style={{color:"#1e3a8a",fontWeight:"bold",background:"#c8d4f0",padding:"1px 4px",borderRadius:3}}>
                      指名料計 {fmtNum(vNom)}
                    </span>
                  )}
                </div>
                {hasComment && (
                  <div style={{marginTop:3,padding:"3px 6px",background:"#fff0f0",borderLeft:"3px solid #dc2626",fontSize:10,color:"#dc2626",fontWeight:"bold"}}>
                    💬 {r.comment}
                  </div>
                )}
              </div>
            );
          })}
          {/* 合計カード */}
          <div style={{border:"2px solid #1e3a8a",borderRadius:4,padding:"6px 10px",background:"#eef2ff",display:"flex",alignItems:"center",gap:12,fontWeight:"bold",color:"#1e3a8a",flexWrap:"wrap"}}>
            <span>合計</span>
            <span>支払 {fmt(totPayment)}</span>
            {!isKasugai && !isTakayama && <span>割引 {fmt(totDiscount)}</span>}
            {!isKasugai && !isTakayama && <span style={{background:"#c8d4f0",padding:"1px 6px",borderRadius:3}}>{(rate*100).toFixed(0)}% {fmt(tot65)}</span>}
            {split && <span>指名料 {fmt(totShimei)}</span>}
            {split && <span>早朝深夜 {fmt(totSoucho)}</span>}
            {split && <span>販売手数料 {fmt(totHanbai)}</span>}
            {split && <span>キャンセル代 {fmt(totCancel)}</span>}
            {!isKasugai && !isTakayama && <span>{split ? "100%計" : "100%"} {fmt(tot100)}</span>}
            <span style={{background:"#c8d4f0",padding:"1px 6px",borderRadius:3}}>指名料計 {fmt(totNom)}</span>
          </div>
        </div>
      </div>
    );
  }

  // 金額入力セルのスタイル（min-widthで見切れ防止）
  const tdAmt = { border:"1px solid #ddd", padding:"2px 3px", minWidth:100 };
  const tdAuto = { ...AutoTD };
  const tdNom  = { ...AutoTD, background:"#dce8ff", fontSize:13 };

  return (
    <div className="scroll-x" style={{overflowX:"auto",WebkitOverflowScrolling:"touch",maxWidth:"100%"}}>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
        <b style={{fontSize:13}}>
          {isKasugai ? "指名料（お客様）" : isTakayama ? "サロン指名料（詳細）" : `サロン売上　還元率 ${(rate*100).toFixed(0)}%`}
        </b>
        <button style={BTN()} onClick={onAdd}>＋行追加</button>
        {!closed && <button style={BTN("#374151")} onClick={()=>pasteRef.current?.focus()}>Excelペースト</button>}
      </div>
      {closed ? (
        <div style={{fontSize:11,color:"#6b7280",marginBottom:6}}>{SALON_CLOSED_NOTE}</div>
      ) : (
      <textarea ref={pasteRef} onPaste={handlePaste} placeholder="Excelからコピー→ペースト"
        style={{width:"100%",height:28,fontSize:11,border:"1px dashed #aaa",padding:3,resize:"none",color:"#999",boxSizing:"border-box",marginBottom:4}}/>
      )}
      <table style={{borderCollapse:"collapse",fontSize:12,tableLayout:"auto"}}>
        <thead>
          <tr>
            <th style={{...TH,width:16}}></th>
            <th style={{...TH,width:70}}>日付</th>
            <th style={{...TH,minWidth:160}}>{isKasugai?"お客様":"お名前"}</th>
            <th style={{...TH,minWidth:100}}>支払</th>
            {!isKasugai && !isTakayama && <th style={{...TH,minWidth:90}}>割引</th>}
            {!isKasugai && !isTakayama && <th style={{...TH,minWidth:90,background:"#c8d4f8"}}>{(rate*100).toFixed(0)}%</th>}
            {split && <th style={{...TH,minWidth:90}}>指名料</th>}
            {split && <th style={{...TH,minWidth:90}}>早朝深夜</th>}
            {split && <th style={{...TH,minWidth:90}}>販売手数料</th>}
            {split && <th style={{...TH,minWidth:90}}>キャンセル代</th>}
            {split && <th style={{...TH,minWidth:90,background:"#c8d4f8"}}>100%計</th>}
            {!isKasugai && !isTakayama && !split && <th style={{...TH,minWidth:90}}>100%</th>}
            {(isKasugai||isTakayama) && <th style={{...TH,minWidth:90}}>指名料</th>}
            {!isKasugai && !isTakayama && <th style={{...TH,minWidth:100,background:"#b0c4f5"}}>指名料計</th>}
            {isTakayama && <th style={{...TH,minWidth:160}}>内容</th>}
            {isKasugai && <th style={{...TH,minWidth:160}}>内容</th>}
            <th style={{...TH,width:32}}>メモ</th>
            <th style={{...TH,width:42}}>削除</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r,i) => {
            const v65  = calcSalon65(r.payment,r.discount,rate);
            const n100 = isKasugai ? 0 : isTakayama ? num(r.nom100) : get100(r, monthKey);
            const vNom = isKasugai ? num(r.nomination) : isTakayama ? num(r.nom100) : calcNomination(r.payment,r.discount,rate,n100);
            // v63: ms-salon から取り込んだ行は薄い水色で区別
            const isImported = !!r.squarePaymentId;
            const rowBg = isImported ? "#eff6ff" : (i%2===0 ? "#fff" : "#f7f8fc");
            return (
              <tr key={r.id||i}
                draggable
                onDragStart={()=>onDragStart(i)}
                onDragOver={e=>onDragOver(e,i)}
                onDragEnd={onDragEnd}
                style={{background:rowBg, opacity: dragIdx.current===i ? 0.4 : 1}}
                title={isImported?`📥 ms-salon取込（${r.squarePaymentId?.slice(0,8)}…）`:undefined}>
                <td style={{...TD,padding:"0 2px",cursor:"grab",color:"#bbb",textAlign:"center",fontSize:14}} title="ドラッグして並べ替え">⋮⋮</td>
                <td style={TD}>
                  {isImported && <span style={{fontSize:10,marginRight:2,color:"#3b82f6"}} title="ms-salon取込">📥</span>}
                  <input value={r.date||""} onChange={e=>handleDateChange(i,e.target.value)} style={INP}/>
                </td>
                <td style={TD}><input value={r.name||""} onChange={e=>onUpdate(i,"name",e.target.value)} style={INP}/></td>
                {/* 支払：入力欄をセル幅いっぱいに */}
                <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.payment} onChange={v=>onUpdate(i,"payment",v)}/></td>
                {!isKasugai && !isTakayama && (
                  <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.discount} onChange={v=>onUpdate(i,"discount",v)}/></td>
                )}
                {/* 65%：自動計算、青背景で強調 v104: 支払なし＆指名料100%のみの行も表示 */}
                {!isKasugai && !isTakayama && (
                  <td style={tdAuto}>{(r.payment!=null || n100>0) ? fmt(v65) : <span style={{color:"#bbb"}}>—</span>}</td>
                )}
                {/* v132: 9月以降は3項目＋100%計 */}
                {split && (
                  <>
                    <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.nomShimei} onChange={v=>onUpdate(i,"nomShimei",v)}/></td>
                    <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.nomSoucho} onChange={v=>onUpdate(i,"nomSoucho",v)}/></td>
                    <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.nomHanbai} onChange={v=>onUpdate(i,"nomHanbai",v)}/></td>
                    <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.nomCancel} onChange={v=>onUpdate(i,"nomCancel",v)}/></td>
                    <td style={tdAuto}>{n100>0 ? fmt(n100) : <span style={{color:"#bbb"}}>—</span>}</td>
                  </>
                )}
                {/* 100%：入力（8月以前） */}
                {!isKasugai && !isTakayama && !split && (
                  <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.nom100} onChange={v=>onUpdate(i,"nom100",v)}/></td>
                )}
                {/* 髙山：指名料入力 */}
                {isTakayama && (
                  <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.nom100} onChange={v=>onUpdate(i,"nom100",v)}/></td>
                )}
                {/* 春日井：指名料入力 */}
                {isKasugai && (
                  <td style={{...tdAmt,padding:0}}><EditableNumberCell value={r.nomination} onChange={v=>onUpdate(i,"nomination",v)}/></td>
                )}
                {/* 指名料合計：最も重要な数値、濃い青背景で強調 v104: 合計が0より大きければ表示 */}
                {!isKasugai && !isTakayama && (
                  <td style={tdNom}>{vNom > 0 ? fmt(vNom) : <span style={{color:"#bbb"}}>—</span>}</td>
                )}
                {/* 髙山：内容プルダウン */}
                {isTakayama && (
                  <td style={TD}>
                    <select value={r.memo||""} onChange={e=>{
                      const sel = TAKAYAMA_NOM_OPTIONS.find(o=>o.label===e.target.value);
                      onUpdate(i,"memo",e.target.value);
                      if(sel && sel.value!=null) onUpdate(i,"nom100",sel.value);
                    }} style={{fontSize:11,width:"100%",border:"1px solid #ccc",padding:"3px 4px",background:"#fff"}}>
                      <option value="">---</option>
                      {TAKAYAMA_NOM_OPTIONS.map(o=><option key={o.label} value={o.label}>{o.label}</option>)}
                    </select>
                  </td>
                )}
                {/* 春日井：内容プルダウン */}
                {isKasugai && (
                  <td style={TD}>
                    <select value={r.memo||""} onChange={e=>{
                      const sel = KASUGAI_SALON_OPTIONS.find(o=>o.label===e.target.value);
                      onUpdate(i,"memo",e.target.value);
                      if(sel && sel.value!=null) onUpdate(i,"nomination",sel.value);
                    }} style={{fontSize:11,width:"100%",border:"1px solid #ccc",padding:"3px 4px",background:"#fff"}}>
                      <option value="">---</option>
                      {KASUGAI_SALON_OPTIONS.map(o=><option key={o.label} value={o.label}>{o.label}</option>)}
                    </select>
                  </td>
                )}
                <td style={TDC}><CommentButton comment={r.comment} onChange={v=>onUpdate(i,"comment",v)} label={`${r.date||''} ${r.name||''}`}/></td>
                <td style={TDC}><button style={BTND} onClick={()=>onDelete(i)}>削除</button></td>
              </tr>
            );
          })}
          {/* 合計行 */}
          <tr style={{background:"#e8ecf6"}}>
            <td style={TSUM}></td>
            <td colSpan={2} style={TSUM}>合計</td>
            <td style={AutoTS}>{fmt(totPayment)}</td>
            {!isKasugai && !isTakayama && !split && <><td style={AutoTS}>{fmt(totDiscount)}</td><td style={{...AutoTS,background:"#b8caf8"}}>{fmt(tot65)}</td><td style={AutoTS}>{fmt(tot100)}</td></>}
            {split && <><td style={AutoTS}>{fmt(totDiscount)}</td><td style={{...AutoTS,background:"#b8caf8"}}>{fmt(tot65)}</td><td style={AutoTS}>{fmt(totShimei)}</td><td style={AutoTS}>{fmt(totSoucho)}</td><td style={AutoTS}>{fmt(totHanbai)}</td><td style={AutoTS}>{fmt(totCancel)}</td><td style={{...AutoTS,background:"#b8caf8"}}>{fmt(tot100)}</td></>}
            {(isKasugai||isTakayama) && <td style={AutoTS}>{fmt(totNom)}</td>}
            {!isKasugai && !isTakayama && <td style={{...AutoTS,background:"#94b0f5",fontSize:13}}>{fmt(totNom)}</td>}
            {(isKasugai||isTakayama) && <td style={TSUM}></td>}
            <td style={TSUM}></td>
            <td style={TSUM}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function JobsTable({ jobs, isKasugai, isTakayama, isAomi, onUpdate, onDelete, onAdd, onPaste, onReorder }) {
  const pasteRef = useRef(null);
  const { isMobile } = useViewport();

  // v113: 日付ソートキー
  const dateToSortKey = (d) => {
    if (!d) return 9999;
    const m = String(d).match(/^(\d{1,2})\/(\d{1,2})$/);
    return m ? Number(m[1]) * 100 + Number(m[2]) : 9999;
  };

  // v113: ドラッグ&ドロップ
  const { onDragStart, onDragOver, onDragEnd, dragIdx } = useRowDragDrop(jobs, onReorder || (()=>{}));

  // v113: 日付変更時に自動ソート
  const handleDateChange = (i, val) => {
    onUpdate(i, "date", val);
    if (!val || !onReorder) return;
    setTimeout(() => {
      const updated = jobs.map((j, idx) => idx === i ? {...j, date: val} : j);
      const sorted = [...updated].sort((a, b) => dateToSortKey(a.date) - dateToSortKey(b.date));
      const changed = sorted.some((j, idx) => j.id !== updated[idx]?.id);
      if (changed) onReorder(sorted);
    }, 300);
  };

  const totalG = jobs.reduce((s,j)=>s+num(j.guarantee),0);
  const totalH = sumJobHalf(jobs); // v138: 手入力の上書きがあればその額で集計
  // 青海：実数値合計（手入力jitsu優先、無ければギャラ÷2）
  const totalJitsu = isAomi ? sumJobHalf(jobs) : 0;
  const totP = isKasugai ? jobs.reduce((s,j)=>s+num(j.payment),0) : 0;

  const handlePaste=(e)=>{
    const text=e.clipboardData.getData("text"); if(!text)return; e.preventDefault();
    const rows=parseTab(text).filter(r=>r.some(c=>c));
    onPaste(rows.map(r=>({id:nid(),date:r[0]||"",client:r[1]||"",
      guarantee:r[2]?Number(r[2].replace(/,/g,""))||null:null,
      payment:r[3]?Number(r[3].replace(/,/g,""))||null:null,
      ...((isAomi||(!isKasugai&&!isTakayama))&&r[3]?{jitsu:Number(r[3].replace(/,/g,""))||null}:{})})));
  };

  const tdAmt  = { border:"1px solid #ddd", padding:"2px 3px", minWidth:isMobile?60:105 };
  const tdAuto = { ...AutoTD };
  // ========== モバイル：カード形式（v37：現場名・お名前を大きく表示） ==========
  const { isNarrow } = useViewport();
  if (isMobile) {
    const S = makeOverviewStyles(isNarrow);
    const delLabel = (j) => `${j.date||'日付未設定'} ${j.client||'現場未設定'}`;
    return (
      <div>
        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
          <b style={{fontSize:12,color:"#1e3a8a"}}>
            {isKasugai ? `■ 現場一覧　${jobs.length}件　ギャラ合計 ${fmt(totalG)}` : `■ 現場一覧　${jobs.length}件　ギャラ合計 ${fmt(totalG)}　50%合計 ${fmt(totalH)}`}
          </b>
          <button style={{...BTN(),fontSize:9,padding:"1px 6px",height:"auto",minHeight:0}} onClick={onAdd}>＋</button>
          <button style={{...BTN("#374151"),fontSize:9,padding:"1px 6px",height:"auto",minHeight:0}} onClick={()=>pasteRef.current?.focus()}>📋</button>
        </div>
        <textarea ref={pasteRef} onPaste={handlePaste} placeholder="Excelペースト"
          style={{width:"100%",height:14,fontSize:8,border:"1px dashed #ccc",padding:1,resize:"none",color:"#bbb",boxSizing:"border-box",marginBottom:4}}/>
        {/* v37: カード形式（1行1カード、現場名を大きく表示） */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {jobs.map((j,i)=>{
            const rieki = num(j.guarantee)-num(j.payment);
            const jitsuAuto = INT(num(j.guarantee)/2);
            const isJitsuManual = j.jitsu != null;
            const hasComment = j.comment && j.comment.trim() !== "";
            return (
              <div key={j.id||i} style={{
                border: hasComment ? "2px solid #dc2626" : "1px solid #d4deea",
                borderRadius:4,
                padding:"6px 8px",
                background: hasComment ? "#fff5f5" : (i%2===0?"#fff":"#f8f9fc"),
              }}>
                {/* 1行目：日付 + 現場名（大きく） + コメント + 削除 */}
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                  <span style={{display:"inline-block",minWidth:"40px",fontSize:11,color:"#1e3a8a",fontWeight:"bold",textAlign:"center",background:"#eef2ff",padding:"2px 4px",borderRadius:3}}>
                    <EditableTextCell value={j.date||""} onChange={v=>onUpdate(i,"date",v)} style={{textAlign:"center",padding:0,fontSize:11,color:"#1e3a8a",fontWeight:"bold"}}/>
                  </span>
                  <div style={{flex:"1 1 auto",minWidth:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                    <EditableTextCell value={j.client||""} onChange={v=>onUpdate(i,"client",v)}
                      style={{fontSize:13,fontWeight:"bold",color:"#111",minWidth:"180px",padding:"2px 3px"}}/>
                  </div>
                  <CommentButton comment={j.comment} onChange={v=>onUpdate(i,"comment",v)} label={delLabel(j)}/>
                  <button onClick={()=>S.confirmDelete(onDelete,i,delLabel(j))}
                    style={{background:"transparent",color:"#999",border:"none",padding:"0 2px",fontSize:14,cursor:"pointer",lineHeight:1}} title="この行を削除">×</button>
                </div>
                {/* 2行目：金額（ギャラ / 50% or 利益 / 実数値など） */}
                <div style={{display:"flex",alignItems:"center",gap:10,fontSize:11,paddingLeft:4}}>
                  <span style={{color:"#666"}}>ギャラ</span>
                  <span style={{minWidth:"70px"}}>
                    <EditableNumberCell value={j.guarantee} onChange={v=>onUpdate(i,"guarantee",v)} placeholder="0"
                      style={{textAlign:"left",padding:"1px 3px",fontWeight:"bold"}}/>
                  </span>
                  {isKasugai && (
                    <>
                      <span style={{color:"#666"}}>支払</span>
                      <span style={{minWidth:"70px"}}>
                        <EditableNumberCell value={j.payment} onChange={v=>onUpdate(i,"payment",v)} placeholder="0"
                          style={{textAlign:"left",padding:"1px 3px"}}/>
                      </span>
                      <span style={{color:"#1e3a8a",fontWeight:"bold",background:"#eef5ff",padding:"1px 4px",borderRadius:3}}>
                        利益 {j.guarantee!=null&&j.payment!=null ? fmtNum(rieki) : "—"}
                      </span>
                    </>
                  )}
                  {isAomi && (
                    <>
                      <span style={{color:"#666"}}>実数値</span>
                      <span style={{minWidth:"70px",background:isJitsuManual?"#fef3c7":"#eef5ff",borderRadius:3}}>
                        <EditableNumberCell value={j.jitsu} onChange={v=>onUpdate(i,"jitsu",v)} placeholder={fmtN(jitsuAuto)}
                          color={isJitsuManual?"#92400e":"#1e3a8a"} fontWeight="bold"
                          style={{textAlign:"left",padding:"1px 3px"}}/>
                      </span>
                    </>
                  )}
                  {!isKasugai && !isTakayama && !isAomi && (
                    <>
                      <span style={{color:"#666"}}>50%</span>
                      <span style={{minWidth:"70px",background:"#eef5ff",borderRadius:3}}>
                        <EditableNumberCell value={j.guarantee!=null ? jobHalf(j) : null} onChange={v=>setJobHalf(j,v,val=>onUpdate(i,"jitsu",val))}
                          color="#1e3a8a" fontWeight="bold"
                          style={{textAlign:"left",padding:"1px 3px"}}/>
                      </span>
                    </>
                  )}
                </div>
                {/* コメント表示（あれば） */}
                {hasComment && (
                  <div style={{marginTop:3,padding:"3px 6px",background:"#fff0f0",borderLeft:"3px solid #dc2626",fontSize:10,color:"#dc2626",fontWeight:"bold"}}>
                    💬 {j.comment}
                  </div>
                )}
              </div>
            );
          })}
          {/* 合計カード */}
          <div style={{border:"2px solid #1e3a8a",borderRadius:4,padding:"6px 10px",background:"#eef2ff",display:"flex",alignItems:"center",gap:12,fontWeight:"bold",color:"#1e3a8a"}}>
            <span>合計</span>
            <span>ギャラ {fmt(totalG)}</span>
            {isKasugai && <span>支払 {fmt(totP)}</span>}
            {isKasugai && <span style={{background:"#c8d4f0",padding:"1px 6px",borderRadius:3}}>利益 {fmt(totalG-totP)}</span>}
            {isAomi && <span style={{background:"#c8d4f0",padding:"1px 6px",borderRadius:3}}>実数値 {fmt(totalJitsu)}</span>}
            {!isKasugai && !isTakayama && !isAomi && <span style={{background:"#c8d4f0",padding:"1px 6px",borderRadius:3}}>50% {fmt(totalH)}</span>}
          </div>
        </div>
        {isAomi && <div style={{fontSize:9,color:"#666",marginTop:3}}></div>}
      </div>
    );
  }

  // ========== PC：従来のテーブル形式 ==========
  return (
    <div className="scroll-x" style={{overflowX:"auto",WebkitOverflowScrolling:"touch",maxWidth:"100%"}}>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
        <b style={FONT.subhead}>{isKasugai?"現場一覧（ヘアセット+現場）":"現場・ギャランティ"}</b>
        <button style={BTN()} onClick={onAdd}>＋行追加</button>
        <button style={BTN("#374151")} onClick={()=>pasteRef.current?.focus()}>Excelペースト</button>
        {isAomi && <span style={{...FONT.label,marginLeft:8}}>※ 実数値は通常ギャラ÷2、イレギュラー時のみ手入力で上書き</span>}
      </div>
      <textarea ref={pasteRef} onPaste={handlePaste} placeholder="Excelからコピー→ペースト"
        style={{width:"100%",height:28,fontSize:11,border:"1px dashed #aaa",padding:3,resize:"none",color:"#999",boxSizing:"border-box",marginBottom:4}}/>
      <table style={{borderCollapse:"collapse",...FONT.table,tableLayout:"auto"}}>
        <thead>
          <tr>
            <th style={{...TH,width:16}}></th>
            <th style={{...TH,width:70}}>日時</th>
            <th style={{...TH,minWidth:220}}>現場</th>
            <th style={{...TH,minWidth:105}}>ギャランティ<br/><span style={{fontSize:9,fontWeight:"normal",color:"#cce"}}>(請求額)</span></th>
            {isKasugai && <th style={{...TH,minWidth:105}}>支払額</th>}
            {isKasugai && <th style={{...TH,minWidth:90,background:"#c8d4f8"}}>利益</th>}
            {isAomi && <th style={{...TH,minWidth:115,background:"#94b0f5"}}>実数値<br/><span style={{fontSize:9,fontWeight:"normal",color:"#000"}}>(青海への支払)</span></th>}
            {!isKasugai && !isTakayama && !isAomi && <th style={{...TH,minWidth:115,background:"#c8d4f8"}}>50%（修正可）</th>}
            <th style={{...TH,width:32}}>メモ</th>
            <th style={{...TH,width:42}}>削除</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j,i)=>{
            const rieki = num(j.guarantee)-num(j.payment);
            const jitsuAuto = INT(num(j.guarantee)/2);
            const jitsuVal = j.jitsu != null ? num(j.jitsu) : jitsuAuto;
            const isJitsuManual = j.jitsu != null;
            return (
              <tr key={j.id||i}
                draggable
                onDragStart={()=>onDragStart(i)}
                onDragOver={e=>onDragOver(e,i)}
                onDragEnd={onDragEnd}
                style={{background:i%2===0?"#fff":"#f7f8fc", opacity: dragIdx.current===i ? 0.4 : 1}}>
                <td style={{...TD,padding:"0 2px",cursor:"grab",color:"#bbb",textAlign:"center",fontSize:14}} title="ドラッグして並べ替え">⋮⋮</td>
                <td style={TD}><input value={j.date||""} onChange={e=>handleDateChange(i,e.target.value)} style={INP}/></td>
                <td style={TD}><input value={j.client||""} onChange={e=>onUpdate(i,"client",e.target.value)} style={INP}/></td>
                <td style={{...tdAmt,padding:0}}><EditableNumberCell value={j.guarantee} onChange={v=>onUpdate(i,"guarantee",v)}/></td>
                {isKasugai && <td style={{...tdAmt,padding:0}}><EditableNumberCell value={j.payment} onChange={v=>onUpdate(i,"payment",v)}/></td>}
                {isKasugai && <td style={tdAuto}>{j.guarantee!=null&&j.payment!=null ? fmt(rieki) : <span style={{color:"#bbb"}}>—</span>}</td>}
                {isAomi && (
                  <td style={{...tdAmt,padding:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:2}}>
                      <div style={{flex:1}}>
                        <EditableNumberCell value={j.jitsu} onChange={v=>onUpdate(i,"jitsu",v)} placeholder={fmtN(jitsuAuto)}
                          color={isJitsuManual?"#92400e":"#1e3a8a"} fontWeight="bold"
                          background={isJitsuManual?"#fef3c7":"#eef5ff"}/>
                      </div>
                      {isJitsuManual && (<button onClick={()=>onUpdate(i,"jitsu",null)} style={{...BTND,fontSize:9,padding:"1px 4px"}} title="自動(÷2)に戻す">✕</button>)}
                    </div>
                  </td>
                )}
                {!isKasugai && !isTakayama && !isAomi && (
                  <td style={{...tdAmt,padding:0}}>
                    <EditableNumberCell value={j.guarantee!=null ? jobHalf(j) : null} onChange={v=>setJobHalf(j,v,val=>onUpdate(i,"jitsu",val))}
                      color="#1e3a8a" fontWeight="bold"
                      background="#eef5ff"/>
                  </td>
                )}
                <td style={TDC}><CommentButton comment={j.comment} onChange={v=>onUpdate(i,"comment",v)} label={`${j.date||''} ${j.client||''}`}/></td>
                <td style={TDC}><button style={BTND} onClick={()=>onDelete(i)}>削除</button></td>
              </tr>
            );
          })}
          <tr style={{background:"#e8ecf6"}}>
            <td style={TSUM}></td><td style={TSUM}>合計</td><td style={TSUM}></td>
            <td style={AutoTS}>{fmt(totalG)}</td>
            {isKasugai && <><td style={AutoTS}>{fmt(totP)}</td><td style={{...AutoTS,background:"#94b0f5"}}>{fmt(totalG-totP)}</td></>}
            {isAomi && <td style={{...AutoTS,background:"#94b0f5",color:"#000"}}>{fmt(totalJitsu)}</td>}
            {!isKasugai && !isTakayama && !isAomi && <td style={{...AutoTS,background:"#b8caf8"}}>{fmt(totalH)}</td>}
            <td style={TSUM}></td>
            <td style={TSUM}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function JobsSharedAomi({ jobs, adjustJobs, onAdjustUpdate, onAdjustAdd, onAdjustDel }) {
  const { isMobile, isNarrow } = useViewport();
  // v45: 共有用ギャラは強制 min(guarantee, 30000) 固定（手動値無視）
  const calcShared = (j) => Math.min(num(j.guarantee), 30000);
  // v99: 旧データ（v95-v97 で jobs に追加された isAdjust:true 行）を除外
  const _jobsClean = jobs.filter(j=>!j.isAdjust);
  // v99: 調整分は同テーブル内の最下段に統合表示（Excel と同じ構造）
  const adj = adjustJobs || [];
  const normalH = _jobsClean.reduce((s,j)=>s+INT(calcShared(j)/2),0);
  const normalG = _jobsClean.reduce((s,j)=>s+calcShared(j),0);
  const adjustG = adj.reduce((s,j)=>s+calcShared(j),0);
  const adjustH = adj.reduce((s,j)=>s+INT(calcShared(j)/2),0);
  const totalSharedG = normalG + adjustG;
  const totalSharedH = normalH + adjustH;

  // ========== モバイル ==========
  if (isMobile) {
    const S = makeOverviewStyles(isNarrow);
    return (
      <div>
        <div style={{display:"flex",gap:4,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
          <b style={{fontSize:12,color:"#1e3a8a"}}>ギャラ（共有用）（{_jobsClean.length + adj.length}件）</b>
          <button
            style={{...BTN("#dc2626"),fontSize:9,padding:"1px 6px",height:"auto",minHeight:0}}
            onClick={()=>onAdjustAdd && onAdjustAdd()}
            title="調整分を追加"
          >+ 調整分</button>
        </div>
        <table style={{borderCollapse:"collapse",width:"100%",tableLayout:"fixed"}}>
          <colgroup>
            <col style={{width:isNarrow?"15%":"14%"}}/>
            <col/>
            <col style={{width:isNarrow?"25%":"24%"}}/>
            <col style={{width:isNarrow?"21%":"20%"}}/>
            <col style={{width:"22px"}}/>
          </colgroup>
          <thead>
            <tr>
              <th style={S.TH}>日</th>
              <th style={S.TH}>現場</th>
              <th style={S.TH}>ギャラ</th>
              <th style={{...S.TH,background:"#c8d4f0"}}>50%</th>
              <th style={{...S.TH,padding:0}}></th>
            </tr>
          </thead>
          <tbody>
            {/* 通常の現場（表示のみ） */}
            {_jobsClean.map((j,i)=>{
              const sharedG = calcShared(j);
              const sharedH = INT(sharedG/2);
              return (
                <tr key={"n"+i} style={{background:"#fff"}}>
                  <td style={{...S.TDC,color:"#000",fontSize:isNarrow?9:10}}>{j.date||""}</td>
                  <td style={{...S.TDS,color:"#000"}}><span style={{display:"inline-block",minWidth:"160px"}}>{j.client||""}</span></td>
                  <td style={{...S.TDR,color:"#000"}}>{fmtNum(sharedG)}</td>
                  <td style={{...S.TDR,color:"#000"}}>{fmtNum(sharedH)}</td>
                  <td></td>
                </tr>
              );
            })}
            {/* 調整分（編集可能、最下段） */}
            {adj.map((j,i)=>{
              const sharedG = calcShared(j);
              const sharedH = INT(sharedG/2);
              return (
                <tr key={"a"+i} style={{background:"#fef2f2"}}>
                  <td style={{...S.TDC,padding:0}}><EditableTextCell value={j.date||""} onChange={v=>onAdjustUpdate(i,"date",v)} style={{textAlign:"center",padding:"2px",fontSize:isNarrow?9:10}}/></td>
                  <td style={{...S.TDS,padding:0}}><EditableTextCell value={j.client||""} onChange={v=>onAdjustUpdate(i,"client",v)} style={{padding:"2px",color:"#dc2626",fontWeight:"bold"}}/></td>
                  <td style={{...S.TDR,padding:0}}><EditableNumberCell value={j.guarantee} onChange={v=>onAdjustUpdate(i,"guarantee",v)} color="#dc2626" fontWeight="bold"/></td>
                  <td style={{...S.TDR,color:"#dc2626",fontWeight:"bold"}}>{fmtNum(sharedH)}</td>
                  <td style={{...S.TDC,padding:0}}>
                    <button onClick={()=>{ if(window.confirm("この調整行を削除しますか?")) onAdjustDel(i); }} style={{...BTND,fontSize:9,padding:"1px 3px"}}>×</button>
                  </td>
                </tr>
              );
            })}
            {/* 合計（通常+調整） */}
            <tr style={{background:"#e5eaf2",fontWeight:"bold"}}>
              <td colSpan={2} style={{...S.TD,textAlign:"right",paddingRight:6,fontWeight:"bold",color:"#000"}}>合計</td>
              <td style={{...S.TDR,fontWeight:"bold",color:"#000"}}>{fmt(totalSharedG)}</td>
              <td style={{...S.TDR,background:"#e5eaf2",color:"#000",fontWeight:"bold"}}>{fmt(totalSharedH)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // ========== PC ==========
  return (
    <div className="scroll-x" style={{overflowX:"auto",WebkitOverflowScrolling:"touch",maxWidth:"100%"}}>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
        <b style={{fontSize:13}}>現場・ギャランティ（共有用）</b>
        <button
          style={{...BTN("#dc2626"),fontSize:11}}
          onClick={()=>onAdjustAdd && onAdjustAdd()}
          title="調整分を追加（会社負担として利益から引かれます）"
        >+ 調整分を追加</button>
      </div>
      <table style={{borderCollapse:"collapse",fontSize:12,tableLayout:"auto"}}>
        <thead>
          <tr>
            <th style={{...TH,width:70}}>日時</th>
            <th style={{...TH,minWidth:220}}>現場</th>
            <th style={{...TH,minWidth:115}}>ギャランティ<br/><span style={{fontSize:9,fontWeight:"normal",color:"#cce"}}>(共有用)</span></th>
            <th style={{...TH,minWidth:90,background:"#c8d4f8"}}>50%</th>
            <th style={{...TH,width:42}}></th>
          </tr>
        </thead>
        <tbody>
          {/* 通常の現場（表示のみ） */}
          {_jobsClean.map((j,i)=>{
            const sharedG = calcShared(j);
            const sharedH = INT(sharedG/2);
            return (
              <tr key={"n"+i} style={{background:"#fff"}}>
                <td style={TD}><span style={{fontSize:11,color:"#000"}}>{j.date||""}</span></td>
                <td style={TD}><span style={{fontSize:11,color:"#000"}}>{j.client||""}</span></td>
                <td style={{border:"1px solid #ddd",padding:"2px 6px",textAlign:"right",color:"#000"}}>{fmt(sharedG)}</td>
                <td style={{border:"1px solid #ddd",padding:"2px 6px",textAlign:"right",color:"#000"}}>{fmt(sharedH)}</td>
                <td style={TD}></td>
              </tr>
            );
          })}
          {/* 調整分（編集可能、最下段） */}
          {adj.map((j,i)=>{
            const sharedG = calcShared(j);
            const sharedH = INT(sharedG/2);
            return (
              <tr key={"a"+i} style={{background:"#fef2f2"}}>
                <td style={TD}><input value={j.date||""} onChange={e=>onAdjustUpdate(i,"date",e.target.value)} style={INP}/></td>
                <td style={TD}><input value={j.client||""} onChange={e=>onAdjustUpdate(i,"client",e.target.value)} style={{...INP,color:"#dc2626",fontWeight:"bold"}}/></td>
                <td style={{border:"1px solid #ddd",padding:0}}><EditableNumberCell value={j.guarantee} onChange={v=>onAdjustUpdate(i,"guarantee",v)} color="#dc2626" fontWeight="bold"/></td>
                <td style={{border:"1px solid #ddd",padding:"2px 6px",textAlign:"right",color:"#dc2626",fontWeight:"bold"}}>{fmt(sharedH)}</td>
                <td style={TDC}><button style={BTND} onClick={()=>{ if(window.confirm("この調整行を削除しますか?")) onAdjustDel(i); }}>削除</button></td>
              </tr>
            );
          })}
          {/* 合計（通常+調整） */}
          <tr style={{background:"#e5eaf2"}}>
            <td style={TSUM}>合計</td><td style={TSUM}></td>
            <td style={{...TSUM,textAlign:"right"}}>{fmt(totalSharedG)}</td>
            <td style={{...TSUM,textAlign:"right",background:"#e5eaf2"}}>{fmt(totalSharedH)}</td>
            <td style={TSUM}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}


// v48: 髙山の「現場（共有用）」タブ用（1件=1,000円固定表示）
// v133: 髙山の共有用歩合給。行ごとに amount を持てる（未入力なら既定1,000円）
const TAKAYAMA_PERJOB = 1000;
function takayamaAmount(j) {
  return (j && j.amount != null) ? num(j.amount) : TAKAYAMA_PERJOB;
}
function takayamaSharedTotal(rows) {
  return (rows || []).reduce((s, j) => s + takayamaAmount(j), 0);
}

// v50/v51: 髙山の「現場（共有用）」タブ - 編集可能・独立データ（jobsShared フィールド使用）
function JobsSharedTakayama({ jobsShared, jobs, onUpdate, onDelete, onAdd, onCopyFromJobs, onPaste }) {
  const { isMobile, isNarrow } = useViewport();
  const pasteRef = useRef(null);
  // 髙山：1件=1,000円固定
  const PERJOB = 1000;

  // v51: 初回マウント時に jobsShared が空なら自動で現場タブからコピー
  const isInitializedRef = useRef(false);
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;
    // jobsShared が未定義または空配列で、jobs に何かある場合、自動コピー
    if ((!jobsShared || jobsShared.length === 0) && jobs && jobs.length > 0) {
      onCopyFromJobs();
    }
  }, []);

  const rows = jobsShared || [];
  // v133: 歩合給は行ごとに編集可能。未入力（null）なら既定の1,000円を使う
  const amountOf = (j) => (j && j.amount != null ? num(j.amount) : PERJOB);
  const total = rows.reduce((s, j) => s + amountOf(j), 0);

  // Excelペースト処理
  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    e.preventDefault();
    const pastedRows = parseTab(text).filter(r => r.some(c=>c));
    if (pastedRows.length === 0) return;
    const newRows = pastedRows.map(r => ({
      id: nid(),
      date: r[0] || "",
      client: r[1] || "",
    }));
    onPaste(newRows);
  };

  // ========== モバイル ==========
  if (isMobile) {
    const S = makeOverviewStyles(isNarrow);
    return (
      <div>
        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
          <b style={{fontSize:12,color:"#000"}}>歩合給（共有用）（{rows.length}件）</b>
          <button style={{...BTN(),fontSize:10,padding:"2px 8px"}} onClick={onAdd}>＋行追加</button>
          <button style={{...BTN("#059669"),fontSize:10,padding:"2px 8px"}} onClick={onCopyFromJobs} title="現場タブから日時・現場を上書きコピー">📋 現場から再コピー</button>
        </div>
        <input ref={pasteRef} placeholder="Excelペースト（日付/現場）" onPaste={handlePaste}
          style={{width:"100%",padding:"6px",marginBottom:6,fontSize:11,border:"1px solid #ccc",borderRadius:3}}/>
        <div style={{fontSize:9,color:"#000",marginBottom:4,padding:"3px 6px",background:"#f5f5f5",border:"1px solid #ddd",borderRadius:3}}>
          ⓘ 歩合給の既定は 1件 = 1,000円。金額欄をタップすると個別に変更できます。
        </div>
        <table style={{borderCollapse:"collapse",width:"100%",tableLayout:"fixed"}}>
          <colgroup>
            <col style={{width:isNarrow?"17%":"16%"}}/>
            <col/>
            <col style={{width:isNarrow?"24%":"22%"}}/>
            <col style={{width:"36px"}}/>
          </colgroup>
          <thead>
            <tr>
              <th style={S.TH}>日</th>
              <th style={S.TH}>現場</th>
              <th style={S.TH}>歩合給</th>
              <th style={{...S.TH,padding:0,fontSize:9}}>削</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((j,i)=>(
              <tr key={j.id||i} style={{background:"#fff"}}>
                <td style={{...S.TDC,padding:0}}>
                  <EditableTextCell value={j.date} onChange={v=>onUpdate(i,"date",v)} placeholder="M/D"/>
                </td>
                <td style={{...S.TDS,padding:0}}>
                  <EditableTextCell value={j.client} onChange={v=>onUpdate(i,"client",v)} placeholder="現場名"/>
                </td>
                <td style={{...S.TDR,padding:0,color:"#000"}}>
                  {j.amount != null ? (
                    <EditableNumberCell value={j.amount} onChange={v=>onUpdate(i,"amount",v)}/>
                  ) : (
                    <span
                      onClick={()=>onUpdate(i,"amount",PERJOB)}
                      style={{display:"block",padding:"2px 4px",textAlign:"right",color:"#9ca3af"}}
                    >{fmtNum(PERJOB)}</span>
                  )}
                </td>
                <td style={{...S.TDC,padding:0}}>
                  <button onClick={()=>onDelete(i)} style={{...BTND,fontSize:9,padding:"1px 3px"}}>✕</button>
                </td>
              </tr>
            ))}
            <tr style={{background:"#e5eaf2",fontWeight:"normal"}}>
              <td colSpan={2} style={{...S.TD,textAlign:"right",paddingRight:6,fontWeight:"normal",color:"#000"}}>合計</td>
              <td style={{...S.TDR,background:"#e5eaf2",color:"#000",fontWeight:"normal"}}>{fmt(total)}</td>
              <td style={S.TD}></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // ========== PC ==========
  return (
    <div className="scroll-x" style={{overflowX:"auto",WebkitOverflowScrolling:"touch",maxWidth:"100%"}}>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
        <b style={{fontSize:13}}>歩合給（共有用）</b>
        <button style={BTN()} onClick={onAdd}>＋行追加</button>
        <button style={BTN("#374151")} onClick={()=>pasteRef.current?.focus()}>Excelペースト</button>
        <button style={BTN("#059669")} onClick={onCopyFromJobs} title="現場タブから日時・現場を上書きコピー">📋 現場から再コピー</button>
        <span style={{fontSize:11,color:"#666",marginLeft:8}}>※ 既定 1件 = 1,000円（金額欄をクリックで変更可・現場タブとは独立編集）</span>
      </div>
      <input ref={pasteRef} placeholder="Excelからコピー→ペースト（日付/現場）" onPaste={handlePaste}
        style={{width:"100%",padding:"6px",marginBottom:6,fontSize:12,border:"1px solid #ccc",borderRadius:3}}/>
      <div style={{fontSize:11,color:"#000",marginBottom:6,padding:"4px 8px",background:"#f5f5f5",border:"1px solid #ddd",borderRadius:3}}>
        ⓘ イレギュラー対応：複数現場を1件にまとめる場合や金額が異なる場合はここで調整してください。現場タブのデータとは独立して保存されます。
      </div>
      <table style={{borderCollapse:"collapse",fontSize:12,tableLayout:"auto"}}>
        <thead>
          <tr>
            <th style={{...TH,width:70}}>日時</th>
            <th style={{...TH,minWidth:220}}>現場</th>
            <th style={{...TH,minWidth:115}}>歩合給</th>
            <th style={{...TH,width:42}}>削除</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((j,i)=>(
            <tr key={j.id||i} style={{background:i%2===0?"#fff":"#f7f8fc"}}>
              <td style={TD}><input value={j.date||""} onChange={e=>onUpdate(i,"date",e.target.value)} style={INP}/></td>
              <td style={TD}><input value={j.client||""} onChange={e=>onUpdate(i,"client",e.target.value)} style={INP}/></td>
              <td style={{border:"1px solid #ddd",padding:0,textAlign:"right",color:"#000"}}>
                {j.amount != null ? (
                  <EditableNumberCell value={j.amount} onChange={v=>onUpdate(i,"amount",v)}/>
                ) : (
                  <span
                    onClick={()=>onUpdate(i,"amount",PERJOB)}
                    title="クリックして金額を変更"
                    style={{display:"block",padding:"2px 6px",textAlign:"right",color:"#9ca3af",cursor:"pointer"}}
                  >{fmtNum(PERJOB)}</span>
                )}
              </td>
              <td style={TDC}><button style={BTND} onClick={()=>onDelete(i)}>削除</button></td>
            </tr>
          ))}
          <tr style={{background:"#e5eaf2"}}>
            <td style={TSUM}>合計</td><td style={TSUM}></td>
            <td style={{...TSUM,textAlign:"right"}}>{fmt(total)}</td>
            <td style={TSUM}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// v81: 保険料調整スケジュール（Excel準拠の1〜6月構造、年間共通データ）
// 行構造: [合計, 1月分(amount), 2月分(amount), 3月分(amount=0, sub), 4月分(amount=0, sub), 5月分(amount=0, sub), 調整月(6月), 差額分(自動)]
// v107: その月の本来の歩合給を計算（保険料調整=0として）
// 保険料調整スケジュールの「歩合給」列の自動値として使用
// v105/v113: 行のドラッグ&ドロップ並べ替えフック（外部ライブラリ不要）
function useRowDragDrop(rows, onReorder) {
  const dragIdx = useRef(null);
  const onDragStart = (i) => { dragIdx.current = i; };
  const onDragOver  = (e, i) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === i) return;
    const next = [...rows];
    const [moved] = next.splice(dragIdx.current, 1);
    next.splice(i, 0, moved);
    dragIdx.current = i;
    onReorder(next);
  };
  const onDragEnd   = () => { dragIdx.current = null; };
  return { onDragStart, onDragOver, onDragEnd, dragIdx };
}

// v127: 保険料調整スケジュールの表示対象月かどうか
// サイクルは 1月分〜5月分 ＋ 調整月6月分（支払 7/24）で完結する。
// 調整月の支払いが済んだ 7月以降は、翌年1月に新しいサイクルが始まるまで非表示にする。
// v127: 調整月（6月分・支払 7/24）かどうか。差額分の自動加算はこの月だけ行う
function isInsAdjMonth(monthKey) {
  return Number(String(monthKey || "").split("-")[1]) === 6;
}

function isInsCycleMonth(monthKey) {
  const mm = Number(String(monthKey || "").split("-")[1]);
  return mm >= 1 && mm <= 6;
}

// v115: 保険料調整スケジュールの差額分を計算（竹内・深澤の調整月用）
// 差額分 = sub合計（3〜5月の実際の歩合給）− 保険料調整合計（前払い済み）
// プラス = 超過（歩合給に追加で上乗せ）／マイナス = 未消化
// v116: sub が未入力(null)の月は、その月の本来の歩合給（自動値）を使う
//       （5月分などが自動表示のみで未保存でも、差額分が正しく計算される）
function calcInsScheduleDiff(staff, year) {
  const rs = staff?.insSchedule;
  if (!rs || rs.length < 7 || rs[0]?.label !== "保険料調整合計") return 0;
  const yr = year || "2026";
  const total = num(rs[0]?.amount);
  const getSub = (i, mm) => {
    if (rs[i]?.sub != null) return num(rs[i].sub);   // 手動入力があれば優先
    const auto = calcOriginalHobu(staff, `${yr}-${mm}`);  // なければ自動値
    return auto != null ? auto : 0;
  };
  const marSub = getSub(3, "03");
  const aprSub = getSub(4, "04");
  const maySub = getSub(5, "05");
  return (marSub + aprSub + maySub) - total;
}

function calcOriginalHobu(staff, monthKey) {
  if (!staff?.months?.[monthKey]) return null;
  const m = staff.months[monthKey];
  const salary = m.salary || {};
  const jobs = m.jobs || [];
  const totalG = jobs.reduce((s,j)=>s+num(j.guarantee),0);
  const halfTotal = sumJobHalf(jobs); // v138
  const kihon = num(salary.基本給);
  const shucchou_auto = extractDays(salary.出張日数) * num(staff?.出張手当単価);
  const shucchou = salary.出張手当手動 != null
    ? num(salary.出張手当手動)
    : (staff?.出張手当単価 != null ? shucchou_auto : num(salary.出張手当));
  // 保険料調整は0として計算（本来の歩合給を出すため）
  // 計算できないケース（データ未入力）は null
  if (kihon === 0 && halfTotal === 0) return null;
  return halfTotal - kihon - shucchou;
}

function InsTable({ rows, onUpdate, onInit, monthlyHobu }) {
  // 初期化：rows が空、または旧構造（isDiff 等）の場合は新構造で再初期化を促す
  const isLegacy = !rows || rows.length === 0 || rows.some(r => r.isDiff) || !rows[0]?.label?.includes("保険料調整合計");
  const isNewSchema = rows && rows.length >= 7 && rows[0]?.label === "保険料調整合計";

  if (!isNewSchema) {
    return (
      <div style={{padding:16}}>
        <div style={{marginBottom:12,padding:12,background:"#fef3c7",border:"1px solid #f59e0b",borderRadius:4,fontSize:13,color:"#92400e"}}>
          {isLegacy
            ? "保険料調整スケジュールが旧構造です。下のボタンを押すと新構造（1〜6月分）に初期化します。既存データは上書きされます。"
            : "保険料調整スケジュールが未初期化です。下のボタンで初期化してください。"}
        </div>
        <button style={{...BTN("#dc2626"),fontSize:13,padding:"8px 18px"}} onClick={onInit}>
          🔄 1〜6月分の構造で初期化
        </button>
      </div>
    );
  }

  // 新構造データの取得（インデックス固定）
  const total = num(rows[0]?.amount); // 保険料調整合計（前払い済み）
  // v107: sub が null なら自動値（その月の本来の歩合給）を使う
  // monthlyHobu は配列 [3月の自動値, 4月の自動値, 5月の自動値]
  const getSubOrAuto = (i) => {
    const r = rows[i];
    if (r?.sub != null) return num(r.sub);
    const idx = i - 3; // rows[3]=3月→monthlyHobu[0], rows[4]=4月→monthlyHobu[1], rows[5]=5月→monthlyHobu[2]
    if (idx >= 0 && idx < 3 && monthlyHobu?.[idx] != null) return num(monthlyHobu[idx]);
    return 0;
  };
  const marSub = getSubOrAuto(3); // 3月分（手動 or 自動）
  const aprSub = getSubOrAuto(4); // 4月分（手動 or 自動）
  const maySub = getSubOrAuto(5); // 5月分（手動 or 自動）
  // v91: 計算式と表示を反転（直感的解釈）
  // 差額分 = sub合計 − 保険料調整合計
  // マイナス＝未消化（赤）／プラス＝超過・追加支払い必要（緑）／ゼロ＝ちょうど（青）
  const diff = (marSub + aprSub + maySub) - total;
  const diffSign = diff > 0 ? "プラス" : diff < 0 ? "マイナス" : "ゼロ";
  const diffColor = diff > 0 ? "#15803d" : diff < 0 ? "#dc2626" : "#1e3a8a";

  // 各行の編集対象（amount のみ / sub のみ / 両方ロック）
  const rowMeta = [
    { label: "保険料調整合計", editAmount: true, editSub: false, hint: "年間合計" },
    { label: "2026年1月分/支払日：2/25", editAmount: true, editSub: false, hint: "前払い済" },
    { label: "2026年2月分/支払日：3/25", editAmount: true, editSub: false, hint: "前払い済" },
    { label: "2026年3月分/支払日：4/24", editAmount: false, editSub: true, amount: 0, hint: "歩合給" },
    { label: "2026年4月分/支払日：5/24", editAmount: false, editSub: true, amount: 0, hint: "歩合給" },
    { label: "2026年5月分/支払日：6/24", editAmount: false, editSub: true, amount: 0, hint: "歩合給" },
    { label: "調整月：2026年6月分/支払日：7/24", editAmount: false, editSub: false, hint: "差額分を歩合給とは別に追加" },
  ];

  return (
    <div>
      <div style={{marginBottom:8}}>
        <b style={{fontSize:13}}>保険料調整スケジュール</b>
      </div>
      <table style={{borderCollapse:"collapse",fontSize:12}}>
        <tbody>
          {rowMeta.map((meta, i) => {
            const r = rows[i] || {};
            return (
              <tr key={i} style={{background: i%2===0?"#fff":"#f7f8fc"}}>
                <td style={{...TD, minWidth:240, fontWeight: i===0 ? "bold" : "normal"}}>{meta.label}</td>
                <td style={{...AmtTD, minWidth:120}}>
                  {meta.editAmount ? (
                    <input type="number" value={r.amount??""} onChange={e=>onUpdate(i,"amount",e.target.value===""?null:Number(e.target.value))} style={INPR}/>
                  ) : meta.amount === 0 ? (
                    <span style={{...TDR, color:"#6b7280", display:"block", padding:"4px 8px"}}>0</span>
                  ) : (
                    <span style={{...TDR, color:"#9ca3af", display:"block", padding:"4px 8px"}}>—</span>
                  )}
                </td>
                <td style={{...AmtTD, minWidth:120}}>
                  {meta.editSub ? (() => {
                    // v107: sub が null なら自動値を placeholder/hint で表示
                    const autoIdx = i - 3;
                    const autoVal = (autoIdx >= 0 && autoIdx < 3) ? monthlyHobu?.[autoIdx] : null;
                    const showAuto = r.sub == null && autoVal != null;
                    return (
                      <div>
                        <input
                          type="number"
                          value={r.sub??""}
                          placeholder={autoVal != null ? String(autoVal) : ""}
                          onChange={e=>onUpdate(i,"sub",e.target.value===""?null:Number(e.target.value))}
                          style={{...INPR, color: showAuto ? "#9ca3af" : undefined}}
                        />
                      </div>
                    );
                  })() : (
                    <span style={{...TDR, color:"#9ca3af", display:"block", padding:"4px 8px"}}>—</span>
                  )}
                </td>
                <td style={{...TD, minWidth:100, fontSize:11, color:"#6b7280"}}>{meta.hint}</td>
              </tr>
            );
          })}
          {/* 差額分（自動計算） */}
          <tr style={{background:"#eef2ff"}}>
            <td style={{...TD,fontWeight:"bold",color:"#1e3a8a"}}>差額分（自動）</td>
            <td style={{...TDR,fontWeight:"bold",color:diffColor,fontSize:14}} colSpan={2}>
              {fmt(diff)}
              <span style={{fontSize:10,marginLeft:8,padding:"1px 6px",background:diffColor,color:"#fff",borderRadius:3}}>
                {diffSign}
              </span>
            </td>
            <td style={{...TD, fontSize:10, color:"#6b7280"}}>
              {diff > 0 ? "超過（追加支払い）" : diff < 0 ? "未消化（あと引く余地あり）" : "ちょうど"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// v81: デフォルトの保険料調整スケジュール（竹内・深澤共通の構造）
function defaultInsSchedule() {
  return [
    { label:"保険料調整合計",                          amount:null, sub:null },
    { label:"2026年1月分/支払日：2/25",                amount:null, sub:null },
    { label:"2026年2月分/支払日：3/25",                amount:null, sub:null },
    { label:"2026年3月分/支払日：4/24",                amount:0,    sub:null },
    { label:"2026年4月分/支払日：5/24",                amount:0,    sub:null },
    { label:"2026年5月分/支払日：6/24",                amount:0,    sub:null },
    { label:"調整月：2026年6月分/支払日：7/24",        amount:null, sub:null },
  ];
}

// 給与計算（竹内・深澤型）
function SalaryTakeuchi({ staff, monthKey, jobs, salon, salary, rate, onUpd }) {
  const totalG = jobs.reduce((s,j)=>s+num(j.guarantee),0);
  const halfTotal = sumJobHalf(jobs); // v138: 手入力の上書きを反映
  const totNom = salon.reduce((s,r)=>s+calcNomination(r.payment,r.discount,rate,get100(r, monthKey)),0);
  // 出張手当自動計算：出張日数 × 単価（手動優先）
  const shucchou_auto = extractDays(salary.出張日数) * (staff?.出張手当単価 ?? 0);
  const shucchou = salary.出張手当手動 != null ? salary.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto : num(salary.出張手当));
  // v115: 歩合給ベース = 50% − 基本給 − 出張手当（保険料調整は含めない）
  const hobugkyuu_base_auto = halfTotal - num(salary.基本給) - shucchou;
  // 手動上書きがあればそちらを使用、なければ自動計算
  const hobugkyuu_base = salary.歩合給手動 != null ? salary.歩合給手動 : hobugkyuu_base_auto;
  // v115: 保険料調整額（調整月の差額分を歩合給に上乗せ）
  //   手動入力(保険料調整額手動)があればそれ、なければスケジュール差額分（プラスのみ自動表示）
  // v116: year を渡して、未入力月は自動値で差額分を計算
  const year_ins = (monthKey || "").split("-")[0] || "2026";
  const insDiff = calcInsScheduleDiff(staff, year_ins);
  // v127: 差額分の自動加算は調整月（6月分・支払7/24）だけ。7月以降は0（手動入力があればそちら優先）
  const insAdjAuto = (isInsAdjMonth(monthKey) && insDiff > 0) ? insDiff : 0;
  const hokenChoseiGaku = salary.保険料調整額手動 != null ? num(salary.保険料調整額手動) : insAdjAuto;
  // v115: 給与明細に表示する歩合給 = 歩合給ベース + 保険料調整額
  const hobugkyuu = hobugkyuu_base + hokenChoseiGaku;
  // v62: 歩合利益 = ギャラ100% - 50% - 役職手当 - オシャレ手当（基本給は引かない）
  const hoburieki = totalG - halfTotal - num(salary.役職手当) - num(salary.オシャレ手当);
  // v62: サロン利益 = 支払 + 早朝深夜料金 - 割引 - 指名料
  const totP = salon.reduce((s,r)=>s+num(r.payment),0);
  const totD = salon.reduce((s,r)=>s+num(r.discount),0);
  const choukin = num(salary.早朝深夜料金);
  const salonrieki = totP + choukin - totD - totNom;

  // v39: サロン指名料の自動連携（サロンタブから自動、手動上書き可）
  const salNomAuto = totNom;
  const salNomManual = salary.サロン指名料;
  const isNomAutoLinked = salNomManual == null;
  const salNomFinal = salNomAuto; // v42: 完全自動（サロンタブ合計を常に使用）
  // v115: 給与明細の歩合給は合算値（hobugkyuu）を表示。保険料調整は歩合給に統合したので項目から除外
  const kyuyo = [
    {label:"基本給",key:"基本給"},{label:"役職手当",key:"役職手当"},
    {label:"通勤手当/非課",key:"通勤費"},{label:"歩合給",key:"歩合給手動",val:hobugkyuu,auto:salary.歩合給手動==null},
    {label:"サロン指名料",key:"サロン指名料",val:salNomFinal,isAutoLinked:isNomAutoLinked,autoVal:salNomAuto},{label:"オシャレ手当",key:"オシャレ手当"},
    {label:"交通費/課税",key:"交通費"},{label:"出張手当",key:"出張手当手動",val:shucchou,auto:salary.出張手当手動==null && staff?.出張手当単価!=null},
    {label:"年末調整還付金",key:"年末調整還付金"},
  ].map(i=>({...i, val:i.val!=null?i.val:num(salary[i.key])}));
  const kojo = [
    {label:"健康保険料",key:"健康保険料"},
    ...(showChildSupport(monthKey, staff?.type) ? [{label:"子ども・子育て支援金",key:"子ども子育て支援金"}] : []),
    {label:"厚生年金保険料",key:"厚生年金保険料"},
    {label:"雇用保険料",key:"雇用保険料"},{label:"所得税",key:"所得税"},{label:"住民税",key:"住民税"},
    {label:"立替金返済分",key:"立替金返済分"},
  ].map(i=>({...i, val:num(salary[i.key])}));
  const totS = kyuyo.reduce((s,i)=>s+i.val,0);
  const totK = kojo.reduce((s,i)=>s+i.val,0);
  const furikomi = totS-totK;

  return (
    <div>
      <SalarySummary items={[
        { label:"ギャランティ合計", value:totalG, total:true },
        { label:"50%合計", value:halfTotal, total:true },
        { label:"歩合利益", value:hoburieki, total:true },
        { label:"サロン利益", value:salonrieki, total:true },
        {
          label:"歩合給",
          auto: salary.歩合給手動==null,
          input: (
            <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end"}}>
              <input type="number" placeholder={fmtN(hobugkyuu_base_auto)} value={salary.歩合給手動??""}
                onChange={e=>onUpd("歩合給手動", e.target.value===""?null:Number(e.target.value))}
                style={{...INPR, width:90, color:"#000"}}/>
              {salary.歩合給手動!=null && (
                <button onClick={()=>onUpd("歩合給手動",null)} style={{...BTND,fontSize:10,padding:"1px 5px"}} title="自動計算に戻す">✕</button>
              )}
            </div>
          )
        },
        {
          // v115: 保険料調整額（歩合給の下）。デフォルトはスケジュール差額分、手動上書き可
          label: "保険料調整額",
          auto: salary.保険料調整額手動==null,
          input: (
            <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end"}}>
              <input type="number" placeholder={fmtN(insAdjAuto)} value={salary.保険料調整額手動??""}
                onChange={e=>onUpd("保険料調整額手動", e.target.value===""?null:Number(e.target.value))}
                style={{...INPR, width:90, color:"#000"}}/>
              {salary.保険料調整額手動!=null && (
                <button onClick={()=>onUpd("保険料調整額手動",null)} style={{...BTND,fontSize:10,padding:"1px 5px"}} title="スケジュール差額分に戻す">✕</button>
              )}
            </div>
          )
        },
        {
          label: `出張手当${staff?.出張手当単価!=null?` (自動${fmt(staff.出張手当単価)}×日数)`:""}`,
          input: staff?.出張手当単価!=null ? (
            <div style={{display:"flex",gap:4,alignItems:"center",justifyContent:"flex-end"}}>
              <input type="number" value={salary.出張手当手動??""} placeholder={fmtN(shucchou_auto)}
                onChange={e=>onUpd("出張手当手動",e.target.value===""?null:Number(e.target.value))}
                style={{...INPR,width:80,color:"#000",fontWeight:"normal"}}/>
              <span style={{fontSize:11,color:"#555",fontWeight:"normal"}}>円</span>
              <input type="number" value={extractDays(salary.出張日数)||""}
                onChange={e=>onUpd("出張日数",e.target.value===""?"":Number(e.target.value))}
                style={{...INPR,width:40,fontSize:11}} placeholder="0"/>
              <span style={{fontSize:11,color:"#555"}}>日</span>
              {salary.出張手当手動!=null && <button onClick={()=>onUpd("出張手当手動",null)} style={{...BTND,fontSize:10,padding:"1px 5px"}} title="自動計算に戻す">✕</button>}
            </div>
          ) : (
            <input type="number" value={salary.出張手当??""} onChange={e=>onUpd("出張手当",e.target.value===""?null:Number(e.target.value))} style={{...INPR,width:"100%"}}/>
          )
        },
        { label:"基本給", input: <EditableNumberCell value={salary.基本給} onChange={v=>onUpd("基本給",v)}/> },
        { label:"役職手当", input: <EditableNumberCell value={salary.役職手当} onChange={v=>onUpd("役職手当",v)}/> },
        { label:"オシャレ手当", input: <EditableNumberCell value={salary.オシャレ手当} onChange={v=>onUpd("オシャレ手当",v)}/> },
        { label:"早朝深夜料金", input: <EditableNumberCell value={salary.早朝深夜料金} onChange={v=>onUpd("早朝深夜料金",v)}/> },
      ]}/>
      <MeisaiTable kyuyo={kyuyo} kojo={kojo} totS={totS} totK={totK} furikomi={furikomi} salary={salary} onUpd={onUpd} rieki={{hobu:hoburieki,salon:salonrieki}}/>
    </div>
  );
}

// 給与計算（青海型）
function SalaryAomi({ staff, monthKey, jobs, salon, salary, rate, onUpd, adjustJobs }) {
  // v98: 調整分は adjustJobs 配列に分離（jobs には含まれない）
  // v99: 旧データ（v95-v97 で jobs に追加された isAdjust:true 行）を除外
  const _jobsClean = jobs.filter(j=>!j.isAdjust);
  const totalG = _jobsClean.reduce((s,j)=>s+num(j.guarantee),0);
  // 実数値合計：手入力 jitsu があればそれ、無ければ guarantee÷2
  const jitsu  = sumJobHalf(_jobsClean);
  // v45: 共有用50%合計（強制 min 固定、手動値は無視）
  // v98: 通常分 + 調整分を加算
  const calcSharedAomi = (j) => Math.min(num(j.guarantee), 30000);
  const adj = adjustJobs || [];
  const normalSharedH = _jobsClean.reduce((s,j)=>s+INT(calcSharedAomi(j)/2),0);
  const totalAdjustH = adj.reduce((s,j)=>s+INT(calcSharedAomi(j)/2),0);
  const totalSharedH = normalSharedH + totalAdjustH;
  const totNom = salon.reduce((s,r)=>s+calcNomination(r.payment,r.discount,rate,get100(r, monthKey)),0);
  // v39: サロン指名料の自動連携（サロンタブから自動、手動上書き可）
  const salNomAuto = totNom;
  const salNomManual = salary.サロン指名料;
  const isNomAutoLinked = salNomManual == null;
  const salNomFinal = salNomAuto; // v42: 完全自動（サロンタブ合計を常に使用）
  // 出張手当自動計算
  const shucchou_auto = extractDays(salary.出張日数) * (staff?.出張手当単価 ?? 0);
  const shucchou = salary.出張手当手動 != null ? salary.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto : num(salary.出張手当));
  // v38: 歩合給 = 共有用50%合計 - 基本給 - 出張手当
  const hobugkyuu = totalSharedH - num(salary.基本給) - shucchou;
  // v62: 歩合利益 = ギャラ100% - 共有用50%合計 - 役職手当
  // v98: 利益 = ギャラ100%(調整なし) - 共有用50%(調整含む) - 役職手当 - 調整分50%(会社負担)
  const hoburieki = totalG - totalSharedH - num(salary.役職手当) - totalAdjustH;
  // v62: サロン利益 = 支払 + 早朝深夜料金 - 割引 - 指名料
  const totP = salon.reduce((s,r)=>s+num(r.payment),0);
  const totD = salon.reduce((s,r)=>s+num(r.discount),0);
  const choukin = num(salary.早朝深夜料金);
  const salonrieki = totP + choukin - totD - totNom;

  const kyuyo = [
    {label:"基本給",key:"基本給"},{label:"役職手当",key:"役職手当"},
    {label:"歩合給",key:null,val:hobugkyuu,auto:true},
    {label:"サロン指名料",key:"サロン指名料",val:salNomFinal,isAutoLinked:isNomAutoLinked,autoVal:salNomAuto},
    {label:"通勤手当/非課",key:"通勤費"},
    {label:"交通費",key:"交通費"},{label:"出張手当",key:"出張手当手動",val:shucchou,auto:salary.出張手当手動==null && staff?.出張手当単価!=null},{label:"年末調整還付金",key:"年末調整還付金"},
  ].map(i=>({...i,val:i.val!=null?i.val:num(salary[i.key])}));
  const kojo = [
    {label:"健康保険料",key:"健康保険料"},
    ...(showChildSupport(monthKey, staff?.type) ? [{label:"子ども・子育て支援金",key:"子ども子育て支援金"}] : []),
    {label:"厚生年金保険料",key:"厚生年金保険料"},
    {label:"雇用保険料",key:"雇用保険料"},{label:"所得税",key:"所得税"},{label:"住民税",key:"住民税"},
    {label:"立替金返済分",key:"立替金返済分"},
  ].map(i=>({...i,val:num(salary[i.key])}));
  const totS=kyuyo.reduce((s,i)=>s+i.val,0); const totK=kojo.reduce((s,i)=>s+i.val,0);
  const furikomi=totS-totK;

  return (
    <div>
      <SalarySummary items={[
        { label:"ギャランティ合計", value:totalG, total:true },
        { label:"実数値合計", value:jitsu, total:true },
        { label:"共有用50%合計", value:totalSharedH, total:true, auto:true },
        { label:"歩合利益（=ギャラ-共有用50%-役職手当）", value:hoburieki, total:true },
        { label:"サロン利益（=支払+早朝深夜-割引-指名料）", value:salonrieki, total:true },
        { label:"歩合給（共有用50%-基本給-出張手当）", value:hobugkyuu, auto:true },
        { label:"早朝深夜料金", input: <EditableNumberCell value={salary.早朝深夜料金} onChange={v=>onUpd("早朝深夜料金",v)}/> },
        {
          label:`出張手当${staff?.出張手当単価!=null?` (自動${fmt(staff.出張手当単価)}×日数)`:""}`,
          input: staff?.出張手当単価!=null ? (
            <div style={{display:"flex",gap:4,alignItems:"center",justifyContent:"flex-end"}}>
              <input type="number" value={salary.出張手当手動??""} placeholder={fmtN(shucchou_auto)}
                onChange={e=>onUpd("出張手当手動",e.target.value===""?null:Number(e.target.value))}
                style={{...INPR,width:80,color:"#000",fontWeight:"normal"}}/>
              <span style={{fontSize:11,color:"#555",fontWeight:"normal"}}>円</span>
              <input type="number" value={extractDays(salary.出張日数)||""} onChange={e=>onUpd("出張日数",e.target.value===""?"":Number(e.target.value))} style={{...INPR,width:40,fontSize:11}} placeholder="0"/>
              <span style={{fontSize:11,color:"#555"}}>日</span>
              {salary.出張手当手動!=null && <button onClick={()=>onUpd("出張手当手動",null)} style={{...BTND,fontSize:10,padding:"1px 5px"}} title="自動計算に戻す">✕</button>}
            </div>
          ) : (
            <input type="number" value={salary.出張手当??""} onChange={e=>onUpd("出張手当",e.target.value===""?null:Number(e.target.value))} style={{...INPR,width:"100%"}}/>
          )
        },
      ]}/>
      <MeisaiTable kyuyo={kyuyo} kojo={kojo} totS={totS} totK={totK} furikomi={furikomi} salary={salary} onUpd={onUpd} rieki={{hobu:hoburieki,salon:salonrieki}}/>
    </div>
  );
}

// 給与計算（髙山型）
function SalaryTakayama({ monthKey, jobs, jobsShared, salon, salary, onUpd }) {
  // v50: 歩合給は jobsShared の件数で計算（独立編集可能）
  // jobsShared が空の場合は jobs の件数で算出（初期状態互換）
  const hobuRows = jobsShared && jobsShared.length > 0 ? jobsShared : jobs;
  const hobugkyuu = takayamaSharedTotal(hobuRows);
  const totNom = salon.reduce((s,r)=>s+num(r.nom100),0);
  // v62: ギャラ全額（jobs の guarantee 合計）
  // 例: 4月 625,000円 (D24=SUM(D4:D23))
  const totalG = jobs.reduce((s,j)=>s+num(j.guarantee),0);
  // v62: 歩合利益 = ギャラ全額 - 基本給 - 役職手当 - 歩合給
  // 例: 4月 625,000 - 213,019 - 10,000 - 20,000 = 381,981
  const hoburieki = totalG - num(salary.基本給) - num(salary.役職手当) - hobugkyuu;
  const totP = salon.reduce((s,r)=>s+num(r.payment),0);
  // v39: サロン指名料の自動連携（サロンタブから自動、手動上書き可）
  const salNomAuto = totNom;
  const salNomManual = salary.サロン指名料;
  const isNomAutoLinked = salNomManual == null;
  const salNomFinal = salNomAuto; // v42: 完全自動（サロンタブ合計を常に使用）
  // v62: サロン利益（髙山）= 支払 + 早朝深夜料金 - 指名料
  // ※ 髙山の早朝深夜料金は内容列で自動集計（指名料に含まれた状態）のため、
  //   既存の totP - salNomFinal がそのまま「支払 + 早朝深夜料金 - 指名料」に相当
  const salonrieki = totP - salNomFinal;
  const kyuyo = [
    {label:"基本給",key:"基本給"},{label:"役職手当",key:"役職手当"},
    {label:"歩合給",key:null,val:hobugkyuu,auto:true},
    {label:"サロン指名料",key:"サロン指名料",val:salNomFinal,isAutoLinked:isNomAutoLinked,autoVal:salNomAuto},
    {label:"交通費",key:"交通費"},{label:"年末調整還付金",key:"年末調整還付金"},
  ].map(i=>({...i,val:i.val!=null?i.val:num(salary[i.key])}));
  const kojo=[
    {label:"健康保険料",key:"健康保険料"},
    ...(showChildSupport(monthKey, "takayama") ? [{label:"子ども・子育て支援金",key:"子ども子育て支援金"}] : []),
    {label:"厚生年金保険料",key:"厚生年金保険料"},
    {label:"雇用保険料",key:"雇用保険料"},{label:"所得税",key:"所得税"},{label:"住民税",key:"住民税"},
    {label:"立替金返済分",key:"立替金返済分"},
  ].map(i=>({...i,val:num(salary[i.key])}));
  const totS=kyuyo.reduce((s,i)=>s+i.val,0); const totK=kojo.reduce((s,i)=>s+i.val,0);
  const furikomi=totS-totK;
  return (
    <div>
      <SalarySummary items={[
        { label:"ギャランティ合計", value:totalG, total:true },
        { label:`現場合計 ${hobuRows.length}件 × 1,000円（歩合給）`, value:hobugkyuu, total:true },
        { label:"歩合利益（=ギャラ-基本給-役職手当-歩合給）", value:hoburieki, total:true },
        { label:"サロン利益（=支払-指名料）", value:salonrieki, total:true },
      ]}/>
      <MeisaiTable kyuyo={kyuyo} kojo={kojo} totS={totS} totK={totK} furikomi={furikomi} salary={salary} onUpd={onUpd} rieki={{hobu:hoburieki,salon:salonrieki}}/>
    </div>
  );
}

// 給与計算（松本型）：v42で他スタッフと同じMeisaiTable形式に統一
function SalaryMatsumoto({ staff, monthKey, jobs, salon, salary, rate, onUpd }) {
  const totalG = jobs.reduce((s,j)=>s+num(j.guarantee),0);
  const totP = salon.reduce((s,r)=>s+num(r.payment),0);
  const totNom = salon.reduce((s,r)=>s+calcNomination(r.payment,r.discount,rate,get100(r, monthKey)),0);
  // 出張手当自動計算：出張日数 × 単価（手動優先）
  const shucchou_auto = extractDays(salary.出張日数) * (staff?.出張手当単価 ?? 0);
  const shucchou = salary.出張手当手動 != null ? salary.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto : num(salary.出張手当));
  // v62: 松本は利益セクション削除（業務上、給与計算と利益管理を分離）
  const dayNum = extractDays(salary.出張日数);

  // v42: サロン指名料の自動連携（完全自動）
  const salNomAuto = totNom;
  // v58: 松本はサロン指名料を給与に含めない（マネフォ仕様。利益計算には影響なし）
  const salNomFinal = 0;

  // 支給項目（kyuyo）：他スタッフと同じMeisaiTable形式
  const kyuyo = [
    {label:"役員報酬",key:"役員報酬"},
    {label:"サロン指名料",key:"サロン指名料",val:salNomFinal,autoVal:salNomFinal},
    {label:"通勤手当/非課",key:"通勤費"},
    {label:"交通費/課税",key:"交通費"},
    {label:"出張手当",key:"出張手当手動",val:shucchou,auto:salary.出張手当手動==null && staff?.出張手当単価!=null},
    {label:"年末調整還付金",key:"年末調整還付金"},
  ].map(i=>({...i, val:i.val!=null?i.val:num(salary[i.key])}));
  const kojo = [
    {label:"健康保険料",key:"健康保険料"},
    {label:"介護保険料",key:"介護保険料"},
    ...(showChildSupport(monthKey, staff?.type) ? [{label:"子ども・子育て支援金",key:"子ども子育て支援金"}] : []),
    {label:"厚生年金保険料",key:"厚生年金保険料"},
    {label:"雇用保険料",key:"雇用保険料"},
    {label:"所得税",key:"所得税"},
    {label:"住民税",key:"住民税"},
    {label:"会社立替返済分",key:"会社立替返済分"},
  ].map(i=>({...i, val:num(salary[i.key])}));
  const totS = kyuyo.reduce((s,i)=>s+i.val,0);
  const totK = kojo.reduce((s,i)=>s+i.val,0);
  const furikomi = totS-totK;

  return (
    <div>
      <SalarySummary items={[
        { label:"ギャランティ合計", value:totalG, total:true },
        {
          label:`出張手当${staff?.出張手当単価!=null?` (自動${fmt(staff.出張手当単価)}×日数)`:""}`,
          input: staff?.出張手当単価!=null ? (
            <div style={{display:"flex",gap:4,alignItems:"center",justifyContent:"flex-end"}}>
              <input type="number" value={salary.出張手当手動??""} placeholder={fmtN(shucchou_auto)}
                onChange={e=>onUpd("出張手当手動",e.target.value===""?null:Number(e.target.value))}
                style={{...INPR,width:80,color:"#000",fontWeight:"normal"}}/>
              <span style={{fontSize:11,color:"#555",fontWeight:"normal"}}>円</span>
              <input type="number" value={dayNum||""} onChange={e=>onUpd("出張日数",e.target.value===""?"":Number(e.target.value))} style={{...INPR,width:40,fontSize:11}} placeholder="0"/>
              <span style={{fontSize:11,color:"#555"}}>日</span>
              {salary.出張手当手動!=null && <button onClick={()=>onUpd("出張手当手動",null)} style={{...BTND,fontSize:10,padding:"1px 5px"}} title="自動計算に戻す">✕</button>}
            </div>
          ) : (
            <input type="number" value={salary.出張手当??""} onChange={e=>onUpd("出張手当",e.target.value===""?null:Number(e.target.value))} style={{...INPR,width:"100%"}}/>
          )
        },
      ]}/>
      <MeisaiTable kyuyo={kyuyo} kojo={kojo} totS={totS} totK={totK} furikomi={furikomi} salary={salary} onUpd={onUpd} rieki={{hobu:null,salon:null}}/>
    </div>
  );
}

// 給与計算（春日井型）
// 春日井専用：交通費明細テーブル
function TrafficTable({ rows, onUpdate, onDelete, onAdd, onPaste }) {
  const pasteRef = useRef(null);
  const { isMobile, isNarrow } = useViewport();
  const total = rows.reduce((s,r)=>s+num(r.amount),0);

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    e.preventDefault();
    const rows2 = parseTab(text).filter(r => r.some(c=>c));
    onPaste(rows2.map(r => ({
      id:nid(),
      date:r[0]||"",
      memo:r[1]||"",
      amount:r[2]?Number(String(r[2]).replace(/,/g,""))||null:null,
    })));
  };

  // ========== モバイル：カード形式（v37：内容を大きく表示） ==========
  if (isMobile) {
    const S = makeOverviewStyles(isNarrow);
    const delLabel = (r) => `${r.date||'日付未設定'} ${r.memo||'内容未設定'}`;
    return (
      <div>
        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
          <b style={{fontSize:12,color:"#1e3a8a"}}>■ 交通費　{rows.length}件　合計 {fmt(total)}</b>
          <button style={{...BTN(),fontSize:9,padding:"1px 6px",height:"auto",minHeight:0}} onClick={onAdd}>＋</button>
        </div>
        <input ref={pasteRef} placeholder="ペースト（日付/内容/金額）" onPaste={handlePaste}
          style={{width:"100%",height:14,fontSize:8,border:"1px dashed #ccc",padding:1,color:"#bbb",boxSizing:"border-box",marginBottom:4}}/>
        {/* v37: カード形式（1行1カード、内容を大きく表示） */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {rows.map((r,i)=>{
            const hasComment = r.comment && r.comment.trim() !== "";
            return (
              <div key={r.id||i} style={{
                border: hasComment ? "2px solid #dc2626" : "1px solid #d4deea",
                borderRadius:4,
                padding:"6px 8px",
                background: hasComment ? "#fff5f5" : (i%2===0?"#fff":"#f8f9fc"),
              }}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                  <span style={{display:"inline-block",minWidth:"40px",fontSize:11,color:"#1e3a8a",fontWeight:"bold",textAlign:"center",background:"#eef2ff",padding:"2px 4px",borderRadius:3}}>
                    <EditableTextCell value={r.date||""} onChange={v=>onUpdate(i,"date",v)} style={{textAlign:"center",padding:0,fontSize:11,color:"#1e3a8a",fontWeight:"bold"}}/>
                  </span>
                  <div style={{flex:"1 1 auto",minWidth:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                    <EditableTextCell value={r.memo||""} onChange={v=>onUpdate(i,"memo",v)}
                      style={{fontSize:13,fontWeight:"bold",color:"#111",minWidth:"180px",padding:"2px 3px"}}/>
                  </div>
                  <CommentButton comment={r.comment} onChange={v=>onUpdate(i,"comment",v)} label={delLabel(r)}/>
                  <button onClick={()=>S.confirmDelete(onDelete,i,delLabel(r))}
                    style={{background:"transparent",color:"#999",border:"none",padding:"0 2px",fontSize:14,cursor:"pointer",lineHeight:1}} title="この行を削除">×</button>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10,fontSize:11,paddingLeft:4}}>
                  <span style={{color:"#666"}}>金額</span>
                  <span style={{minWidth:"90px"}}>
                    <EditableNumberCell value={r.amount} onChange={v=>onUpdate(i,"amount",v)}
                      style={{textAlign:"left",padding:"1px 3px",fontWeight:"bold"}}/>
                  </span>
                </div>
                {hasComment && (
                  <div style={{marginTop:3,padding:"3px 6px",background:"#fff0f0",borderLeft:"3px solid #dc2626",fontSize:10,color:"#dc2626",fontWeight:"bold"}}>
                    💬 {r.comment}
                  </div>
                )}
              </div>
            );
          })}
          {/* 合計カード */}
          <div style={{border:"2px solid #1e3a8a",borderRadius:4,padding:"6px 10px",background:"#eef2ff",display:"flex",alignItems:"center",gap:12,fontWeight:"bold",color:"#1e3a8a"}}>
            <span>合計</span>
            <span>{fmt(total)}</span>
          </div>
        </div>
      </div>
    );
  }
  // ========== PC：従来テーブル ==========
  return (
    <div>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
        <button onClick={onAdd} style={BTN()}>＋ 行追加</button>
        <input ref={pasteRef} placeholder="ここにペースト（日付/内容/金額）" onPaste={handlePaste} style={{...INP,width:240,fontSize:11}}/>
        <span style={{marginLeft:"auto",fontSize:13,fontWeight:"bold",color:"#1e3a8a"}}>
          交通費合計: {fmt(total)}
        </span>
      </div>
      <table style={{borderCollapse:"collapse",fontSize:12,width:"100%"}}>
        <thead>
          <tr>
            <th style={{...TH,width:70,textAlign:"center"}}>日付</th>
            <th style={{...TH,textAlign:"center"}}>内容</th>
            <th style={{...TH,width:100,textAlign:"center"}}>金額</th>
            <th style={{...TH,width:40,textAlign:"center"}}>メモ</th>
            <th style={{...TH,width:50,textAlign:"center"}}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r,i)=>(
            <tr key={r.id||i}>
              <td style={{...TD,verticalAlign:"middle"}}>
                <input value={r.date||""} onChange={e=>onUpdate(i,"date",e.target.value)} style={{...INP,width:"100%",fontSize:11}}/>
              </td>
              <td style={{...TD,verticalAlign:"middle"}}>
                <input value={r.memo||""} onChange={e=>onUpdate(i,"memo",e.target.value)} style={{...INP,width:"100%",fontSize:11}}/>
              </td>
              <td style={{...TD,verticalAlign:"middle"}}>
                <input type="number" value={r.amount??""} onChange={e=>onUpdate(i,"amount",e.target.value===""?null:Number(e.target.value))} style={{...INPR,width:"100%"}}/>
              </td>
              <td style={{...TD,verticalAlign:"middle",textAlign:"center"}}>
                <CommentButton comment={r.comment} onChange={v=>onUpdate(i,"comment",v)} label={`${r.date||''} ${r.memo||''}`}/>
              </td>
              <td style={{...TD,verticalAlign:"middle",textAlign:"center"}}>
                <button onClick={()=>onDelete(i)} style={BTND}>×</button>
              </td>
            </tr>
          ))}
          <tr style={{background:"#e8edf8"}}>
            <td colSpan={2} style={{...TD,fontWeight:"bold",textAlign:"right",background:"#e8edf8"}}>合計</td>
            <td style={{...TSUM,fontSize:13,color:"#1e3a8a"}}>{fmt(total)}</td>
            <td style={{...TD,background:"#e8edf8"}}></td>
            <td style={{...TD,background:"#e8edf8"}}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SalaryKasugai({ jobs, salon, traffic, salary, onUpd }) {
  // === 現場集計 ===
  const totG   = jobs.reduce((s,j)=>s+num(j.guarantee),0);
  const totPay = jobs.reduce((s,j)=>s+num(j.payment),0);  // 支給額合計 = 「現場+ヘアセット」
  // v129: 現場粗利（決済手数料は最後にまとめて引くのでここでは引かない）
  const rieki0 = totG - totPay;

  // === サロン指名料合計 ===
  const totNom = salon.reduce((s,r)=>s+num(r.nomination),0);
  // v129: サロン粗利 = サロン支払合計 − 指名料計
  const salonPay    = salon.reduce((s,r)=>s+num(r.payment),0);
  const salonRieki0 = salonPay - totNom;

  // === 交通費合計（タブから自動集計） ===
  const totTraffic = (traffic||[]).reduce((s,t)=>s+num(t.amount),0);

  // === 全自動計算 ===
  // 支払合計 = 現場+ヘアセット(=totPay) + 交通費 + 指名料 - 講習費(天引き) + 備品代/交通費
  const koushuu = num(salary.講習費天引き);
  const bihin   = num(salary.備品代交通費);
  const shiharaiGoukei = totPay + totTraffic + totNom - koushuu + bihin;
  // 税抜金額 = 支払合計 / 1.1
  const zeinuki = Math.round(shiharaiGoukei / 1.1);
  // 消費税 = 支払合計 - 税抜
  const shouhizei = shiharaiGoukei - zeinuki;
  // 税込金額 = 支払合計
  const zeikomi = shiharaiGoukei;

  // v129【重要】決済手数料は支払合計に対して1回だけかかる（Square決済）
  //   旧実装は「現場の粗利 × 3.6%」としていたため、対象額も金額も誤っていた。
  //   現場・サロンには按分せず、粗利合計から最後にまとめて差し引く。
  // v131: 交通費（実費）も最終利益から差し引く
  const kessai   = Math.round(shiharaiGoukei * 0.036);
  const arariAll = rieki0 + salonRieki0;                   // 粗利合計（現場＋サロン）
  const rieki    = arariAll - totTraffic - kessai;         // 最終利益

  return (
    <div>
      {/* 支払明細 */}
      <div style={{marginBottom:8,fontWeight:"bold",fontSize:13,color:"#333"}}>支払明細</div>
      <SalarySummary items={[
        { label:"現場＋ヘアセット", value:totPay, auto:true, total:true },
        { label:"交通費（明細タブから）", value:totTraffic, auto:true, total:true },
        { label:"指名料", value:totNom, auto:true, total:true },
        {
          label:"講習費（天引き）",
          input: <EditableNumberCell value={salary.講習費天引き} onChange={v=>onUpd("講習費天引き",v)}/>
        },
        {
          label:"備品代/交通費",
          input: <EditableNumberCell value={salary.備品代交通費} onChange={v=>onUpd("備品代交通費",v)}/>
        },
        { label:"支払合計", value:shiharaiGoukei, color:"#1e3a8a", total:true },
      ]}/>

      {/* 税抜金額ブロック（全自動） */}
      <SalarySummary items={[
        { label:"税抜金額（支払合計÷1.1）", value:zeinuki, auto:true, total:true },
        { label:"消費税", value:shouhizei, auto:true, total:true },
        { label:"税込金額", value:zeikomi, color:"#1e3a8a", total:true },
      ]}/>

      {/* v62: 利益ブロック（ギャランティ - 支払合計 - 決済手数料3.6%） */}
      <div style={{marginBottom:8,marginTop:12,fontWeight:"bold",fontSize:13,color:"#333"}}>利益</div>
      <SalarySummary items={[
        { label:"売上合計（現場ギャラ＋サロン支払）", value:totG + salonPay, auto:true, total:true },
        { label:"　現場ギャランティ", value:totG, auto:true, total:true },
        { label:"　サロン支払", value:salonPay, auto:true, total:true },
        { label:"− 外注費（現場支払＋指名料）", value:totPay + totNom, auto:true, total:true },
        { label:"　現場支払", value:totPay, auto:true, total:true },
        { label:"　指名料", value:totNom, auto:true, total:true },
        { label:"粗利", value:arariAll, auto:true, total:true },
        { label:"− 交通費", value:totTraffic, auto:true, total:true },
        { label:`− 決済手数料（支払合計 ${fmtNum(shiharaiGoukei)}円 × 3.6%）`, value:kessai, auto:true, total:true },
        { label:"最終利益", value:rieki, color:"#1e3a8a", total:true },
      ]}/>
      <PdfDrop/>
    </div>
  );
}

// 給与計算上部の「サマリー情報」を一覧出力風に表示するコンポーネント（v31新規）
// v39: 全数字を黒・通常サイズで統一（強調なし）
function SalarySummary({ items }) {
  // items: [{ label, value, color?, auto?, editable?, input?, total? }]
  // total:true の項目は合計扱いで「円」付き表示
  const { isMobile, isNarrow } = useViewport();
  const fmtVal = (v, isTotal) => isTotal ? fmt(v) : fmtNum(v);
  if (isMobile) {
    // モバイル：一覧出力と同じスタイルの2列テーブル
    const S = makeOverviewStyles(isNarrow);
    return (
      <div style={{marginBottom:12}}>
        <table style={{borderCollapse:"collapse",width:"100%",tableLayout:"fixed"}}>
          <colgroup>
            <col/>
            <col style={{width:isNarrow?"45%":"42%"}}/>
          </colgroup>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} style={{background:it.total?"#f0f4f9":(i%2===0?"#fff":"#f8f9fc")}}>
                <td style={{...S.TD,background:it.total?"#e5eaf2":"#f4f6fa",fontWeight:"normal",color:"#333"}}>
                  {it.label}
                </td>
                <td style={S.TDR}>
                  {it.input ? it.input : (
                    <span style={{color:"#000",fontWeight:"normal"}}>{fmtVal(it.value, it.total)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  // PC：従来の横並び（2列×N行）
  return (
    <div style={{marginBottom:12}}>
      <table style={{borderCollapse:"collapse",fontSize:12}}>
        <tbody>
          {/* 2項目ずつ1行に並べる */}
          {Array.from({length:Math.ceil(items.length/2)}).map((_, rowIdx) => (
            <tr key={rowIdx}>
              {[0,1].map(colIdx => {
                const it = items[rowIdx*2 + colIdx];
                if (!it) return <React.Fragment key={colIdx}><td></td><td></td></React.Fragment>;
                return (
                  <React.Fragment key={colIdx}>
                    <td style={{...TD,background:it.total?"#e5eaf2":"#d9d9d9",fontWeight:"normal",width:200,paddingLeft:colIdx>0?16:undefined,color:"#333"}}>
                      {it.label}
                    </td>
                    <td style={{...TDR,width:110,color:"#000",fontWeight:"normal"}}>
                      {it.input ? it.input : fmtVal(it.value, it.total)}
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MeisaiTable({ kyuyo, kojo, totS, totK, furikomi, salary, onUpd, rieki }) {
  const { isMobile, isNarrow } = useViewport();

  // 入力欄レンダリング（共通化）
  const renderInputCell = (k) => {
    if (!k) return null;
    if (k.auto) return <span style={{color:"#1e3a8a",fontWeight:"bold"}}>{fmt(k.val)}</span>;
    if (k.key==="歩合給手動") {
      return <span style={{color: salary[k.key]!=null?"#d97706":"#1e3a8a", fontWeight:"bold"}}>
        {fmt(k.val)}
        {salary[k.key]!=null && <span style={{fontSize:10,color:"#d97706"}}> ✎</span>}
      </span>;
    }
    if (k.key==="出張手当手動") {
      return (
        <div style={{display:"flex",alignItems:"center",gap:2}}>
          <input type="number" value={salary.出張手当手動??""} placeholder={fmt(k.val)}
            onChange={e=>onUpd("出張手当手動", e.target.value===""?null:Number(e.target.value))}
            style={{...INPR, width:"100%", textAlign:"right", color:"#000"}}/>
          {salary.出張手当手動!=null && (
            <button onClick={()=>onUpd("出張手当手動",null)} style={{...BTND,fontSize:10,padding:"1px 5px"}} title="自動計算に戻す">✕</button>
          )}
        </div>
      );
    }
    return <input type="number" value={salary[k.key]??""} onChange={e=>onUpd(k.key,e.target.value===""?null:Number(e.target.value))} style={{...INPR,width:"100%",textAlign:"right"}}/>;
  };

  // ========== モバイル：一覧出力タブと同じ美しいスタイル（v39: 色統一） ==========
  if (isMobile) {
    const S = makeOverviewStyles(isNarrow);
    const rowCount = Math.max(kyuyo.length, kojo.length);
    return (
      <div style={{padding:2}}>
        <div style={{marginBottom:8}}>
          <b style={{fontSize:13,color:"#333"}}>給与明細</b>
          {rieki && (
            <div style={{fontSize:10,marginTop:4,display:"flex",flexWrap:"wrap",gap:8}}>
              {rieki.hobu!=null && <span>歩合利益: <b style={{color:"#000"}}>{fmt(rieki.hobu)}</b></span>}
              {rieki.salon!=null && <span>サロン利益: <b style={{color:"#000"}}>{fmt(rieki.salon)}</b></span>}
            </div>
          )}
        </div>
        <table style={{borderCollapse:"collapse",width:"100%",tableLayout:"fixed"}}>
          <colgroup>
            <col/>{/* 支給項目 */}
            <col style={{width:isNarrow?"28%":"26%"}}/>{/* 金額 */}
            <col/>{/* 控除項目 */}
            <col style={{width:isNarrow?"24%":"22%"}}/>{/* 金額 */}
          </colgroup>
          <thead>
            <tr>
              <th style={{...S.TH,background:"#e5eaf2"}}>支給項目</th>
              <th style={{...S.TH,background:"#e5eaf2"}}>金額</th>
              <th style={{...S.TH,background:"#f2e5e5",color:"#333"}}>控除項目</th>
              <th style={{...S.TH,background:"#f2e5e5",color:"#333"}}>金額</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({length:rowCount}).map((_,i)=>{
              const k=kyuyo[i]; const ko=kojo[i];
              return (
                <tr key={i} style={{background:"#fff"}}>
                  <td style={{...S.TD,background:"#fff",fontSize:isNarrow?9:10,color:"#000"}}>{k?.label||""}</td>
                  <td style={{...S.TDR,padding:0,background:"#fff"}}>
                    {k ? (
                      k.auto
                        ? <span style={{color:"#000",fontWeight:"normal",display:"block",padding:"2px 3px",textAlign:"right"}}>{fmtNum(k.val)}</span>
                        : k.key==="歩合給手動"
                          ? <span style={{color:salary[k.key]!=null?"#d97706":"#000",fontWeight:"normal",display:"block",padding:"2px 3px",textAlign:"right"}}>{fmtNum(k.val)}{salary[k.key]!=null && <span style={{fontSize:8}}> ✎</span>}</span>
                          : k.key==="出張手当手動"
                            ? <EditableNumberCell value={salary.出張手当手動} onChange={v=>onUpd("出張手当手動",v)} placeholder={fmtN(k.val)}
                                color="#000" fontWeight="normal"/>
                            : k.key==="サロン指名料"
                              ? <span style={{color:"#000",fontWeight:"normal",display:"block",padding:"2px 3px",textAlign:"right"}}>{fmtNum(k.autoVal != null ? k.autoVal : k.val)}</span>
                              : <EditableNumberCell value={salary[k.key]} onChange={v=>onUpd(k.key,v)} color="#000"/>
                    ) : ""}
                  </td>
                  <td style={{...S.TD,background:"#fff",fontSize:isNarrow?9:10,color:"#000"}}>{ko?.label||""}</td>
                  <td style={{...S.TDR,padding:0,background:"#fff"}}>
                    {ko ? <EditableNumberCell value={salary[ko.key]} onChange={v=>onUpd(ko.key,v)} color="#000"/> : ""}
                  </td>
                </tr>
              );
            })}
            <tr style={{background:"#f0f4f9",fontWeight:"normal"}}>
              <td style={{...S.TD,background:"#e5eaf2",fontWeight:"normal",color:"#000"}}>支給合計</td>
              <td style={{...S.TDR,background:"#e5eaf2",color:"#000",fontWeight:"normal"}}>{fmtNum(totS)}</td>
              <td style={{...S.TD,background:"#f2e5e5",fontWeight:"normal",color:"#000"}}>控除合計</td>
              <td style={{...S.TDR,background:"#f2e5e5",color:"#000",fontWeight:"normal"}}>{fmtNum(totK)}</td>
            </tr>
            <tr>
              <td colSpan={4} style={{border:"1px solid #888",background:"#333",color:"#fff",fontSize:13,fontWeight:"normal",padding:"7px 10px",textAlign:"right"}}>
                振込支給額　{fmtNum(furikomi)}
              </td>
            </tr>
          </tbody>
        </table>
        {/* v110: 給与備考欄（モバイル） */}
        {onUpd && (
          <div style={{marginTop:10}}>
            <div style={{fontSize:11,color:"#555",marginBottom:4,fontWeight:"bold"}}>備考</div>
            <textarea
              value={salary.給与備考 ?? ""}
              onChange={e => onUpd("給与備考", e.target.value === "" ? null : e.target.value)}
              placeholder="例）出張手当 800円×16日など"
              rows={3}
              style={{width:"100%",fontSize:11,padding:"6px 8px",border:"1px solid #ccc",borderRadius:4,resize:"vertical",color:"#000",boxSizing:"border-box",fontFamily:"inherit"}}
            />
          </div>
        )}
        <PdfDrop/>
      </div>
    );
  }
  // ========== PC：従来テーブル ==========
  return (
    <div style={{border:"1px solid #ccc",padding:14,maxWidth:720,background:"#fafafa"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <b style={FONT.heading}>給与明細</b>
        {rieki && (
          <div style={{display:"flex",gap:16,...FONT.table}}>
            {rieki.hobu!=null && <span>歩合利益: <b style={{color:"#000"}}>{fmt(rieki.hobu)}</b></span>}
            {rieki.salon!=null && <span>サロン利益: <b style={{color:"#000"}}>{fmt(rieki.salon)}</b></span>}
          </div>
        )}
      </div>
      <table style={{borderCollapse:"collapse",width:"100%",...FONT.table,marginBottom:8}}>
        <thead>
          <tr>
            <th style={TH}>勤怠</th>
            <th style={{...TH,width:130}}>支給項目</th>
            <th style={{...TH,width:100}}>金額</th>
            <th style={{...TH,width:130}}>控除項目</th>
            <th style={{...TH,width:100}}>金額</th>
            <th style={{...TH,width:90}}>その他</th>
            <th style={{...TH,width:100}}>金額</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({length:Math.max(kyuyo.length,kojo.length)}).map((_,i)=>{
            const k=kyuyo[i]; const ko=kojo[i];
            return (
              <tr key={i}>
                <td style={{...TD,width:50,background:"#fff"}}></td>
                <td style={{...TD,width:130,background:"#fff",color:"#000"}}>{k?.label||""}</td>
                <td style={{...TDR,width:100,background:"#fff"}}>
                  {k ? (
                    k.auto
                      ? <span style={{color:"#000",fontWeight:"normal"}}>{fmtNum(k.val)}</span>
                      : k.key==="歩合給手動"
                        ? <span style={{color: salary[k.key]!=null?"#d97706":"#000", fontWeight:"normal"}}>
                            {fmtNum(k.val)}
                            {salary[k.key]!=null && <span style={{fontSize:10,color:"#d97706"}}> ✎</span>}
                          </span>
                        : k.key==="出張手当手動"
                          ? <div style={{display:"flex",alignItems:"center",gap:2}}>
                              <input type="number"
                                value={salary.出張手当手動??""}
                                placeholder={fmtN(k.val)}
                                onChange={e=>onUpd("出張手当手動", e.target.value===""?null:Number(e.target.value))}
                                style={{...INPR, width:"100%", textAlign:"right", color:"#000"}}
                              />
                              {salary.出張手当手動!=null && (
                                <button onClick={()=>onUpd("出張手当手動",null)} style={{...BTND,fontSize:10,padding:"1px 5px"}} title="自動計算に戻す">✕</button>
                              )}
                            </div>
                          : k.key==="サロン指名料"
                            ? <span style={{color:"#000",fontWeight:"normal"}}>{fmtNum(k.autoVal != null ? k.autoVal : k.val)}</span>
                            : <EditableNumberCell value={salary[k.key]} onChange={v=>onUpd(k.key,v)} color="#000"/>
                  ) : ""}
                </td>
                <td style={{...TD,width:130,background:"#fff",color:"#000"}}>{ko?.label||""}</td>
                <td style={{...TDR,width:100,background:"#fff"}}>
                  {ko ? <EditableNumberCell value={salary[ko.key]} onChange={v=>onUpd(ko.key,v)} color="#000"/> : ""}
                </td>
                <td style={{...TD,width:90,background:"#fff",fontWeight:"normal",color:"#000"}}>{i===0?"差引合計":""}</td>
                <td style={{...TDR,width:100,background:"#fff"}}>
                  {i===0 ? <span style={{color:"#000",fontSize:13}}>{fmtNum(furikomi)}</span> : ""}
                </td>
              </tr>
            );
          })}
          <tr style={{background:"#eef0f4"}}>
            <td style={{...TD,background:"#eef0f4"}}></td>
            <td style={{...TD,fontWeight:"normal",background:"#eef0f4"}}>合計</td>
            <td style={{...TSUM,fontSize:12,color:"#000",fontWeight:"normal"}}>{fmtNum(totS)}</td>
            <td style={{...TD,fontWeight:"normal",background:"#eef0f4"}}>合計</td>
            <td style={{...TSUM,fontSize:12,color:"#000",fontWeight:"normal"}}>{fmtNum(totK)}</td>
            <td style={{...TD,fontWeight:"normal",background:"#eef0f4"}}>差引支給合計</td>
            <td style={{...TSUM,fontSize:12,color:"#000",fontWeight:"normal"}}>{fmtNum(furikomi)}</td>
          </tr>
        </tbody>
      </table>
      <div style={{textAlign:"right",padding:"8px 4px 0",fontSize:13,fontWeight:"normal",borderTop:"2px solid #333",color:"#000"}}>
        振込支給額　{fmtNum(furikomi)}
      </div>
      {/* v110: 給与備考欄（松本・竹内・深澤・青海・髙山のみ） */}
      {onUpd && (
        <div style={{marginTop:12}}>
          <div style={{fontSize:11,color:"#555",marginBottom:4,fontWeight:"bold"}}>備考</div>
          <textarea
            value={salary.給与備考 ?? ""}
            onChange={e => onUpd("給与備考", e.target.value === "" ? null : e.target.value)}
            placeholder="例）出張手当 800円×16日、ブラシ先一覧（共先）など"
            rows={3}
            style={{width:"100%",fontSize:11,padding:"6px 8px",border:"1px solid #ccc",borderRadius:4,resize:"vertical",color:"#000",boxSizing:"border-box",fontFamily:"inherit"}}
          />
        </div>
      )}
      <PdfDrop/>
    </div>
  );
}

function PdfDrop() {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const ref = useRef(null);
  if (file) {
    const url = URL.createObjectURL(file);
    return (
      <div style={{marginTop:10}}>
        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,fontSize:12}}>
          <span>📄 {file.name}</span>
          <button style={BTND} onClick={()=>setFile(null)}>削除</button>
          <a href={url} target="_blank" rel="noreferrer" style={{color:"#1e3a8a",fontSize:11}}>別タブで開く</a>
        </div>
        <iframe src={url} style={{width:"100%",height:320,border:"1px solid #888"}} title="給与明細PDF"/>
      </div>
    );
  }
  return (
    <div
      onDragOver={e=>{e.preventDefault();setDrag(true);}}
      onDragLeave={()=>setDrag(false)}
      onDrop={e=>{e.preventDefault();setDrag(false);const f=e.dataTransfer.files[0];if(f?.type==="application/pdf")setFile(f);}}
      onClick={()=>ref.current?.click()}
      style={{marginTop:10,border:`1px dashed ${drag?"#1e3a8a":"#888"}`,padding:"10px 8px",textAlign:"center",cursor:"pointer",fontSize:12,color:"#666",background:drag?"#f0f0ff":"#fff"}}
    >
      <input ref={ref} type="file" accept="application/pdf" style={{display:"none"}} onChange={e=>{if(e.target.files[0])setFile(e.target.files[0]);}}/>
      マネーフォワード給与 PDF をドロップ、またはクリックしてアップロード
    </div>
  );
}

// ===================== StaffView =====================
function StaffView({ staff, monthKey, onUpdate, onUpdateStaffMeta }) {
  const data = staff.months[monthKey];
  const [tab, setTab] = useState("jobs");
  // v63: ms-salon 取り込みモーダルの表示状態
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSalonAppModal, setShowSalonAppModal] = useState(false);
  const [showGuaranteeModal, setShowGuaranteeModal] = useState(false); // v140
  const salonClosed = useSalonClosed(staff.type, monthKey); // v135: アプリの確定状態を読む
  if (!data) return (
    <div style={{padding:16,color:"#666"}}>
      {monthKey}のデータがありません。
      <button style={{...BTN(),marginLeft:8}} onClick={()=>onUpdate(staff.id,monthKey,"init",null)}>新規作成</button>
    </div>
  );
  const isKasugai  = staff.type==="kasugai";
  const isTakayama = staff.type==="takayama";
  const isAomi     = staff.type==="aomi";
  // v64: 業務委託（秋野・平澤）
  const isGyoumu   = staff.type==="akino" || staff.type==="hirasawa";
  // v65: 年間支払調書を持つスタッフ（春日井・業務委託）
  const hasYearlyReport = isKasugai || isGyoumu;
  const tabs = isGyoumu ? [
    {key:"jobs",     label:"交通費明細"},
    {key:"salary",   label:"給与計算・明細"},
    {key:"uriage",   label:"売上・利益"},
    {key:"yearly",   label:"📑 年間支払調書"},
    {key:"overview", label:"📋 一覧・出力"},
  ] : [
    {key:"jobs",     label:"現場"},
    ...((isAomi||isTakayama)?[{key:"jobs_shared",label:"現場（共有用）"}]:[]),
    {key:"salon",    label:"サロン"},
    ...(isKasugai?[{key:"traffic",label:"交通費"}]:[]),
    {key:"salary",   label:"給与計算・明細"},
    ...((["takeuchi","fukazawa"].includes(staff.type))?[{key:"ins",label:"保険料調整"}]:[]),
    ...(hasYearlyReport?[{key:"yearly",label:"📑 年間支払調書"}]:[]),
    {key:"overview", label:"📋 一覧・出力"},
  ];
  const upd=(action,payload)=>onUpdate(staff.id,monthKey,action,payload);

  const handlePrint = () => {
    window.print();
  };

  // v35: CDNライブラリを動的にロード（UMD対応）
  const loadScript = (src) => new Promise((resolve, reject) => {
    // 既にロード済みならスキップ
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
  const loadHtml2Canvas = async () => {
    if (!window.html2canvas) {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
    }
    return window.html2canvas;
  };
  const loadJsPdf = async () => {
    if (!window.jspdf) {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
    }
    return window.jspdf.jsPDF;
  };

  const handleSaveJpg = async () => {
    const el = document.getElementById("overview-print-area");
    if (!el) { alert("印刷エリアが見つかりません"); return; }
    try {
      const html2canvas = await loadHtml2Canvas();
      // v72: scale:4 で最高解像度（400dpi相当）、PNG形式で無損失保存
      const canvas = await html2canvas(el, {
        scale: 4,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 15000,
        letterRendering: true,
        allowTaint: false,
        // スクロール位置を無視してエリア全体を描画
        scrollX: 0,
        scrollY: 0,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
      const a = document.createElement("a");
      // v71: 高画質JPEG（品質0.98）
      a.href = canvas.toDataURL("image/jpeg", 0.98);
      a.download = `${staff.name}_${monthKey}_歩合表.jpg`;
      a.click();
    } catch(e) {
      console.error(e);
      alert("JPG出力に失敗しました。印刷から「PDFとして保存」をお試しください。");
    }
  };

  // v71: PDF出力（A4サイズ、複数ページ対応）jsPDF + html2canvas、高画質化
  // v93: スマートページ区切り（セクション境界 .print-block を分割しない）
  const handleSavePdf = async () => {
    const el = document.getElementById("overview-print-area");
    if (!el) { alert("印刷エリアが見つかりません"); return; }
    try {
      const html2canvas = await loadHtml2Canvas();
      const jsPDF = await loadJsPdf();
      // v72: scale:4 で最高解像度（400dpi相当）
      const canvas = await html2canvas(el, {
        scale: 4,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        letterRendering: true,
        allowTaint: false,
        scrollX: 0,
        scrollY: 0,
      });
      // A4 縦向き: 210mm × 297mm
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
      const pageW = 210, pageH = 297;
      const margin = 8; // 上下左右8mm余白
      const usableW = pageW - margin*2;
      const usableH = pageH - margin*2;
      const imgW = usableW;
      const imgH = (canvas.height * imgW) / canvas.width;
      // 1ページ内に収まるかチェック、収まらなければ複数ページに分割
      if (imgH <= usableH) {
        // v71: PNG（無損失）で埋め込み
        const imgData = canvas.toDataURL("image/png");
        pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH, undefined, "FAST");
      } else {
        // v93: スマートページ区切り
        // .print-block 要素の境界（DOM上の Y 座標）を取得して、ページ境界がセクションを跨がないよう調整
        const elRect = el.getBoundingClientRect();
        const blocks = Array.from(el.querySelectorAll(".print-block"));
        // 各ブロックの開始・終了Y座標を画像座標系に変換（scale:4 倍）
        const scale = canvas.height / elRect.height;
        const blockBoundaries = []; // [{ start, end }]
        blocks.forEach(b => {
          const r = b.getBoundingClientRect();
          blockBoundaries.push({
            start: Math.floor((r.top - elRect.top) * scale),
            end: Math.ceil((r.bottom - elRect.top) * scale)
          });
        });
        const pageCanvasH = Math.floor(canvas.width * usableH / usableW);
        let offsetY = 0;
        let pageIdx = 0;
        while (offsetY < canvas.height) {
          let sliceH = Math.min(pageCanvasH, canvas.height - offsetY);
          // sliceの終端がセクションを途中で切っていないかチェック
          if (offsetY + sliceH < canvas.height) {
            const sliceEnd = offsetY + sliceH;
            // ページ境界がセクション内に入っているか
            const cuttingBlock = blockBoundaries.find(b => b.start < sliceEnd && sliceEnd < b.end);
            if (cuttingBlock) {
              // セクション開始位置までを今ページ、それ以降を次ページに
              const newSliceH = cuttingBlock.start - offsetY;
              // ただし新しいスライス高さがあまりに小さい場合（ページの30%未満）は強制分割
              if (newSliceH > pageCanvasH * 0.3) {
                sliceH = newSliceH;
              }
            }
          }
          const tmp = document.createElement("canvas");
          tmp.width = canvas.width;
          tmp.height = sliceH;
          const ctx = tmp.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0,0,tmp.width,tmp.height);
          ctx.drawImage(canvas, 0, offsetY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
          // v71: PNG（無損失）で埋め込み
          const sliceData = tmp.toDataURL("image/png");
          const sliceMmH = (sliceH * imgW) / canvas.width;
          if (pageIdx > 0) pdf.addPage();
          pdf.addImage(sliceData, "PNG", margin, margin, imgW, sliceMmH, undefined, "FAST");
          offsetY += sliceH;
          pageIdx++;
        }
      }
      pdf.save(`${staff.name}_${monthKey}_歩合表.pdf`);
    } catch(e) {
      console.error(e);
      alert("PDF出力に失敗しました。印刷から「PDFとして保存」をお試しください。");
    }
  };

  return (
    <div>
      <div className="tab-bar" style={{display:"flex",borderBottom:"1px solid #888",marginBottom:12}}>
        {tabs.map(t=><button key={t.key} className="tab-btn" style={TAB(tab===t.key)} onClick={()=>setTab(t.key)}>{t.label}</button>)}
      </div>
      {tab==="jobs" && GUARANTEE_STAFF_NAME[staff.type] && (
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"6px 8px",background:"#ecfdf5",border:"1px solid #a7f3d0",borderRadius:4}}>
          <span style={{fontSize:11,color:"#065f46"}}>
            💡 Ms-guarantee の案件から、日付と現場名を取り込めます（ギャランティは手入力のまま）
          </span>
          <button
            style={{...BTN("#059669"),fontSize:11,padding:"4px 10px",marginLeft:"auto"}}
            onClick={()=>setShowGuaranteeModal(true)}
          >
            📥 Ms-guarantee から取り込み
          </button>
        </div>
      )}
      {showGuaranteeModal && (
        <GuaranteeImportModal
          staff={staff}
          monthKey={monthKey}
          currentJobs={data.jobs||[]}
          onClose={()=>setShowGuaranteeModal(false)}
          onReplace={(rows)=>upd("job_reorder",{rows})}
        />
      )}
      {tab==="jobs" && (
        isGyoumu ? (
          <JobsTableGyoumu
            jobs={data.jobs}
            onUpdate={(i,f,v)=>upd("job_upd",{i,f,v})}
            onDelete={i=>upd("job_del",{i})}
            onAdd={()=>upd("job_add",null)}
            onPaste={rows=>upd("job_paste",{rows})}
          />
        ) : (
          <JobsTable
            jobs={data.jobs} isKasugai={isKasugai} isTakayama={isTakayama} isAomi={isAomi}
            onUpdate={(i,f,v)=>upd("job_upd",{i,f,v})}
            onDelete={i=>upd("job_del",{i})}
            onAdd={(extra)=>upd("job_add",extra||null)}
            onPaste={rows=>upd("job_paste",{rows})}
            onReorder={rows=>upd("job_reorder",{rows})}
          />
        )
      )}
      {tab==="jobs_shared" && isAomi && (
        <JobsSharedAomi
          jobs={data.jobs}
          adjustJobs={data.adjustJobs}
          onAdjustUpdate={(i,f,v)=>upd("adjust_upd",{i,f,v})}
          onAdjustAdd={()=>upd("adjust_add",null)}
          onAdjustDel={i=>upd("adjust_del",{i})}
        />
      )}
      {/* v123: 業務委託（秋野・平澤）の売上・利益タブ */}
      {tab==="uriage" && isGyoumu && (
        <UriageTable
          rows={data.uriage||[]}
          trafficJobs={data.jobs||[]}
          salary={data.salary||{}}
          onUpdate={(i,f,v)=>upd("uriage_upd",{i,f,v})}
          onDelete={i=>upd("uriage_del",{i})}
          onAdd={()=>upd("uriage_add",null)}
          onPaste={rows=>upd("uriage_paste",{rows})}
        />
      )}
      {tab==="jobs_shared" && isTakayama && (
        <JobsSharedTakayama
          jobsShared={data.jobsShared}
          jobs={data.jobs}
          onUpdate={(i,f,v)=>upd("jobs_shared_upd",{i,f,v})}
          onDelete={i=>upd("jobs_shared_del",{i})}
          onAdd={()=>upd("jobs_shared_add",null)}
          onCopyFromJobs={()=>upd("jobs_shared_copy_from_jobs",null)}
          onPaste={rows=>upd("jobs_shared_paste",{rows})}
        />
      )}
      {tab==="salon" && (
        <>
          {/* サロン指名料アプリ取り込み（松本・竹内・深澤・青海・髙山） */}
          {SALON_APP_STAFF_ID[staff.type] && (
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"6px 8px",background:"#f5f3ff",border:"1px solid #ddd6fe",borderRadius:4}}>
              <span style={{fontSize:11,color:"#4c1d95"}}>
                💡 サロン指名料アプリの内容を取り込めます
              </span>
              {salonClosed ? (
                <span style={{fontSize:11,color:"#6b7280",marginLeft:"auto"}}>🔒 確定済みの月のため取り込みできません（取り消しはサロン指名料アプリの管理画面から）</span>
              ) : (
              <button
                style={{...BTN("#7c3aed"),fontSize:11,padding:"4px 10px",marginLeft:"auto"}}
                onClick={()=>setShowSalonAppModal(true)}
              >
                📥 サロン指名料アプリから取り込み
              </button>
              )}
            </div>
          )}
          {/* v63.2: ms-salon 取り込みボタン（サロン指名料アプリ対象外のみ＝春日井） */}
          {MS_SALON_STAFF_NAME[staff.type] && !SALON_APP_STAFF_ID[staff.type] && (
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,padding:"6px 8px",background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:4}}>
              <span style={{fontSize:11,color:"#0c4a6e"}}>
                💡 ms-salon と連携して、Square 経由の取引を取り込めます
              </span>
              {salonClosed ? (
                <span style={{fontSize:11,color:"#6b7280",marginLeft:"auto"}}>🔒 確定済みの月のため取り込みできません（取り消しはサロン指名料アプリの管理画面から）</span>
              ) : (
              <button
                style={{...BTN("#0284c7"),fontSize:11,padding:"4px 10px",marginLeft:"auto"}}
                onClick={()=>setShowImportModal(true)}
              >
                📥 ms-salon から取り込み
              </button>
              )}
            </div>
          )}
          <SalonTable
            rows={data.salon} rate={staff.commissionRate} isKasugai={isKasugai} isTakayama={isTakayama}
            monthKey={monthKey}
            closed={salonClosed}
            onUpdate={(i,f,v)=>upd("salon_upd",{i,f,v})}
            onDelete={i=>upd("salon_del",{i})}
            onAdd={()=>upd("salon_add",null)}
            onPaste={rows=>upd("salon_paste",{rows})}
            onReorder={rows=>upd("salon_reorder",{rows})}
          />
          {/* v63: ms-salon 取り込みモーダル */}
          {showImportModal && !salonClosed && (
            <MsSalonImportModal
              staff={staff}
              monthKey={monthKey}
              currentSalon={data.salon}
              onClose={()=>setShowImportModal(false)}
              onImport={(newRows)=>{
                if (salonClosed) return; // v134
                if (newRows.length > 0) {
                  upd("salon_paste", { rows: newRows });
                }
              }}
            />
          )}
          {showSalonAppModal && !salonClosed && (
            <SalonAppImportModal
              staff={staff}
              monthKey={monthKey}
              currentSalon={data.salon}
              onClose={() => setShowSalonAppModal(false)}
              onReplace={(rows) => { if (!salonClosed) upd("salon_reorder", { rows }); }}
            />
          )}
        </>
      )}
      {tab==="traffic" && isKasugai && (
        <TrafficTable
          rows={data.traffic||[]}
          onUpdate={(i,f,v)=>upd("traffic_upd",{i,f,v})}
          onDelete={i=>upd("traffic_del",{i})}
          onAdd={()=>upd("traffic_add",null)}
          onPaste={rows=>upd("traffic_paste",{rows})}
        />
      )}
      {tab==="salary" && (
        isGyoumu ? (
          <SalaryGyoumu staff={staff} monthKey={monthKey} jobs={data.jobs} salary={data.salary} onUpd={(k,v)=>upd("sal_upd",{k,v})}/>
        ) :
        staff.type==="takeuchi"||staff.type==="fukazawa" ? (
          <SalaryTakeuchi staff={staff} monthKey={monthKey} jobs={data.jobs} salon={data.salon} salary={data.salary} rate={staff.commissionRate} onUpd={(k,v)=>upd("sal_upd",{k,v})}/>
        ) : staff.type==="aomi" ? (
          <SalaryAomi staff={staff} monthKey={monthKey} jobs={data.jobs} salon={data.salon} salary={data.salary} rate={staff.commissionRate} adjustJobs={data.adjustJobs} onUpd={(k,v)=>upd("sal_upd",{k,v})}/>
        ) : staff.type==="takayama" ? (
          <SalaryTakayama monthKey={monthKey} jobs={data.jobs} jobsShared={data.jobsShared} salon={data.salon} salary={data.salary} onUpd={(k,v)=>upd("sal_upd",{k,v})}/>
        ) : staff.type==="kasugai" ? (
          <SalaryKasugai jobs={data.jobs} salon={data.salon} traffic={data.traffic||[]} salary={data.salary} onUpd={(k,v)=>upd("sal_upd",{k,v})}/>
        ) : (
          <SalaryMatsumoto staff={staff} monthKey={monthKey} jobs={data.jobs} salon={data.salon} salary={data.salary} rate={staff.commissionRate} onUpd={(k,v)=>upd("sal_upd",{k,v})}/>
        )
      )}
      {tab==="ins" && ["takeuchi","fukazawa"].includes(staff.type) && (() => {
        // v107: 3, 4, 5月の本来の歩合給を計算して渡す
        // 月キーは monthKey から年を抽出（例: "2026-05" → "2026"）
        const year = (monthKey || "").split("-")[0] || "2026";
        const monthlyHobu = [
          calcOriginalHobu(staff, `${year}-03`),
          calcOriginalHobu(staff, `${year}-04`),
          calcOriginalHobu(staff, `${year}-05`),
        ];
        return (
          <InsTable
            rows={staff.insSchedule || []}
            onUpdate={(i,f,v)=>onUpdateStaffMeta(staff.id,"insSchedule_upd",{i,f,v})}
            onInit={()=>onUpdateStaffMeta(staff.id,"insSchedule_init",null)}
            monthlyHobu={monthlyHobu}
          />
        );
      })()}
      {tab==="yearly" && hasYearlyReport && (
        <YearlyReport staff={staff} onUpdateStaffMeta={onUpdateStaffMeta}/>
      )}
      {tab==="overview" && (
        <OverviewTab
          staff={staff} monthKey={monthKey} data={data}
          onPrint={handlePrint} onSaveJpg={handleSaveJpg} onSavePdf={handleSavePdf}
        />
      )}
    </div>
  );
}

// ===================== 総合一覧・出力タブ =====================
function OverviewTab({ staff, monthKey, data, onPrint, onSaveJpg, onSavePdf }) {
  const [y, mo] = monthKey.split("-");
  const monthLabel = `${y}年${Number(mo)}月`;
  const isKasugai  = staff.type==="kasugai";
  const isTakayama = staff.type==="takayama";
  const isAomi     = staff.type==="aomi";
  const rate = staff.commissionRate;
  // v86→v92: サロン売上を非表示にする条件を髙山のみに戻す（竹内・深澤は青海と同様に表示）
  const hideSalonForEmployee = isTakayama;

  // 画面幅に応じて表示列を切り替え
  // isNarrow: 420px以下（Galaxy Z Fold4など）→ 最小列
  // isMobile: 500px以下（iPhone 17など）→ 簡略列
  // PC: それ以上 → フル列
  const [winW, setWinW] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWinW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const isMobile = winW <= 520;   // iPhone・スマホ全般
  const isNarrow = winW <= 380;   // Galaxy Z Fold4など極狭

  // v74: 従業員向けモード（利益セクションを非表示にする）
  const [hideRieki, setHideRieki] = useState(false);

  // 現場集計
  // v99: 青海の場合は旧データ（v95-v97 で誤って混入した isAdjust 行）を除外
  const totalG   = data.jobs.reduce((s,j)=>(isAomi && j.isAdjust)?s:s+num(j.guarantee),0);
  const totalH   = sumJobHalf(isAomi ? data.jobs.filter(j=>!j.isAdjust) : data.jobs); // v138
  const totP_job = isKasugai ? data.jobs.reduce((s,j)=>s+num(j.payment),0) : 0;
  // 青海：実数値合計（jitsu優先、無ければギャラ÷2）、旧 isAdjust 行は除外
  const totalJitsu = isAomi ? sumJobHalf(data.jobs.filter(j=>!j.isAdjust)) : 0;

  // サロン集計
  const totPayment = data.salon.reduce((s,r)=>s+num(r.payment),0);
  const tot65all   = data.salon.reduce((s,r)=>s+calcSalon65(r.payment,r.discount,rate),0);
  const tot100all  = isKasugai || isTakayama
    ? data.salon.reduce((s,r)=>s+num(r.nom100),0)
    : data.salon.reduce((s,r)=>s+get100(r, monthKey),0);
  const totDiscall = data.salon.reduce((s,r)=>s+num(r.discount),0);
  const totNom = isKasugai
    ? data.salon.reduce((s,r)=>s+num(r.nomination),0)
    : isTakayama
      ? data.salon.reduce((s,r)=>s+num(r.nom100),0)
      : data.salon.reduce((s,r)=>s+calcNomination(r.payment,r.discount,rate,get100(r, monthKey)),0);

  // 給与明細計算（スタッフ種別ごと）- 手動上書き値を優先参照
  const sal = data.salary || {};
  let kyuItems = [], kojItems = [], furikomi = 0;
  // v57: 利益計算（スタッフ種別ごと）
  let hoburieki = null, salonrieki = null;
  // v129: 春日井の決済手数料（支払合計×3.6%）と対象額。利益セクションで表示する
  let kasugaiKessai = null, kasugaiShiharai = null;
  // v131: 売上・外注費・交通費の内訳（利益セクションで表示）
  let kasugaiUriage = null, kasugaiGenbaG = null, kasugaiSalonPay = null;
  let kasugaiGaichu = null, kasugaiGenbaPay = null, kasugaiNom = null, kasugaiTraffic = null;

  // v88: 出張手当のラベルを動的に「出張手当 (自動2,000円×15日)」のように表示
  // 対象: 松本・竹内・深澤・青海（出張手当単価が設定されているスタッフ）
  const _days = extractDays(sal.出張日数);
  const _unit = staff?.出張手当単価;
  const _shucchouLabel = (_unit != null && _days > 0 && sal.出張手当手動 == null)
    ? `出張手当 (自動${fmt(_unit)}×${_days}日)`
    : "出張手当";

  if (staff.type==="takeuchi"||staff.type==="fukazawa") {
    // 出張手当自動計算
    const shucchou_auto = extractDays(sal.出張日数) * (staff?.出張手当単価 ?? 0);
    const shucchou = sal.出張手当手動 != null ? sal.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto : num(sal.出張手当));
    // v118: SalaryTakeuchi(v116)と同じロジックに統一
    //   歩合給ベース = 50% − 基本給 − 出張手当（保険料調整は含めない）
    const hobugkyuu_base_auto = totalH - num(sal.基本給) - shucchou;
    const hobugkyuu_base = sal.歩合給手動 != null ? sal.歩合給手動 : hobugkyuu_base_auto;
    // v118: 保険料調整額（調整月の差額分、手動優先／なければスケジュール差額分プラスのみ）
    const year_ins_ov = (monthKey || "").split("-")[0] || "2026";
    const insDiff_ov = calcInsScheduleDiff(staff, year_ins_ov);
    const insAdjAuto_ov = (isInsAdjMonth(monthKey) && insDiff_ov > 0) ? insDiff_ov : 0;  // v127: 調整月のみ
    const hokenChoseiGaku_ov = sal.保険料調整額手動 != null ? num(sal.保険料調整額手動) : insAdjAuto_ov;
    // v118: 給与明細の歩合給 = 歩合給ベース + 保険料調整額
    const hobugkyuu = hobugkyuu_base + hokenChoseiGaku_ov;
    // v46: サロン指名料は完全自動（手動値無視、SalaryTakeuchiと同じ）
    const salNom = totNom;
    // v62: 利益計算（基本給は引かない、早朝深夜料金加算、割引減算）
    hoburieki = totalG - totalH - num(sal.役職手当) - num(sal.オシャレ手当);
    salonrieki = totPayment + num(sal.早朝深夜料金) - totDiscall - totNom;
    // v118: 「保険料調整」項目は歩合給に統合したので支給項目から除外（二重計上防止）
    kyuItems = [
      {l:"基本給",v:num(sal.基本給)},{l:"役職手当",v:num(sal.役職手当)},
      {l:"通勤手当",v:num(sal.通勤費)},
      {l:"歩合給",v:hobugkyuu, manual: sal.歩合給手動!=null},
      {l:"サロン指名料",v:salNom},{l:"オシャレ手当",v:num(sal.オシャレ手当)},
      {l:"交通費",v:num(sal.交通費)},{l:_shucchouLabel,v:shucchou, manual: sal.出張手当手動!=null},
      {l:"年末調整還付金",v:num(sal.年末調整還付金)},
    ];
    kojItems = [
      {l:"健康保険料",v:num(sal.健康保険料)},
      ...(showChildSupport(monthKey, staff.type) ? [{l:"子ども・子育て支援金",v:num(sal.子ども子育て支援金)}] : []),
      {l:"厚生年金",v:num(sal.厚生年金保険料)},
      {l:"雇用保険料",v:num(sal.雇用保険料)},{l:"所得税",v:num(sal.所得税)},{l:"住民税",v:num(sal.住民税)},
      {l:"立替金返済分",v:num(sal.立替金返済分)},
    ];
  } else if (staff.type==="aomi") {
    // v98: 調整分は adjustJobs に分離
    // v99: 旧データ（v95-v97 で jobs に追加された isAdjust:true 行）を除外
    const _jobsClean = data.jobs.filter(j=>!j.isAdjust);
    const _adj = data.adjustJobs || [];
    // 実数値合計：手入力 jitsu があればそれ、無ければ guarantee÷2
    const jitsu = sumJobHalf(_jobsClean);
    // v46: 共有用50%合計（強制 min 固定、SalaryAomiと同じロジック）
    // v98: 通常分 + 調整分の合計
    const calcSharedAomi = (j) => Math.min(num(j.guarantee), 30000);
    const _normalSharedH = _jobsClean.reduce((s,j)=>s+INT(calcSharedAomi(j)/2),0);
    const totalAdjustH_ = _adj.reduce((s,j)=>s+INT(calcSharedAomi(j)/2),0);
    const totalSharedH = _normalSharedH + totalAdjustH_;
    // 出張手当自動計算
    const shucchou_auto = extractDays(sal.出張日数) * (staff?.出張手当単価 ?? 0);
    const shucchou = sal.出張手当手動 != null ? sal.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto : num(sal.出張手当));
    // v46: 歩合給 = 共有用50%合計 - 基本給 - 出張手当
    const hobugkyuu_auto = totalSharedH - num(sal.基本給) - shucchou;
    const hobugkyuu = sal.歩合給手動 != null ? sal.歩合給手動 : hobugkyuu_auto;
    // v46: サロン指名料は完全自動（手動値無視）
    const salNom = totNom;
    // v98: 利益 = ギャラ100%(jobs) - 共有用50%(jobs+adjust) - 役職手当 - 調整分50%(会社負担)
    // v99: 利益計算も _jobsClean を使用
    const _totalGClean = _jobsClean.reduce((s,j)=>s+num(j.guarantee),0);
    hoburieki = _totalGClean - totalSharedH - num(sal.役職手当) - totalAdjustH_;
    salonrieki = totPayment + num(sal.早朝深夜料金) - totDiscall - totNom;
    kyuItems = [
      {l:"基本給",v:num(sal.基本給)},{l:"役職手当",v:num(sal.役職手当)},
      {l:"歩合給",v:hobugkyuu, manual: sal.歩合給手動!=null},{l:"サロン指名料",v:salNom},
      {l:"通勤手当",v:num(sal.通勤費)},
      {l:"交通費",v:num(sal.交通費)},{l:_shucchouLabel,v:shucchou, manual: sal.出張手当手動!=null},{l:"年末調整還付金",v:num(sal.年末調整還付金)},
    ];
    kojItems = [
      {l:"健康保険料",v:num(sal.健康保険料)},
      ...(showChildSupport(monthKey, staff.type) ? [{l:"子ども・子育て支援金",v:num(sal.子ども子育て支援金)}] : []),
      {l:"厚生年金",v:num(sal.厚生年金保険料)},
      {l:"雇用保険料",v:num(sal.雇用保険料)},{l:"所得税",v:num(sal.所得税)},{l:"住民税",v:num(sal.住民税)},
      {l:"立替金返済分",v:num(sal.立替金返済分)},
    ];
  } else if (staff.type==="takayama") {
    // v50: 歩合給は jobsShared の件数で計算
    const hobuRows = data.jobsShared && data.jobsShared.length > 0 ? data.jobsShared : data.jobs;
    const hobugkyuu = takayamaSharedTotal(hobuRows);
    // v46: サロン指名料は完全自動（手動値無視）
    const salNom = totNom;
    // v62: 歩合利益 = ギャラ全額 - 基本給 - 役職手当 - 歩合給
    // 例: 4月 625,000 - 213,019 - 10,000 - 20,000 = 381,981
    hoburieki = totalG - num(sal.基本給) - num(sal.役職手当) - hobugkyuu;
    // v62: サロン利益（髙山）= 支払 - 指名料（早朝深夜料金は内容列で自動集計済み）
    salonrieki = totPayment - totNom;
    kyuItems = [
      {l:"基本給",v:num(sal.基本給)},{l:"役職手当",v:num(sal.役職手当)},
      {l:"歩合給",v:hobugkyuu},{l:"サロン指名料",v:salNom},
      {l:"交通費",v:num(sal.交通費)},{l:"年末調整還付金",v:num(sal.年末調整還付金)},
    ];
    kojItems = [
      {l:"健康保険料",v:num(sal.健康保険料)},
      ...(showChildSupport(monthKey, staff.type) ? [{l:"子ども・子育て支援金",v:num(sal.子ども子育て支援金)}] : []),
      {l:"厚生年金",v:num(sal.厚生年金保険料)},
      {l:"雇用保険料",v:num(sal.雇用保険料)},{l:"所得税",v:num(sal.所得税)},{l:"住民税",v:num(sal.住民税)},
      {l:"立替金返済分",v:num(sal.立替金返済分)},
    ];
  } else if (staff.type==="matsumoto") {
    // 出張手当自動計算
    const shucchou_auto = extractDays(sal.出張日数) * (staff?.出張手当単価 ?? 0);
    const shucchou = sal.出張手当手動 != null ? sal.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto : num(sal.出張手当));
    // v58: 松本はサロン指名料を給与に含めない（マネフォ仕様）
    const salNom = 0;
    // v62: 松本は利益セクション削除（hoburieki/salonrieki は null のまま）
    kyuItems = [
      {l:"役員報酬",v:num(sal.役員報酬)},
      {l:"サロン指名料",v:salNom},
      {l:"通勤手当/非課",v:num(sal.通勤費)},
      {l:"交通費/課税",v:num(sal.交通費)},
      {l:_shucchouLabel,v:shucchou, manual: sal.出張手当手動!=null},
      {l:"年末調整還付金",v:num(sal.年末調整還付金)},
    ];
    kojItems = [
      {l:"健康保険料",v:num(sal.健康保険料)},{l:"介護保険料",v:num(sal.介護保険料)},
      ...(showChildSupport(monthKey, staff.type) ? [{l:"子ども・子育て支援金",v:num(sal.子ども子育て支援金)}] : []),
      {l:"厚生年金",v:num(sal.厚生年金保険料)},
      {l:"雇用保険料",v:num(sal.雇用保険料)},{l:"所得税",v:num(sal.所得税)},
      {l:"住民税",v:num(sal.住民税)},{l:"会社立替",v:num(sal.会社立替返済分)},
    ];
  } else if (staff.type==="kasugai") {
    // 春日井 v25：全自動計算（Image 3と同じ表示）
    const totPay2 = data.jobs.reduce((s,j)=>s+num(j.payment),0);  // 現場+ヘアセット
    const totTraffic2 = (data.traffic||[]).reduce((s,t)=>s+num(t.amount),0);
    const koushuu2 = num(sal.講習費天引き);
    const bihin2 = num(sal.備品代交通費);
    const shiharai2 = totPay2 + totTraffic2 + totNom - koushuu2 + bihin2;
    const zeinuki2 = Math.round(shiharai2 / 1.1);
    const shouhi2 = shiharai2 - zeinuki2;
    // v129: 春日井の利益計算
    //   現場粗利・サロン粗利は手数料前で出し、決済手数料は支払合計に1回だけ掛けて最後に引く
    const totG2 = data.jobs.reduce((s,j)=>s+num(j.guarantee),0);
    hoburieki = totG2 - totPay2;                                   // 現場利益（手数料前）
    const salonPay_k = (data.salon||[]).reduce((s,r)=>s+num(r.payment),0);
    salonrieki = salonPay_k - totNom;                               // サロン利益（手数料前）
    kasugaiKessai = Math.round(shiharai2 * 0.036);                  // 決済手数料（支払合計×3.6%）
    kasugaiShiharai = shiharai2;                                    // 手数料の対象額（表示用）
    // v131: 売上・外注費・交通費の内訳
    kasugaiGenbaG   = totG2;                    // 現場ギャランティ（お客様への請求）
    kasugaiSalonPay = salonPay_k;               // サロン支払（お客様の支払）
    kasugaiUriage   = totG2 + salonPay_k;       // 売上合計
    kasugaiGenbaPay = totPay2;                  // 現場支払（スタッフへ）
    kasugaiNom      = totNom;                   // 指名料（スタッフへ）
    kasugaiGaichu   = totPay2 + totNom;         // 外注費合計
    kasugaiTraffic  = totTraffic2;              // 交通費（実費）
    // 給与明細項目 (支払明細ブロック)
    kyuItems = [
      {l:"現場＋ヘアセット",v:totPay2},
      {l:"交通費",v:totTraffic2},
      {l:"指名料",v:totNom},
      {l:"講習費（天引き）",v:-koushuu2},
      {l:"備品代/交通費",v:bihin2},
      {l:"支払合計",v:shiharai2, isSubTotal:true},
      {l:"税抜金額",v:zeinuki2, isInternal:true},
      {l:"消費税",v:shouhi2, isInternal:true},
      {l:"税込金額",v:shiharai2, isTotal:true},
    ];
    kojItems = [];
  } else if (staff.type==="akino" || staff.type==="hirasawa") {
    // v64: 業務委託（秋野・平澤）：基本給 + 交通費合計
    const subtotalOf = (r) => {
      if (r.note === "基本給に含む") return 0;
      if (r.subtotal != null) return num(r.subtotal);
      if (num(r.roundtrip) > 0) return num(r.roundtrip);
      return num(r.oneway);
    };
    const transportTotal = data.jobs.reduce((s, r) => s + subtotalOf(r), 0);
    const basic_g = num(sal.基本給);
    const total_g = basic_g + transportTotal;
    kyuItems = [
      {l:"基本給",v:basic_g},
      {l:"交通費",v:transportTotal},
      {l:"支払合計",v:total_g, isSubTotal:true},
      {l:"支払額",v:total_g, isTotal:true},
    ];
    kojItems = [];
    hoburieki = null;
    salonrieki = null;
  }

  const totS = kyuItems.reduce((s,i)=>{
    // 春日井の isInternal/isSubTotal/isTotal は合計に含めない（重複防止）
    if (i.isInternal || i.isSubTotal || i.isTotal) return s;
    return s+i.v;
  },0);
  const totK = kojItems.reduce((s,i)=>s+i.v,0);
  // 春日井・業務委託の場合は振込支給額 = 税込金額/支払額 = isTotal
  furikomi = (staff.type==="kasugai" || staff.type==="akino" || staff.type==="hirasawa")
    ? kyuItems.find(i=>i.isTotal)?.v ?? 0
    : totS - totK;

  // v35: 印刷用CSS（A4完全対応＋自動改ページ）
  const printStyle = `
    @media print {
      /* すべての要素を非表示に */
      body * { visibility: hidden; }
      /* 印刷エリアのみ表示 */
      #overview-print-area, #overview-print-area * { visibility: visible; }
      #overview-print-area {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        padding: 0;
        margin: 0;
        box-sizing: border-box;
        background: #fff !important;
      }
      /* 印刷時は不要な要素を完全非表示 */
      .no-print { display: none !important; }
      /* A4サイズ設定（縦向き、10mm余白） */
      @page {
        size: A4 portrait;
        margin: 10mm 8mm;
      }
      /* テーブル全般：固定レイアウト＋適切なフォント */
      table {
        table-layout: fixed !important;
        width: 100% !important;
        border-collapse: collapse !important;
        page-break-inside: auto !important;
      }
      /* 行の途中で切れないようにする */
      tr { page-break-inside: avoid !important; page-break-after: auto !important; }
      /* テーブルヘッダ（thead）はページをまたぐとき自動で各ページに複製される */
      thead { display: table-header-group !important; }
      tfoot { display: table-footer-group !important; }
      /* セル：フォントサイズとパディングをA4向けに最適化 */
      td, th {
        font-size: 9pt !important;
        padding: 3px 5px !important;
        overflow: hidden;
        word-wrap: break-word;
      }
      /* 見出しエリア（ブロック単位で改ページ） */
      .print-block {
        page-break-inside: auto;
        margin-bottom: 8px;
      }
      .print-block-title {
        page-break-after: avoid;
        page-break-before: auto;
      }
      /* 給与明細ブロックは極力1ページに収める */
      .print-meisai { page-break-inside: avoid; }
      /* 色を鮮明に印刷（Chromeのカラー印刷） */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      /* 薄青ヘッダ・合計行の背景色を印刷でも維持 */
      th { background: #dde3f0 !important; color: #1e3a8a !important; }
      /* 金額セルの色維持 */
      .sum-row { background: #dde3f0 !important; }
    }
    /* v93: クリックでコピーできるセルのホバーハイライト */
    .copy-target:hover {
      outline: 2px solid #06b6d4;
      outline-offset: -2px;
    }
  `;

  // 印刷用テーブルセルスタイル（v56: v54とv55の間・ほんの少し広げる）
  const PTH = {...TH, fontSize:11, padding:"3px 7px", whiteSpace:"nowrap", overflow:"hidden"};
  const PTD = {...TD, fontSize:11, padding:"3px 7px", verticalAlign:"middle"};
  const PTDR = {...TDR, fontSize:11, padding:"3px 9px", whiteSpace:"nowrap", verticalAlign:"middle"};
  const PTSUM = {...TSUM, fontSize:12, padding:"4px 9px", verticalAlign:"middle", whiteSpace:"nowrap"};

  // v80: LINE用コピーテキスト生成（既存のチェックボックス hideRieki に連動）
  const [copyStatus, setCopyStatus] = useState(""); // "" | "done" | "error"
  // v93: 1セルタップでコピー機能
  const [cellCopyMsg, setCellCopyMsg] = useState(""); // 一時的なトースト表示用
  const copyCell = async (label, value) => {
    try {
      const text = `${label} ${value}`;
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCellCopyMsg(`✅ コピー: ${text}`);
      setTimeout(() => setCellCopyMsg(""), 1800);
    } catch (e) {
      setCellCopyMsg("❌ コピー失敗");
      setTimeout(() => setCellCopyMsg(""), 1800);
    }
  };
  // クリック可能セル用のスタイル
  const cellClickable = { cursor: "pointer" };

  const generateLineText = () => {
    const L = [];
    const SEP = "━━━━━━━━━━━━━━━";
    const yen = (v) => `${fmtNum(v)} 円`;

    L.push(SEP);
    L.push(`📋 M's株式会社 給与明細`);
    L.push(`${monthLabel}　${staff.name}`);
    if (hideRieki) L.push(`（従業員向け）`);
    L.push(SEP);
    L.push("");

    // 現場一覧（青海・髙山の従業員モードでは非表示）
    // v124: 業務委託（秋野・平澤）は jobs が交通費明細なので現場一覧としては出さない
    if (!(hideRieki && (isAomi || isTakayama)) && !(staff.type==="akino"||staff.type==="hirasawa") && data.jobs.length > 0) {
      L.push(`【現場一覧】${data.jobs.length}件`);
      data.jobs.forEach(j => {
        const parts = [j.date, j.client || ""];
        if (j.guarantee != null) parts.push(`ギャラ${yen(j.guarantee)}`);
        if (isKasugai && j.payment != null) parts.push(`支払${yen(j.payment)}`);
        if (isAomi) {
          if (j.guarantee != null) parts.push(`実数値${yen(jobHalf(j))}`);
        }
        L.push(parts.filter(Boolean).join(" "));
      });
      L.push(`─ ギャラ合計：${yen(totalG)}`);
      if (isAomi) L.push(`─ 実数値合計：${yen(totalJitsu)}`);
      else if (isKasugai) {
        L.push(`─ 支払合計：${yen(totP_job)}`);
        if (!hideRieki) L.push(`─ 利益：${yen(totalG - totP_job)}`);
      } else if (!isTakayama) L.push(`─ 50%合計：${yen(totalH)}`);
      L.push("");
    }

    // 青海・現場一覧（共有用）
    if (isAomi) {
      const calcShared = (j) => Math.min(num(j.guarantee), 30000);
      const totalSharedG = data.jobs.reduce((s,j)=>s+calcShared(j),0);
      const totalSharedH = data.jobs.reduce((s,j)=>s+INT(calcShared(j)/2),0);
      L.push(`【現場一覧${hideRieki?"":"(共有用)"}】${data.jobs.length}件`);
      data.jobs.forEach(j => {
        const sharedG = calcShared(j);
        const sharedH = INT(sharedG/2);
        // v119: 共有用50%は歩合給の計算根拠なので従業員モードでも表示
        L.push([j.date, j.client || "", `ギャラ${yen(sharedG)}`, `50%${yen(sharedH)}`].join(" "));
      });
      L.push(`─ ギャランティ合計：${yen(totalSharedG)}`);
      L.push(`─ 50%合計：${yen(totalSharedH)}`);
      L.push("");
      // v122: 歩合給内訳（共有用50% − 基本給 − 出張手当）
      {
        const kihon_ = num(sal.基本給);
        const shucchou_auto_ = extractDays(sal.出張日数) * (staff?.出張手当単価 ?? 0);
        const shucchou_ = sal.出張手当手動 != null ? sal.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto_ : num(sal.出張手当));
        const hobu_auto_ = totalSharedH - kihon_ - shucchou_;
        const isOverridden_ = sal.歩合給手動 != null;
        const hobu_ = isOverridden_ ? num(sal.歩合給手動) : hobu_auto_;
        L.push(`【歩合給内訳】`);
        if (!isOverridden_) {
          L.push(`共有用50%　　 ${yen(totalSharedH)}`);
          L.push(`− 基本給　　　 ${yen(kihon_)}`);
          L.push(`− 出張手当　　 ${yen(shucchou_)}`);
        }
        L.push(`歩合給：${yen(hobu_)}`);
        L.push("");
      }
    }

    // 髙山・現場一覧（共有用）
    if (isTakayama) {
      const hobuRows = data.jobsShared && data.jobsShared.length > 0 ? data.jobsShared : data.jobs;
      const totalShared = takayamaSharedTotal(hobuRows);
      L.push(`【現場一覧${hideRieki?"":"(共有用)"}】${hobuRows.length}件`);
      hobuRows.forEach(j => {
        L.push(`${j.date} ${j.client || ""} 歩合給${yen(takayamaAmount(j))}`);
      });
      L.push(`─ 歩合給合計：${yen(totalShared)}`);
      L.push("");
    }

    // サロン売上 v79: 髙山・従業員モードでは非表示 v92: 竹内・深澤は表示に戻す（青海と同様）
    const _hideSalonForEmp = isTakayama;
    if (data.salon.length > 0 && !(hideRieki && _hideSalonForEmp)) {
      L.push(`【サロン売上】${data.salon.length}件`);
      data.salon.forEach(r => {
        const vNom = isKasugai ? num(r.nomination) : isTakayama ? num(r.nom100) : calcNomination(r.payment,r.discount,rate,get100(r, monthKey));
        const parts = [r.date, r.name || ""];
        if (r.payment != null) parts.push(`支払${yen(r.payment)}`);
        if (!isKasugai && !isTakayama && r.discount) parts.push(`割引${yen(r.discount)}`);
        if (vNom > 0) parts.push(`指名料${yen(vNom)}`);
        if ((isKasugai||isTakayama) && r.memo) parts.push(r.memo);
        L.push(parts.join(" "));
      });
      L.push(`─ 支払合計：${yen(totPayment)}`);
      L.push(`─ 指名料計：${yen(totNom)}`);
      // v132: 9月以降の100%内訳（従業員向けでも可）
      if (isSplitMonth(monthKey) && !isKasugai && !isTakayama) {
        const sShimei = data.salon.reduce((s,r)=>s+num(r.nomShimei),0);
        const sSoucho = data.salon.reduce((s,r)=>s+num(r.nomSoucho),0);
        const sHanbai = data.salon.reduce((s,r)=>s+num(r.nomHanbai),0);
        const sCancel = data.salon.reduce((s,r)=>s+num(r.nomCancel),0);
        L.push(`【100%内訳】`);
        L.push(`スタッフ指名料：${yen(sShimei)}`);
        L.push(`早朝深夜料金：${yen(sSoucho)}`);
        L.push(`販売手数料：${yen(sHanbai)}`);
        L.push(`キャンセル代：${yen(sCancel)}`);
      }
      L.push("");
    }

    // v124: 交通費明細（業務委託・秋野/平澤）
    if ((staff.type==="akino"||staff.type==="hirasawa") && (data.jobs||[]).length > 0) {
      const tRows = data.jobs || [];
      const subOf = (r) => {
        if (r.note === "基本給に含む") return null;
        if (r.subtotal != null) return num(r.subtotal);
        if (num(r.roundtrip) > 0) return num(r.roundtrip);
        return num(r.oneway);
      };
      const totT = tRows.reduce((s,r)=>s+(subOf(r)||0),0);
      L.push(`【交通費明細】${tRows.length}件`);
      tRows.forEach(r => {
        const sub = subOf(r);
        L.push([r.date, r.content || "", sub!=null?yen(sub):"（基本給に含む）"].filter(Boolean).join(" "));
      });
      L.push(`─ 交通費合計：${yen(totT)}`);
      L.push("");
    }

    // 春日井・交通費明細
    if (isKasugai && (data.traffic||[]).length > 0) {
      const totTraffic = (data.traffic||[]).reduce((s,t)=>s+num(t.amount),0);
      L.push(`【交通費明細】${data.traffic.length}件`);
      data.traffic.forEach(t => {
        L.push(`${t.date||""} ${t.memo||""} ${yen(t.amount)}`);
      });
      L.push(`─ 合計：${yen(totTraffic)}`);
      L.push("");
    }

    // 給与明細
    L.push(`【給与明細】`);
    if (kyuItems.length > 0) {
      L.push(`─ 支給 ─`);
      kyuItems.forEach(i => {
        if (i.isInternal) return; // 税抜・消費税などはスキップ
        const prefix = i.isSubTotal ? "（小計）" : i.isTotal ? "▼ " : "";
        const mark = i.manual ? " ✎" : "";
        L.push(`${prefix}${i.l}：${yen(i.v)}${mark}`);
      });
      if (staff.type !== "kasugai" && staff.type !== "akino" && staff.type !== "hirasawa") {
        L.push(`支給合計：${yen(totS)}`);
      }
    }
    if (kojItems.length > 0) {
      L.push("");
      L.push(`─ 控除 ─`);
      kojItems.forEach(i => {
        L.push(`${i.l}：${yen(i.v)}`);
      });
      L.push(`控除合計：${yen(totK)}`);
    }
    L.push("");
    const furikomiLabel = staff.type==="kasugai" ? "支払合計" : (staff.type==="akino"||staff.type==="hirasawa") ? "支払額" : "振込支給額";
    L.push(`💴 ${furikomiLabel}：${yen(furikomi)}`);

    // v123: 業務委託（秋野・平澤）の売上・利益（会社用のみ）
    if (!hideRieki && (staff.type==="akino"||staff.type==="hirasawa") && (data.uriage||[]).length > 0) {
      const uRows = data.uriage || [];
      const kg = {};
      uRows.forEach(r => { const k=(r.kubun||"その他").trim()||"その他"; if(!kg[k])kg[k]={count:0,sum:0}; kg[k].count++; kg[k].sum+=num(r.amount); });
      const totU = uRows.reduce((s,r)=>s+num(r.amount),0);
      const kotei_ = num(sal.基本給);
      const arari_ = totU - kotei_;
      const rate_ = totU > 0 ? (arari_/totU*100).toFixed(1) : null;
      L.push("");
      L.push(`【売上・利益】`);
      L.push(`売上合計：${yen(totU)}（${Object.entries(kg).map(([k,g])=>`${k}${g.count}件`).join("・")}）`);
      L.push(`固定給(原価)：${yen(kotei_)}`);
      L.push(`粗利：${yen(arari_)}${rate_!=null?`（粗利率 ${rate_}%）`:""}`);
    }

    // v81: 保険料調整スケジュール（竹内・深澤、従業員モードでも表示） v84: Excel準拠の計算式
    if (["takeuchi","fukazawa"].includes(staff.type) && staff.insSchedule && staff.insSchedule.length >= 7) {
      // v87/v118: 歩合給内訳（保険料調整額を反映した構造）
      const shucchou_auto_ = extractDays(sal.出張日数) * (staff?.出張手当単価 ?? 0);
      const shucchou_ = sal.出張手当手動 != null ? sal.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto_ : num(sal.出張手当));
      const half_ = totalH;
      const kihon_ = num(sal.基本給);
      const hobu_base_auto_ = half_ - kihon_ - shucchou_;
      const hobu_base_ = sal.歩合給手動 != null ? num(sal.歩合給手動) : hobu_base_auto_;
      const isOverridden_ = sal.歩合給手動 != null;
      const year_ins_ = (monthKey || "").split("-")[0] || "2026";
      const insDiff_ = calcInsScheduleDiff(staff, year_ins_);
      const insAdjAuto_ = (isInsAdjMonth(monthKey) && insDiff_ > 0) ? insDiff_ : 0;  // v127: 調整月のみ
      const hokenChoseiGaku_ = sal.保険料調整額手動 != null ? num(sal.保険料調整額手動) : insAdjAuto_;
      const hobu_final_ = hobu_base_ + hokenChoseiGaku_;
      L.push("");
      L.push(`【歩合給内訳】`);
      if (!isOverridden_) {
        L.push(`50%　　　　　 ${yen(half_)}`);
        L.push(`− 基本給　　　 ${yen(kihon_)}`);
        L.push(`− 出張手当　　 ${yen(shucchou_)}`);
      } else {
        L.push(`歩合給ベース（手動）${yen(hobu_base_)}`);
      }
      L.push(`+ 保険料調整額 ${yen(hokenChoseiGaku_)}`);
      L.push(`歩合給：${yen(hobu_final_)}`);
    }

    // v127: 調整月（6月分・支払7/24）を過ぎた7月以降は、翌年1月の新サイクルまで非表示
    if (["takeuchi","fukazawa"].includes(staff.type) && isInsCycleMonth(monthKey) && staff.insSchedule && staff.insSchedule.length >= 7) {
      const rs = staff.insSchedule;
      const total_ = num(rs[0]?.amount);
      const marSub_ = num(rs[3]?.sub);
      const aprSub_ = num(rs[4]?.sub);
      const maySub_ = num(rs[5]?.sub);
      // v91: 差額分 = sub合計 − 保険料調整合計（マイナス=未消化、プラス=超過）
      const diff_ = (marSub_ + aprSub_ + maySub_) - total_;
      const diffSign_ = diff_ > 0 ? "プラス（超過・追加支払い）" : diff_ < 0 ? "マイナス（未消化）" : "ゼロ";
      L.push("");
      L.push(`【保険料調整スケジュール】`);
      L.push(`保険料調整合計：${rs[0]?.amount == null ? "—" : yen(rs[0].amount)}`);
      L.push(`2026年1月分/支払日 2/25：${rs[1]?.amount == null ? "—" : yen(rs[1].amount)}`);
      L.push(`2026年2月分/支払日 3/25：${rs[2]?.amount == null ? "—" : yen(rs[2].amount)}`);
      L.push(`2026年3月分/支払日 4/24：amount 0 / sub ${rs[3]?.sub == null ? "—" : yen(rs[3].sub)}`);
      L.push(`2026年4月分/支払日 5/24：amount 0 / sub ${rs[4]?.sub == null ? "—" : yen(rs[4].sub)}`);
      L.push(`2026年5月分/支払日 6/24：amount 0 / sub ${rs[5]?.sub == null ? "—" : yen(rs[5].sub)}`);
      L.push(`調整月：2026年6月分/支払日 7/24`);
      L.push(`▼ 差額分（自動）：${yen(diff_)}（${diffSign_}）`);
    }

    // 利益（hideRieki=false かつ松本以外で表示）
    if (!hideRieki && staff.type !== "matsumoto" && (hoburieki != null || salonrieki != null)) {
      L.push("");
      L.push(`【利益】`);
      // v131: 春日井は 売上 → 外注費 → 粗利 → 交通費・手数料 → 最終利益
      if (staff.type==="kasugai" && kasugaiKessai != null) {
        L.push(`売上合計：${yen(kasugaiUriage)}`);
        L.push(`　現場ギャランティ：${yen(kasugaiGenbaG)}`);
        L.push(`　サロン支払：${yen(kasugaiSalonPay)}`);
        L.push(`− 外注費：${yen(kasugaiGaichu)}`);
        L.push(`　現場支払：${yen(kasugaiGenbaPay)}`);
        L.push(`　指名料：${yen(kasugaiNom)}`);
        L.push(`粗利：${yen(hoburieki + salonrieki)}`);
        L.push(`− 交通費：${yen(kasugaiTraffic)}`);
        L.push(`− 決済手数料（支払合計 ${fmtNum(kasugaiShiharai)}円 × 3.6%）：${yen(kasugaiKessai)}`);
        L.push(`最終利益：${yen(hoburieki + salonrieki - kasugaiTraffic - kasugaiKessai)}`);
      } else {
        if (hoburieki != null) L.push(`歩合利益：${yen(hoburieki)}`);
        if (salonrieki != null) L.push(`サロン利益：${yen(salonrieki)}`);
      }
    }

    L.push("");
    L.push(SEP);

    return L.join("\n");
  };

  const handleCopyLine = async () => {
    try {
      const text = generateLineText();
      // navigator.clipboard が使えない環境（古いブラウザ・httpなど）への fallback
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopyStatus("done");
      setTimeout(() => setCopyStatus(""), 2500);
    } catch (e) {
      console.error(e);
      setCopyStatus("error");
      setTimeout(() => setCopyStatus(""), 2500);
    }
  };

  return (
    <div>
      <style>{printStyle}</style>
      {/* v93: 1セルコピーのトースト */}
      {cellCopyMsg && (
        <div className="no-print" style={{
          position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)",
          background:"#1e3a8a", color:"#fff", padding:"8px 18px", borderRadius:6,
          fontSize:13, fontWeight:"bold", zIndex:9999, boxShadow:"0 4px 12px rgba(0,0,0,0.2)",
          pointerEvents:"none"
        }}>{cellCopyMsg}</div>
      )}
      {/* 操作ボタン */}
      <div className="no-print" style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
        <button style={{...BTN(),padding:"6px 18px",fontSize:13}} onClick={onPrint}>
          🖨️ 印刷
        </button>
        <button style={{...BTN("#059669"),padding:"6px 18px",fontSize:13}} onClick={onSavePdf}>
          📄 PDF保存
        </button>
        <button style={{...BTN("#374151"),padding:"6px 18px",fontSize:13}} onClick={onSaveJpg}>
          🖼️ JPG保存
        </button>
        {/* v80: LINE用テキストコピー（hideRieki に連動して会社用/従業員用を切替） */}
        <button
          style={{...BTN(copyStatus==="done"?"#16a34a":copyStatus==="error"?"#dc2626":"#06b6d4"),padding:"6px 18px",fontSize:13}}
          onClick={handleCopyLine}
          title="LINEメッセージにそのまま貼れる形式でクリップボードにコピーします"
        >
          {copyStatus==="done" ? "✅ コピー完了" : copyStatus==="error" ? "❌ コピー失敗" : `📋 LINE用コピー（${hideRieki?"従業員":"会社"}用）`}
        </button>
        {/* v74: 従業員向けモード切替（利益セクション非表示） */}
        <label style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:12,padding:"6px 12px",border:"1px solid #d1d5db",borderRadius:4,background:hideRieki?"#fef3c7":"#fff",cursor:"pointer",userSelect:"none"}}>
          <input type="checkbox" checked={hideRieki} onChange={e=>setHideRieki(e.target.checked)} style={{cursor:"pointer"}}/>
          <span style={{color:hideRieki?"#92400e":"#374151",fontWeight:hideRieki?"bold":"normal"}}>👤 従業員向け（利益非表示）</span>
        </label>
        <span style={{fontSize:11,color:"#888"}}>PDFボタンで直接PDFとして保存（モバイル対応）</span>
      </div>

      {/* 印刷エリア */}
      <div id="overview-print-area" style={{background:"#fff",padding:16,border:"1px solid #ddd",maxWidth:900}}>

        {/* ヘッダー v86: 従業員モード時は大見出し表示 */}
        <div className="print-block-title" style={{borderBottom:"3px solid #1e3a8a",paddingBottom:8,marginBottom:14}}>
          {hideRieki ? (
            <>
              <div style={{fontSize:22,fontWeight:"bold",color:"#000",textAlign:"center",letterSpacing:"0.05em"}}>
                {monthLabel}　{staff.name}　給与明細
              </div>
              <div style={{fontSize:11,color:"#666",marginTop:6,textAlign:"right"}}>M's株式会社</div>
            </>
          ) : (
            <>
              <div style={{fontSize:17,fontWeight:"bold",color:"#000"}}>M's株式会社　給与管理システム</div>
              <div style={{fontSize:13,marginTop:3}}>{monthLabel}　{staff.name}　歩合表</div>
              <div style={{fontSize:10,color:"#666",marginTop:2}}>従業員番号 {staff.no}　{staff.commissionRate>0?`還元率 ${(staff.commissionRate*100).toFixed(0)}%`:staff.type==="kasugai"?"業務委託":(staff.type==="akino"||staff.type==="hirasawa")?"業務委託（固定給+交通費）":staff.type==="takayama"?"固定1,000円/現場":"役員"}</div>
            </>
          )}
        </div>

        {/* 現場一覧 v74: 従業員向けモードかつ青海の場合は非表示（共有用のみ表示） v76: 髙山も同様 */}
        {/* v124: 業務委託（秋野・平澤）は jobs が交通費明細なのでギャラ0円の空表になる → 非表示 */}
        {!(hideRieki && (isAomi || isTakayama)) && !(staff.type==="akino"||staff.type==="hirasawa") && (
        <div className="print-block" style={{marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
            ■ 現場一覧　{(isAomi ? data.jobs.filter(j=>!j.isAdjust).length : data.jobs.length)}件　ギャランティ合計 {fmt(totalG)}{isAomi?`　→ 実数値合計 ${fmt(totalJitsu)}`:!isKasugai&&!isTakayama?`　→ 50%合計 ${fmt(totalH)}`:isKasugai?`　支払合計 ${fmt(totP_job)}　利益 ${fmt(totalG-totP_job)}`:""}
          </div>
          <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
            <colgroup>
              <col style={{width:isNarrow?"14%":isMobile?"13%":"9%"}}/>
              <col/>
              {isMobile ? (
                <>
                  <col style={{width:isNarrow?"24%":"22%"}}/>
                  {(!isKasugai&&!isTakayama&&!isAomi) && <col style={{width:isNarrow?"19%":"20%"}}/>}
                  {isAomi && <col style={{width:isNarrow?"19%":"20%"}}/>}
                  {isKasugai && <><col style={{width:"17%"}}/><col style={{width:"17%"}}/></>}
                </>
              ) : (
                <>
                  <col style={{width:"18%"}}/>
                  {isKasugai && <col style={{width:"18%"}}/>}
                  {isKasugai && <col style={{width:"18%"}}/>}
                  {isAomi && <col style={{width:"18%"}}/>}
                  {!isKasugai && !isTakayama && !isAomi && <col style={{width:"18%"}}/>}
                  {isTakayama && <col style={{width:"18%"}}/>}
                </>
              )}
            </colgroup>
            <thead>
              <tr>
                <th style={PTH}>日付</th>
                <th style={PTH}>現場</th>
                <th style={{...PTH,textAlign:"center",whiteSpace:"normal",wordBreak:"keep-all"}}>ギャランティ</th>
                {isKasugai && <th style={{...PTH,textAlign:"center"}}>支払額</th>}
                {isKasugai && <th style={{...PTH,textAlign:"center",background:"#f0f0f0"}}>利益</th>}
                {isAomi && <th style={{...PTH,textAlign:"center",background:"#f0f0f0"}}>実数値</th>}
                {!isKasugai && !isTakayama && !isAomi && <th style={{...PTH,textAlign:"center",background:"#f0f0f0"}}>50%</th>}
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((j,i)=>{
                // v99: 青海のみ、旧 isAdjust 行は実数値表に表示しない
                if (isAomi && j.isAdjust) return null;
                const jitsuVal = jobHalf(j);
                const isJitsuManual = isAomi && isHalfManual(j);
                return (
                  <tr key={i} style={{background:"#fff"}}>
                    <td style={{...PTD,whiteSpace:"nowrap",fontSize:isNarrow?8:10,textAlign:"center",verticalAlign:"middle"}}>{j.date}</td>
                    <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.client}</td>
                    <td style={PTDR}>{fmtNum(j.guarantee)}</td>
                    {isKasugai && <td style={PTDR}>{fmtNum(j.payment)}</td>}
                    {isKasugai && <td style={{...PTDR,color:"#000",background:"#fff"}}>{j.guarantee!=null&&j.payment!=null?fmtNum(num(j.guarantee)-num(j.payment)):""}</td>}
                    {isAomi && <td style={{...PTDR,color:isJitsuManual?"#92400e":"#000",background:isJitsuManual?"#fef3c7":"#fff"}}>{j.guarantee!=null?<>{fmtNum(jitsuVal)}{isJitsuManual && <span style={{fontSize:8,marginLeft:2}}>✎</span>}</>:""}</td>}
                    {!isKasugai && !isTakayama && !isAomi && <td style={{...PTDR,color:"#000",background:"#fff"}}>{j.guarantee!=null?fmtNum(jitsuVal):""}</td>}
                  </tr>
                );
              })}
              <tr style={{background:"#dde3f0",fontWeight:"bold"}}>
                <td colSpan={2} style={{...PTSUM,textAlign:"right",paddingRight:8}}>合計</td>
                <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totalG)}</td>
                {isKasugai && <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totP_job)}</td>}
                {isKasugai && <td style={{...PTSUM,background:"#b8caf8",whiteSpace:"nowrap"}}>{fmt(totalG-totP_job)}</td>}
                {isAomi && <td style={{...PTSUM,background:"#f0f0f0",color:"#000",whiteSpace:"nowrap"}}>{fmt(totalJitsu)}</td>}
                {!isKasugai && !isTakayama && !isAomi && <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totalH)}</td>}
              </tr>
            </tbody>
          </table>
        </div>
        )}

        {/* 青海専用：現場一覧（共有用） v98: jobs + adjustJobs を結合表示 v99: 旧データの isAdjust:true 行を除外 */}
        {isAomi && (() => {
          const calcShared = (j) => Math.min(num(j.guarantee), 30000);
          // v99: 旧データ（v95-v97 で jobs に追加されてしまった調整行）を除外
          const _jobsClean = data.jobs.filter(j=>!j.isAdjust);
          const _adj = data.adjustJobs || [];
          // 通常分
          const normalG = _jobsClean.reduce((s,j)=>s+calcShared(j),0);
          const normalH = _jobsClean.reduce((s,j)=>s+INT(calcShared(j)/2),0);
          // 調整分
          const adjustG = _adj.reduce((s,j)=>s+calcShared(j),0);
          const adjustH = _adj.reduce((s,j)=>s+INT(calcShared(j)/2),0);
          // 合計
          const totalSharedG = normalG + adjustG;
          const totalSharedH = normalH + adjustH;
          const totalCount = _jobsClean.length + _adj.length;
          // 表示用に結合（通常→調整の順）
          const combined = [
            ..._jobsClean.map(j=>({...j, _adj:false})),
            ..._adj.map(j=>({...j, _adj:true})),
          ];
          return (
            <div className="print-block" style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                ■ 現場一覧{!hideRieki && "(共有用)"}　{totalCount}件　ギャランティ合計 {fmt(totalSharedG)}　→ 50%合計 {fmt(totalSharedH)}
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
                <colgroup>
                  <col style={{width:isNarrow?"14%":isMobile?"13%":"9%"}}/>
                  <col/>
                  <col style={{width:isMobile?"19%":"18%"}}/>
                  {/* v119: 共有用50%は歩合給の計算根拠なので従業員モードでも表示 */}
                  <col style={{width:isMobile?"19%":"18%"}}/>
                </colgroup>
                <thead>
                  <tr>
                    <th style={PTH}>日付</th>
                    <th style={PTH}>現場</th>
                    <th style={{...PTH,textAlign:"center"}}>ギャランティ</th>
                    <th style={{...PTH,textAlign:"center",background:"#f0f0f0"}}>50%</th>
                  </tr>
                </thead>
                <tbody>
                  {combined.map((j,i)=>{
                    const sharedG = calcShared(j);
                    const sharedH = INT(sharedG/2);
                    const isAdj = j._adj;
                    const rowBg = isAdj ? "#fee2e2" : "#fff";
                    return (
                      <tr key={i} style={{background:rowBg}}>
                        <td style={{...PTD,whiteSpace:"nowrap",fontSize:isNarrow?8:10,textAlign:"center",verticalAlign:"middle",color:isAdj?"#dc2626":undefined}}>{j.date}</td>
                        <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap", fontWeight:isAdj?"bold":"normal", color: isAdj ? "#dc2626" : undefined}}>
                          {isAdj && <span style={{fontSize:8,marginRight:3,padding:"0 4px",background:"#dc2626",color:"#fff",borderRadius:2,verticalAlign:"middle"}}>調整</span>}
                          {j.client}
                        </td>
                        <td style={{...PTDR,background:isAdj?"#fee2e2":"#fff",color:isAdj?"#dc2626":"#000",fontWeight:isAdj?"bold":"normal"}}>
                          {fmtNum(sharedG)}
                        </td>
                        <td style={{...PTDR,color:isAdj?"#dc2626":"#000",background:isAdj?"#fee2e2":"#fff",fontWeight:isAdj?"bold":"normal"}}>{fmtNum(sharedH)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{background:"#dde3f0",fontWeight:"bold"}}>
                    <td colSpan={2} style={{...PTSUM,textAlign:"right",paddingRight:8}}>合計</td>
                    <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totalSharedG)}</td>
                    <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totalSharedH)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* v48/v50: 髙山専用：現場一覧(共有用) - v133: 行ごとの金額対応 */}
        {isTakayama && (() => {
          // v50: jobsShared が定義されていればそれを使う、なければ jobs
          const hobuRows = data.jobsShared && data.jobsShared.length > 0 ? data.jobsShared : data.jobs;
          const totalShared = takayamaSharedTotal(hobuRows);
          return (
            <div className="print-block" style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                ■ 現場一覧{!hideRieki && "(共有用)"}　{hobuRows.length}件　歩合給合計 {fmt(totalShared)}
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
                <colgroup>
                  <col style={{width:isNarrow?"14%":isMobile?"13%":"9%"}}/>
                  <col/>
                  <col style={{width:isMobile?"22%":"18%"}}/>
                </colgroup>
                <thead>
                  <tr>
                    <th style={PTH}>日付</th>
                    <th style={PTH}>現場</th>
                    <th style={{...PTH,textAlign:"center"}}>歩合給</th>
                  </tr>
                </thead>
                <tbody>
                  {hobuRows.map((j,i)=>(
                    <tr key={i} style={{background:"#fff"}}>
                      <td style={{...PTD,whiteSpace:"nowrap",fontSize:isNarrow?8:10,textAlign:"center",verticalAlign:"middle"}}>{j.date}</td>
                      <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.client}</td>
                      <td style={{...PTDR,color:j.amount!=null?"#000":"#9ca3af",background:"#fff"}}>{fmtNum(takayamaAmount(j))}</td>
                    </tr>
                  ))}
                  <tr style={{background:"#dde3f0",fontWeight:"bold"}}>
                    <td colSpan={2} style={{...PTSUM,textAlign:"right",paddingRight:8}}>合計</td>
                    <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totalShared)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* 交通費明細（春日井専用） v35 */}
        {isKasugai && (data.traffic||[]).length > 0 && (() => {
          const trafficRows = data.traffic || [];
          const totTraffic = trafficRows.reduce((s,t)=>s+num(t.amount),0);
          return (
            <div className="print-block" style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                ■ 交通費明細　{trafficRows.length}件　合計 {fmt(totTraffic)}
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
                <colgroup>
                  <col style={{width:isNarrow?"15%":isMobile?"14%":"11%"}}/>
                  <col/>
                  <col style={{width:isNarrow?"22%":"18%"}}/>
                </colgroup>
                <thead>
                  <tr>
                    <th style={PTH}>日付</th>
                    <th style={PTH}>内容</th>
                    <th style={PTH}>金額</th>
                  </tr>
                </thead>
                <tbody>
                  {trafficRows.map((r,i)=>(
                    <tr key={i} style={{background:"#fff"}}>
                      <td style={{...PTD,whiteSpace:"nowrap",fontSize:isNarrow?8:10,textAlign:"center",verticalAlign:"middle"}}>{r.date}</td>
                      <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.memo}</td>
                      <td style={PTDR}>{fmtNum(r.amount)}</td>
                    </tr>
                  ))}
                  <tr style={{background:"#dde3f0",fontWeight:"bold"}}>
                    <td colSpan={2} style={{...PTSUM,textAlign:"right",paddingRight:8}}>合計</td>
                    <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totTraffic)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* v124: 交通費明細（業務委託・秋野/平澤）現場一覧の代わりに表示 */}
        {(staff.type==="akino"||staff.type==="hirasawa") && (data.jobs||[]).length > 0 && (() => {
          const tRows = data.jobs || [];
          const subOf = (r) => {
            if (r.note === "基本給に含む") return null;
            if (r.subtotal != null) return num(r.subtotal);
            if (num(r.roundtrip) > 0) return num(r.roundtrip);
            return num(r.oneway);
          };
          const totT = tRows.reduce((s,r)=>s+(subOf(r)||0),0);
          return (
            <div className="print-block" style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                ■ 交通費明細　{tRows.length}件　合計 {fmt(totT)}
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:11,width:"100%",tableLayout:"fixed"}}>
                <colgroup>
                  <col style={{width:isNarrow?"14%":"9%"}}/>
                  <col/>
                  {!isMobile && <col style={{width:"14%"}}/>}
                  {!isMobile && <col style={{width:"14%"}}/>}
                  <col style={{width:isNarrow?"20%":"14%"}}/>
                </colgroup>
                <thead>
                  <tr>
                    <th style={PTH}>日付</th>
                    <th style={PTH}>案件名／現場</th>
                    {!isMobile && <th style={PTH}>出発</th>}
                    {!isMobile && <th style={PTH}>到着</th>}
                    <th style={{...PTH,textAlign:"center"}}>小計</th>
                  </tr>
                </thead>
                <tbody>
                  {tRows.map((r,i)=>{
                    const sub = subOf(r);
                    const inBase = r.note === "基本給に含む";
                    return (
                      <tr key={r.id||i} style={{background:i%2===0?"#fff":"#f7f8fc"}}>
                        <td style={{...PTD,textAlign:"center",whiteSpace:"nowrap"}}>{r.date}</td>
                        <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:inBase?"#94a3b8":undefined}}>{r.content}{inBase?"（基本給に含む）":""}</td>
                        {!isMobile && <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.depart}</td>}
                        {!isMobile && <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.arrive}</td>}
                        <td style={{...PTDR,color:inBase?"#94a3b8":"#000"}}>{sub!=null?fmtNum(sub):"—"}</td>
                      </tr>
                    );
                  })}
                  <tr style={{background:"#dde3f0",fontWeight:"bold"}}>
                    <td colSpan={isMobile?2:4} style={{...PTSUM,textAlign:"right",paddingRight:8}}>交通費 合計</td>
                    <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totT)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* サロン売上 v79: 髙山・従業員モードON時はブロック全体を非表示 v86: 竹内・深澤も同様 */}
        {/* v139.1: 9月以降・率方式は100%を4項目に分けて出す */}
        {data.salon.length > 0 && !(hideRieki && hideSalonForEmployee) && (() => { const splitPrint = isSplitMonth(monthKey) && !isKasugai && !isTakayama; return (
          <div className="print-block" style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
              ■ サロン売上{!(hideRieki && isTakayama) && `　支払 ${fmt(totPayment)}　指名料計 ${fmt(totNom)}`}
            </div>
            <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
              <colgroup>
                {/* v52: 列幅のバランス改善 */}
                <col style={{width:isNarrow?"14%":isMobile?"13%":(splitPrint?"7%":"9%")}}/>
                <col/>
                <col style={{width:isNarrow?"20%":isMobile?"18%":(splitPrint?"9%":"13%")}}/>
                {!isMobile && !isKasugai && !isTakayama && <col style={{width:splitPrint?"6%":"10%"}}/>}
                {!isMobile && !isKasugai && !isTakayama && <col style={{width:splitPrint?"8%":"12%"}}/>}
                {!isMobile && !isKasugai && !isTakayama && splitPrint && <col style={{width:"7%"}}/>}
                {!isMobile && !isKasugai && !isTakayama && splitPrint && <col style={{width:"8%"}}/>}
                {!isMobile && !isKasugai && !isTakayama && splitPrint && <col style={{width:"8%"}}/>}
                {!isMobile && !isKasugai && !isTakayama && splitPrint && <col style={{width:"8%"}}/>}
                {!isMobile && !isKasugai && !isTakayama && <col style={{width:splitPrint?"8%":"10%"}}/>}
                {(isKasugai||isTakayama) && !(hideRieki && isTakayama) && <col style={{width:isNarrow?"22%":"18%"}}/>}
                {!isKasugai && !isTakayama && <col style={{width:isNarrow?"22%":isMobile?"20%":(splitPrint?"10%":"14%")}}/>}
                {!isMobile && (isKasugai||isTakayama) && <col style={{width:"22%"}}/>}
              </colgroup>
              <thead>
                <tr>
                  <th style={{...PTH,fontSize:isNarrow?8:10}}>日付</th>
                  <th style={{...PTH,fontSize:isNarrow?8:10}}>お名前</th>
                  <th style={{...PTH,textAlign:"right",fontSize:isNarrow?8:10}}>支払</th>
                  {!isMobile && !isKasugai && !isTakayama && <th style={{...PTH,textAlign:"right"}}>割引</th>}
                  {!isMobile && !isKasugai && !isTakayama && <th style={{...PTH,textAlign:"right",background:"#f0f0f0"}}>{(rate*100).toFixed(0)}%</th>}
                  {!isMobile && !isKasugai && !isTakayama && splitPrint && <>
                    <th style={{...PTH,textAlign:"right",fontSize:isNarrow?8:9}}>指名料</th>
                    <th style={{...PTH,textAlign:"right",fontSize:isNarrow?8:9}}>早朝深夜</th>
                    <th style={{...PTH,textAlign:"right",fontSize:isNarrow?8:9}}>販売手数料</th>
                    <th style={{...PTH,textAlign:"right",fontSize:isNarrow?8:9}}>キャンセル代</th>
                  </>}
                  {!isMobile && !isKasugai && !isTakayama && <th style={{...PTH,textAlign:"right"}}>100%計</th>}
                  {(isKasugai||isTakayama) && !(hideRieki && isTakayama) && <th style={{...PTH,textAlign:"right",fontSize:isNarrow?8:10}}>指名料</th>}
                  {!isKasugai && !isTakayama && <th style={{...PTH,textAlign:"right",background:"#f0f0f0",fontSize:isNarrow?8:10}}>指名料計</th>}
                  {!isMobile && (isKasugai||isTakayama) && <th style={PTH}>内容</th>}
                </tr>
              </thead>
              <tbody>
                {data.salon.map((r,i)=>{
                  const v65  = calcSalon65(r.payment,r.discount,rate);
                  const n100 = isKasugai ? 0 : isTakayama ? num(r.nom100) : get100(r, monthKey);
                  const vNom = isKasugai ? num(r.nomination) : isTakayama ? num(r.nom100) : calcNomination(r.payment,r.discount,rate,n100);
                  const fs = isNarrow ? 8 : 10;
                  return (
                    <tr key={i} style={{background:"#fff"}}>
                      <td style={{...PTD,fontSize:fs,textAlign:"center",verticalAlign:"middle"}}>{r.date}</td>
                      <td style={{...PTD,fontSize:fs,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</td>
                      <td style={{...PTDR,fontSize:fs}}>{fmtNum(r.payment)}</td>
                      {!isMobile && !isKasugai && !isTakayama && <td style={PTDR}>{r.discount?fmtNum(r.discount):"—"}</td>}
                      {!isMobile && !isKasugai && !isTakayama && <td style={{...PTDR,color:"#000",background:"#fff"}}>{(r.payment!=null||n100>0)?fmtNum(v65):"—"}</td>}
                      {!isMobile && !isKasugai && !isTakayama && splitPrint && <>
                        <td style={PTDR}>{num(r.nomShimei)?fmtNum(r.nomShimei):"—"}</td>
                        <td style={PTDR}>{num(r.nomSoucho)?fmtNum(r.nomSoucho):"—"}</td>
                        <td style={PTDR}>{num(r.nomHanbai)?fmtNum(r.nomHanbai):"—"}</td>
                        <td style={PTDR}>{num(r.nomCancel)?fmtNum(r.nomCancel):"—"}</td>
                      </>}
                      {!isMobile && !isKasugai && !isTakayama && <td style={PTDR}>{n100?fmtNum(n100):"—"}</td>}
                      {(isKasugai||isTakayama) && !(hideRieki && isTakayama) && <td style={{...PTDR,fontSize:fs,color:"#000"}}>{fmtNum(isKasugai?r.nomination:r.nom100)}</td>}
                      {!isKasugai && !isTakayama && <td style={{...PTDR,fontSize:fs,color:"#000",background:"#fff"}}>{vNom>0?fmtNum(vNom):"—"}</td>}
                      {!isMobile && (isKasugai||isTakayama) && <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.memo}</td>}
                    </tr>
                  );
                })}
                {!(hideRieki && isTakayama) && (
                <tr style={{background:"#dde3f0",fontWeight:"bold"}}>
                  <td colSpan={2} style={{...PTSUM,textAlign:"right",paddingRight:8}}>合計</td>
                  <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totPayment)}</td>
                  {!isMobile && !isKasugai && !isTakayama && <>
                    <td style={PTSUM}>{fmt(totDiscall)}</td>
                    <td style={PTSUM}>{fmt(tot65all)}</td>
                    {splitPrint && <>
                      <td style={PTSUM}>{fmt(data.salon.reduce((s,r)=>s+num(r.nomShimei),0))}</td>
                      <td style={PTSUM}>{fmt(data.salon.reduce((s,r)=>s+num(r.nomSoucho),0))}</td>
                      <td style={PTSUM}>{fmt(data.salon.reduce((s,r)=>s+num(r.nomHanbai),0))}</td>
                      <td style={PTSUM}>{fmt(data.salon.reduce((s,r)=>s+num(r.nomCancel),0))}</td>
                    </>}
                    <td style={PTSUM}>{fmt(tot100all)}</td>
                  </>}
                  {(isKasugai||isTakayama) && <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totNom)}</td>}
                  {!isKasugai && !isTakayama && <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totNom)}</td>}
                  {!isMobile && (isKasugai||isTakayama) && <td style={PTSUM}></td>}
                </tr>
                )}
              </tbody>
            </table>
          </div>
        ); })()}

        {/* v132: 100%内訳（9月以降・率方式スタッフのみ。従業員向けでも表示） */}
        {isSplitMonth(monthKey) && !isKasugai && !isTakayama && data.salon.length > 0 && (() => {
          const sShimei = data.salon.reduce((s,r)=>s+num(r.nomShimei),0);
          const sSoucho = data.salon.reduce((s,r)=>s+num(r.nomSoucho),0);
          const sHanbai = data.salon.reduce((s,r)=>s+num(r.nomHanbai),0);
          const sCancel = data.salon.reduce((s,r)=>s+num(r.nomCancel),0);
          const sTotal = sShimei + sSoucho + sHanbai + sCancel;
          return (
            <div className="print-block" style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                ■ 100%内訳
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:11,width:isNarrow?"100%":360}}>
                <tbody>
                  <tr style={{background:"#fff"}}>
                    <td style={PTD}>スタッフ指名料</td>
                    <td style={PTDR}>{fmt(sShimei)}</td>
                  </tr>
                  <tr style={{background:"#f7f8fc"}}>
                    <td style={PTD}>早朝深夜料金</td>
                    <td style={PTDR}>{fmt(sSoucho)}</td>
                  </tr>
                  <tr style={{background:"#fff"}}>
                    <td style={PTD}>販売手数料</td>
                    <td style={PTDR}>{fmt(sHanbai)}</td>
                  </tr>
                  <tr style={{background:"#f7f8fc"}}>
                    <td style={PTD}>キャンセル代</td>
                    <td style={PTDR}>{fmt(sCancel)}</td>
                  </tr>
                  <tr style={{background:"#dde3f0",fontWeight:"bold"}}>
                    <td style={PTSUM}>合計</td>
                    <td style={PTSUM}>{fmt(sTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* 給与明細サマリー */}
        {kyuItems.length > 0 && (
          <div className="print-block print-meisai" style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
              ■ 給与明細　{staff.type==="kasugai" ? "支払合計" : (staff.type==="akino"||staff.type==="hirasawa") ? "支払額" : "振込支給額"} <span style={{color:"#000",fontSize:13}}>{fmt(furikomi)}</span>
            </div>
            <table style={{borderCollapse:"collapse",fontSize:isNarrow?8:10,width:"100%",tableLayout:"fixed"}}>
              <colgroup>
                <col style={{width:"26%"}}/>
                <col style={{width:"24%"}}/>
                <col style={{width:"26%"}}/>
                <col style={{width:"24%"}}/>
              </colgroup>
              <thead>
                <tr>
                  <th style={{...PTH,background:"#e5eaf2",textAlign:"center"}}>支給項目</th>
                  <th style={{...PTH,background:"#e5eaf2",textAlign:"center"}}>金額</th>
                  <th style={{...PTH,background:"#f2e5e5",textAlign:"center"}}>控除項目</th>
                  <th style={{...PTH,background:"#f2e5e5",textAlign:"center"}}>金額</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({length:Math.max(kyuItems.length,kojItems.length)}).map((_,i)=>{
                  const k=kyuItems[i]; const ko=kojItems[i];
                  // 春日井の特殊行スタイル
                  const isSpecialKasugai = k && (k.isInternal || k.isSubTotal || k.isTotal);
                  const rowBg = isSpecialKasugai
                    ? (k.isTotal ? "#dde8ff" : k.isSubTotal ? "#e8f0ff" : "#f0f5fb")
                    : (i%2===0?"#fff":"#f8f9fc");
                  return (
                    <tr key={i} style={{background:rowBg}}>
                      <td style={{...PTD,background:isSpecialKasugai?rowBg:"#fff",fontWeight:isSpecialKasugai?"bold":"normal",color:"#000"}}>{k?.l||""}</td>
                      <td
                        style={{...PTDR,background:isSpecialKasugai?rowBg:(k?.manual?"#fff8e1":"#fff"), color: k?.manual?"#d97706":"#000", fontWeight: isSpecialKasugai && k?.isTotal?"bold":"normal", fontSize:k?.isTotal?12:undefined, ...(k ? cellClickable : {})}}
                        className={k ? "no-print copy-target" : ""}
                        onClick={k ? () => copyCell(k.l, fmtNum(k.v)) : undefined}
                        title={k ? "クリックでコピー" : ""}
                      >
                        {k ? <>{fmtNum(k.v)}{k.manual && <span style={{fontSize:9,marginLeft:2}}>✎</span>}</> : ""}
                      </td>
                      <td style={{...PTD,background:"#fff",color:"#000"}}>{ko?.l||""}</td>
                      <td
                        style={{...PTDR,background:"#fff",color:"#000",fontWeight:"normal", ...(ko ? cellClickable : {})}}
                        className={ko ? "no-print copy-target" : ""}
                        onClick={ko ? () => copyCell(ko.l, fmtNum(ko.v)) : undefined}
                        title={ko ? "クリックでコピー" : ""}
                      >
                        {ko ? fmtNum(ko.v) : ""}
                      </td>
                    </tr>
                  );
                })}
                {staff.type!=="kasugai" && (
                  <tr style={{background:"#e8ecf4",fontWeight:"bold"}}>
                    <td style={{...PTSUM,background:"#dde3f0"}}>支給合計</td>
                    <td
                      style={{...PTSUM,background:"#dde3f0",color:"#000",...cellClickable}}
                      onClick={() => copyCell("支給合計", fmt(totS))}
                      title="クリックでコピー"
                    >{fmt(totS)}</td>
                    <td style={{...PTSUM,background:"#f2e5e5"}}>控除合計</td>
                    <td
                      style={{...PTSUM,background:"#f2e5e5",color:"#000",...cellClickable}}
                      onClick={() => copyCell("控除合計", fmt(totK))}
                      title="クリックでコピー"
                    >{fmt(totK)}</td>
                  </tr>
                )}
                <tr>
                  <td
                    colSpan={4}
                    style={{...PTSUM,background:"#1e3a8a",color:"#fff",fontSize:13,fontWeight:"bold",padding:"7px 13px",textAlign:"right",...cellClickable}}
                    onClick={() => copyCell(staff.type==="kasugai" ? "支払合計" : (staff.type==="akino"||staff.type==="hirasawa") ? "支払額" : "振込支給額", fmt(furikomi))}
                    title="クリックでコピー"
                  >
                    {staff.type==="kasugai" ? "支払合計" : (staff.type==="akino"||staff.type==="hirasawa") ? "支払額" : "振込支給額"}　{fmt(furikomi)}
                  </td>
                </tr>
              </tbody>
            </table>
            {/* v110: 給与備考（松本・竹内・深澤・青海・髙山のみ、入力済みの場合に表示） */}
            {["matsumoto","takeuchi","fukazawa","aomi","takayama"].includes(staff.type) && sal.給与備考 && (
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:11,width:"100%",tableLayout:"fixed",marginTop:4}}>
                <tbody>
                  <tr style={{background:"#fff",borderTop:"1px solid #d4deea"}}>
                    <td style={{...PTD,fontWeight:"bold",width:"18%",background:"#f0f4f9"}}>備考</td>
                    <td style={{...PTD,whiteSpace:"pre-wrap",lineHeight:1.6}}>{sal.給与備考}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* v87: 歩合給内訳（竹内・深澤のみ） v118: 保険料調整額を反映した構造に更新 */}
        {["takeuchi","fukazawa"].includes(staff.type) && (() => {
          const shucchou_auto_ = extractDays(sal.出張日数) * (staff?.出張手当単価 ?? 0);
          const shucchou_ = sal.出張手当手動 != null ? sal.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto_ : num(sal.出張手当));
          const half_ = totalH;
          const kihon_ = num(sal.基本給);
          // v118: 歩合給ベース = 50% − 基本給 − 出張手当（保険料調整は含めない）
          const hobu_base_auto_ = half_ - kihon_ - shucchou_;
          const hobu_base_ = sal.歩合給手動 != null ? num(sal.歩合給手動) : hobu_base_auto_;
          const isOverridden_ = sal.歩合給手動 != null && num(sal.歩合給手動) !== hobu_base_auto_;
          // v118: 保険料調整額（調整月の差額分）
          const year_ins_ = (monthKey || "").split("-")[0] || "2026";
          const insDiff_ = calcInsScheduleDiff(staff, year_ins_);
          const insAdjAuto_ = (isInsAdjMonth(monthKey) && insDiff_ > 0) ? insDiff_ : 0;  // v127: 調整月のみ
          const hokenChoseiGaku_ = sal.保険料調整額手動 != null ? num(sal.保険料調整額手動) : insAdjAuto_;
          // v118: 最終的な歩合給 = 歩合給ベース + 保険料調整額
          const hobu_final_ = hobu_base_ + hokenChoseiGaku_;

          return (
            <div className="print-block" style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                ■ 歩合給内訳{isOverridden_ ? "（歩合給ベースは手動上書き）" : ""}
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
                <colgroup>
                  <col/>
                  <col style={{width:isMobile?"30%":"28%"}}/>
                </colgroup>
                <tbody>
                  {!isOverridden_ ? (
                    <>
                      <tr style={{background:"#fff"}}>
                        <td style={PTD}>50%（ギャラの50%合計）</td>
                        <td style={{...PTDR,...cellClickable}} onClick={() => copyCell("50%", fmt(half_))} title="クリックでコピー">{fmt(half_)}</td>
                      </tr>
                      <tr style={{background:"#f7f8fc"}}>
                        <td style={PTD}>− 基本給</td>
                        <td style={{...PTDR,...cellClickable}} onClick={() => copyCell("基本給", fmt(kihon_))} title="クリックでコピー">{fmt(kihon_)}</td>
                      </tr>
                      <tr style={{background:"#fff"}}>
                        <td style={PTD}>− 出張手当</td>
                        <td style={{...PTDR,...cellClickable}} onClick={() => copyCell("出張手当", fmt(shucchou_))} title="クリックでコピー">{fmt(shucchou_)}</td>
                      </tr>
                    </>
                  ) : (
                    <tr style={{background:"#fff"}}>
                      <td style={PTD}>歩合給ベース（手動入力）</td>
                      <td style={{...PTDR,...cellClickable}} onClick={() => copyCell("歩合給ベース", fmt(hobu_base_))} title="クリックでコピー">{fmt(hobu_base_)}</td>
                    </tr>
                  )}
                  <tr style={{background:"#f7f8fc"}}>
                    <td style={PTD}>+ 保険料調整額</td>
                    <td style={{...PTDR,...cellClickable}} onClick={() => copyCell("保険料調整額", fmt(hokenChoseiGaku_))} title="クリックでコピー">{fmt(hokenChoseiGaku_)}</td>
                  </tr>
                  <tr style={{background:"#eef2ff"}}>
                    <td style={{...PTSUM,fontWeight:"bold",color:"#1e3a8a"}}>歩合給</td>
                    <td
                      style={{...PTSUM,textAlign:"right",fontWeight:"bold",color:"#1e3a8a",fontSize:13,...cellClickable}}
                      onClick={() => copyCell("歩合給", fmt(hobu_final_))}
                      title="クリックでコピー"
                    >
                      {fmt(hobu_final_)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* v122: 歩合給内訳（青海のみ） 共有用50% − 基本給 − 出張手当 */}
        {isAomi && (() => {
          const calcSharedAomi_ = (j) => Math.min(num(j.guarantee), 30000);
          const _jobsClean_ = data.jobs.filter(j => !j.isAdjust);
          const _adj_ = data.adjustJobs || [];
          // 共有用50%合計（通常分＋調整分）
          const sharedH_ = _jobsClean_.reduce((s,j)=>s+INT(calcSharedAomi_(j)/2),0)
                         + _adj_.reduce((s,j)=>s+INT(calcSharedAomi_(j)/2),0);
          const kihon_ = num(sal.基本給);
          const shucchou_auto_ = extractDays(sal.出張日数) * (staff?.出張手当単価 ?? 0);
          const shucchou_ = sal.出張手当手動 != null ? sal.出張手当手動 : (staff?.出張手当単価 != null ? shucchou_auto_ : num(sal.出張手当));
          const hobu_auto_ = sharedH_ - kihon_ - shucchou_;
          const isOverridden_ = sal.歩合給手動 != null;
          const hobu_ = isOverridden_ ? num(sal.歩合給手動) : hobu_auto_;

          return (
            <div className="print-block" style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                ■ 歩合給内訳{isOverridden_ ? "（歩合給は手動入力）" : ""}
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
                <colgroup>
                  <col/>
                  <col style={{width:isMobile?"30%":"28%"}}/>
                </colgroup>
                <tbody>
                  {!isOverridden_ && (
                    <>
                      <tr style={{background:"#fff"}}>
                        <td style={PTD}>共有用50%（ギャラの50%合計）</td>
                        <td style={{...PTDR,...cellClickable}} onClick={() => copyCell("共有用50%", fmt(sharedH_))} title="クリックでコピー">{fmt(sharedH_)}</td>
                      </tr>
                      <tr style={{background:"#f7f8fc"}}>
                        <td style={PTD}>− 基本給</td>
                        <td style={{...PTDR,...cellClickable}} onClick={() => copyCell("基本給", fmt(kihon_))} title="クリックでコピー">{fmt(kihon_)}</td>
                      </tr>
                      <tr style={{background:"#fff"}}>
                        <td style={PTD}>− 出張手当</td>
                        <td style={{...PTDR,...cellClickable}} onClick={() => copyCell("出張手当", fmt(shucchou_))} title="クリックでコピー">{fmt(shucchou_)}</td>
                      </tr>
                    </>
                  )}
                  <tr style={{background:"#eef2ff"}}>
                    <td style={{...PTSUM,fontWeight:"bold",color:"#1e3a8a"}}>歩合給</td>
                    <td
                      style={{...PTSUM,textAlign:"right",fontWeight:"bold",color:"#1e3a8a",fontSize:13,...cellClickable}}
                      onClick={() => copyCell("歩合給", fmt(hobu_))}
                      title="クリックでコピー"
                    >
                      {fmt(hobu_)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* v81: 保険料調整スケジュール（竹内・深澤）従業員モードでも表示 v91: 計算式と色を直感的解釈に変更 */}
        {/* v107: sub が null なら自動値（その月の本来の歩合給）を使う */}
        {["takeuchi","fukazawa"].includes(staff.type) && isInsCycleMonth(monthKey) && staff.insSchedule && staff.insSchedule.length >= 7 && (() => {
          const rs = staff.insSchedule;
          const total_ = num(rs[0]?.amount);
          // v107: 各月の本来の歩合給を取得
          const year_ = (monthKey || "").split("-")[0] || "2026";
          const monthlyHobu_ = [
            calcOriginalHobu(staff, `${year_}-03`),
            calcOriginalHobu(staff, `${year_}-04`),
            calcOriginalHobu(staff, `${year_}-05`),
          ];
          // sub が null なら自動値を使う
          const getSubOrAuto_ = (i) => {
            if (rs[i]?.sub != null) return num(rs[i].sub);
            const idx = i - 3;
            if (idx >= 0 && idx < 3 && monthlyHobu_[idx] != null) return num(monthlyHobu_[idx]);
            return 0;
          };
          const marSub_ = getSubOrAuto_(3);
          const aprSub_ = getSubOrAuto_(4);
          const maySub_ = getSubOrAuto_(5);
          // 表示用の sub（手動or自動を判別）
          const getSubDisp_ = (i) => {
            if (rs[i]?.sub != null) return { value: num(rs[i].sub), isAuto: false };
            const idx = i - 3;
            if (idx >= 0 && idx < 3 && monthlyHobu_[idx] != null) return { value: num(monthlyHobu_[idx]), isAuto: true };
            return { value: null, isAuto: false };
          };
          // v91: 差額分 = sub合計 − 保険料調整合計
          // マイナス＝未消化（赤）／プラス＝超過（緑）
          const diff_ = (marSub_ + aprSub_ + maySub_) - total_;
          const diffSign_ = diff_ > 0 ? "プラス" : diff_ < 0 ? "マイナス" : "ゼロ";
          const diffColor_ = diff_ > 0 ? "#15803d" : diff_ < 0 ? "#dc2626" : "#1e3a8a";
          const sub3_ = getSubDisp_(3);
          const sub4_ = getSubDisp_(4);
          const sub5_ = getSubDisp_(5);
          // 行表示用（amount / sub の表示制御）
          const dispRows = [
            { label:"保険料調整合計",                          amount: rs[0]?.amount, sub: null,        amountIs0:false, subAuto:false },
            { label:"2026年1月分/支払日：2/25",                amount: rs[1]?.amount, sub: null,        amountIs0:false, subAuto:false },
            { label:"2026年2月分/支払日：3/25",                amount: rs[2]?.amount, sub: null,        amountIs0:false, subAuto:false },
            { label:"2026年3月分/支払日：4/24",                amount: 0,             sub: sub3_.value, amountIs0:true,  subAuto: sub3_.isAuto },
            { label:"2026年4月分/支払日：5/24",                amount: 0,             sub: sub4_.value, amountIs0:true,  subAuto: sub4_.isAuto },
            { label:"2026年5月分/支払日：6/24",                amount: 0,             sub: sub5_.value, amountIs0:true,  subAuto: sub5_.isAuto },
            { label:"調整月：2026年6月分/支払日：7/24",        amount: null,          sub: null,        amountIs0:false, subAuto:false },
          ];
          return (
            <div className="print-block" style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                ■ 保険料調整スケジュール
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
                <colgroup>
                  <col/>
                  <col style={{width:isMobile?"24%":"22%"}}/>
                  <col style={{width:isMobile?"24%":"22%"}}/>
                </colgroup>
                <tbody>
                  {dispRows.map((r,i) => {
                    const amountVal = r.amountIs0 ? "0" : r.amount == null ? null : fmtNum(r.amount);
                    const subVal = r.sub == null ? null : fmtNum(r.sub);
                    return (
                      <tr key={i} style={{background:i%2===0?"#fff":"#f7f8fc"}}>
                        <td style={{...PTD,fontWeight:i===0?"bold":"normal"}}>{r.label}</td>
                        <td
                          style={{...PTDR, ...(amountVal != null ? cellClickable : {})}}
                          onClick={amountVal != null ? () => copyCell(r.label + " 金額", amountVal) : undefined}
                          title={amountVal != null ? "クリックでコピー" : ""}
                        >
                          {r.amountIs0 ? <span style={{color:"#6b7280"}}>0</span>
                           : r.amount == null ? <span style={{color:"#9ca3af"}}>—</span>
                           : fmtNum(r.amount)}
                        </td>
                        <td
                          style={{...PTDR, ...(subVal != null && !r.subAuto ? cellClickable : {})}}
                          onClick={subVal != null && !r.subAuto ? () => copyCell(r.label + " sub", subVal) : undefined}
                          title={subVal != null && !r.subAuto ? "クリックでコピー" : (r.subAuto ? "自動値（保険料調整タブで手動上書き可）" : "")}
                        >
                          {r.sub == null
                            ? <span style={{color:"#9ca3af"}}>—</span>
                            : r.subAuto
                              ? fmtNum(r.sub)
                              : fmtNum(r.sub)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{background:"#eef2ff"}}>
                    <td style={{...PTSUM,fontWeight:"bold",color:"#1e3a8a"}}>差額分（自動）</td>
                    <td
                      colSpan={2}
                      style={{...PTSUM,textAlign:"right",fontWeight:"bold",color:diffColor_,fontSize:13,...cellClickable}}
                      onClick={() => copyCell("差額分", fmt(diff_))}
                      title="クリックでコピー"
                    >
                      {fmt(diff_)}
                      <span style={{fontSize:9,marginLeft:6,padding:"1px 5px",background:diffColor_,color:"#fff",borderRadius:3}}>{diffSign_}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* v62: 利益セクション（松本以外、null でなければ表示） v74: 従業員向けモードでは非表示 */}
        {!hideRieki && staff.type!=="matsumoto" && (hoburieki != null || salonrieki != null) && (
          <div className="print-block" style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
              ■ 利益
            </div>
            <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
              <colgroup>
                <col style={{width:"50%"}}/>
                <col style={{width:"50%"}}/>
              </colgroup>
              <tbody>
                {/* v131: 春日井は 売上 → 外注費 → 粗利 → 交通費・手数料 → 最終利益 の流れで表示 */}
                {staff.type==="kasugai" && hoburieki != null && salonrieki != null && kasugaiKessai != null ? (
                  <>
                    <tr style={{background:"#eef2ff"}}>
                      <td style={{...PTD,fontWeight:"bold"}}>売上合計</td>
                      <td style={{...PTDR,fontWeight:"bold",fontSize:12}}>{fmt(kasugaiUriage)}</td>
                    </tr>
                    <tr style={{background:"#fff"}}>
                      <td style={{...PTD,paddingLeft:20,color:"#555"}}>現場ギャランティ</td>
                      <td style={{...PTDR,color:"#555"}}>{fmt(kasugaiGenbaG)}</td>
                    </tr>
                    <tr style={{background:"#fff"}}>
                      <td style={{...PTD,paddingLeft:20,color:"#555"}}>サロン支払</td>
                      <td style={{...PTDR,color:"#555"}}>{fmt(kasugaiSalonPay)}</td>
                    </tr>
                    <tr style={{background:"#f7f8fc"}}>
                      <td style={{...PTD,fontWeight:"bold"}}>− 外注費</td>
                      <td style={{...PTDR,fontWeight:"bold",fontSize:12}}>{fmt(kasugaiGaichu)}</td>
                    </tr>
                    <tr style={{background:"#fff"}}>
                      <td style={{...PTD,paddingLeft:20,color:"#555"}}>現場支払</td>
                      <td style={{...PTDR,color:"#555"}}>{fmt(kasugaiGenbaPay)}</td>
                    </tr>
                    <tr style={{background:"#fff"}}>
                      <td style={{...PTD,paddingLeft:20,color:"#555"}}>指名料</td>
                      <td style={{...PTDR,color:"#555"}}>{fmt(kasugaiNom)}</td>
                    </tr>
                    <tr style={{background:"#eef2ff"}}>
                      <td style={{...PTD,fontWeight:"bold"}}>粗利</td>
                      <td style={{...PTDR,fontWeight:"bold",fontSize:12}}>{fmt(hoburieki + salonrieki)}</td>
                    </tr>
                    <tr style={{background:"#fff"}}>
                      <td style={PTD}>− 交通費</td>
                      <td style={PTDR}>{fmt(kasugaiTraffic)}</td>
                    </tr>
                    <tr style={{background:"#fff"}}>
                      <td style={PTD}>− 決済手数料（支払合計 {fmtNum(kasugaiShiharai)} 円 × 3.6%）</td>
                      <td style={PTDR}>{fmt(kasugaiKessai)}</td>
                    </tr>
                    <tr style={{background:"#dde3f0"}}>
                      <td style={{...PTSUM,fontWeight:"bold",color:"#1e3a8a"}}>最終利益</td>
                      <td style={{...PTSUM,textAlign:"right",fontWeight:"bold",fontSize:13,color:"#1e3a8a"}}>
                        {fmt(hoburieki + salonrieki - kasugaiTraffic - kasugaiKessai)}
                      </td>
                    </tr>
                  </>
                ) : (
                  <>
                    {hoburieki != null && (
                      <tr style={{background:"#fff"}}>
                        <td style={{...PTD,fontWeight:"bold"}}>歩合利益</td>
                        <td style={{...PTDR,fontWeight:"bold",fontSize:12}}>{fmt(hoburieki)}</td>
                      </tr>
                    )}
                    {salonrieki != null && (
                      <tr style={{background:"#fff"}}>
                        <td style={{...PTD,fontWeight:"bold"}}>サロン利益</td>
                        <td style={{...PTDR,fontWeight:"bold",fontSize:12}}>{fmt(salonrieki)}</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* v123: 業務委託（秋野・平澤）の売上明細＋利益（会社モードのみ・従業員向けでは非表示） */}
        {!hideRieki && (staff.type==="akino"||staff.type==="hirasawa") && (data.uriage||[]).length > 0 && (() => {
          const uRows = data.uriage || [];
          const kubunGroups = {};
          uRows.forEach(r => {
            const k = (r.kubun || "その他").trim() || "その他";
            if (!kubunGroups[k]) kubunGroups[k] = { count: 0, sum: 0 };
            kubunGroups[k].count++;
            kubunGroups[k].sum += num(r.amount);
          });
          const totalUriage = uRows.reduce((s, r) => s + num(r.amount), 0);
          const kotei = num(sal.基本給);
          const arari = totalUriage - kotei;
          const arariRate = totalUriage > 0 ? (arari / totalUriage * 100) : null;
          const trafficTotal = (data.jobs || []).reduce((s, r) => {
            if (r.note === "基本給に含む") return s;
            if (r.subtotal != null) return s + num(r.subtotal);
            if (num(r.roundtrip) > 0) return s + num(r.roundtrip);
            return s + num(r.oneway);
          }, 0);
          const kubunLabel = Object.entries(kubunGroups).map(([k,g]) => `${k}${g.count}件`).join("・");
          return (
            <>
              <div className="print-block" style={{marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                  ■ 売上明細　{uRows.length}件　売上合計 {fmt(totalUriage)}
                </div>
                <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:11,width:"100%",tableLayout:"fixed"}}>
                  <colgroup>
                    <col style={{width:isNarrow?"14%":"9%"}}/>
                    <col style={{width:"18%"}}/>
                    <col/>
                    <col style={{width:"16%"}}/>
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={PTH}>日付</th>
                      <th style={PTH}>区分</th>
                      <th style={PTH}>案件名／現場</th>
                      <th style={{...PTH,textAlign:"center"}}>売上(税込)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uRows.map((r,i)=>(
                      <tr key={r.id||i} style={{background:i%2===0?"#fff":"#f7f8fc"}}>
                        <td style={PTD}>{r.date}</td>
                        <td style={PTD}>{r.kubun}</td>
                        <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.client}</td>
                        <td style={PTDR}>{r.amount!=null?fmtNum(r.amount):"—"}</td>
                      </tr>
                    ))}
                    <tr style={{background:"#dde3f0",fontWeight:"bold"}}>
                      <td colSpan={3} style={{...PTSUM,textAlign:"right",paddingRight:8}}>合計（{kubunLabel}）</td>
                      <td style={{...PTSUM,whiteSpace:"nowrap"}}>{fmt(totalUriage)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="print-block" style={{marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:"bold",background:"#dde3f0",padding:"3px 8px",marginBottom:5}}>
                  ■ 利益
                </div>
                <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
                  <colgroup>
                    <col style={{width:"50%"}}/>
                    <col style={{width:"50%"}}/>
                  </colgroup>
                  <tbody>
                    <tr style={{background:"#fff"}}>
                      <td style={{...PTD,fontWeight:"bold"}}>売上合計</td>
                      <td style={{...PTDR,fontWeight:"bold",fontSize:12}}>{fmt(totalUriage)}</td>
                    </tr>
                    <tr style={{background:"#f7f8fc"}}>
                      <td style={PTD}>固定給（原価）</td>
                      <td style={PTDR}>{fmt(kotei)}</td>
                    </tr>
                    <tr style={{background:"#fff"}}>
                      <td style={{...PTD,fontWeight:"bold"}}>粗利</td>
                      <td style={{...PTDR,fontWeight:"bold",fontSize:12,color:arari<0?"#dc2626":"#15803d"}}>{fmt(arari)}</td>
                    </tr>
                    <tr style={{background:"#f7f8fc"}}>
                      <td style={{...PTD,fontWeight:"bold"}}>粗利率</td>
                      <td style={{...PTDR,fontWeight:"bold",fontSize:12,color:arari<0?"#dc2626":"#15803d"}}>{arariRate!=null?`${arariRate.toFixed(1)}\u00a0%`:"—"}</td>
                    </tr>
                    <tr style={{background:"#fff"}}>
                      <td style={{...PTD,color:"#666",fontSize:9}}>交通費（参考・原価外）</td>
                      <td style={{...PTDR,color:"#666",fontSize:9}}>{fmt(trafficTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          );
        })()}

        {/* 修正メモ一覧（コメントがある行をまとめて表示） v36 */}
        {(() => {
          const comments = [];
          (data.jobs||[]).forEach(j => {
            if (j.comment && j.comment.trim()) {
              comments.push({ section:"現場", date:j.date, name:j.client, text:j.comment });
            }
          });
          (data.salon||[]).forEach(r => {
            if (r.comment && r.comment.trim()) {
              comments.push({ section:"サロン", date:r.date, name:r.name, text:r.comment });
            }
          });
          (data.traffic||[]).forEach(r => {
            if (r.comment && r.comment.trim()) {
              comments.push({ section:"交通費", date:r.date, name:r.memo, text:r.comment });
            }
          });
          if (comments.length === 0) return null;
          return (
            <div className="print-block" style={{marginBottom:16,marginTop:16}}>
              <div style={{fontSize:12,fontWeight:"bold",background:"#fff0f0",color:"#dc2626",padding:"3px 8px",marginBottom:5,border:"1px solid #fecaca"}}>
                💬 修正メモ一覧　{comments.length}件
              </div>
              <table style={{borderCollapse:"collapse",fontSize:isNarrow?9:10,width:"100%",tableLayout:"fixed"}}>
                <colgroup>
                  <col style={{width:isNarrow?"14%":"10%"}}/>
                  <col style={{width:isNarrow?"14%":"10%"}}/>
                  <col style={{width:isNarrow?"25%":"22%"}}/>
                  <col/>
                </colgroup>
                <thead>
                  <tr>
                    <th style={PTH}>区分</th>
                    <th style={PTH}>日付</th>
                    <th style={PTH}>対象</th>
                    <th style={PTH}>修正メモ</th>
                  </tr>
                </thead>
                <tbody>
                  {comments.map((c,i) => (
                    <tr key={i} style={{background:i%2===0?"#fff":"#fff5f5"}}>
                      <td style={{...PTD,textAlign:"center",fontWeight:"bold",color:"#000"}}>{c.section}</td>
                      <td style={{...PTD,textAlign:"center",fontSize:isNarrow?8:10}}>{c.date||"—"}</td>
                      <td style={{...PTD,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name||"—"}</td>
                      <td style={{...PTD,color:"#dc2626",fontWeight:"bold"}}>{c.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ===================== スタッフビュー =====================
// ===================== v63: ms-salon 取り込みモーダル =====================
function MsSalonImportModal({ staff, monthKey, currentSalon, onClose, onImport }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState({});

  const [yearStr, monthStr] = (monthKey || "").split("-");
  const yearNum = parseInt(yearStr, 10);
  const monthNum = parseInt(monthStr, 10);

  // 既存の取り込み済み squarePaymentId 一覧（重複検知用）
  const existingPaymentIds = new Set((currentSalon || []).map(r => r.squarePaymentId).filter(Boolean));

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchMsSalonRecords(yearNum, monthNum, staff.type)
      .then(recs => {
        if (!active) return;
        // 日付昇順でソート
        recs.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
        setRecords(recs);
        // v63.1: デフォルト「全て未選択」（ユーザーが意識的に選ぶ）
        setSelected({});
        setLoading(false);
      })
      .catch(e => {
        if (!active) return;
        setError(e.message || String(e));
        setLoading(false);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearNum, monthNum, staff.type]);

  const toggleSelect = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selectAll = (val) => {
    const next = {};
    records.forEach(r => {
      const isExisting = r.squarePaymentId && existingPaymentIds.has(r.squarePaymentId);
      next[r.id] = val && !isExisting;
    });
    setSelected(next);
  };

  const selectedCount = records.filter(r => selected[r.id]).length;
  const skipCount = records.filter(r => r.squarePaymentId && existingPaymentIds.has(r.squarePaymentId)).length;

  const handleImport = () => {
    const today = new Date().toISOString().slice(0, 10);
    // v63.2: 春日井・髙山はメモ列がプルダウン式なので、paymentLabel等は comment(メモ💬) に入れる
    const isMemoDropdown = staff.type === "kasugai" || staff.type === "takayama";
    const newRows = records
      .filter(r => selected[r.id])
      .map(r => {
        // 現金時のみ amount あり、カード/QR/請求書時は null
        const paymentVal = (r.payment === "cash" && r.amount != null) ? Number(r.amount) : null;
        // メモ：paymentLabel と既存メモを併記
        const memoParts = [];
        if (r.paymentLabel) memoParts.push(`[${r.paymentLabel}]`);
        if (r.memo) memoParts.push(r.memo);
        const memoText = memoParts.join(" ");
        return {
          id: nid(),
          date: isoDateToMD(r.date),
          name: r.name || "",
          payment: paymentVal,
          discount: null,
          nom100: null,
          nomination: null,
          // 髙山・春日井：内容列はプルダウンなので memo は空、paymentLabel等は comment に
          memo: isMemoDropdown ? "" : memoText,
          comment: isMemoDropdown ? memoText : "",
          squarePaymentId: r.squarePaymentId || null,
          importedFrom: "ms-salon",
          importedAt: today,
        };
      });
    onImport(newRows);
    onClose();
  };

  return (
    <div style={{position:"fixed",inset:0,background:"#00000066",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:10}}>
      <div style={{background:"#fff",border:"1px solid #888",padding:16,minWidth:340,maxWidth:680,maxHeight:"90vh",display:"flex",flexDirection:"column",borderRadius:6}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <b style={{fontSize:14}}>📥 ms-salon から取り込み</b>
          <button onClick={onClose} style={{background:"transparent",border:"none",fontSize:18,cursor:"pointer",color:"#666"}}>×</button>
        </div>
        <div style={{fontSize:12,color:"#444",marginBottom:8}}>
          対象: <b>{staff.name || MS_SALON_STAFF_NAME[staff.type]}</b> / <b>{yearNum}年{monthNum}月分</b>
        </div>

        {loading && <div style={{padding:20,textAlign:"center",color:"#666",fontSize:13}}>📡 ms-salon から読み込み中...</div>}

        {error && (
          <div style={{padding:12,background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:4,color:"#991b1b",fontSize:12}}>
            ⚠️ エラー: {error}
            <div style={{marginTop:6,fontSize:11,color:"#666"}}>
              ms-salon の Firestore ルールでこの取得が許可されているか確認してください。
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* v63.1: ステータスバー（選択件数を強調表示） */}
            <div style={{padding:"8px 10px",background:selectedCount>0?"#dbeafe":"#f3f4f6",border:`1px solid ${selectedCount>0?"#60a5fa":"#d1d5db"}`,borderRadius:4,marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
              <div style={{fontSize:12,color:"#1e3a8a"}}>
                <b style={{fontSize:14}}>{selectedCount}</b> 件選択中
                <span style={{color:"#666",marginLeft:8,fontSize:11}}>（全{records.length}件 / 取込済{skipCount}件）</span>
              </div>
              <div style={{display:"flex",gap:6}}>
                <button style={{...BTN("#374151"),fontSize:11,padding:"4px 10px"}} onClick={()=>selectAll(true)}>☑ 全選択</button>
                <button style={{...BTN("#374151"),fontSize:11,padding:"4px 10px"}} onClick={()=>selectAll(false)}>☐ 全解除</button>
              </div>
            </div>

            {/* v63.1: 操作ヒント */}
            <div style={{fontSize:11,color:"#0c4a6e",background:"#f0f9ff",padding:"4px 8px",borderRadius:3,marginBottom:6}}>
              💡 各行をクリック（または ☐ をタップ）で選択・選択解除できます
            </div>

            <div style={{flex:1,overflowY:"auto",border:"1px solid #ddd",borderRadius:4}}>
              {records.length === 0 ? (
                <div style={{padding:20,textAlign:"center",color:"#888",fontSize:12}}>
                  該当する取引が ms-salon にありません
                </div>
              ) : (
                <table style={{borderCollapse:"collapse",width:"100%",fontSize:11}}>
                  <thead style={{position:"sticky",top:0,background:"#dde3f0",zIndex:1}}>
                    <tr>
                      <th style={{...TH,padding:"6px 6px",width:42}}>選択</th>
                      <th style={{...TH,padding:"6px 6px",width:60}}>日付</th>
                      <th style={{...TH,padding:"6px 6px"}}>お名前</th>
                      <th style={{...TH,padding:"6px 6px",width:90}}>金額</th>
                      <th style={{...TH,padding:"6px 6px",width:70}}>支払</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(r => {
                      const isExisting = r.squarePaymentId && existingPaymentIds.has(r.squarePaymentId);
                      const isCash = r.payment === "cash";
                      const isSelected = !!selected[r.id];
                      const amountDisplay = r.amount != null ? `¥${Number(r.amount).toLocaleString()}` : (isCash ? "—" : "—");
                      // v63.1: 視覚的に強くハイライト
                      const rowBg = isExisting ? "#f3f4f6" : (isSelected ? "#bfdbfe" : "#fff");
                      const rowBorder = isSelected && !isExisting ? "2px solid #3b82f6" : "1px solid #e5e7eb";
                      return (
                        <tr
                          key={r.id}
                          onClick={()=>{ if(!isExisting) toggleSelect(r.id); }}
                          style={{
                            background: rowBg,
                            opacity: isExisting ? 0.5 : 1,
                            cursor: isExisting ? "not-allowed" : "pointer",
                            borderLeft: rowBorder,
                            transition: "background 0.1s",
                          }}
                        >
                          <td style={{...TDC,padding:"6px 4px"}}>
                            {isExisting ? (
                              <span title="取り込み済み" style={{fontSize:14}}>⏭</span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={()=>toggleSelect(r.id)}
                                onClick={e=>e.stopPropagation()}
                                style={{width:18,height:18,cursor:"pointer"}}
                              />
                            )}
                          </td>
                          <td style={{...TD,padding:"6px 6px",fontSize:11,fontWeight:isSelected?"bold":"normal"}}>{isoDateToMD(r.date)}</td>
                          <td style={{...TD,padding:"6px 6px",fontSize:11,fontWeight:isSelected?"bold":"normal"}}>{r.name || ""}</td>
                          <td style={{...TDR,padding:"6px 6px",fontSize:11,fontWeight:isSelected?"bold":"normal"}}>{amountDisplay}</td>
                          <td style={{...TD,padding:"6px 6px",fontSize:10,color:"#555"}}>{r.paymentLabel || r.payment || ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{fontSize:10,color:"#888",marginTop:6,lineHeight:1.4}}>
              ※ カード・QR・請求書払いは ms-salon 側で金額が記録されていないため、支払欄は空欄で取り込まれます（後で Square レシートを確認して手動入力してください）。<br/>
              ※ 取り込み後の各行は通常通り編集・削除できます。
            </div>
          </>
        )}

        <div style={{display:"flex",gap:8,marginTop:10,justifyContent:"flex-end",alignItems:"center"}}>
          <button style={BTN("#374151")} onClick={onClose}>キャンセル</button>
          <button
            style={{...BTN(),opacity:(loading||error||selectedCount===0)?0.4:1,cursor:(loading||error||selectedCount===0)?"not-allowed":"pointer",fontSize:13,padding:"6px 14px",fontWeight:"bold"}}
            disabled={loading||!!error||selectedCount===0}
            onClick={handleImport}
          >
            {selectedCount === 0 ? "取り込む行を選んでください" : `📥 ${selectedCount}件を取り込む`}
          </button>
        </div>
      </div>
    </div>
  );
}

// サロン指名料アプリからの取り込みモーダル
/* v140: Ms-guarantee から現場（日付・現場名）を取り込む */
function GuaranteeImportModal({ staff, monthKey, currentJobs, onClose, onReplace }) {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setState("loading");
        const r = await fetchGuaranteeJobs(monthKey, staff.type);
        if (!alive) return;
        setRows(r);
        setState("ready");
      } catch (e) {
        if (!alive) return;
        setError(String(e && e.message ? e.message : e));
        setState("error");
      }
    })();
    return () => { alive = false; };
  }, [monthKey, staff.type]);

  const diff = diffGuaranteeJobs(currentJobs, rows, nid);
  const changes = diff.stats.updated + diff.stats.added + diff.stats.removed;
  const manualCount = (currentJobs || []).filter(j => !j.gid).length;
  const STATUS_LABEL = { same: "変更なし", updated: "更新", added: "追加" };
  const STATUS_STYLE = {
    same: { color: "#9ca3af" },
    updated: { color: "#b45309", fontWeight: 600 },
    added: { color: "#1d4ed8", fontWeight: 600 },
  };

  const doImport = () => {
    if (changes === 0) { onClose(); return; }
    onReplace(diff.result);
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 6, width: "100%", maxWidth: 720, maxHeight: "86vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center" }}>
          <b style={{ fontSize: 14 }}>Ms-guarantee から取り込み（{staff.name}・{monthKey}）</b>
          <button onClick={onClose} style={{ ...BTN("#6b7280"), fontSize: 12, marginLeft: "auto" }}>閉じる</button>
        </div>

        <div style={{ padding: "12px 16px", overflow: "auto", fontSize: 12 }}>
          {state === "loading" && <p style={{ margin: 0, color: "#6b7280" }}>読み込んでいます…（はじめに Google ログインを求められます）</p>}
          {state === "error" && (
            <p style={{ margin: 0, color: "#b91c1c" }}>
              読み込めませんでした：{error}<br />
              <span style={{ color: "#6b7280" }}>Ms-guarantee を使えるアカウント（鈴木・松本）でログインしてください。</span>
            </p>
          )}
          {state === "ready" && (
            <>
              <p style={{ margin: "0 0 10px", color: "#374151" }}>
                Ms-guarantee の {rows.length} 件が {staff.name}さんの担当です。日付と現場名だけ取り込みます（ギャランティは変わりません）。
                {manualCount > 0 && <>　手で入れた {manualCount} 行はそのまま残します。</>}
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "0 0 10px" }}>
                <span style={{ padding: "3px 8px", borderRadius: 4, background: "#f3f4f6", color: "#6b7280" }}>変更なし {diff.stats.same}件</span>
                <span style={{ padding: "3px 8px", borderRadius: 4, background: "#fef3c7", color: "#92400e" }}>更新 {diff.stats.updated}件</span>
                <span style={{ padding: "3px 8px", borderRadius: 4, background: "#dbeafe", color: "#1e40af" }}>追加 {diff.stats.added}件</span>
                <span style={{ padding: "3px 8px", borderRadius: 4, background: "#fee2e2", color: "#991b1b" }}>削除 {diff.stats.removed}件</span>
              </div>
              {rows.length === 0 ? (
                <p style={{ margin: 0, color: "#6b7280" }}>この月に、{staff.name}さんが担当の案件はありませんでした。</p>
              ) : (
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      <th style={{ textAlign: "left", padding: "4px 6px", width: 70 }}>状態</th>
                      <th style={{ textAlign: "left", padding: "4px 6px", width: 60 }}>日付</th>
                      <th style={{ textAlign: "left", padding: "4px 6px" }}>現場</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.gid} style={{ borderBottom: "1px solid #eee", opacity: diff.status.get(r.gid) === "same" ? 0.55 : 1 }}>
                        <td style={{ padding: "4px 6px", whiteSpace: "nowrap", ...(STATUS_STYLE[diff.status.get(r.gid)] || {}) }}>
                          {STATUS_LABEL[diff.status.get(r.gid)] || ""}
                        </td>
                        <td style={{ padding: "4px 6px" }}>{r.date}</td>
                        <td style={{ padding: "4px 6px" }}>{r.client}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {diff.removed.length > 0 && (
                <div style={{ marginTop: 12, padding: "8px 10px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 4, color: "#991b1b" }}>
                  <b>Ms-guarantee から消えたため、現場タブから外す行（{diff.removed.length}件）</b>
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {diff.removed.map(j => <li key={j.id}>{j.date}　{j.client}　{yenStr(j.guarantee)}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: "10px 16px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ ...BTN("#6b7280"), fontSize: 12 }}>キャンセル</button>
          <button
            onClick={doImport}
            disabled={state !== "ready" || changes === 0}
            style={{ ...BTN("#059669"), fontSize: 12, opacity: state === "ready" && changes > 0 ? 1 : 0.5 }}
          >
            {changes === 0 ? "変更はありません" : `取り込む（更新${diff.stats.updated}・追加${diff.stats.added}・削除${diff.stats.removed}）`}
          </button>
        </div>
      </div>
    </div>
  );
}

function SalonAppImportModal({ staff, monthKey, currentSalon, onClose, onReplace }) {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetchSalonAppRows(monthKey, staff.type);
        if (!alive) return;
        setRows(r);
        setState("ready");
      } catch (e) {
        if (!alive) return;
        setErr(e.code || e.message || String(e));
        setState("error");
      }
    })();
    return () => { alive = false; };
  }, [monthKey, staff.type]);

  const converted = salonAppRowsToSalon(rows, staff.type, monthKey);
  const existingApp = (currentSalon || []).filter(r => r.appRowId).length;
  const manualKept = (currentSalon || []).filter(r => !r.appRowId).length;
  const splitPreview = isSplitMonth(monthKey) && staff.type !== "takayama";
  const sumNom = converted.reduce((a, r) => a + (splitPreview ? nz(r.nomShimei)+nz(r.nomSoucho)+nz(r.nomHanbai)+nz(r.nomCancel) : nz(r.nom100)), 0);
  const sumPay = converted.reduce((a, r) => a + nz(r.payment), 0);
  const sumShimei = converted.reduce((a, r) => a + nz(r.nomShimei), 0);
  const sumSoucho = converted.reduce((a, r) => a + nz(r.nomSoucho), 0);
  const sumHanbai = converted.reduce((a, r) => a + nz(r.nomHanbai), 0);
  const sumCancel = converted.reduce((a, r) => a + nz(r.nomCancel), 0);

  // v135: 今あるサロン行をすべてアプリの内容で置き換える（1〜8月はExcelから入れた行がアプリと紐づいていないため）
  const [replaceAll, setReplaceAll] = useState(isSalonClosedMonth(monthKey) && manualKept > 0);
  // v137: 変わった行だけ更新する（変更なしの行はそのまま）
  const diff = diffSalonImport(currentSalon, converted);
  const changes = diff.stats.updated + diff.stats.added + diff.stats.removed;
  const doImport = () => {
    if (replaceAll) {
      if (manualKept > 0 &&
          !window.confirm(`今あるサロン行（手で入れた ${manualKept} 行を含む）をすべて消して、アプリの ${converted.length} 行に置き換えます。よろしいですか？`)) return;
      // 全置き換え：アプリの行に指紋を付けて入れる
      const tagged = [];
      groupByAppRow(converted).forEach(group => {
        const hash = salonGroupHash(group);
        tagged.push(...group.map(r => ({ ...r, appHash: hash })));
      });
      onReplace(tagged);
      onClose();
      return;
    }
    if (changes === 0) { onClose(); return; }
    onReplace(diff.result);
    onClose();
  };
  const STATUS_LABEL = { same: "変更なし", updated: "更新", added: "追加" };
  const STATUS_STYLE = {
    same: { color: "#9ca3af" },
    updated: { color: "#b45309", fontWeight: 600 },
    added: { color: "#1d4ed8", fontWeight: 600 },
  };

  const [y, m] = monthKey.split("-");
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 8, width: splitPreview ? 760 : 640, maxWidth: "94vw", maxHeight: "85vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "baseline", gap: 10 }}>
          <b style={{ fontSize: 14 }}>サロン指名料アプリから取り込み</b>
          <span style={{ fontSize: 12, color: "#6b7280" }}>{staff.name}　{y}年{Number(m)}月</span>
          <button onClick={onClose} style={{ marginLeft: "auto", border: 0, background: "none", fontSize: 18, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: 16, overflowY: "auto", fontSize: 12 }}>
          {state === "loading" && <p>読み込んでいます…（初回はGoogleのログイン画面が出ます）</p>}
          {state === "error" && (
            <p style={{ color: "#b91c1c" }}>読み込めませんでした（{err}）<br />
              管理者のGoogleアカウント（info@hairmakematching.jp など）でログインしてください。</p>
          )}
          {state === "ready" && rows.length === 0 && <p>この月のデータはアプリ側にまだありません。</p>}
          {state === "ready" && rows.length > 0 && (
            <>
              <p style={{ margin: "0 0 10px", color: "#374151" }}>
                アプリの {rows.length} 件 → サロンタブの {converted.length} 行。
                {manualKept > 0 && !replaceAll && <>　手で入れた {manualKept} 行はそのまま残します。</>}
                {manualKept > 0 && replaceAll && <>　手で入れた {manualKept} 行も消して、アプリの内容だけにします。</>}
              </p>
              {!replaceAll && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "0 0 10px" }}>
                  <span style={{ padding: "3px 8px", borderRadius: 4, background: "#f3f4f6", color: "#6b7280" }}>変更なし {diff.stats.same}件</span>
                  <span style={{ padding: "3px 8px", borderRadius: 4, background: "#fef3c7", color: "#92400e" }}>更新 {diff.stats.updated}件</span>
                  <span style={{ padding: "3px 8px", borderRadius: 4, background: "#dbeafe", color: "#1e40af" }}>追加 {diff.stats.added}件</span>
                  <span style={{ padding: "3px 8px", borderRadius: 4, background: "#fee2e2", color: "#991b1b" }}>削除 {diff.stats.removed}件</span>
                  {existingApp > 0 && changes === 0 && <span style={{ color: "#6b7280", alignSelf: "center" }}>前回の取り込みから変わった行はありません。</span>}
                </div>
              )}
              {manualKept > 0 && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 6, margin: "0 0 10px", padding: "8px 10px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 4, color: "#92400e", cursor: "pointer" }}>
                  <input type="checkbox" checked={replaceAll} onChange={e => setReplaceAll(e.target.checked)} style={{ marginTop: 2 }} />
                  <span>
                    今あるサロン行をすべてアプリの内容で置き換える（手で入れた行も消えます）<br />
                    <small>2026年8月以前の月はExcelから入れた行がアプリと紐づいていないため、チェックを外すと行が二重になります。</small>
                  </span>
                </label>
              )}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    {!replaceAll && <th style={{ textAlign: "left", padding: "4px 6px" }}>状態</th>}
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>日付</th>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>お客様</th>
                    <th style={{ textAlign: "right", padding: "4px 6px" }}>支払</th>
                    {splitPreview ? (
                      <>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>スタッフ指名料</th>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>早朝深夜</th>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>販売手数料</th>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>キャンセル代</th>
                      </>
                    ) : (
                      <th style={{ textAlign: "right", padding: "4px 6px" }}>指名料(100%)</th>
                    )}
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>内容</th>
                  </tr>
                </thead>
                <tbody>
                  {converted.map(r => (
                    <tr key={r.id} style={{ borderBottom: "1px solid #eee", opacity: !replaceAll && diff.status.get(r.appRowId) === "same" ? 0.55 : 1 }}>
                      {!replaceAll && (
                        <td style={{ padding: "4px 6px", whiteSpace: "nowrap", ...(STATUS_STYLE[diff.status.get(r.appRowId)] || {}) }}>
                          {STATUS_LABEL[diff.status.get(r.appRowId)] || ""}
                        </td>
                      )}
                      <td style={{ padding: "4px 6px" }}>{r.date}</td>
                      <td style={{ padding: "4px 6px" }}>{r.name}</td>
                      <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(r.payment)}</td>
                      {splitPreview ? (
                        <>
                          <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(r.nomShimei)}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(r.nomSoucho)}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(r.nomHanbai)}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(r.nomCancel)}</td>
                        </>
                      ) : (
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(r.nom100)}</td>
                      )}
                      <td style={{ padding: "4px 6px", color: "#6b7280", whiteSpace: "pre-wrap" }}>{r.memo || r.comment}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#f9fafb", fontWeight: 600 }}>
                    <td colSpan={replaceAll ? 2 : 3} style={{ padding: "4px 6px" }}>合計</td>
                    <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(sumPay)}</td>
                    {splitPreview ? (
                      <>
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(sumShimei)}</td>
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(sumSoucho)}</td>
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(sumHanbai)}</td>
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(sumCancel)}</td>
                      </>
                    ) : (
                      <td style={{ padding: "4px 6px", textAlign: "right" }}>{yenStr(sumNom)}</td>
                    )}
                    <td />
                  </tr>
                </tfoot>
              </table>
              {!replaceAll && diff.removed.length > 0 && (
                <div style={{ marginTop: 12, padding: "8px 10px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 4, color: "#991b1b" }}>
                  <b>アプリから消えたため、サロンタブから外す行（{diff.removed.length}件）</b>
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {diff.removed.map(g => (
                      <li key={g[0].appRowId}>{g[0].date}　{g[0].name}　{yenStr(g[0].payment)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: "10px 16px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ ...BTN("#6b7280"), fontSize: 12 }}>キャンセル</button>
          <button
            onClick={doImport}
            disabled={state !== "ready" || (replaceAll ? converted.length === 0 : changes === 0)}
            style={{ ...BTN("#0284c7"), fontSize: 12, opacity: state === "ready" && (replaceAll ? converted.length > 0 : changes > 0) ? 1 : 0.5 }}
          >
            {replaceAll
              ? (manualKept > 0 ? `すべて置き換えて取り込む（${converted.length}行）` : `取り込む（${converted.length}行）`)
              : (changes === 0 ? "変更はありません" : `変わった行だけ取り込む（更新${diff.stats.updated}・追加${diff.stats.added}・削除${diff.stats.removed}）`)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===================== v65: 年間支払調書（業務委託・春日井向け） =====================
// v66: 国税庁公式書式準拠

// M's株式会社の支払者情報
const MS_COMPANY_INFO = {
  postcode: "169-0075",
  address: "東京都新宿区高田馬場 4-9-11 藤和高田馬場コープ2-502号室",
  name: "M's株式会社",
  corporateNumber: "3010001206559",
  tel: "03-5937-2950",
};

// 月ごとの支払額を計算（スタッフタイプ別ロジック）
function calcMonthlyPayment(staff, monthData) {
  if (!monthData) return null;
  const sal = monthData.salary || {};
  const jobs = monthData.jobs || [];
  const salon = monthData.salon || [];
  const traffic = monthData.traffic || [];
  const rate = staff.commissionRate || 0;

  if (staff.type === "akino" || staff.type === "hirasawa") {
    // 業務委託：基本給 + 交通費合計
    const subtotalOf = (r) => {
      if (r.note === "基本給に含む") return 0;
      if (r.subtotal != null) return num(r.subtotal);
      if (num(r.roundtrip) > 0) return num(r.roundtrip);
      return num(r.oneway);
    };
    const transport = jobs.reduce((s, r) => s + subtotalOf(r), 0);
    return num(sal.基本給) + transport;
  }
  if (staff.type === "kasugai") {
    // 春日井：現場+ヘアセット + 交通費 + 指名料合計 - 講習費 + 備品代
    const totPay = jobs.reduce((s, j) => s + num(j.payment), 0);
    const totTraffic = traffic.reduce((s, t) => s + num(t.amount), 0);
    const totNom = salon.reduce((s, r) => s + num(r.nomination), 0);
    const koushuu = num(sal.講習費天引き);
    const bihin = num(sal.備品代交通費);
    return totPay + totTraffic + totNom - koushuu + bihin;
  }
  return null;
}

function YearlyReport({ staff, onUpdateStaffMeta }) {
  // 利用可能な年を月キーから抽出
  const yearsAvailable = Array.from(new Set(
    Object.keys(staff.months || {}).map(k => k.split("-")[0])
  )).sort();
  const currentYear = new Date().getFullYear().toString();
  const defaultYear = yearsAvailable.includes(currentYear) ? currentYear :
                      (yearsAvailable[yearsAvailable.length - 1] || currentYear);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  // v70: 本人控え用に固定（税務署提出は税理士判断で不要）
  const isRecipientMode = true;

  // v66: 区分・細目（編集可、staffに保存）
  const kubun = staff.payeeKubun || "ヘアメイク業務委託料";
  const saimoku = staff.payeeSaimoku || "役務提供";

  // 該当年の月データを 1-12 月で集計
  const months = [];
  let totalIncl = 0;
  for (let m = 1; m <= 12; m++) {
    const monthKey = `${selectedYear}-${String(m).padStart(2, "0")}`;
    const monthData = staff.months?.[monthKey];
    const payment = monthData ? calcMonthlyPayment(staff, monthData) : null;
    months.push({ monthKey, monthNum: m, payment, hasData: !!monthData });
    if (payment != null && payment > 0) totalIncl += payment;
  }
  // 税抜・源泉
  const totalExcl = Math.round(totalIncl / 1.1);
  const consumptionTax = totalIncl - totalExcl;
  const withhold = 0; // 源泉徴収は0円固定（業務委託・実質税込）

  // 和暦変換（簡易：2019年5月以降は令和、それ以前は平成）
  const yearNum = parseInt(selectedYear, 10);
  const reiwa = yearNum - 2018;
  const eraStr = reiwa >= 1 ? `令和${reiwa}年` : `${selectedYear}年`;

  // v66: 支払者情報（M's株式会社、固定）
  const company = MS_COMPANY_INFO;

  // PDF/JPG出力
  const loadScript = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
  const loadHtml2Canvas = async () => {
    if (!window.html2canvas) await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
    return window.html2canvas;
  };
  const loadJsPdf = async () => {
    if (!window.jspdf) await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
    return window.jspdf.jsPDF;
  };

  const handleSavePdf = async () => {
    const el = document.getElementById(`yearly-report-area-${staff.id}`);
    if (!el) { alert("出力エリアが見つかりません"); return; }
    try {
      const h2c = await loadHtml2Canvas();
      const jsPDF = await loadJsPdf();
      // v72: scale:4 で最高解像度（400dpi相当）
      const canvas = await h2c(el, {
        scale: 4,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        letterRendering: true,
        allowTaint: false,
        scrollX: 0,
        scrollY: 0,
      });
      // v67: A4横向きで出力（公式書式の比率に近づける）
      const pdf = new jsPDF({ orientation:"landscape", unit:"mm", format:"a4", compress: true });
      const pageW=297, pageH=210, margin=10;
      const imgW = pageW - margin*2;
      const imgH = (canvas.height * imgW) / canvas.width;
      if (imgH <= pageH - margin*2) {
        // v71: PNG（無損失）で埋め込み
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, margin, imgW, imgH, undefined, "FAST");
      } else {
        const pageCanvasH = Math.floor(canvas.width * (pageH - margin*2) / imgW);
        let offsetY = 0, pageIdx = 0;
        while (offsetY < canvas.height) {
          const sliceH = Math.min(pageCanvasH, canvas.height - offsetY);
          const tmp = document.createElement("canvas");
          tmp.width = canvas.width;
          tmp.height = sliceH;
          const ctx = tmp.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0,0,tmp.width,tmp.height);
          ctx.drawImage(canvas, 0, offsetY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
          const sliceMmH = (sliceH * imgW) / canvas.width;
          if (pageIdx > 0) pdf.addPage();
          // v71: PNG（無損失）で埋め込み
          pdf.addImage(tmp.toDataURL("image/png"), "PNG", margin, margin, imgW, sliceMmH, undefined, "FAST");
          offsetY += sliceH;
          pageIdx++;
        }
      }
      pdf.save(`${staff.name}_${selectedYear}年_支払調書.pdf`);
    } catch(e) {
      console.error(e);
      alert("PDF出力に失敗しました");
    }
  };

  const handleSaveJpg = async () => {
    const el = document.getElementById(`yearly-report-area-${staff.id}`);
    if (!el) { alert("出力エリアが見つかりません"); return; }
    try {
      const h2c = await loadHtml2Canvas();
      // v72: scale:4 で最高解像度（400dpi相当）
      const canvas = await h2c(el, {
        scale: 4,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        letterRendering: true,
        allowTaint: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
      const a = document.createElement("a");
      // v71: 高画質JPEG（品質0.98）
      a.href = canvas.toDataURL("image/jpeg", 0.98);
      a.download = `${staff.name}_${selectedYear}年_支払調書.jpg`;
      a.click();
    } catch(e) {
      console.error(e);
      alert("JPG出力に失敗しました");
    }
  };

  // セルスタイル（公式書式風）
  const formCell = { border:"1.5px solid #000", padding:"6px 10px", fontSize:11, verticalAlign:"middle" };
  const formLabel = { ...formCell, background:"#f0f0f0", fontSize:10, color:"#000", whiteSpace:"nowrap", textAlign:"center", fontWeight:"normal", letterSpacing:"0.05em" };

  // 縦書きラベル（公式書式の左端のラベル列）
  const verticalLabel = {
    ...formCell,
    background:"#f0f0f0",
    textAlign:"center",
    fontSize:12,
    fontWeight:"bold",
    writingMode:"vertical-rl",
    textOrientation:"upright",
    letterSpacing:"0.5em",
    padding:"16px 6px",
    width:36,
    verticalAlign:"middle",
  };

  // 個人番号・法人番号のマス目表示（13桁）
  const renderNumberBoxes = (numStr, digits=13) => {
    const arr = (numStr || "").replace(/\s|-/g, "").split("");
    return (
      <div style={{display:"flex",gap:0,justifyContent:"flex-start"}}>
        {Array.from({length:digits}).map((_, i) => (
          <div key={i} style={{
            width:18, height:22,
            border:"1px solid #999",
            borderLeft: i===0 ? "1px solid #999" : "none",
            display:"flex",alignItems:"center",justifyContent:"center",
            fontFamily:"'Courier New', monospace",
            fontSize:13,
            fontWeight:"bold",
            background: arr[i] ? "#fff" : "transparent",
            color:"#000",
          }}>
            {arr[i] || ""}
          </div>
        ))}
      </div>
    );
  };

  // 金額セル（左上「内 千円」、右に金額、最後に「円」）
  // v68: fmt() は「円」を含むため fmtN() を使い、別途「円」を1つだけ付与
  const renderAmountCell = (amount, color="#000") => (
    <td style={{...formCell,padding:"4px 10px 4px 6px",position:"relative",height:46,verticalAlign:"top"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <span style={{fontSize:9,color:"#444",letterSpacing:"0.1em"}}>内</span>
        <span style={{fontSize:9,color:"#444"}}>千円</span>
      </div>
      <div style={{display:"flex",justifyContent:"flex-end",alignItems:"flex-end",marginTop:4,gap:2}}>
        <span style={{fontSize:15,fontWeight:"bold",color:color,fontFamily:"'Courier New', monospace"}}>{fmtN(amount)}</span>
        <span style={{fontSize:11,color:"#000",marginBottom:1}}>円</span>
      </div>
    </td>
  );

  // 受給者情報入力（手動）
  const updMeta = (field, value) => {
    if (onUpdateStaffMeta) onUpdateStaffMeta(staff.id, field, value);
  };

  return (
    <div>
      {/* 操作バー */}
      <div className="no-print" style={{display:"flex",gap:8,alignItems:"center",marginBottom:12,padding:"6px 10px",background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:4,flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:"#0c4a6e",fontWeight:"bold"}}>📑 年間支払調書（本人控え用）</span>
        <label style={{fontSize:12,color:"#0c4a6e",marginLeft:"auto"}}>
          年度：
          <select value={selectedYear} onChange={e=>setSelectedYear(e.target.value)} style={{fontSize:12,marginLeft:4,padding:"3px 6px",border:"1px solid #bae6fd"}}>
            {yearsAvailable.length === 0 && <option value={currentYear}>{currentYear}年</option>}
            {yearsAvailable.map(y => <option key={y} value={y}>{y}年（令和{parseInt(y,10)-2018}年）</option>)}
            {!yearsAvailable.includes(currentYear) && <option value={currentYear}>{currentYear}年</option>}
          </select>
        </label>
        <button style={{...BTN("#0284c7"),fontSize:11,padding:"4px 10px"}} onClick={()=>window.print()}>🖨 印刷</button>
        <button style={{...BTN("#15803d"),fontSize:11,padding:"4px 10px"}} onClick={handleSaveJpg}>📷 JPG</button>
        <button style={{...BTN("#7c3aed"),fontSize:11,padding:"4px 10px"}} onClick={handleSavePdf}>📄 PDF</button>
      </div>

      {/* 受給者情報入力（編集可、印刷エリア外） */}
      <div className="no-print" style={{padding:"8px 10px",background:"#fef9c3",border:"1px solid #fde047",borderRadius:4,marginBottom:10,fontSize:11}}>
        <div style={{fontWeight:"bold",color:"#713f12",marginBottom:6}}>📝 受給者情報入力（自動保存）</div>
        <div style={{display:"flex",gap:8,marginBottom:4,alignItems:"center"}}>
          <label style={{minWidth:60}}>住所:</label>
          <input value={staff.payeeAddress||""} onChange={e=>updMeta("payeeAddress", e.target.value)}
            placeholder="〒XXX-XXXX 東京都..." style={{...INP,flex:1,fontSize:11}}/>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:4,alignItems:"center"}}>
          <label style={{minWidth:60}}>個人番号:</label>
          <input value={staff.payeeMyNumber||""} onChange={e=>updMeta("payeeMyNumber", e.target.value.replace(/\D/g,""))}
            placeholder="マイナンバー（12桁）" maxLength={12} style={{...INP,flex:1,fontSize:11,fontFamily:"monospace",letterSpacing:"0.2em"}}/>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:4,alignItems:"center"}}>
          <label style={{minWidth:60}}>区分:</label>
          <input value={staff.payeeKubun||""} onChange={e=>updMeta("payeeKubun", e.target.value)}
            placeholder="ヘアメイク業務委託料" style={{...INP,flex:1,fontSize:11}}/>
          <label style={{minWidth:50,marginLeft:8}}>細目:</label>
          <input value={staff.payeeSaimoku||""} onChange={e=>updMeta("payeeSaimoku", e.target.value)}
            placeholder="役務提供" style={{...INP,flex:1,fontSize:11}}/>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:4,alignItems:"flex-start"}}>
          <label style={{minWidth:60,marginTop:4}}>摘要:</label>
          <textarea value={staff.payeeTekiyou||""} onChange={e=>updMeta("payeeTekiyou", e.target.value)}
            placeholder="（通常は空欄。災害猶予や家族診療等、特記事項のみ記載）"
            rows={2} style={{...INP,flex:1,fontSize:11,resize:"vertical",fontFamily:"inherit"}}/>
        </div>
        <div style={{fontSize:10,color:"#713f12",marginTop:4}}>
          ※ 本書は受給者本人へ交付するため、個人番号は記載されません（番号法準拠・自動的に空欄）<br/>
          ※ 摘要欄は通常空欄。家族診療・災害猶予・賞金が金銭以外の場合などの特記事項がある時のみ記載
        </div>
      </div>

      {/* 印刷エリア（公式書式準拠・A4横向き想定） */}
      <div id={`yearly-report-area-${staff.id}`} style={{background:"#fff",padding:24,fontSize:11,maxWidth:880,margin:"0 auto"}}>
        {/* タイトル */}
        <div style={{textAlign:"center",marginBottom:14,fontSize:16,fontWeight:"bold",letterSpacing:"0.08em",position:"relative"}}>
          {eraStr}分　報酬、料金、契約金及び賞金の支払調書
          {isRecipientMode && (
            <div style={{position:"absolute",top:"50%",right:0,transform:"translateY(-50%)",fontSize:10,color:"#1e3a8a",fontWeight:"normal",border:"1px solid #1e3a8a",padding:"3px 8px",borderRadius:3,background:"#dbeafe"}}>
              本人控え
            </div>
          )}
        </div>

        {/* === 受給者情報 === */}
        <table style={{borderCollapse:"collapse",width:"100%",marginBottom:0,border:"1.5px solid #000"}}>
          <colgroup>
            <col style={{width:48}}/>
            <col style={{width:120}}/>
            <col/>
          </colgroup>
          <tbody>
            <tr>
              <td rowSpan={3} style={verticalLabel}>支払を受ける者</td>
              <td style={formLabel}>住所（居所）<br/>又は所在地</td>
              <td style={{...formCell,height:34}}>{staff.payeeAddress || ""}</td>
            </tr>
            <tr>
              <td style={formLabel}>氏 名 又 は<br/>名 称</td>
              <td style={{...formCell,fontSize:15,fontWeight:"bold",height:34}}>{staff.name}</td>
            </tr>
            <tr>
              <td style={formLabel}>個人番号又は<br/>法人番号</td>
              <td style={{...formCell,padding:"4px 10px",height:30}}>
                {/* v69: 本人控え用は番号法により個人番号は記載NG */}
                {isRecipientMode ? renderNumberBoxes("", 13) : renderNumberBoxes(staff.payeeMyNumber, 13)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* === 区分・細目・支払金額・源泉徴収税額 === */}
        <table style={{borderCollapse:"collapse",width:"100%",marginTop:-1,border:"1.5px solid #000"}}>
          <colgroup>
            <col style={{width:"22%"}}/>
            <col style={{width:"23%"}}/>
            <col style={{width:"27.5%"}}/>
            <col style={{width:"27.5%"}}/>
          </colgroup>
          <thead>
            <tr>
              <th style={{...formLabel,padding:"5px 6px",fontSize:11,letterSpacing:"0.5em"}}>区　　　分</th>
              <th style={{...formLabel,padding:"5px 6px",fontSize:11,letterSpacing:"0.5em"}}>細　　目</th>
              <th style={{...formLabel,padding:"5px 6px",fontSize:11,letterSpacing:"0.3em"}}>支　払　金　額</th>
              <th style={{...formLabel,padding:"5px 6px",fontSize:11,letterSpacing:"0.2em"}}>源 泉 徴 収 税 額</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{...formCell,textAlign:"center",height:46,fontWeight:"bold"}}>{kubun}</td>
              <td style={{...formCell,textAlign:"center",height:46,fontSize:11}}>{saimoku}</td>
              {renderAmountCell(totalExcl, "#000")}
              {renderAmountCell(0, "#15803d")}
            </tr>
          </tbody>
        </table>

        {/* 摘要欄（v68: 手動入力・デフォルト空欄） */}
        <table style={{borderCollapse:"collapse",width:"100%",marginTop:-1,marginBottom:0,border:"1.5px solid #000"}}>
          <tbody>
            <tr>
              <td style={{...formLabel,width:48,padding:"6px 4px"}}>(摘要)</td>
              <td style={{...formCell,minHeight:40,height:40,verticalAlign:"top",fontSize:10,lineHeight:1.6,padding:"6px 10px"}}>
                {staff.payeeTekiyou || ""}
              </td>
            </tr>
          </tbody>
        </table>

        {/* === 支払者情報 === */}
        <table style={{borderCollapse:"collapse",width:"100%",marginTop:-1,border:"1.5px solid #000"}}>
          <colgroup>
            <col style={{width:48}}/>
            <col style={{width:120}}/>
            <col/>
          </colgroup>
          <tbody>
            <tr>
              <td rowSpan={4} style={verticalLabel}>支払者</td>
              <td style={formLabel}>住所（居所）<br/>又は所在地</td>
              <td style={{...formCell,height:30}}>〒{company.postcode}　{company.address}</td>
            </tr>
            <tr>
              <td style={formLabel}>氏 名 又 は<br/>名 称</td>
              <td style={{...formCell,fontWeight:"bold",fontSize:13,height:30}}>{company.name}</td>
            </tr>
            <tr>
              <td style={formLabel}>個人番号又は<br/>法人番号</td>
              <td style={{...formCell,padding:"4px 10px",height:30}}>
                {renderNumberBoxes(company.corporateNumber, 13)}
              </td>
            </tr>
            <tr>
              <td style={formLabel}>(電話)</td>
              <td style={{...formCell,height:24,fontFamily:"'Courier New', monospace",letterSpacing:"0.05em"}}>{company.tel}</td>
            </tr>
          </tbody>
        </table>

        {/* 整理欄 */}
        <table style={{borderCollapse:"collapse",width:"100%",marginTop:-1,marginBottom:14,border:"1.5px solid #000"}}>
          <tbody>
            <tr>
              <td style={{...formLabel,width:48,padding:"6px 4px"}}>整 理 欄</td>
              <td style={{...formCell,width:"50%",fontSize:10,color:"#666",height:24,verticalAlign:"top"}}>①</td>
              <td style={{...formCell,fontSize:10,color:"#666",height:24,verticalAlign:"top"}}>②</td>
            </tr>
          </tbody>
        </table>

        {/* v70: 本人控え用の確定申告案内（受給者向け・簡潔版） */}
        <div style={{padding:"10px 14px",background:"#f0f9ff",border:"1px solid #93c5fd",borderRadius:4,fontSize:10,color:"#0c4a6e",lineHeight:1.7,marginBottom:14}}>
          <div style={{fontWeight:"bold",fontSize:11,marginBottom:4}}>📋 確定申告に関するご案内</div>
          ・本書は<b>{staff.name}様</b>の{eraStr}分の年間支払額をお知らせする支払調書です。<br/>
          ・M's株式会社からのお支払いは<b>税込金額の満額</b>で行っており、<b>源泉徴収は行っておりません（源泉0円）</b>。<br/>
          ・支払調書記載額：<b>税抜 {fmtN(totalExcl)}円</b>（税込合計 {fmtN(totalIncl)}円 ÷ 1.1）
        </div>

        {/* === 【参考】月別支払明細（社内管理用、印刷エリア内・支払調書下部）=== */}
        <div style={{marginTop:24,paddingTop:12,borderTop:"1px dashed #999"}}>
          <div style={{fontSize:11,fontWeight:"bold",color:"#555",marginBottom:6}}>【参考】月別支払明細（社内管理用・税務署提出時は不要）</div>
          <table style={{borderCollapse:"collapse",width:"100%",fontSize:10}}>
            <thead>
              <tr>
                <th style={{...TH,padding:"3px 6px",width:60}}>支払月</th>
                <th style={{...TH,padding:"3px 6px"}}>備考</th>
                <th style={{...TH,padding:"3px 6px",width:120}}>支払額（税込）</th>
              </tr>
            </thead>
            <tbody>
              {months.map(({monthNum, payment, hasData}, i) => (
                <tr key={monthNum} style={{background: i%2===0 ? "#fff" : "#f7f8fc"}}>
                  <td style={{...TDC,padding:"3px 6px",fontWeight:"bold"}}>{monthNum}月</td>
                  <td style={{...TD,padding:"3px 6px",color:"#666",fontSize:9}}>
                    {!hasData ? "（データ未登録）" : (payment != null && payment > 0 ? "" : "支払なし")}
                  </td>
                  <td style={{...TDR,padding:"3px 6px",fontWeight:hasData && payment > 0 ? "bold" : "normal", color: hasData && payment > 0 ? "#000" : "#bbb"}}>
                    {hasData && payment != null && payment > 0 ? fmt(payment) : "—"}
                  </td>
                </tr>
              ))}
              <tr style={{background:"#1e3a8a",color:"#fff"}}>
                <td colSpan={2} style={{...TDR,padding:"4px 6px",fontWeight:"bold",color:"#fff"}}>合計（税込）</td>
                <td style={{...TDR,padding:"4px 6px",fontWeight:"bold",color:"#fff"}}>{fmt(totalIncl)}</td>
              </tr>
              <tr style={{background:"#fef9c3"}}>
                <td colSpan={2} style={{...TDR,padding:"4px 6px",fontWeight:"bold",color:"#713f12"}}>支払調書記載額（税抜＝÷1.1）</td>
                <td style={{...TDR,padding:"4px 6px",fontWeight:"bold",color:"#713f12"}}>{fmt(totalExcl)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{textAlign:"right",fontSize:9,color:"#888",marginTop:6}}>
            作成日：{new Date().toLocaleDateString("ja-JP")}　ms-payroll
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== v64: 業務委託専用テーブル =====================
// 業務委託（秋野・平澤など）：交通費明細テーブル
// 列：日付・案件名／現場・出発駅・到着駅・片道・往復・小計・備考
function JobsTableGyoumu({ jobs, onUpdate, onDelete, onAdd, onPaste }) {
  const pasteRef = useRef(null);
  const { isMobile, isNarrow } = useViewport();
  // 小計：往復があれば往復、なければ片道
  const subtotalOf = (r) => {
    if (r.subtotal != null) return num(r.subtotal);
    if (num(r.roundtrip) > 0) return num(r.roundtrip);
    return num(r.oneway);
  };
  const total = jobs.reduce((s, r) => s + subtotalOf(r), 0);

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    e.preventDefault();
    const rows2 = parseTab(text).filter(r => r.some(c => c));
    onPaste(rows2.map(r => ({
      id: nid(),
      date: r[0] || "",
      content: r[1] || "",
      depart: r[2] || "",
      arrive: r[3] || "",
      oneway: r[4] ? Number(String(r[4]).replace(/,/g, "")) || null : null,
      roundtrip: r[5] ? Number(String(r[5]).replace(/,/g, "")) || null : null,
      note: r[7] || "",
    })));
  };

  // ========== モバイル：カード形式 ==========
  if (isMobile) {
    const S = makeOverviewStyles(isNarrow);
    const delLabel = (r) => `${r.date || '日付未設定'} ${r.content || '案件未設定'}`;
    return (
      <div>
        <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
          <b style={{fontSize:12,color:"#1e3a8a"}}>■ 交通費明細　{jobs.length}件　合計 {fmt(total)}</b>
          <button style={{...BTN(),fontSize:9,padding:"1px 6px",height:"auto",minHeight:0}} onClick={onAdd}>＋</button>
        </div>
        <input ref={pasteRef} placeholder="ペースト（日付/案件/出発/到着/片道/往復/小計/備考）" onPaste={handlePaste}
          style={{width:"100%",height:14,fontSize:8,border:"1px dashed #ccc",padding:1,color:"#bbb",boxSizing:"border-box",marginBottom:4}}/>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {jobs.map((r, i) => {
            const isBaseIncluded = r.note === "基本給に含む";
            const sub = subtotalOf(r);
            return (
              <div key={r.id || i} style={{
                border: isBaseIncluded ? "1px dashed #94a3b8" : "1px solid #d4deea",
                borderRadius: 4,
                padding: "6px 8px",
                background: isBaseIncluded ? "#f8fafc" : (i % 2 === 0 ? "#fff" : "#f8f9fc"),
                opacity: isBaseIncluded ? 0.75 : 1,
              }}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                  <span style={{display:"inline-block",minWidth:"40px",fontSize:11,color:"#1e3a8a",fontWeight:"bold",textAlign:"center",background:"#eef2ff",padding:"2px 4px",borderRadius:3}}>
                    <EditableTextCell value={r.date || ""} onChange={v=>onUpdate(i, "date", v)} style={{textAlign:"center",padding:0,fontSize:11,color:"#1e3a8a",fontWeight:"bold"}}/>
                  </span>
                  <div style={{flex:"1 1 auto",minWidth:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                    <EditableTextCell value={r.content || ""} onChange={v=>onUpdate(i, "content", v)}
                      style={{fontSize:13,fontWeight:"bold",color:"#111",minWidth:"180px",padding:"2px 3px"}}/>
                  </div>
                  <button onClick={()=>S.confirmDelete(onDelete, i, delLabel(r))}
                    style={{background:"transparent",color:"#999",border:"none",padding:"0 2px",fontSize:14,cursor:"pointer",lineHeight:1}} title="この行を削除">×</button>
                </div>
                {!isBaseIncluded && (
                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:10,paddingLeft:4,marginBottom:2}}>
                    <span style={{color:"#666",minWidth:30}}>出発</span>
                    <span style={{flex:"1 1 80px"}}><EditableTextCell value={r.depart || ""} onChange={v=>onUpdate(i, "depart", v)} style={{fontSize:10,padding:"1px 3px"}}/></span>
                    <span style={{color:"#666",minWidth:30}}>到着</span>
                    <span style={{flex:"1 1 80px"}}><EditableTextCell value={r.arrive || ""} onChange={v=>onUpdate(i, "arrive", v)} style={{fontSize:10,padding:"1px 3px"}}/></span>
                  </div>
                )}
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:10,paddingLeft:4}}>
                  <span style={{color:"#666",minWidth:30}}>片道</span>
                  <span style={{minWidth:60}}><EditableNumberCell value={r.oneway} onChange={v=>onUpdate(i,"oneway",v)} style={{textAlign:"left",padding:"1px 3px"}}/></span>
                  <span style={{color:"#666",minWidth:30}}>往復</span>
                  <span style={{minWidth:60}}><EditableNumberCell value={r.roundtrip} onChange={v=>onUpdate(i,"roundtrip",v)} style={{textAlign:"left",padding:"1px 3px"}}/></span>
                  <span style={{color:"#1e3a8a",fontWeight:"bold",marginLeft:"auto"}}>{isBaseIncluded ? "—" : fmt(sub)}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:10,paddingLeft:4,marginTop:2}}>
                  <span style={{color:"#666",minWidth:30}}>備考</span>
                  <span style={{flex:1}}><EditableTextCell value={r.note || ""} onChange={v=>onUpdate(i,"note",v)} style={{fontSize:10,padding:"1px 3px",color: isBaseIncluded ? "#94a3b8" : undefined}}/></span>
                </div>
              </div>
            );
          })}
          <div style={{border:"2px solid #1e3a8a",borderRadius:4,padding:"6px 10px",background:"#eef2ff",display:"flex",alignItems:"center",gap:12,fontWeight:"bold",color:"#1e3a8a"}}>
            <span>交通費合計</span>
            <span style={{marginLeft:"auto"}}>{fmt(total)}</span>
          </div>
        </div>
      </div>
    );
  }

  // ========== PC：テーブル ==========
  return (
    <div>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
        <b style={{fontSize:13,color:"#1e3a8a"}}>■ 交通費明細</b>
        <button style={BTN()} onClick={onAdd}>＋ 行追加</button>
        <input ref={pasteRef} placeholder="貼付：日付/案件/出発駅/到着駅/片道/往復/小計/備考" onPaste={handlePaste}
          style={{flex:1,height:22,fontSize:10,border:"1px dashed #ccc",padding:"2px 6px",color:"#aaa"}}/>
        <span style={{fontSize:12,color:"#1e3a8a",fontWeight:"bold"}}>合計 {fmt(total)}</span>
      </div>
      <table style={{borderCollapse:"collapse",width:"100%"}}>
        <thead>
          <tr>
            <th style={{...TH,width:60}}>日付</th>
            <th style={TH}>案件名／現場</th>
            <th style={{...TH,width:100}}>出発駅</th>
            <th style={{...TH,width:100}}>到着駅</th>
            <th style={{...TH,width:80}}>片道</th>
            <th style={{...TH,width:80}}>往復</th>
            <th style={{...TH,width:90}}>小計</th>
            <th style={{...TH,width:120}}>備考</th>
            <th style={{...TH,width:36}}></th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((r, i) => {
            const isBaseIncluded = r.note === "基本給に含む";
            const sub = subtotalOf(r);
            return (
              <tr key={r.id || i} style={{background: isBaseIncluded ? "#f8fafc" : (i % 2 === 0 ? "#fff" : "#f7f8fc"), opacity: isBaseIncluded ? 0.7 : 1}}>
                <td style={TD}><input value={r.date || ""} onChange={e=>onUpdate(i, "date", e.target.value)} style={INP}/></td>
                <td style={TD}><input value={r.content || ""} onChange={e=>onUpdate(i, "content", e.target.value)} style={INP}/></td>
                <td style={TD}><input value={r.depart || ""} onChange={e=>onUpdate(i, "depart", e.target.value)} style={INP} disabled={isBaseIncluded}/></td>
                <td style={TD}><input value={r.arrive || ""} onChange={e=>onUpdate(i, "arrive", e.target.value)} style={INP} disabled={isBaseIncluded}/></td>
                <td style={{...TD,padding:0}}><EditableNumberCell value={r.oneway} onChange={v=>onUpdate(i,"oneway",v)}/></td>
                <td style={{...TD,padding:0}}><EditableNumberCell value={r.roundtrip} onChange={v=>onUpdate(i,"roundtrip",v)}/></td>
                <td style={{...TDR,fontWeight:"bold",background:isBaseIncluded?"transparent":"#f0f9ff",color:isBaseIncluded?"#94a3b8":"#1e3a8a"}}>{isBaseIncluded ? "—" : fmt(sub)}</td>
                <td style={TD}><input value={r.note || ""} onChange={e=>onUpdate(i, "note", e.target.value)} style={{...INP,color:isBaseIncluded?"#94a3b8":undefined}}/></td>
                <td style={TDC}>
                  <button style={BTND} onClick={()=>{ if(window.confirm(`この行を削除しますか？\n${r.date||''} ${r.content||''}`)) onDelete(i); }}>×</button>
                </td>
              </tr>
            );
          })}
          <tr>
            <td colSpan={6} style={{...TDR,fontWeight:"bold",background:"#eef2ff",color:"#1e3a8a"}}>交通費 合計</td>
            <td style={{...TDR,fontWeight:"bold",background:"#1e3a8a",color:"#fff"}}>{fmt(total)}</td>
            <td colSpan={2} style={{background:"#eef2ff"}}></td>
          </tr>
        </tbody>
      </table>
      <div style={{fontSize:10,color:"#888",marginTop:6}}>
        ※ 備考に「基本給に含む」と入力された行は交通費合計に算入されません<br/>
        ※ 小計：往復に金額があれば往復、なければ片道の金額が使われます
      </div>
    </div>
  );
}

// v123: 業務委託（秋野・平澤）の売上明細＋利益（Excelの「案件別利益」シート準拠）
// 粗利 = 売上合計 − 固定給(原価)。交通費は参考・原価外（Excelと同じ扱い）
function UriageTable({ rows, trafficJobs, salary, onUpdate, onDelete, onAdd, onPaste }) {
  const pasteRef = useRef(null);
  const { isMobile } = useViewport();

  // 集計：区分別（ナイトセット／アシスタント等）
  const kubunGroups = {};
  rows.forEach(r => {
    const k = (r.kubun || "その他").trim() || "その他";
    if (!kubunGroups[k]) kubunGroups[k] = { count: 0, sum: 0 };
    kubunGroups[k].count++;
    kubunGroups[k].sum += num(r.amount);
  });
  const totalUriage = rows.reduce((s, r) => s + num(r.amount), 0);
  const kotei = num(salary.基本給);
  const arari = totalUriage - kotei;
  const arariRate = totalUriage > 0 ? (arari / totalUriage * 100) : null;
  // 交通費合計（参考・原価外）：交通費明細タブと同じロジック
  const trafficTotal = (trafficJobs || []).reduce((s, r) => {
    if (r.note === "基本給に含む") return s;
    if (r.subtotal != null) return s + num(r.subtotal);
    if (num(r.roundtrip) > 0) return s + num(r.roundtrip);
    return s + num(r.oneway);
  }, 0);

  // Excelペースト：案件別利益シートの月ブロックを丸ごとコピペOK
  // 日付が M/D 形式の行だけ取り込み、「小計」「売上合計」等の集計行は自動スキップ
  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    e.preventDefault();
    const rows2 = parseTab(text).filter(r => r.some(c => c));
    const dataRows = rows2.filter(r => /^\d{1,2}\/\d{1,2}$/.test(String(r[0]||"").trim()));
    onPaste(dataRows.map(r => ({
      id: nid(),
      date: String(r[0]).trim(),
      kubun: String(r[1]||"").trim(),
      client: String(r[2]||"").trim(),
      amount: r[3] != null && String(r[3]).trim() !== "" ? (Number(String(r[3]).replace(/[,円\s]/g, "")) || null) : null,
      memo: r[4] || "",
    })));
  };

  const riekiRows = (
    <table style={{borderCollapse:"collapse",width:isMobile?"100%":420,marginTop:12}}>
      <tbody>
        {Object.entries(kubunGroups).map(([k, g]) => (
          <tr key={k} style={{background:"#fff"}}>
            <td style={{...TD,fontSize:11}}>{k} 小計（{g.count}件）</td>
            <td style={{...TDR,fontSize:11}}>{fmt(g.sum)}</td>
          </tr>
        ))}
        <tr style={{background:"#eef2ff"}}>
          <td style={{...TD,fontWeight:"bold"}}>売上合計</td>
          <td style={{...TDR,fontWeight:"bold"}}>{fmt(totalUriage)}</td>
        </tr>
        <tr style={{background:"#fff"}}>
          <td style={TD}>固定給（原価）</td>
          <td style={TDR}>{fmt(kotei)}</td>
        </tr>
        <tr style={{background:"#eef2ff"}}>
          <td style={{...TD,fontWeight:"bold"}}>粗利</td>
          <td style={{...TDR,fontWeight:"bold",color:arari<0?"#dc2626":"#15803d"}}>{fmt(arari)}</td>
        </tr>
        <tr style={{background:"#fff"}}>
          <td style={{...TD,fontWeight:"bold"}}>粗利率</td>
          <td style={{...TDR,fontWeight:"bold",color:arari<0?"#dc2626":"#15803d"}}>{arariRate != null ? `${arariRate.toFixed(1)}\u00a0%` : "—"}</td>
        </tr>
        <tr style={{background:"#f8fafc"}}>
          <td style={{...TD,color:"#666",fontSize:10}}>交通費（参考・原価外）</td>
          <td style={{...TDR,color:"#666",fontSize:10}}>{fmt(trafficTotal)}</td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <div>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
        <b style={{fontSize:13,color:"#1e3a8a"}}>■ 売上明細</b>
        <button style={BTN()} onClick={onAdd}>＋ 行追加</button>
        <input ref={pasteRef} placeholder="貼付：案件別利益シートの月ブロックを丸ごとコピペOK（日付/区分/案件名/売上/備考）" onPaste={handlePaste}
          style={{flex:1,minWidth:200,height:22,fontSize:10,border:"1px dashed #ccc",padding:"2px 6px",color:"#aaa"}}/>
        <span style={{fontSize:12,color:"#1e3a8a",fontWeight:"bold"}}>売上合計 {fmt(totalUriage)}</span>
      </div>
      <div style={{fontSize:10,color:"#888",marginBottom:6}}>
        ※ Excelの「案件別利益」シートから小計・合計行ごと選択してペーストしても、日付のある行だけ自動で取り込まれます
      </div>
      <table style={{borderCollapse:"collapse",width:"100%"}}>
        <thead>
          <tr>
            <th style={{...TH,width:60}}>日付</th>
            <th style={{...TH,width:110}}>区分</th>
            <th style={TH}>案件名／現場</th>
            <th style={{...TH,width:100}}>売上(税込)</th>
            <th style={{...TH,width:130}}>備考</th>
            <th style={{...TH,width:36}}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} style={{background:i%2===0?"#fff":"#f7f8fc"}}>
              <td style={TD}><input value={r.date || ""} onChange={e=>onUpdate(i,"date",e.target.value)} style={INP}/></td>
              <td style={TD}><input value={r.kubun || ""} onChange={e=>onUpdate(i,"kubun",e.target.value)} style={INP} placeholder="ナイトセット"/></td>
              <td style={TD}><input value={r.client || ""} onChange={e=>onUpdate(i,"client",e.target.value)} style={INP}/></td>
              <td style={{...TD,padding:0}}><EditableNumberCell value={r.amount} onChange={v=>onUpdate(i,"amount",v)}/></td>
              <td style={TD}><input value={r.memo || ""} onChange={e=>onUpdate(i,"memo",e.target.value)} style={INP}/></td>
              <td style={TDC}>
                <button style={BTND} onClick={()=>{ if(window.confirm(`この行を削除しますか？\n${r.date||''} ${r.client||''}`)) onDelete(i); }}>×</button>
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} style={{...TDR,fontWeight:"bold",background:"#eef2ff",color:"#1e3a8a"}}>売上 合計</td>
            <td style={{...TDR,fontWeight:"bold",background:"#1e3a8a",color:"#fff"}}>{fmt(totalUriage)}</td>
            <td colSpan={2} style={{background:"#eef2ff"}}></td>
          </tr>
        </tbody>
      </table>
      <div style={{marginTop:16}}>
        <b style={{fontSize:13,color:"#1e3a8a"}}>■ 利益（会社用・従業員向け出力には表示されません）</b>
        {riekiRows}
      </div>
    </div>
  );
}

// 業務委託（秋野・平澤）の給与計算・明細
function SalaryGyoumu({ staff, monthKey, jobs, salary, onUpd }) {
  const subtotalOf = (r) => {
    if (r.note === "基本給に含む") return 0;
    if (r.subtotal != null) return num(r.subtotal);
    if (num(r.roundtrip) > 0) return num(r.roundtrip);
    return num(r.oneway);
  };
  const transportTotal = jobs.reduce((s, r) => s + subtotalOf(r), 0);
  const basic = num(salary?.基本給);
  const total = basic + transportTotal;

  return (
    <div>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,padding:"8px 10px",background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:4}}>
        <b style={{fontSize:13,color:"#0c4a6e"}}>■ {staff.name}　{monthKey} 業務委託 給与明細</b>
      </div>

      <table style={{borderCollapse:"collapse",width:"100%",maxWidth:520}}>
        <tbody>
          <tr>
            <td style={{...TD,fontWeight:"bold",background:"#dde3f0",width:160}}>基本給</td>
            <td style={{...TDR,padding:0}}>
              <EditableNumberCell value={salary?.基本給} onChange={v=>onUpd("基本給", v)} style={{padding:"6px 8px",fontWeight:"bold"}}/>
            </td>
          </tr>
          <tr>
            <td style={{...TD,fontWeight:"bold",background:"#dde3f0"}}>交通費合計（自動）</td>
            <td style={{...TDR,fontWeight:"bold",color:"#1e3a8a",background:"#f0f9ff"}}>{fmt(transportTotal)}</td>
          </tr>
          <tr>
            <td style={{...TD,fontWeight:"bold",background:"#1e3a8a",color:"#fff",fontSize:13}}>支払額</td>
            <td style={{...TDR,fontWeight:"bold",background:"#1e3a8a",color:"#fff",fontSize:14}}>{fmt(total)}</td>
          </tr>
        </tbody>
      </table>

      <div style={{marginTop:12,padding:"8px 10px",background:"#fef9c3",border:"1px solid #fde047",borderRadius:4,fontSize:11,color:"#713f12"}}>
        <b>📋 契約条件</b><br/>
        ・契約形態：業務委託（実質税込・源泉徴収対象外）<br/>
        ・基本給：180,000円/月（2026年4月以降の固定給制）<br/>
        ・交通費：実費精算
        {staff.type === "akino" && <><br/>・2月・3月は案件単位精算（基本給欄にヘアセット報酬を入力）</>}
        {staff.type === "hirasawa" && <><br/>・2月・3月は交通費のみ（基本給は0）</>}
      </div>
    </div>
  );
}

function AddStaffDlg({ onAdd, onClose }) {
  const [f, setF] = useState({name:"",no:"",type:"takeuchi",rate:0.65});
  const types=[
    {value:"takeuchi", label:"正社員（竹内型：50%歩合・保険料調整）"},
    {value:"fukazawa", label:"正社員（深澤型：50%歩合・保険料調整）"},
    {value:"aomi",     label:"正社員（青海型：実数値・還元率35%）"},
    {value:"takayama", label:"正社員（髙山型：1000円/現場固定）"},
    {value:"matsumoto",label:"役員（松本型：役員報酬固定）"},
    {value:"kasugai",  label:"業務委託（春日井型：現場利益計算）"},
    {value:"akino",    label:"業務委託（秋野型：固定給+交通費）"},
    {value:"hirasawa", label:"業務委託（平澤型：固定給+交通費）"},
  ];
  return (
    <div style={{position:"fixed",inset:0,background:"#00000055",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}>
      <div style={{background:"#fff",border:"1px solid #888",padding:20,minWidth:360}}>
        <b style={{fontSize:14}}>スタッフ追加</b>
        <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:8}}>
          <label style={{fontSize:12}}>氏名<input value={f.name} onChange={e=>setF(p=>({...p,name:e.target.value}))} style={{...INP,marginLeft:8,width:180}}/></label>
          <label style={{fontSize:12}}>従業員番号<input value={f.no} onChange={e=>setF(p=>({...p,no:e.target.value}))} style={{...INP,marginLeft:8,width:60}}/></label>
          <label style={{fontSize:12}}>種別<br/>
            <select value={f.type} onChange={e=>setF(p=>({...p,type:e.target.value}))} style={{fontSize:12,marginTop:4,width:"100%"}}>
              {types.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label style={{fontSize:12}}>還元率<input type="number" step="0.01" min="0" max="1" value={f.rate} onChange={e=>setF(p=>({...p,rate:Number(e.target.value)}))} style={{...INPR,marginLeft:8,width:60}}/></label>
        </div>
        <div style={{display:"flex",gap:8,marginTop:12}}>
          <button style={BTN()} onClick={()=>{if(f.name){onAdd(f);onClose();}}}>追加</button>
          <button style={BTN("#374151")} onClick={onClose}>キャンセル</button>
        </div>
      </div>
    </div>
  );
}

// ===================== 初期データ組み立て =====================

// Firestore データと defaultStaff をマージする関数
// 月データが空（jobs/salonが空配列）の場合、defaultStaff側のデータで補完する
// ユーザー入力を尊重しつつ、未入力の月にはソース側の初期データを反映させる
function mergeWithDefault(firestoreStaffList, defaultStaffList) {
  return defaultStaffList.map(defaultS => {
    const fsStaff = firestoreStaffList.find(s => s.id === defaultS.id);
    if (!fsStaff) {
      // Firestoreに存在しないスタッフはdefaultStaffから追加
      return defaultS;
    }
    // Firestoreに存在する場合は月ごとにマージ
    const mergedMonths = {};
    // defaultStaffの全月をチェック
    const allMonthKeys = new Set([
      ...Object.keys(defaultS.months || {}),
      ...Object.keys(fsStaff.months || {})
    ]);
    for (const monthKey of allMonthKeys) {
      const defM = defaultS.months?.[monthKey];
      const fsM = fsStaff.months?.[monthKey];
      if (!fsM) {
        // Firestoreにその月がない → defaultStaffから
        mergedMonths[monthKey] = defM;
        continue;
      }
      if (!defM) {
        // defaultStaffにその月がない → Firestoreをそのまま
        mergedMonths[monthKey] = fsM;
        continue;
      }
      // 両方ある場合：jobs/salon/trafficが空配列ならdefaultStaffから補完
      const fsJobs    = Array.isArray(fsM.jobs)    ? fsM.jobs    : [];
      const fsSalon   = Array.isArray(fsM.salon)   ? fsM.salon   : [];
      const fsTraffic = Array.isArray(fsM.traffic) ? fsM.traffic : null;
      // v125【重要】Firestore の月データを土台にする（...fsM）
      //   旧実装は jobs/salon/salary/traffic/ins だけを拾って新オブジェクトを作っていたため、
      //   uriage（売上明細）や adjustJobs（青海の調整分）など、後から追加したフィールドが
      //   読み込みのたびに丸ごと消えていた。必ず fsM を展開してから個別項目を上書きすること。
      const merged = {
        ...defM,   // defaultStaff 側の未知フィールドも拾う
        ...fsM,    // Firestore を優先（uriage / adjustJobs などはここで保持される）
        jobs:   fsJobs.length  === 0 && Array.isArray(defM.jobs)  && defM.jobs.length  > 0 ? defM.jobs  : fsJobs,
        salon:  fsSalon.length === 0 && Array.isArray(defM.salon) && defM.salon.length > 0 ? defM.salon : fsSalon,
        // salary は Firestore のデータを優先するが、null/undefinedのキーはdefaultStaffから補完
        salary: { ...(defM.salary || {}), ...(fsM.salary || {}) },
      };
      // traffic（春日井のみ）：Firestoreにフィールドが存在し空配列でなければ採用、それ以外はdefault
      if (fsTraffic !== null && fsTraffic.length > 0) {
        merged.traffic = fsTraffic;
      } else if (Array.isArray(defM.traffic)) {
        merged.traffic = defM.traffic;
      }
      // ins (保険料調整) は Firestore のデータを優先
      if (fsM.ins !== undefined) merged.ins = fsM.ins;
      else if (defM.ins !== undefined) merged.ins = defM.ins;
      mergedMonths[monthKey] = merged;
    }
    return { ...defaultS, ...fsStaff, months: mergedMonths };
  });
}

const defaultStaff = [
  { id:"matsumoto", name:"松本江里子", no:"05", type:"matsumoto", commissionRate:0.65, 出張手当単価:3500,
    months:{
      "2026-01":{ jobs:MATSUMOTO_JAN_JOBS, salon:MATSUMOTO_JAN_SALON, salary:MATSUMOTO_JAN_SAL },
      "2026-02":{ jobs:MATSUMOTO_FEB_JOBS, salon:MATSUMOTO_FEB_SALON, salary:MATSUMOTO_FEB_SAL },
      "2026-03":{ jobs:MATSUMOTO_MAR_JOBS, salon:MATSUMOTO_MAR_SALON, salary:MATSUMOTO_MAR_SAL },
    }},
  { id:"takeuchi", name:"竹内奈奈", no:"01", type:"takeuchi", commissionRate:0.65, 出張手当単価:2000,
    months:{
      "2026-01":{ jobs:TAKEUCHI_JAN_JOBS, salon:TAKEUCHI_JAN_SALON, salary:TAKEUCHI_JAN_SAL, ins:TAKEUCHI_JAN_INS },
      "2026-02":{ jobs:TAKEUCHI_FEB_JOBS, salon:TAKEUCHI_FEB_SALON, salary:TAKEUCHI_FEB_SAL, ins:TAKEUCHI_FEB_INS },
      "2026-03":{ jobs:TAKEUCHI_MAR_JOBS, salon:TAKEUCHI_MAR_SALON, salary:TAKEUCHI_MAR_SAL, ins:TAKEUCHI_MAR_INS },
    }},
  { id:"fukazawa", name:"深澤莉恵", no:"02", type:"fukazawa", commissionRate:0.65, 出張手当単価:1500,
    months:{
      "2026-01":{ jobs:FUKAZAWA_JAN_JOBS, salon:FUKAZAWA_JAN_SALON, salary:FUKAZAWA_JAN_SAL, ins:FUKAZAWA_JAN_INS },
      "2026-02":{ jobs:FUKAZAWA_FEB_JOBS, salon:FUKAZAWA_FEB_SALON, salary:FUKAZAWA_FEB_SAL, ins:FUKAZAWA_FEB_INS },
      "2026-03":{ jobs:FUKAZAWA_MAR_JOBS, salon:FUKAZAWA_MAR_SALON, salary:FUKAZAWA_MAR_SAL, ins:FUKAZAWA_MAR_INS },
    }},
  { id:"aomi", name:"青海美莉", no:"03", type:"aomi", commissionRate:0.35, 出張手当単価:800,
    months:{
      "2026-01":{ jobs:AOMI_JAN_JOBS, salon:AOMI_JAN_SALON, salary:AOMI_JAN_SAL },
      "2026-02":{ jobs:AOMI_FEB_JOBS, salon:AOMI_FEB_SALON, salary:AOMI_FEB_SAL },
      "2026-03":{ jobs:AOMI_MAR_JOBS, salon:AOMI_MAR_SALON, salary:AOMI_MAR_SAL },
    }},
  { id:"takayama", name:"髙山愛梨", no:"04", type:"takayama", commissionRate:0,
    months:{
      "2026-01":{ jobs:TAKAYAMA_JAN_JOBS, salon:TAKAYAMA_JAN_SALON, salary:TAKAYAMA_JAN_SAL },
      "2026-02":{ jobs:TAKAYAMA_FEB_JOBS, salon:TAKAYAMA_FEB_SALON, salary:TAKAYAMA_FEB_SAL },
      "2026-03":{ jobs:TAKAYAMA_MAR_JOBS, salon:TAKAYAMA_MAR_SALON, salary:TAKAYAMA_MAR_SAL },
    }},
  // v64: 業務委託（秋野桃葉）
  { id:"akino", name:"秋野桃葉", no:"07", type:"akino", commissionRate:0,
    months:{
      "2026-02":{ jobs:AKINO_FEB_JOBS, salon:[], salary:AKINO_FEB_SAL },
      "2026-03":{ jobs:AKINO_MAR_JOBS, salon:[], salary:AKINO_MAR_SAL },
      "2026-04":{ jobs:AKINO_APR_JOBS, salon:[], salary:AKINO_APR_SAL },
    }},
  // v64: 業務委託（平澤梨央）
  { id:"hirasawa", name:"平澤梨央", no:"08", type:"hirasawa", commissionRate:0,
    months:{
      "2026-02":{ jobs:HIRASAWA_FEB_JOBS, salon:[], salary:HIRASAWA_FEB_SAL },
      "2026-03":{ jobs:HIRASAWA_MAR_JOBS, salon:[], salary:HIRASAWA_MAR_SAL },
      "2026-04":{ jobs:HIRASAWA_APR_JOBS, salon:[], salary:HIRASAWA_APR_SAL },
    }},
  { id:"kasugai", name:"春日井慕丹", no:"06", type:"kasugai", commissionRate:0,
    months:{
      "2026-01":{ jobs:KASUGAI_JAN_JOBS, salon:KASUGAI_JAN_SALON, traffic:KASUGAI_JAN_TRAFFIC, salary:KASUGAI_JAN_SAL },
      "2026-02":{ jobs:KASUGAI_FEB_JOBS, salon:KASUGAI_FEB_SALON, traffic:KASUGAI_FEB_TRAFFIC, salary:KASUGAI_FEB_SAL },
      "2026-03":{ jobs:KASUGAI_MAR_JOBS, salon:KASUGAI_MAR_SALON, traffic:KASUGAI_MAR_TRAFFIC, salary:KASUGAI_MAR_SAL },
    }},
];

// ===================== メインアプリ =====================
export default function App() {
  const [staffList, setStaffList] = useState(defaultStaff);
  const [selId, setSelId] = useState("matsumoto");
  const [selMonth, setSelMonth] = useState("2026-01");
  const [showAdd, setShowAdd] = useState(false);
  const [newMonthInput, setNewMonthInput] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [editNameVal, setEditNameVal] = useState("");
  const [saveStatus, setSaveStatus] = useState(""); // "saving" | "saved" | "error"
  const isInitialLoad = useRef(true);

  // Firestore からデータ読み込み（初回）＋ defaultStaff とマージ
  useEffect(() => {
    const loadData = async () => {
      try {
        const snap = await getDoc(doc(db, "payroll", "data"));
        if (snap.exists()) {
          const d = snap.data();
          if (d.staffList && d.staffList.length > 0) {
            // マージ：Firestoreのデータを基本に、不足分をdefaultStaffで補完
            let merged = mergeWithDefault(d.staffList, defaultStaff);
            // v44: 青海の guaranteeShared を一括クリア（自動値に戻す）
            // 過去に手動で誤入力された値をリセットするマイグレーション
            // フラグ migrations.v44_aomi_shared_cleared が無ければ実行
            const migrations = d.migrations || {};
            if (!migrations.v44_aomi_shared_cleared) {
              merged = merged.map(staff => {
                if (staff.id !== "aomi") return staff;
                const newMonths = {};
                for (const [mk, mv] of Object.entries(staff.months || {})) {
                  newMonths[mk] = {
                    ...mv,
                    jobs: (mv.jobs || []).map(j => {
                      const { guaranteeShared, ...rest } = j;
                      return rest;
                    }),
                  };
                }
                return { ...staff, months: newMonths };
              });
              // マイグレーション完了フラグを保存
              try {
                await setDoc(doc(db, "payroll", "data"), {
                  staffList: merged,
                  migrations: { ...migrations, v44_aomi_shared_cleared: true },
                  updatedAt: Date.now(),
                });
                console.log("v44マイグレーション完了: 青海guaranteeSharedクリア");
              } catch (e) {
                console.error("v44マイグレーション保存失敗:", e);
              }
            }
            setStaffList(merged);
            setSaveStatus("loaded");
            setTimeout(()=>setSaveStatus(""),2000);
          }
        }
      } catch (e) {
        console.error("読み込み失敗:", e);
        setSaveStatus("error");
      } finally {
        isInitialLoad.current = false;
      }
    };
    loadData();
  }, []);

  // staffList 変更時に自動保存
  // v40: 自動保存（debounce 800ms、ページ離脱前は強制保存）
  useEffect(() => {
    if (isInitialLoad.current) return;
    setSaveStatus("saving");
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      try {
        await setDoc(doc(db, "payroll", "data"), { staffList, updatedAt: Date.now() });
        setSaveStatus("saved");
        setTimeout(()=>setSaveStatus(""),3000);
      } catch (e) {
        console.error("保存失敗:", e);
        setSaveStatus("error");
      }
    }, 800);
  }, [staffList]);

  // v40: ページ離脱時に未保存があれば警告
  useEffect(() => {
    const handler = (e) => {
      if (saveStatus === "saving") {
        e.preventDefault();
        e.returnValue = "保存中です。本当に離れますか？";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveStatus]);

  const sel = staffList.find(s=>s.id===selId);

  const handleUpdate = useCallback((staffId,monthKey,action,payload)=>{
    setStaffList(prev=>prev.map(staff=>{
      if(staff.id!==staffId) return staff;
      const months={...staff.months};
      if(action==="init"){
        // v64: 業務委託（秋野・平澤）は別テンプレート
        if (staff.type === "akino" || staff.type === "hirasawa") {
          months[monthKey] = { jobs:[], salon:[], salary:{基本給:180000} };
        } else {
          months[monthKey]={ jobs:[], salon:[], salary:{基本給:213091,出張手当:0,出張日数:"",保険料調整:0,役職手当:0,交通費:0,通勤費:0,オシャレ手当:0,年末調整還付金:null,健康保険料:0,厚生年金保険料:0,雇用保険料:0,所得税:0,住民税:0} };
        }
        return {...staff,months};
      }
      const md={...months[monthKey]};
      if(action==="job_upd"){const j=[...md.jobs];j[payload.i]={...j[payload.i],[payload.f]:payload.v};md.jobs=j;}
      if(action==="job_del"){md.jobs=md.jobs.filter((_,i)=>i!==payload.i);}
      if(action==="job_add"){
        // v64: 業務委託は交通費明細スキーマ
        if (staff.type === "akino" || staff.type === "hirasawa") {
          md.jobs=[...md.jobs,{id:nid(),date:"",content:"",depart:"",arrive:"",oneway:null,roundtrip:null,note:""}];
        } else {
          md.jobs=[...md.jobs,{id:nid(),date:"",client:"",guarantee:null,payment:null}];
        }
      }
      if(action==="job_paste"){md.jobs=[...md.jobs,...payload.rows];}
      if(action==="job_reorder"){md.jobs=payload.rows;}   // v113: ドラッグ並べ替え
      if(action==="salon_reorder"){md.salon=payload.rows;} // v113: ドラッグ並べ替え
      // v98: 青海専用 - 調整分（adjustJobs）の管理
      if(action==="adjust_upd"){const a=[...(md.adjustJobs||[])];a[payload.i]={...a[payload.i],[payload.f]:payload.v};md.adjustJobs=a;}
      if(action==="adjust_del"){md.adjustJobs=(md.adjustJobs||[]).filter((_,i)=>i!==payload.i);}
      if(action==="adjust_add"){md.adjustJobs=[...(md.adjustJobs||[]),{id:nid(),date:"",client:"調整分",guarantee:null}];}
      // v50: 髙山の共有用タブ独立編集
      if(action==="jobs_shared_upd"){const j=[...(md.jobsShared||[])];j[payload.i]={...j[payload.i],[payload.f]:payload.v};md.jobsShared=j;}
      if(action==="jobs_shared_del"){md.jobsShared=(md.jobsShared||[]).filter((_,i)=>i!==payload.i);}
      if(action==="jobs_shared_add"){md.jobsShared=[...(md.jobsShared||[]),{id:nid(),date:"",client:""}];}
      if(action==="jobs_shared_copy_from_jobs"){md.jobsShared=(md.jobs||[]).map(j=>({id:nid(),date:j.date||"",client:j.client||""}));}
      if(action==="jobs_shared_paste"){md.jobsShared=[...(md.jobsShared||[]),...payload.rows];}
      // v123: 業務委託（秋野・平澤）の売上明細
      if(action==="uriage_upd"){const u=[...(md.uriage||[])];u[payload.i]={...u[payload.i],[payload.f]:payload.v};md.uriage=u;}
      if(action==="uriage_del"){md.uriage=(md.uriage||[]).filter((_,i)=>i!==payload.i);}
      if(action==="uriage_add"){md.uriage=[...(md.uriage||[]),{id:nid(),date:"",kubun:"ナイトセット",client:"",amount:null,memo:""}];}
      if(action==="uriage_paste"){md.uriage=[...(md.uriage||[]),...payload.rows];}
      if(action==="salon_upd"){const r=[...md.salon];r[payload.i]={...r[payload.i],[payload.f]:payload.v};md.salon=r;}
      if(action==="salon_del"){md.salon=md.salon.filter((_,i)=>i!==payload.i);}
      if(action==="salon_add"){md.salon=[...md.salon,{id:nid(),date:"",name:"",payment:null,discount:null,nom100:null,nomShimei:null,nomSoucho:null,nomHanbai:null,nomCancel:null,nomination:null,memo:""}];}
      if(action==="salon_paste"){md.salon=[...md.salon,...payload.rows];}
      if(action==="traffic_upd"){const r=[...(md.traffic||[])];r[payload.i]={...r[payload.i],[payload.f]:payload.v};md.traffic=r;}
      if(action==="traffic_del"){md.traffic=(md.traffic||[]).filter((_,i)=>i!==payload.i);}
      if(action==="traffic_add"){md.traffic=[...(md.traffic||[]),{id:nid(),date:"",memo:"",amount:null}];}
      if(action==="traffic_paste"){md.traffic=[...(md.traffic||[]),...payload.rows];}
      if(action==="sal_upd"){md.salary={...md.salary,[payload.k]:payload.v};}
      if(action==="ins_upd"){const r=[...(md.ins||[])];r[payload.i]={...r[payload.i],[payload.f]:payload.v};md.ins=r;}
      if(action==="ins_del"){md.ins=(md.ins||[]).filter((_,i)=>i!==payload.i);}
      if(action==="ins_add"){md.ins=[...(md.ins||[]),{label:"",amount:null,sub:null}];}
      months[monthKey]=md;
      return {...staff,months};
    }));
  },[]);

  const handleAddStaff=(f)=>{
    const id=f.name.replace(/\s/g,"_")+"_"+Date.now();
    setStaffList(prev=>[...prev,{id,name:f.name,no:f.no,type:f.type,commissionRate:f.rate,months:{}}]);
    setSelId(id);
  };
  const handleDelStaff=(id)=>{
    if(!window.confirm("このスタッフを削除しますか？"))return;
    setStaffList(prev=>prev.filter(s=>s.id!==id));
    if(selId===id)setSelId(staffList.find(s=>s.id!==id)?.id);
  };
  // v66: スタッフのメタ情報（住所・個人番号・区分・細目）を更新
  const handleUpdateStaffMeta = useCallback((staffId, field, value) => {
    setStaffList(prev => prev.map(s => {
      if (s.id !== staffId) return s;
      // v81: 保険料調整スケジュールの行単位更新
      if (field === "insSchedule_upd") {
        const cur = s.insSchedule || [];
        const next = [...cur];
        next[value.i] = {...(next[value.i]||{}), [value.f]: value.v};
        return {...s, insSchedule: next};
      }
      // v81: 保険料調整スケジュールの初期化（既存上書き）
      if (field === "insSchedule_init") {
        return {...s, insSchedule: defaultInsSchedule()};
      }
      // 従来のフィールド単純代入
      return {...s, [field]: value};
    }));
  }, []);

  const availMonths = sel ? Object.keys(sel.months).sort() : [];
  const monthLabel=(m)=>{const [y,mo]=m.split("-");return `${y}年${Number(mo)}月`;};

  return (
    <div style={{fontFamily:"'Meiryo UI','Hiragino Kaku Gothic Pro',Meiryo,sans-serif",fontSize:13,background:"#fff",color:"#000",minHeight:"100vh"}}>
      <style>{GLOBAL_CSS}</style>
      <style>{`
        /* === グローバル：フォント・タイポグラフィ統一 === */
        body, html { font-family: 'Meiryo UI','Hiragino Kaku Gothic Pro',Meiryo,sans-serif; }
        button { font-family: inherit; }
        input, select, textarea { font-family: inherit; box-sizing: border-box; }
        input[type="number"] { -moz-appearance: textfield; }
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

        @media (max-width: 640px) {
          /* モバイル：基本フォントとタッチエリア最適化 */
          input, select, button { font-size: 14px !important; min-height: 32px; }
          input[type="number"], input[type="text"], select { padding: 4px 6px !important; }
          .tab-btn { padding: 6px 10px !important; font-size: 12px !important; }
          /* タブが横並びで切れないようスクロール許可 */
          .tab-bar { overflow-x: auto; -webkit-overflow-scrolling: touch; white-space: nowrap; }
        }
        @media (max-width: 600px) {
          .main-layout { flex-direction: column !important; }
          .sidebar { width: 100% !important; border-right: none !important; border-bottom: 1px solid #ccc !important; padding: 4px 6px !important; }
          .sidebar-staff-list { display: flex !important; flex-wrap: wrap !important; gap: 3px !important; width: 100% !important; }
          .sidebar-label { font-size: 10px !important; margin-bottom: 3px !important; }
          .staff-item { flex: 0 0 auto !important; }
          .add-month-wrap { flex-wrap: wrap !important; gap: 3px !important; }
          .add-month-wrap input[type="month"] { width: 110px !important; }
        }
        /* 印刷時：A4×ページ自動改ページ（v28→v29で本格対応予定） */
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>
      <div style={{background:"#1e3a8a",color:"#fff",padding:"7px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
        <b className="main-title" style={{fontSize:14}}>M's株式会社　給与管理システム　2026年</b>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{
            fontSize:12,
            padding:"3px 10px",
            borderRadius:4,
            fontWeight:"bold",
            background: saveStatus==="saving" ? "#f59e0b" : saveStatus==="saved" ? "#10b981" : saveStatus==="error" ? "#dc2626" : saveStatus==="loaded" ? "#3b82f6" : "transparent",
            color: saveStatus ? "#fff" : "transparent",
            transition:"all 0.3s",
            minWidth: 90,
            textAlign:"center",
          }}>
            {saveStatus==="saving" && "💾 保存中..."}
            {saveStatus==="saved" && "✅ 保存済み"}
            {saveStatus==="loaded" && "📂 読込完了"}
            {saveStatus==="error" && "⚠️ 保存エラー"}
            {!saveStatus && "　"}
          </span>
          <button
            onClick={async () => {
              setSaveStatus("saving");
              try {
                await setDoc(doc(db, "payroll", "data"), { staffList, updatedAt: Date.now() });
                setSaveStatus("saved");
                setTimeout(()=>setSaveStatus(""),3000);
              } catch (e) {
                console.error("手動保存失敗:", e);
                setSaveStatus("error");
              }
            }}
            style={{background:"#10b981",color:"#fff",border:"none",padding:"4px 12px",borderRadius:4,cursor:"pointer",fontSize:12,fontWeight:"bold"}}
            title="今すぐFirestoreに保存"
          >💾 手動保存</button>
        </div>
      </div>
      <div className="main-layout" style={{display:"flex",minHeight:"calc(100vh - 38px)"}}>
        {/* サイドバー */}
        <div className="sidebar" style={{width:165,borderRight:"1px solid #ccc",background:"#f5f5f5",padding:8,flexShrink:0}}>
          <div className="sidebar-label" style={{fontSize:11,fontWeight:"bold",color:"#333",marginBottom:6}}>スタッフ</div>
          <div className="sidebar-staff-list">
            {staffList.map(s=>(
              <div key={s.id} className="staff-item" style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"3px 5px",background:selId===s.id?"#dbeafe":"transparent",cursor:"pointer",border:selId===s.id?"1px solid #93c5fd":"1px solid #e0e0e0",marginBottom:2,borderRadius:3,minWidth:0,flex:"1 1 auto",maxWidth:"100%"}} onClick={()=>setSelId(s.id)}>
                {selId===s.id && editingName
                  ? <input
                      autoFocus
                      value={editNameVal}
                      onChange={e=>setEditNameVal(e.target.value)}
                      onBlur={()=>{
                        if(editNameVal.trim()) setStaffList(prev=>prev.map(st=>st.id===s.id?{...st,name:editNameVal.trim()}:st));
                        setEditingName(false);
                      }}
                      onKeyDown={e=>{if(e.key==="Enter"){if(editNameVal.trim())setStaffList(prev=>prev.map(st=>st.id===s.id?{...st,name:editNameVal.trim()}:st));setEditingName(false);}if(e.key==="Escape")setEditingName(false);}}
                      onClick={e=>e.stopPropagation()}
                      style={{fontSize:12,width:"100%",border:"1px solid #93c5fd",padding:"1px 4px"}}
                    />
                  : <span style={{fontSize:12,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} onDoubleClick={e=>{e.stopPropagation();setEditNameVal(s.name);setEditingName(true);}}>{s.name}</span>
                }
                <button style={{...BTND,padding:"1px 5px",fontSize:10,marginLeft:2,flexShrink:0}} onClick={e=>{e.stopPropagation();handleDelStaff(s.id);}}>×</button>
              </div>
            ))}
          </div>
          <div style={{fontSize:10,color:"#888",marginTop:4,marginBottom:4}}>※名前をダブルクリックで編集</div>
          <button style={{...BTN(),width:"100%",marginTop:4,fontSize:12}} onClick={()=>setShowAdd(true)}>＋ スタッフ追加</button>
        </div>
        {/* メイン */}
        <div className="main-content" style={{flex:1,padding:12,overflow:"auto",minWidth:0}}>
          {sel ? (
            <>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                <b style={{fontSize:14}}>{sel.name}</b>
                <span style={{fontSize:11,color:"#555"}}>従業員番号 {sel.no}　{sel.commissionRate>0?`還元率 ${(sel.commissionRate*100).toFixed(0)}%`:sel.type==="kasugai"?"業務委託":(sel.type==="akino"||sel.type==="hirasawa")?"業務委託（固定給+交通費）":sel.type==="takayama"?"固定1,000円/現場":"役員"}</span>
              </div>
              <div className="add-month-wrap" style={{display:"flex",gap:3,flexWrap:"wrap",alignItems:"center",marginBottom:8}}>
                {availMonths.map(m=>(
                  <button key={m} className="month-btn tab-btn" style={TAB(selMonth===m)} onClick={()=>setSelMonth(m)}>{monthLabel(m)}</button>
                ))}
                <input type="month" value={newMonthInput} onChange={e=>setNewMonthInput(e.target.value)} style={{...INP,width:120,fontSize:11}}/>
                <button style={{...BTN(),fontSize:11}} onClick={()=>{if(newMonthInput){handleUpdate(sel.id,newMonthInput,"init",null);setSelMonth(newMonthInput);setNewMonthInput("");}}}>＋月追加</button>
              </div>
              {sel.months[selMonth] && (
                <div style={{fontSize:12,fontWeight:"bold",marginBottom:8,borderBottom:"2px solid #1e3a8a",paddingBottom:3}}>
                  {monthLabel(selMonth)}　{sel.name}　歩合表
                </div>
              )}
              <StaffView staff={sel} monthKey={selMonth} onUpdate={handleUpdate} onUpdateStaffMeta={handleUpdateStaffMeta}/>
            </>
          ) : (
            <div style={{color:"#666",padding:20}}>スタッフを選択してください</div>
          )}
        </div>
      </div>
      {showAdd && <AddStaffDlg onAdd={handleAddStaff} onClose={()=>setShowAdd(false)}/>}
    </div>
  );
}
